import * as i0 from '@angular/core';
import { Component, isDevMode, EventEmitter, Input, Output, NgModule } from '@angular/core';
import { Fullscreen, Button, WidgetHelper, ButtonGroup, CalendarField, Checkbox, ChipView, ColorField, ColorPicker, Combo, ConstraintTypePicker, Container, CycleResolutionPopup, DateField, DatePicker, DateTimeField, DependencyTypePicker, DisplayField, DurationField, EffortField, EndDateField, EventColorField, EventColorPicker, FieldFilterPicker, FieldFilterPickerGroup, FileField, FilePicker, FilterField, GanttTaskEditor, StringHelper, Grid, GridBase, GridFieldFilterPicker, GridFieldFilterPickerGroup, GroupBar, Label, List, Menu, ModelCombo, NumberField, PagingToolbar, Panel, PasswordField, ProjectCombo, ProjectModel, Radio, RadioGroup, ResourceCombo, ResourceFilter, ResourceHistogram, ResourceUtilization, Scheduler, SchedulerBase, SchedulerDatePicker, SchedulerPro, SchedulerProBase, SchedulerTaskEditor, SchedulingDirectionPicker, SchedulingIssueResolutionPopup, SchedulingModePicker, Slider, SlideToggle, Splitter, StartDateField, TabPanel, TextAreaField, TextAreaPickerField, TextField, TimeField, Timeline, TimelineHistogram, TimePicker, Toolbar, TreeCombo, TreeGrid, UndoRedo, VersionGrid, ViewPresetCombo, Widget, YearPicker } from '@bryntum/schedulerpro';

/**
 * Angular wrapper for Bryntum Fullscreen button
 */
class BryntumFullscreenButtonComponent {
    constructor(element) {
        this.elementRef = element;
    }
    /**
     * Initializes component
     */
    ngOnInit() {
        if (!Fullscreen.enabled) {
            return;
        }
        this.button = new Button({
            appendTo: this.elementRef.nativeElement,
            icon: 'b-icon b-icon-fullscreen',
            tooltip: 'Fullscreen',
            onClick() {
                if (Fullscreen.enabled) {
                    if (!Fullscreen.isFullscreen) {
                        Fullscreen.request(document.body);
                    }
                    else {
                        Fullscreen.exit();
                    }
                }
            }
        });
    }
    /**
     * Destroys component
     */
    ngOnDestroy() {
        if (this.button) {
            this.button.destroy();
        }
    }
}
BryntumFullscreenButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFullscreenButtonComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumFullscreenButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumFullscreenButtonComponent, selector: "bryntum-fullscreen-button", ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFullscreenButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-fullscreen-button',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

class WrapperHelper {
    /**
     * Development warning. Showed when environment is set to 'development'.
     */
    static devWarning(clsName, msg) {
        // @ts-ignore
        if (window.bryntum && window.bryntum.isTestEnv || isDevMode()) {
            console.warn(`Bryntum${clsName}Component development warning!\n${msg}\n` +
                'Please check Angular integration guide: https://bryntum.com/products/schedulerpro/docs/guide/SchedulerPro/integration/angular/guide');
        }
    }
    /**
     * Development warning for container parameter.
     */
    static devWarningContainer(clsName, containerParam) {
        WrapperHelper.devWarning(clsName, `Using "${containerParam}" parameter for configuration is not recommended.\n` +
            'Widget is placed automatically inside it\'s container element' +
            `Solution: remove "${containerParam}" parameter from configuration.`);
    }
    /**
     * Development warning for config parameter.
     */
    static devWarningConfigProp(clsName, prop) {
        WrapperHelper.devWarning(clsName, `Using "${prop}" parameter for configuration is not recommended.\n` +
            `Solution: Use separate parameter for each "${prop}" value to enable reactive updates of the API instance`);
    }
    /**
     * Development warning for config property.
     */
    static devWarningUpdateProp(clsName, prop) {
        WrapperHelper.devWarning(clsName, `"${prop}" is a static config option for component constructor only. No runtime changes are supported!`);
    }
    /**
     * Applies property value to Bryntum config or instance.
     * @param {Object} configOrInstance target object
     * @param {string} prop property name
     * @param {any} value value
     * @param {Boolean} isConfig config setting mode
     */
    static applyPropValue(configOrInstance, prop, value, isConfig = true) {
        if (prop === 'project') {
            // Allow use ProjectModel component as project
            if (value && typeof value === 'object') {
                configOrInstance[prop] = value.instance || value;
            }
        }
        else if (prop === 'features' && typeof value === 'object') {
            Object.keys(value).forEach(key => WrapperHelper.applyPropValue(configOrInstance, `${key}Feature`, value[key], isConfig));
        }
        else if (prop === 'config' && typeof value === 'object') {
            Object.keys(value).forEach(key => WrapperHelper.applyPropValue(configOrInstance, key, value[key], isConfig));
        }
        else if (prop === 'columns' && !isConfig) {
            configOrInstance['columns'].data = value;
        }
        else if (prop.endsWith('Feature')) {
            const features = configOrInstance['features'], featureName = prop.replace('Feature', '');
            if (isConfig) {
                features[featureName] = value;
            }
            else {
                const feature = features[featureName];
                if (feature) {
                    feature.setConfig(value);
                }
            }
        }
        else {
            configOrInstance[prop] = value;
        }
    }
}

/**
 * Angular wrapper for Bryntum Button
 */
class BryntumButtonComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeShowMenu = new EventEmitter();
        this.onBeforeToggle = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onClick = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToggle = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumButtonComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumButtonComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumButtonComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumButtonComponent.instanceClass = Button;
BryntumButtonComponent.instanceName = 'Button';
BryntumButtonComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onBeforeShowMenu',
    'onBeforeToggle',
    'onCatchAll',
    'onClick',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onToggle'
];
BryntumButtonComponent.bryntumFeatureNames = [];
BryntumButtonComponent.bryntumConfigs = BryntumButtonComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'badge',
    'behaviorType',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'color',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'href',
    'html',
    'htmlCls',
    'icon',
    'iconAlign',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'menu',
    'menuIcon',
    'minHeight',
    'minWidth',
    'monitorResize',
    'owner',
    'positioned',
    'pressed',
    'pressedIcon',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'supportsPressedClick',
    'tab',
    'tag',
    'target',
    'text',
    'textAlign',
    'title',
    'toggleable',
    'toggleGroup',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumButtonComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'htmlCls',
    'ignoreParentReadOnly',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'supportsPressedClick',
    'tab',
    'tag',
    'textAlign',
    'title',
    'ui',
    'weight'
];
BryntumButtonComponent.bryntumProps = BryntumButtonComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'behaviorType',
    'cls',
    'color',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'href',
    'html',
    'icon',
    'iconAlign',
    'id',
    'insertBefore',
    'insertFirst',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'menu',
    'menuIcon',
    'minHeight',
    'minWidth',
    'pressed',
    'pressedIcon',
    'readOnly',
    'scrollable',
    'target',
    'text',
    'toggleable',
    'toggleGroup',
    'tooltip',
    'width',
    'x',
    'y'
]);
BryntumButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumButtonComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumButtonComponent, selector: "bryntum-button", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", supportsPressedClick: "supportsPressedClick", tab: "tab", tag: "tag", textAlign: "textAlign", title: "title", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", behaviorType: "behaviorType", cls: "cls", color: "color", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", href: "href", html: "html", icon: "icon", iconAlign: "iconAlign", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", menu: "menu", menuIcon: "menuIcon", minHeight: "minHeight", minWidth: "minWidth", pressed: "pressed", pressedIcon: "pressedIcon", readOnly: "readOnly", scrollable: "scrollable", target: "target", text: "text", toggleable: "toggleable", toggleGroup: "toggleGroup", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onBeforeShowMenu: "onBeforeShowMenu", onBeforeToggle: "onBeforeToggle", onCatchAll: "onCatchAll", onClick: "onClick", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onToggle: "onToggle" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-button',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], supportsPressedClick: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], behaviorType: [{
                type: Input
            }], cls: [{
                type: Input
            }], color: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], href: [{
                type: Input
            }], html: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconAlign: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], menu: [{
                type: Input
            }], menuIcon: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], pressed: [{
                type: Input
            }], pressedIcon: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], target: [{
                type: Input
            }], text: [{
                type: Input
            }], toggleable: [{
                type: Input
            }], toggleGroup: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeShowMenu: [{
                type: Output
            }], onBeforeToggle: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onClick: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToggle: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Demo Header
 */
class BryntumDemoHeaderComponent {
    constructor(element) {
        // Convert:
        // https://bryntum.com/products/schedulerpro/examples-scheduler/frameworks/angular/basic/dist/basic/
        // https://bryntum.com/products/schedulerpro/examples/frameworks/angular/basic/dist/basic/
        // To:
        // https://bryntum.com/products/schedulerpro/examples/#example-examples-scheduler-frameworks-angular-basic
        // https://bryntum.com/products/schedulerpro/examples/#example-frameworks-angular-basic
        this.title = document.title.split(' - ')[1] || document.title;
        this.link = '';
        this.elementRef = element;
        const match = /(.*?\/)(examples.*?\/frameworks\/.*?)\/dist/.exec(document.location.href);
        this.link = match ? `${match[1]}examples/#example-${match[2].replace(/\//gm, '-').replace('examples-frameworks', 'frameworks')}` : '#';
    }
}
BryntumDemoHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDemoHeaderComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumDemoHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumDemoHeaderComponent, selector: "bryntum-demo-header", ngImport: i0, template: `
        <header class="demo-header">
            <div id="title-container">
                <a id="title" href="{{ link }}">
                    <h1>{{ title }}</h1>
                </a>
            </div>
            <ng-content></ng-content>
            <div id="tools">
                <bryntum-button
                    text="Download Trial"
                    href="https://bryntum.com/download/?product=schedulerpro"
                    cls="b-green b-raised"
                ></bryntum-button>
                <bryntum-fullscreen-button></bryntum-fullscreen-button>
            </div>
        </header>
`, isInline: true, components: [{ type: BryntumButtonComponent, selector: "bryntum-button", inputs: ["adopt", "align", "anchor", "ariaDescription", "ariaLabel", "bubbleEvents", "callOnFunctions", "centered", "config", "constrainTo", "contentElementCls", "defaultBindProperty", "dock", "draggable", "floating", "hideAnimation", "htmlCls", "ignoreParentReadOnly", "listeners", "localeClass", "localizable", "localizableProperties", "maskDefaults", "masked", "monitorResize", "owner", "positioned", "preventTooltipOnTouch", "relayStoreEvents", "ripple", "rootElement", "scrollAction", "showAnimation", "showTooltipWhenDisabled", "supportsPressedClick", "tab", "tag", "textAlign", "title", "ui", "weight", "alignSelf", "appendTo", "ariaLive", "badge", "behaviorType", "cls", "color", "content", "dataset", "disabled", "extraData", "flex", "height", "hidden", "href", "html", "icon", "iconAlign", "id", "insertBefore", "insertFirst", "margin", "maxHeight", "maximizeOnMobile", "maxWidth", "menu", "menuIcon", "minHeight", "minWidth", "pressed", "pressedIcon", "readOnly", "scrollable", "target", "text", "toggleable", "toggleGroup", "tooltip", "width", "x", "y", "accessibility", "anchorSize"], outputs: ["onAction", "onBeforeDestroy", "onBeforeHide", "onBeforeShow", "onBeforeShowMenu", "onBeforeToggle", "onCatchAll", "onClick", "onDestroy", "onFocusIn", "onFocusOut", "onHide", "onPaint", "onReadOnly", "onRecompose", "onResize", "onShow", "onToggle"] }, { type: BryntumFullscreenButtonComponent, selector: "bryntum-fullscreen-button" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDemoHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-demo-header',
                    template: `
        <header class="demo-header">
            <div id="title-container">
                <a id="title" href="{{ link }}">
                    <h1>{{ title }}</h1>
                </a>
            </div>
            <ng-content></ng-content>
            <div id="tools">
                <bryntum-button
                    text="Download Trial"
                    href="https://bryntum.com/download/?product=schedulerpro"
                    cls="b-green b-raised"
                ></bryntum-button>
                <bryntum-fullscreen-button></bryntum-fullscreen-button>
            </div>
        </header>
`
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

/**
 * Angular wrapper for Bryntum ButtonGroup
 */
class BryntumButtonGroupComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onClick = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToggle = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumButtonGroupComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumButtonGroupComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumButtonGroupComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumButtonGroupComponent.instanceClass = ButtonGroup;
BryntumButtonGroupComponent.instanceName = 'ButtonGroup';
BryntumButtonGroupComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onClick',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onToggle'
];
BryntumButtonGroupComponent.bryntumFeatureNames = [];
BryntumButtonGroupComponent.bryntumConfigs = BryntumButtonGroupComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'color',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'strictRecordMapping',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'toggleGroup',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumButtonGroupComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'color',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'toggleGroup',
    'ui',
    'weight'
];
BryntumButtonGroupComponent.bryntumProps = BryntumButtonGroupComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'strictRecordMapping',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumButtonGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumButtonGroupComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumButtonGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumButtonGroupComponent, selector: "bryntum-button-group", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", color: "color", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", textContent: "textContent", title: "title", toggleGroup: "toggleGroup", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", values: "values" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onClick: "onClick", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onToggle: "onToggle" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumButtonGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-button-group',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], color: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], title: [{
                type: Input
            }], toggleGroup: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], values: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onClick: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToggle: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum CalendarField
 */
class BryntumCalendarFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumCalendarFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumCalendarFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumCalendarFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumCalendarFieldComponent.instanceClass = CalendarField;
BryntumCalendarFieldComponent.instanceName = 'CalendarField';
BryntumCalendarFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onTrigger'
];
BryntumCalendarFieldComponent.bryntumFeatureNames = [];
BryntumCalendarFieldComponent.bryntumConfigs = BryntumCalendarFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'editable',
    'emptyText',
    'encodeFilterParams',
    'extraData',
    'filterOnEnter',
    'filterOperator',
    'filterParamName',
    'filterSelected',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minChars',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggerAction',
    'triggers',
    'ui',
    'validateFilter',
    'validateOnInput',
    'value',
    'valueField',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumCalendarFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'defaultBindProperty',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'emptyText',
    'encodeFilterParams',
    'filterOnEnter',
    'filterParamName',
    'filterSelected',
    'floating',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minChars',
    'minLength',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'triggerAction',
    'ui',
    'validateFilter',
    'validateOnInput',
    'valueField',
    'weight'
];
BryntumCalendarFieldComponent.bryntumProps = BryntumCalendarFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'filterOperator',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'picker',
    'readOnly',
    'scrollable',
    'store',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumCalendarFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumCalendarFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumCalendarFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumCalendarFieldComponent, selector: "bryntum-calendar-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", caseSensitive: "caseSensitive", centered: "centered", chipView: "chipView", clearable: "clearable", clearTextOnPickerHide: "clearTextOnPickerHide", clearTextOnSelection: "clearTextOnSelection", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", createOnUnmatched: "createOnUnmatched", defaultBindProperty: "defaultBindProperty", displayField: "displayField", displayValueRenderer: "displayValueRenderer", dock: "dock", draggable: "draggable", emptyText: "emptyText", encodeFilterParams: "encodeFilterParams", filterOnEnter: "filterOnEnter", filterParamName: "filterParamName", filterSelected: "filterSelected", floating: "floating", hideAnimation: "hideAnimation", hidePickerOnSelect: "hidePickerOnSelect", hideTrigger: "hideTrigger", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inlinePicker: "inlinePicker", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", items: "items", keyStrokeChangeDelay: "keyStrokeChangeDelay", keyStrokeFilterDelay: "keyStrokeFilterDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listCls: "listCls", listeners: "listeners", listItemTpl: "listItemTpl", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minChars: "minChars", minLength: "minLength", monitorResize: "monitorResize", multiSelect: "multiSelect", multiValueSeparator: "multiValueSeparator", name: "name", overlayAnchor: "overlayAnchor", owner: "owner", pickerAlignElement: "pickerAlignElement", pickerWidth: "pickerWidth", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", primaryFilter: "primaryFilter", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", triggerAction: "triggerAction", ui: "ui", validateFilter: "validateFilter", validateOnInput: "validateOnInput", valueField: "valueField", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", filterOperator: "filterOperator", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", picker: "picker", readOnly: "readOnly", store: "store", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumCalendarFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-calendar-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], caseSensitive: [{
                type: Input
            }], centered: [{
                type: Input
            }], chipView: [{
                type: Input
            }], clearable: [{
                type: Input
            }], clearTextOnPickerHide: [{
                type: Input
            }], clearTextOnSelection: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], createOnUnmatched: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], displayValueRenderer: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], encodeFilterParams: [{
                type: Input
            }], filterOnEnter: [{
                type: Input
            }], filterParamName: [{
                type: Input
            }], filterSelected: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hidePickerOnSelect: [{
                type: Input
            }], hideTrigger: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inlinePicker: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], items: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], keyStrokeFilterDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listCls: [{
                type: Input
            }], listeners: [{
                type: Input
            }], listItemTpl: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minChars: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], multiValueSeparator: [{
                type: Input
            }], name: [{
                type: Input
            }], overlayAnchor: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerWidth: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], primaryFilter: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerAction: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateFilter: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], valueField: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], filterOperator: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], picker: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], store: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Checkbox
 */
class BryntumCheckboxComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeChange = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onClick = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumCheckboxComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumCheckboxComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumCheckboxComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumCheckboxComponent.instanceClass = Checkbox;
BryntumCheckboxComponent.instanceName = 'Checkbox';
BryntumCheckboxComponent.bryntumEvents = [
    'onAction',
    'onBeforeChange',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onClick',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumCheckboxComponent.bryntumFeatureNames = [];
BryntumCheckboxComponent.bryntumConfigs = BryntumCheckboxComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoCollapse',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'checked',
    'checkedValue',
    'clearable',
    'cls',
    'color',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'text',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'uncheckedValue',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumCheckboxComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoCollapse',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'checkedValue',
    'clearable',
    'color',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'text',
    'textAlign',
    'title',
    'ui',
    'uncheckedValue',
    'validateOnInput',
    'weight'
];
BryntumCheckboxComponent.bryntumProps = BryntumCheckboxComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'checked',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'name',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumCheckboxComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumCheckboxComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumCheckboxComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumCheckboxComponent, selector: "bryntum-checkbox", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoCollapse: "autoCollapse", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", checkedValue: "checkedValue", clearable: "clearable", color: "color", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", text: "text", textAlign: "textAlign", title: "title", ui: "ui", uncheckedValue: "uncheckedValue", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", checked: "checked", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", name: "name", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeChange: "onBeforeChange", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onClick: "onClick", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumCheckboxComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-checkbox',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoCollapse: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], checkedValue: [{
                type: Input
            }], clearable: [{
                type: Input
            }], color: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], text: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], uncheckedValue: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], checked: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], name: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeChange: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onClick: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum ChipView
 */
class BryntumChipViewComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeItem = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onItem = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumChipViewComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumChipViewComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumChipViewComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumChipViewComponent.instanceClass = ChipView;
BryntumChipViewComponent.instanceName = 'ChipView';
BryntumChipViewComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeItem',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onItem',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelectionChange',
    'onShow',
    'onToggleGroup'
];
BryntumChipViewComponent.bryntumFeatureNames = [];
BryntumChipViewComponent.bryntumConfigs = BryntumChipViewComponent.bryntumFeatureNames.concat([
    'activateOnMouseover',
    'adopt',
    'align',
    'alignSelf',
    'allowGroupSelect',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'closable',
    'closeHandler',
    'cls',
    'collapsibleGroups',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'height',
    'hidden',
    'hideAnimation',
    'html',
    'htmlCls',
    'iconTpl',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'items',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'selectAllItem',
    'selected',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'tab',
    'tag',
    'textAlign',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumChipViewComponent.bryntumConfigsOnly = [
    'activateOnMouseover',
    'adopt',
    'align',
    'allowGroupSelect',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'closable',
    'closeHandler',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'displayField',
    'dock',
    'draggable',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'hideAnimation',
    'htmlCls',
    'iconTpl',
    'ignoreParentReadOnly',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'selectAllItem',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'ui',
    'weight'
];
BryntumChipViewComponent.bryntumProps = BryntumChipViewComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'collapsibleGroups',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'items',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'multiSelect',
    'readOnly',
    'scrollable',
    'selected',
    'store',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'width',
    'x',
    'y'
]);
BryntumChipViewComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumChipViewComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumChipViewComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumChipViewComponent, selector: "bryntum-chip-view", inputs: { activateOnMouseover: "activateOnMouseover", adopt: "adopt", align: "align", allowGroupSelect: "allowGroupSelect", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearSelectionOnEmptySpaceClick: "clearSelectionOnEmptySpaceClick", closable: "closable", closeHandler: "closeHandler", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", displayField: "displayField", dock: "dock", draggable: "draggable", floating: "floating", getItemCls: "getItemCls", getItemStyle: "getItemStyle", groupHeaderTpl: "groupHeaderTpl", hideAnimation: "hideAnimation", htmlCls: "htmlCls", iconTpl: "iconTpl", ignoreParentReadOnly: "ignoreParentReadOnly", itemTpl: "itemTpl", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", selectAllItem: "selectAllItem", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", collapsibleGroups: "collapsibleGroups", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", multiSelect: "multiSelect", readOnly: "readOnly", scrollable: "scrollable", selected: "selected", store: "store", title: "title", toggleAllIfCtrlPressed: "toggleAllIfCtrlPressed", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeItem: "onBeforeItem", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onItem: "onItem", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelectionChange: "onSelectionChange", onShow: "onShow", onToggleGroup: "onToggleGroup" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumChipViewComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-chip-view',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { activateOnMouseover: [{
                type: Input
            }], adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], allowGroupSelect: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearSelectionOnEmptySpaceClick: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeHandler: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], getItemCls: [{
                type: Input
            }], getItemStyle: [{
                type: Input
            }], groupHeaderTpl: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], iconTpl: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemTpl: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], selectAllItem: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], collapsibleGroups: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selected: [{
                type: Input
            }], store: [{
                type: Input
            }], title: [{
                type: Input
            }], toggleAllIfCtrlPressed: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeItem: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onItem: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum ColorField
 */
class BryntumColorFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumColorFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumColorFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumColorFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumColorFieldComponent.instanceClass = ColorField;
BryntumColorFieldComponent.instanceName = 'ColorField';
BryntumColorFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumColorFieldComponent.bryntumFeatureNames = [];
BryntumColorFieldComponent.bryntumConfigs = BryntumColorFieldComponent.bryntumFeatureNames.concat([
    'addNoColorItem',
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'colors',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'pickerAlignElement',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumColorFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minLength',
    'monitorResize',
    'name',
    'owner',
    'pickerAlignElement',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weight'
];
BryntumColorFieldComponent.bryntumProps = BryntumColorFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'addNoColorItem',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'colors',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumColorFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumColorFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumColorFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumColorFieldComponent, selector: "bryntum-color-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minLength: "minLength", monitorResize: "monitorResize", name: "name", owner: "owner", pickerAlignElement: "pickerAlignElement", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weight: "weight", addNoColorItem: "addNoColorItem", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", colors: "colors", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", editable: "editable", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumColorFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-color-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], addNoColorItem: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], colors: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], editable: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum ColorPicker
 */
class BryntumColorPickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeItem = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onItem = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumColorPickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumColorPickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumColorPickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumColorPickerComponent.instanceClass = ColorPicker;
BryntumColorPickerComponent.instanceName = 'ColorPicker';
BryntumColorPickerComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeItem',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onItem',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelectionChange',
    'onShow',
    'onToggleGroup'
];
BryntumColorPickerComponent.bryntumFeatureNames = [];
BryntumColorPickerComponent.bryntumConfigs = BryntumColorPickerComponent.bryntumFeatureNames.concat([
    'activateOnMouseover',
    'addNoColorItem',
    'adopt',
    'align',
    'alignSelf',
    'allowGroupSelect',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'cls',
    'collapsibleGroups',
    'colors',
    'colorSelected',
    'columns',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'height',
    'hidden',
    'hideAnimation',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'items',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'selectAllItem',
    'selected',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'tab',
    'tag',
    'textAlign',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumColorPickerComponent.bryntumConfigsOnly = [
    'activateOnMouseover',
    'adopt',
    'align',
    'allowGroupSelect',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'colorSelected',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'displayField',
    'dock',
    'draggable',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'hideAnimation',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'selectAllItem',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'ui',
    'weight'
];
BryntumColorPickerComponent.bryntumProps = BryntumColorPickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'addNoColorItem',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'collapsibleGroups',
    'colors',
    'columns',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'items',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'multiSelect',
    'readOnly',
    'scrollable',
    'selected',
    'store',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'width',
    'x',
    'y'
]);
BryntumColorPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumColorPickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumColorPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumColorPickerComponent, selector: "bryntum-color-picker", inputs: { activateOnMouseover: "activateOnMouseover", adopt: "adopt", align: "align", allowGroupSelect: "allowGroupSelect", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearSelectionOnEmptySpaceClick: "clearSelectionOnEmptySpaceClick", colorSelected: "colorSelected", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", displayField: "displayField", dock: "dock", draggable: "draggable", floating: "floating", getItemCls: "getItemCls", getItemStyle: "getItemStyle", groupHeaderTpl: "groupHeaderTpl", hideAnimation: "hideAnimation", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemTpl: "itemTpl", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", selectAllItem: "selectAllItem", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", ui: "ui", weight: "weight", addNoColorItem: "addNoColorItem", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", collapsibleGroups: "collapsibleGroups", colors: "colors", columns: "columns", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", multiSelect: "multiSelect", readOnly: "readOnly", scrollable: "scrollable", selected: "selected", store: "store", title: "title", toggleAllIfCtrlPressed: "toggleAllIfCtrlPressed", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeItem: "onBeforeItem", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onItem: "onItem", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelectionChange: "onSelectionChange", onShow: "onShow", onToggleGroup: "onToggleGroup" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumColorPickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-color-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { activateOnMouseover: [{
                type: Input
            }], adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], allowGroupSelect: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearSelectionOnEmptySpaceClick: [{
                type: Input
            }], colorSelected: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], getItemCls: [{
                type: Input
            }], getItemStyle: [{
                type: Input
            }], groupHeaderTpl: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemTpl: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], selectAllItem: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], addNoColorItem: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], collapsibleGroups: [{
                type: Input
            }], colors: [{
                type: Input
            }], columns: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selected: [{
                type: Input
            }], store: [{
                type: Input
            }], title: [{
                type: Input
            }], toggleAllIfCtrlPressed: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeItem: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onItem: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Combo
 */
class BryntumComboComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumComboComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumComboComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumComboComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumComboComponent.instanceClass = Combo;
BryntumComboComponent.instanceName = 'Combo';
BryntumComboComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onTrigger'
];
BryntumComboComponent.bryntumFeatureNames = [];
BryntumComboComponent.bryntumConfigs = BryntumComboComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'editable',
    'emptyText',
    'encodeFilterParams',
    'extraData',
    'filterOnEnter',
    'filterOperator',
    'filterParamName',
    'filterSelected',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minChars',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggerAction',
    'triggers',
    'ui',
    'validateFilter',
    'validateOnInput',
    'value',
    'valueField',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumComboComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'defaultBindProperty',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'emptyText',
    'encodeFilterParams',
    'filterOnEnter',
    'filterParamName',
    'filterSelected',
    'floating',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minChars',
    'minLength',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'triggerAction',
    'ui',
    'validateFilter',
    'validateOnInput',
    'valueField',
    'weight'
];
BryntumComboComponent.bryntumProps = BryntumComboComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'filterOperator',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'picker',
    'readOnly',
    'scrollable',
    'store',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumComboComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumComboComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumComboComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumComboComponent, selector: "bryntum-combo", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", caseSensitive: "caseSensitive", centered: "centered", chipView: "chipView", clearable: "clearable", clearTextOnPickerHide: "clearTextOnPickerHide", clearTextOnSelection: "clearTextOnSelection", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", createOnUnmatched: "createOnUnmatched", defaultBindProperty: "defaultBindProperty", displayField: "displayField", displayValueRenderer: "displayValueRenderer", dock: "dock", draggable: "draggable", emptyText: "emptyText", encodeFilterParams: "encodeFilterParams", filterOnEnter: "filterOnEnter", filterParamName: "filterParamName", filterSelected: "filterSelected", floating: "floating", hideAnimation: "hideAnimation", hidePickerOnSelect: "hidePickerOnSelect", hideTrigger: "hideTrigger", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inlinePicker: "inlinePicker", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", items: "items", keyStrokeChangeDelay: "keyStrokeChangeDelay", keyStrokeFilterDelay: "keyStrokeFilterDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listCls: "listCls", listeners: "listeners", listItemTpl: "listItemTpl", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minChars: "minChars", minLength: "minLength", monitorResize: "monitorResize", multiSelect: "multiSelect", multiValueSeparator: "multiValueSeparator", name: "name", overlayAnchor: "overlayAnchor", owner: "owner", pickerAlignElement: "pickerAlignElement", pickerWidth: "pickerWidth", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", primaryFilter: "primaryFilter", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", triggerAction: "triggerAction", ui: "ui", validateFilter: "validateFilter", validateOnInput: "validateOnInput", valueField: "valueField", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", filterOperator: "filterOperator", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", picker: "picker", readOnly: "readOnly", store: "store", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumComboComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-combo',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], caseSensitive: [{
                type: Input
            }], centered: [{
                type: Input
            }], chipView: [{
                type: Input
            }], clearable: [{
                type: Input
            }], clearTextOnPickerHide: [{
                type: Input
            }], clearTextOnSelection: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], createOnUnmatched: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], displayValueRenderer: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], encodeFilterParams: [{
                type: Input
            }], filterOnEnter: [{
                type: Input
            }], filterParamName: [{
                type: Input
            }], filterSelected: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hidePickerOnSelect: [{
                type: Input
            }], hideTrigger: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inlinePicker: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], items: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], keyStrokeFilterDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listCls: [{
                type: Input
            }], listeners: [{
                type: Input
            }], listItemTpl: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minChars: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], multiValueSeparator: [{
                type: Input
            }], name: [{
                type: Input
            }], overlayAnchor: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerWidth: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], primaryFilter: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerAction: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateFilter: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], valueField: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], filterOperator: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], picker: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], store: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum ConstraintTypePicker
 */
class BryntumConstraintTypePickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumConstraintTypePickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumConstraintTypePickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumConstraintTypePickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumConstraintTypePickerComponent.instanceClass = ConstraintTypePicker;
BryntumConstraintTypePickerComponent.instanceName = 'ConstraintTypePicker';
BryntumConstraintTypePickerComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onTrigger'
];
BryntumConstraintTypePickerComponent.bryntumFeatureNames = [];
BryntumConstraintTypePickerComponent.bryntumConfigs = BryntumConstraintTypePickerComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'editable',
    'emptyText',
    'encodeFilterParams',
    'extraData',
    'filterOnEnter',
    'filterOperator',
    'filterParamName',
    'filterSelected',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minChars',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggerAction',
    'triggers',
    'ui',
    'validateFilter',
    'validateOnInput',
    'value',
    'valueField',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumConstraintTypePickerComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'defaultBindProperty',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'emptyText',
    'encodeFilterParams',
    'filterOnEnter',
    'filterParamName',
    'filterSelected',
    'floating',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minChars',
    'minLength',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'triggerAction',
    'ui',
    'validateFilter',
    'validateOnInput',
    'valueField',
    'weight'
];
BryntumConstraintTypePickerComponent.bryntumProps = BryntumConstraintTypePickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'filterOperator',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'picker',
    'readOnly',
    'scrollable',
    'store',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumConstraintTypePickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumConstraintTypePickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumConstraintTypePickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumConstraintTypePickerComponent, selector: "bryntum-constraint-type-picker", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", caseSensitive: "caseSensitive", centered: "centered", chipView: "chipView", clearable: "clearable", clearTextOnPickerHide: "clearTextOnPickerHide", clearTextOnSelection: "clearTextOnSelection", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", createOnUnmatched: "createOnUnmatched", defaultBindProperty: "defaultBindProperty", displayField: "displayField", displayValueRenderer: "displayValueRenderer", dock: "dock", draggable: "draggable", emptyText: "emptyText", encodeFilterParams: "encodeFilterParams", filterOnEnter: "filterOnEnter", filterParamName: "filterParamName", filterSelected: "filterSelected", floating: "floating", hideAnimation: "hideAnimation", hidePickerOnSelect: "hidePickerOnSelect", hideTrigger: "hideTrigger", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inlinePicker: "inlinePicker", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", items: "items", keyStrokeChangeDelay: "keyStrokeChangeDelay", keyStrokeFilterDelay: "keyStrokeFilterDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listCls: "listCls", listeners: "listeners", listItemTpl: "listItemTpl", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minChars: "minChars", minLength: "minLength", monitorResize: "monitorResize", multiSelect: "multiSelect", multiValueSeparator: "multiValueSeparator", name: "name", overlayAnchor: "overlayAnchor", owner: "owner", pickerAlignElement: "pickerAlignElement", pickerWidth: "pickerWidth", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", primaryFilter: "primaryFilter", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", triggerAction: "triggerAction", ui: "ui", validateFilter: "validateFilter", validateOnInput: "validateOnInput", valueField: "valueField", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", filterOperator: "filterOperator", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", picker: "picker", readOnly: "readOnly", store: "store", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumConstraintTypePickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-constraint-type-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], caseSensitive: [{
                type: Input
            }], centered: [{
                type: Input
            }], chipView: [{
                type: Input
            }], clearable: [{
                type: Input
            }], clearTextOnPickerHide: [{
                type: Input
            }], clearTextOnSelection: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], createOnUnmatched: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], displayValueRenderer: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], encodeFilterParams: [{
                type: Input
            }], filterOnEnter: [{
                type: Input
            }], filterParamName: [{
                type: Input
            }], filterSelected: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hidePickerOnSelect: [{
                type: Input
            }], hideTrigger: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inlinePicker: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], items: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], keyStrokeFilterDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listCls: [{
                type: Input
            }], listeners: [{
                type: Input
            }], listItemTpl: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minChars: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], multiValueSeparator: [{
                type: Input
            }], name: [{
                type: Input
            }], overlayAnchor: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerWidth: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], primaryFilter: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerAction: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateFilter: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], valueField: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], filterOperator: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], picker: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], store: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Container
 */
class BryntumContainerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumContainerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumContainerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumContainerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumContainerComponent.instanceClass = Container;
BryntumContainerComponent.instanceName = 'Container';
BryntumContainerComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumContainerComponent.bryntumFeatureNames = [];
BryntumContainerComponent.bryntumConfigs = BryntumContainerComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'strictRecordMapping',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumContainerComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'ui',
    'weight'
];
BryntumContainerComponent.bryntumProps = BryntumContainerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'strictRecordMapping',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumContainerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumContainerComponent, selector: "bryntum-container", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", textContent: "textContent", title: "title", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumContainerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-container',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum CycleResolutionPopup
 */
class BryntumCycleResolutionPopupComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeClose = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumCycleResolutionPopupComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumCycleResolutionPopupComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumCycleResolutionPopupComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumCycleResolutionPopupComponent.instanceClass = CycleResolutionPopup;
BryntumCycleResolutionPopupComponent.instanceName = 'CycleResolutionPopup';
BryntumCycleResolutionPopupComponent.bryntumEvents = [
    'onBeforeClose',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onCollapse',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onToolClick'
];
BryntumCycleResolutionPopupComponent.bryntumFeatureNames = [];
BryntumCycleResolutionPopupComponent.bryntumConfigs = BryntumCycleResolutionPopupComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoShow',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'focusOnToFront',
    'footer',
    'forElement',
    'header',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'modal',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showOnClick',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumCycleResolutionPopupComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoShow',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'focusOnToFront',
    'footer',
    'forElement',
    'header',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maximizable',
    'modal',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showOnClick',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'trapFocus',
    'ui',
    'weight'
];
BryntumCycleResolutionPopupComponent.bryntumProps = BryntumCycleResolutionPopupComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximized',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumCycleResolutionPopupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumCycleResolutionPopupComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumCycleResolutionPopupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumCycleResolutionPopupComponent, selector: "bryntum-cycle-resolution-popup", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoShow: "autoShow", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", header: "header", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", modal: "modal", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showOnClick: "showOnClick", showTooltipWhenDisabled: "showTooltipWhenDisabled", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", trapFocus: "trapFocus", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", state: "state", values: "values" }, outputs: { onBeforeClose: "onBeforeClose", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumCycleResolutionPopupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-cycle-resolution-popup',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], header: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], state: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeClose: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum DateField
 */
class BryntumDateFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumDateFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumDateFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumDateFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumDateFieldComponent.instanceClass = DateField;
BryntumDateFieldComponent.instanceName = 'DateField';
BryntumDateFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumDateFieldComponent.bryntumFeatureNames = [];
BryntumDateFieldComponent.bryntumConfigs = BryntumDateFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'format',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keepTime',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'min',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerFormat',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'step',
    'strictParsing',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weekStartDay',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumDateFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keepTime',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minLength',
    'monitorResize',
    'name',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerFormat',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'strictParsing',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weekStartDay',
    'weight'
];
BryntumDateFieldComponent.bryntumProps = BryntumDateFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'format',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'min',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'step',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumDateFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDateFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumDateFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumDateFieldComponent, selector: "bryntum-date-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keepTime: "keepTime", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minLength: "minLength", monitorResize: "monitorResize", name: "name", owner: "owner", picker: "picker", pickerAlignElement: "pickerAlignElement", pickerFormat: "pickerFormat", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", strictParsing: "strictParsing", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weekStartDay: "weekStartDay", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", format: "format", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", max: "max", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", min: "min", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", step: "step", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDateFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-date-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keepTime: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], picker: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerFormat: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], strictParsing: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], format: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], max: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], min: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], step: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum DatePicker
 */
class BryntumDatePickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeRefresh = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDateChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onRefresh = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumDatePickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumDatePickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumDatePickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumDatePickerComponent.instanceClass = DatePicker;
BryntumDatePickerComponent.instanceName = 'DatePicker';
BryntumDatePickerComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeRefresh',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onCollapse',
    'onDateChange',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onRefresh',
    'onResize',
    'onSelectionChange',
    'onShow',
    'onToolClick'
];
BryntumDatePickerComponent.bryntumFeatureNames = [];
BryntumDatePickerComponent.bryntumConfigs = BryntumDatePickerComponent.bryntumFeatureNames.concat([
    'activeDate',
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'animateTimeShift',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'cellRenderer',
    'centered',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'date',
    'dayNameFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'disabledCls',
    'disabledDates',
    'disableNonWorkingDays',
    'disableOtherMonthCells',
    'disableWeekends',
    'dock',
    'draggable',
    'editMonth',
    'extraData',
    'flex',
    'floating',
    'focusDisabledDates',
    'footer',
    'header',
    'headerRenderer',
    'height',
    'hidden',
    'hideAnimation',
    'hideOtherMonthCells',
    'hideWhenEmpty',
    'highlightSelectedWeek',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxDate',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minColumnWidth',
    'minDate',
    'minHeight',
    'minRowHeight',
    'minWidth',
    'monitorResize',
    'month',
    'multiSelect',
    'namedItems',
    'nonWorkingDayCls',
    'nonWorkingDays',
    'otherMonthCls',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'selection',
    'showAnimation',
    'showTooltipWhenDisabled',
    'showWeekColumn',
    'showWeekNumber',
    'sixWeeks',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'tip',
    'title',
    'todayCls',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'weekendCls',
    'weekRenderer',
    'weekStartDay',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumDatePickerComponent.bryntumConfigsOnly = [
    'activeDate',
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'cellRenderer',
    'centered',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'dayNameFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabledCls',
    'disabledDates',
    'disableNonWorkingDays',
    'disableOtherMonthCells',
    'disableWeekends',
    'dock',
    'draggable',
    'editMonth',
    'floating',
    'focusDisabledDates',
    'footer',
    'header',
    'headerRenderer',
    'hideAnimation',
    'hideOtherMonthCells',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxDate',
    'minColumnWidth',
    'minDate',
    'minRowHeight',
    'monitorResize',
    'month',
    'multiSelect',
    'namedItems',
    'nonWorkingDayCls',
    'nonWorkingDays',
    'otherMonthCls',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'showWeekColumn',
    'showWeekNumber',
    'sixWeeks',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'tip',
    'todayCls',
    'trapFocus',
    'ui',
    'weekendCls',
    'weekRenderer',
    'weekStartDay',
    'weight'
];
BryntumDatePickerComponent.bryntumProps = BryntumDatePickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'animateTimeShift',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'date',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'highlightSelectedWeek',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'selection',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumDatePickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDatePickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumDatePickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumDatePickerComponent, selector: "bryntum-date-picker", inputs: { activeDate: "activeDate", adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", cellRenderer: "cellRenderer", centered: "centered", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", dayNameFormat: "dayNameFormat", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", disabledCls: "disabledCls", disabledDates: "disabledDates", disableNonWorkingDays: "disableNonWorkingDays", disableOtherMonthCells: "disableOtherMonthCells", disableWeekends: "disableWeekends", dock: "dock", draggable: "draggable", editMonth: "editMonth", floating: "floating", focusDisabledDates: "focusDisabledDates", footer: "footer", header: "header", headerRenderer: "headerRenderer", hideAnimation: "hideAnimation", hideOtherMonthCells: "hideOtherMonthCells", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxDate: "maxDate", minColumnWidth: "minColumnWidth", minDate: "minDate", minRowHeight: "minRowHeight", monitorResize: "monitorResize", month: "month", multiSelect: "multiSelect", namedItems: "namedItems", nonWorkingDayCls: "nonWorkingDayCls", nonWorkingDays: "nonWorkingDays", otherMonthCls: "otherMonthCls", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", showWeekColumn: "showWeekColumn", showWeekNumber: "showWeekNumber", sixWeeks: "sixWeeks", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", tip: "tip", todayCls: "todayCls", trapFocus: "trapFocus", ui: "ui", weekendCls: "weekendCls", weekRenderer: "weekRenderer", weekStartDay: "weekStartDay", weight: "weight", alignSelf: "alignSelf", animateTimeShift: "animateTimeShift", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", date: "date", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", highlightSelectedWeek: "highlightSelectedWeek", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", selection: "selection", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", state: "state", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeRefresh: "onBeforeRefresh", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDateChange: "onDateChange", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onRefresh: "onRefresh", onResize: "onResize", onSelectionChange: "onSelectionChange", onShow: "onShow", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDatePickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-date-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { activeDate: [{
                type: Input
            }], adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], cellRenderer: [{
                type: Input
            }], centered: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], dayNameFormat: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], disabledCls: [{
                type: Input
            }], disabledDates: [{
                type: Input
            }], disableNonWorkingDays: [{
                type: Input
            }], disableOtherMonthCells: [{
                type: Input
            }], disableWeekends: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], editMonth: [{
                type: Input
            }], floating: [{
                type: Input
            }], focusDisabledDates: [{
                type: Input
            }], footer: [{
                type: Input
            }], header: [{
                type: Input
            }], headerRenderer: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideOtherMonthCells: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], minColumnWidth: [{
                type: Input
            }], minDate: [{
                type: Input
            }], minRowHeight: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], month: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], nonWorkingDayCls: [{
                type: Input
            }], nonWorkingDays: [{
                type: Input
            }], otherMonthCls: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], showWeekColumn: [{
                type: Input
            }], showWeekNumber: [{
                type: Input
            }], sixWeeks: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], tip: [{
                type: Input
            }], todayCls: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weekendCls: [{
                type: Input
            }], weekRenderer: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], animateTimeShift: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], date: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], highlightSelectedWeek: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selection: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], state: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeRefresh: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDateChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onRefresh: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum DateTimeField
 */
class BryntumDateTimeFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumDateTimeFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumDateTimeFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumDateTimeFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumDateTimeFieldComponent.instanceClass = DateTimeField;
BryntumDateTimeFieldComponent.instanceName = 'DateTimeField';
BryntumDateTimeFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumDateTimeFieldComponent.bryntumFeatureNames = [];
BryntumDateTimeFieldComponent.bryntumConfigs = BryntumDateTimeFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'dateField',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'textAlign',
    'timeField',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weekStartDay',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumDateTimeFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dateField',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'textAlign',
    'timeField',
    'title',
    'ui',
    'validateOnInput',
    'weekStartDay',
    'weight'
];
BryntumDateTimeFieldComponent.bryntumProps = BryntumDateTimeFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumDateTimeFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDateTimeFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumDateTimeFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumDateTimeFieldComponent, selector: "bryntum-date-time-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", dateField: "dateField", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", name: "name", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", textAlign: "textAlign", timeField: "timeField", title: "title", ui: "ui", validateOnInput: "validateOnInput", weekStartDay: "weekStartDay", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDateTimeFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-date-time-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], dateField: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], timeField: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum DependencyTypePicker
 */
class BryntumDependencyTypePickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumDependencyTypePickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumDependencyTypePickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumDependencyTypePickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumDependencyTypePickerComponent.instanceClass = DependencyTypePicker;
BryntumDependencyTypePickerComponent.instanceName = 'DependencyTypePicker';
BryntumDependencyTypePickerComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onTrigger'
];
BryntumDependencyTypePickerComponent.bryntumFeatureNames = [];
BryntumDependencyTypePickerComponent.bryntumConfigs = BryntumDependencyTypePickerComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'editable',
    'emptyText',
    'encodeFilterParams',
    'extraData',
    'filterOnEnter',
    'filterOperator',
    'filterParamName',
    'filterSelected',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minChars',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggerAction',
    'triggers',
    'ui',
    'validateFilter',
    'validateOnInput',
    'value',
    'valueField',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumDependencyTypePickerComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'defaultBindProperty',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'emptyText',
    'encodeFilterParams',
    'filterOnEnter',
    'filterParamName',
    'filterSelected',
    'floating',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minChars',
    'minLength',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'triggerAction',
    'ui',
    'validateFilter',
    'validateOnInput',
    'valueField',
    'weight'
];
BryntumDependencyTypePickerComponent.bryntumProps = BryntumDependencyTypePickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'filterOperator',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'picker',
    'readOnly',
    'scrollable',
    'store',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumDependencyTypePickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDependencyTypePickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumDependencyTypePickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumDependencyTypePickerComponent, selector: "bryntum-dependency-type-picker", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", caseSensitive: "caseSensitive", centered: "centered", chipView: "chipView", clearable: "clearable", clearTextOnPickerHide: "clearTextOnPickerHide", clearTextOnSelection: "clearTextOnSelection", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", createOnUnmatched: "createOnUnmatched", defaultBindProperty: "defaultBindProperty", displayField: "displayField", displayValueRenderer: "displayValueRenderer", dock: "dock", draggable: "draggable", emptyText: "emptyText", encodeFilterParams: "encodeFilterParams", filterOnEnter: "filterOnEnter", filterParamName: "filterParamName", filterSelected: "filterSelected", floating: "floating", hideAnimation: "hideAnimation", hidePickerOnSelect: "hidePickerOnSelect", hideTrigger: "hideTrigger", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inlinePicker: "inlinePicker", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", items: "items", keyStrokeChangeDelay: "keyStrokeChangeDelay", keyStrokeFilterDelay: "keyStrokeFilterDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listCls: "listCls", listeners: "listeners", listItemTpl: "listItemTpl", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minChars: "minChars", minLength: "minLength", monitorResize: "monitorResize", multiSelect: "multiSelect", multiValueSeparator: "multiValueSeparator", name: "name", overlayAnchor: "overlayAnchor", owner: "owner", pickerAlignElement: "pickerAlignElement", pickerWidth: "pickerWidth", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", primaryFilter: "primaryFilter", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", triggerAction: "triggerAction", ui: "ui", validateFilter: "validateFilter", validateOnInput: "validateOnInput", valueField: "valueField", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", filterOperator: "filterOperator", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", picker: "picker", readOnly: "readOnly", store: "store", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDependencyTypePickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-dependency-type-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], caseSensitive: [{
                type: Input
            }], centered: [{
                type: Input
            }], chipView: [{
                type: Input
            }], clearable: [{
                type: Input
            }], clearTextOnPickerHide: [{
                type: Input
            }], clearTextOnSelection: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], createOnUnmatched: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], displayValueRenderer: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], encodeFilterParams: [{
                type: Input
            }], filterOnEnter: [{
                type: Input
            }], filterParamName: [{
                type: Input
            }], filterSelected: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hidePickerOnSelect: [{
                type: Input
            }], hideTrigger: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inlinePicker: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], items: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], keyStrokeFilterDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listCls: [{
                type: Input
            }], listeners: [{
                type: Input
            }], listItemTpl: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minChars: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], multiValueSeparator: [{
                type: Input
            }], name: [{
                type: Input
            }], overlayAnchor: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerWidth: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], primaryFilter: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerAction: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateFilter: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], valueField: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], filterOperator: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], picker: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], store: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum DisplayField
 */
class BryntumDisplayFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumDisplayFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumDisplayFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumDisplayFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumDisplayFieldComponent.instanceClass = DisplayField;
BryntumDisplayFieldComponent.instanceName = 'DisplayField';
BryntumDisplayFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumDisplayFieldComponent.bryntumFeatureNames = [];
BryntumDisplayFieldComponent.bryntumConfigs = BryntumDisplayFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'template',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumDisplayFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'template',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weight'
];
BryntumDisplayFieldComponent.bryntumProps = BryntumDisplayFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumDisplayFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDisplayFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumDisplayFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumDisplayFieldComponent, selector: "bryntum-display-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", name: "name", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", template: "template", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDisplayFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-display-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], template: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum DurationField
 */
class BryntumDurationFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumDurationFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumDurationFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumDurationFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumDurationFieldComponent.instanceClass = DurationField;
BryntumDurationFieldComponent.instanceName = 'DurationField';
BryntumDurationFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumDurationFieldComponent.bryntumFeatureNames = [];
BryntumDurationFieldComponent.bryntumConfigs = BryntumDurationFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'allowedUnits',
    'allowNegative',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'decimalPrecision',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'magnitude',
    'margin',
    'maskDefaults',
    'masked',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'min',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'step',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'unit',
    'useAbbreviation',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumDurationFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'allowNegative',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'decimalPrecision',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minLength',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'step',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'ui',
    'useAbbreviation',
    'validateOnInput',
    'weight'
];
BryntumDurationFieldComponent.bryntumProps = BryntumDurationFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'allowedUnits',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'magnitude',
    'margin',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'min',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'unit',
    'value',
    'width',
    'x',
    'y'
]);
BryntumDurationFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDurationFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumDurationFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumDurationFieldComponent, selector: "bryntum-duration-field", inputs: { adopt: "adopt", align: "align", allowNegative: "allowNegative", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", decimalPrecision: "decimalPrecision", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minLength: "minLength", monitorResize: "monitorResize", name: "name", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", step: "step", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", ui: "ui", useAbbreviation: "useAbbreviation", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", allowedUnits: "allowedUnits", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", magnitude: "magnitude", margin: "margin", max: "max", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", min: "min", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", unit: "unit", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumDurationFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-duration-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], allowNegative: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], decimalPrecision: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], step: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], useAbbreviation: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], allowedUnits: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], magnitude: [{
                type: Input
            }], margin: [{
                type: Input
            }], max: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], min: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], unit: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum EffortField
 */
class BryntumEffortFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumEffortFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumEffortFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumEffortFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumEffortFieldComponent.instanceClass = EffortField;
BryntumEffortFieldComponent.instanceName = 'EffortField';
BryntumEffortFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumEffortFieldComponent.bryntumFeatureNames = [];
BryntumEffortFieldComponent.bryntumConfigs = BryntumEffortFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'allowedUnits',
    'allowNegative',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'decimalPrecision',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'magnitude',
    'margin',
    'maskDefaults',
    'masked',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'min',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'step',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'unit',
    'useAbbreviation',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumEffortFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'allowNegative',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'decimalPrecision',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minLength',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'step',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'ui',
    'useAbbreviation',
    'validateOnInput',
    'weight'
];
BryntumEffortFieldComponent.bryntumProps = BryntumEffortFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'allowedUnits',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'magnitude',
    'margin',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'min',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'unit',
    'value',
    'width',
    'x',
    'y'
]);
BryntumEffortFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumEffortFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumEffortFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumEffortFieldComponent, selector: "bryntum-effort-field", inputs: { adopt: "adopt", align: "align", allowNegative: "allowNegative", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", decimalPrecision: "decimalPrecision", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minLength: "minLength", monitorResize: "monitorResize", name: "name", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", step: "step", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", ui: "ui", useAbbreviation: "useAbbreviation", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", allowedUnits: "allowedUnits", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", magnitude: "magnitude", margin: "margin", max: "max", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", min: "min", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", unit: "unit", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumEffortFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-effort-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], allowNegative: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], decimalPrecision: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], step: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], useAbbreviation: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], allowedUnits: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], magnitude: [{
                type: Input
            }], margin: [{
                type: Input
            }], max: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], min: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], unit: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum EndDateField
 */
class BryntumEndDateFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumEndDateFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumEndDateFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumEndDateFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumEndDateFieldComponent.instanceClass = EndDateField;
BryntumEndDateFieldComponent.instanceName = 'EndDateField';
BryntumEndDateFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumEndDateFieldComponent.bryntumFeatureNames = [];
BryntumEndDateFieldComponent.bryntumConfigs = BryntumEndDateFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'eventRecord',
    'extraData',
    'flex',
    'floating',
    'format',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keepTime',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'min',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerFormat',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'project',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'step',
    'strictParsing',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weekStartDay',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumEndDateFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'eventRecord',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keepTime',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minLength',
    'monitorResize',
    'name',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerFormat',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'project',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'strictParsing',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weekStartDay',
    'weight'
];
BryntumEndDateFieldComponent.bryntumProps = BryntumEndDateFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'format',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'min',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'step',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumEndDateFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumEndDateFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumEndDateFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumEndDateFieldComponent, selector: "bryntum-end-date-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", eventRecord: "eventRecord", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keepTime: "keepTime", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minLength: "minLength", monitorResize: "monitorResize", name: "name", owner: "owner", picker: "picker", pickerAlignElement: "pickerAlignElement", pickerFormat: "pickerFormat", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", project: "project", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", strictParsing: "strictParsing", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weekStartDay: "weekStartDay", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", format: "format", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", max: "max", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", min: "min", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", step: "step", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumEndDateFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-end-date-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], eventRecord: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keepTime: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], picker: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerFormat: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], project: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], strictParsing: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], format: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], max: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], min: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], step: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum EventColorField
 */
class BryntumEventColorFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumEventColorFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumEventColorFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumEventColorFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumEventColorFieldComponent.instanceClass = EventColorField;
BryntumEventColorFieldComponent.instanceName = 'EventColorField';
BryntumEventColorFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumEventColorFieldComponent.bryntumFeatureNames = [];
BryntumEventColorFieldComponent.bryntumConfigs = BryntumEventColorFieldComponent.bryntumFeatureNames.concat([
    'addNoColorItem',
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'colors',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'pickerAlignElement',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumEventColorFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minLength',
    'monitorResize',
    'name',
    'owner',
    'pickerAlignElement',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weight'
];
BryntumEventColorFieldComponent.bryntumProps = BryntumEventColorFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'addNoColorItem',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'colors',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumEventColorFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumEventColorFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumEventColorFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumEventColorFieldComponent, selector: "bryntum-event-color-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minLength: "minLength", monitorResize: "monitorResize", name: "name", owner: "owner", pickerAlignElement: "pickerAlignElement", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weight: "weight", addNoColorItem: "addNoColorItem", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", colors: "colors", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", editable: "editable", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumEventColorFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-event-color-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], addNoColorItem: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], colors: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], editable: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum EventColorPicker
 */
class BryntumEventColorPickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeItem = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onItem = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumEventColorPickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumEventColorPickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumEventColorPickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumEventColorPickerComponent.instanceClass = EventColorPicker;
BryntumEventColorPickerComponent.instanceName = 'EventColorPicker';
BryntumEventColorPickerComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeItem',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onItem',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelectionChange',
    'onShow',
    'onToggleGroup'
];
BryntumEventColorPickerComponent.bryntumFeatureNames = [];
BryntumEventColorPickerComponent.bryntumConfigs = BryntumEventColorPickerComponent.bryntumFeatureNames.concat([
    'activateOnMouseover',
    'addNoColorItem',
    'adopt',
    'align',
    'alignSelf',
    'allowGroupSelect',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'cls',
    'collapsibleGroups',
    'colorSelected',
    'columns',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'height',
    'hidden',
    'hideAnimation',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'items',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'selectAllItem',
    'selected',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'tab',
    'tag',
    'textAlign',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumEventColorPickerComponent.bryntumConfigsOnly = [
    'activateOnMouseover',
    'adopt',
    'align',
    'allowGroupSelect',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'colorSelected',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'displayField',
    'dock',
    'draggable',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'hideAnimation',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'selectAllItem',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'ui',
    'weight'
];
BryntumEventColorPickerComponent.bryntumProps = BryntumEventColorPickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'addNoColorItem',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'collapsibleGroups',
    'colors',
    'columns',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'items',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'multiSelect',
    'readOnly',
    'scrollable',
    'selected',
    'store',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'width',
    'x',
    'y'
]);
BryntumEventColorPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumEventColorPickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumEventColorPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumEventColorPickerComponent, selector: "bryntum-event-color-picker", inputs: { activateOnMouseover: "activateOnMouseover", adopt: "adopt", align: "align", allowGroupSelect: "allowGroupSelect", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearSelectionOnEmptySpaceClick: "clearSelectionOnEmptySpaceClick", colorSelected: "colorSelected", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", displayField: "displayField", dock: "dock", draggable: "draggable", floating: "floating", getItemCls: "getItemCls", getItemStyle: "getItemStyle", groupHeaderTpl: "groupHeaderTpl", hideAnimation: "hideAnimation", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemTpl: "itemTpl", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", record: "record", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", selectAllItem: "selectAllItem", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", ui: "ui", weight: "weight", addNoColorItem: "addNoColorItem", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", collapsibleGroups: "collapsibleGroups", columns: "columns", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", multiSelect: "multiSelect", readOnly: "readOnly", scrollable: "scrollable", selected: "selected", store: "store", title: "title", toggleAllIfCtrlPressed: "toggleAllIfCtrlPressed", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", colors: "colors" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeItem: "onBeforeItem", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onItem: "onItem", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelectionChange: "onSelectionChange", onShow: "onShow", onToggleGroup: "onToggleGroup" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumEventColorPickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-event-color-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { activateOnMouseover: [{
                type: Input
            }], adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], allowGroupSelect: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearSelectionOnEmptySpaceClick: [{
                type: Input
            }], colorSelected: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], getItemCls: [{
                type: Input
            }], getItemStyle: [{
                type: Input
            }], groupHeaderTpl: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemTpl: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], record: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], selectAllItem: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], addNoColorItem: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], collapsibleGroups: [{
                type: Input
            }], columns: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selected: [{
                type: Input
            }], store: [{
                type: Input
            }], title: [{
                type: Input
            }], toggleAllIfCtrlPressed: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], colors: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeItem: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onItem: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum FieldFilterPicker
 */
class BryntumFieldFilterPickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumFieldFilterPickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumFieldFilterPickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumFieldFilterPickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumFieldFilterPickerComponent.instanceClass = FieldFilterPicker;
BryntumFieldFilterPickerComponent.instanceName = 'FieldFilterPicker';
BryntumFieldFilterPickerComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumFieldFilterPickerComponent.bryntumFeatureNames = [];
BryntumFieldFilterPickerComponent.bryntumConfigs = BryntumFieldFilterPickerComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'dateFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'fields',
    'filter',
    'flex',
    'floating',
    'getValueFieldConfig',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'operators',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'propertyFieldConfig',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'strictRecordMapping',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'tooltip',
    'triggerChangeOnInput',
    'ui',
    'valueFieldPlaceholders',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumFieldFilterPickerComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'dateFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'fields',
    'filter',
    'floating',
    'getValueFieldConfig',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'operators',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'propertyFieldConfig',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'triggerChangeOnInput',
    'ui',
    'valueFieldPlaceholders',
    'weight'
];
BryntumFieldFilterPickerComponent.bryntumProps = BryntumFieldFilterPickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'strictRecordMapping',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumFieldFilterPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFieldFilterPickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumFieldFilterPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumFieldFilterPickerComponent, selector: "bryntum-field-filter-picker", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", dateFormat: "dateFormat", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", fields: "fields", filter: "filter", floating: "floating", getValueFieldConfig: "getValueFieldConfig", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", operators: "operators", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", propertyFieldConfig: "propertyFieldConfig", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", store: "store", tab: "tab", tag: "tag", textAlign: "textAlign", textContent: "textContent", title: "title", triggerChangeOnInput: "triggerChangeOnInput", ui: "ui", valueFieldPlaceholders: "valueFieldPlaceholders", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFieldFilterPickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-field-filter-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], dateFormat: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], fields: [{
                type: Input
            }], filter: [{
                type: Input
            }], floating: [{
                type: Input
            }], getValueFieldConfig: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], operators: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], propertyFieldConfig: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], store: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerChangeOnInput: [{
                type: Input
            }], ui: [{
                type: Input
            }], valueFieldPlaceholders: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum FieldFilterPickerGroup
 */
class BryntumFieldFilterPickerGroupComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeAddFilter = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumFieldFilterPickerGroupComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumFieldFilterPickerGroupComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumFieldFilterPickerGroupComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumFieldFilterPickerGroupComponent.instanceClass = FieldFilterPickerGroup;
BryntumFieldFilterPickerGroupComponent.instanceName = 'FieldFilterPickerGroup';
BryntumFieldFilterPickerGroupComponent.bryntumEvents = [
    'onBeforeAddFilter',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumFieldFilterPickerGroupComponent.bryntumFeatureNames = [];
BryntumFieldFilterPickerGroupComponent.bryntumConfigs = BryntumFieldFilterPickerGroupComponent.bryntumFeatureNames.concat([
    'addFilterButtonText',
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'canDeleteFilter',
    'canManageFilter',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'dateFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'fields',
    'filters',
    'flex',
    'floating',
    'getFieldFilterPickerConfig',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'limitToProperty',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'operators',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAddFilterButton',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'strictRecordMapping',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'tooltip',
    'triggerChangeOnInput',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumFieldFilterPickerGroupComponent.bryntumConfigsOnly = [
    'addFilterButtonText',
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'canDeleteFilter',
    'canManageFilter',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'dateFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'fields',
    'filters',
    'floating',
    'getFieldFilterPickerConfig',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'limitToProperty',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'operators',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAddFilterButton',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'triggerChangeOnInput',
    'ui',
    'weight'
];
BryntumFieldFilterPickerGroupComponent.bryntumProps = BryntumFieldFilterPickerGroupComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'strictRecordMapping',
    'tooltip',
    'value',
    'values',
    'width',
    'x',
    'y'
]);
BryntumFieldFilterPickerGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFieldFilterPickerGroupComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumFieldFilterPickerGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumFieldFilterPickerGroupComponent, selector: "bryntum-field-filter-picker-group", inputs: { addFilterButtonText: "addFilterButtonText", adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", canDeleteFilter: "canDeleteFilter", canManageFilter: "canManageFilter", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", dateFormat: "dateFormat", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", fields: "fields", filters: "filters", floating: "floating", getFieldFilterPickerConfig: "getFieldFilterPickerConfig", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", limitToProperty: "limitToProperty", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", operators: "operators", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAddFilterButton: "showAddFilterButton", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", store: "store", tab: "tab", tag: "tag", textAlign: "textAlign", textContent: "textContent", title: "title", triggerChangeOnInput: "triggerChangeOnInput", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", value: "value", values: "values" }, outputs: { onBeforeAddFilter: "onBeforeAddFilter", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFieldFilterPickerGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-field-filter-picker-group',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { addFilterButtonText: [{
                type: Input
            }], adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], canDeleteFilter: [{
                type: Input
            }], canManageFilter: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], dateFormat: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], fields: [{
                type: Input
            }], filters: [{
                type: Input
            }], floating: [{
                type: Input
            }], getFieldFilterPickerConfig: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], limitToProperty: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], operators: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAddFilterButton: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], store: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerChangeOnInput: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], value: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeAddFilter: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum FileField
 */
class BryntumFileFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumFileFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumFileFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumFileFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumFileFieldComponent.instanceClass = FileField;
BryntumFileFieldComponent.instanceName = 'FileField';
BryntumFileFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumFileFieldComponent.bryntumFeatureNames = [];
BryntumFileFieldComponent.bryntumConfigs = BryntumFileFieldComponent.bryntumFeatureNames.concat([
    'accept',
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'multiple',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumFileFieldComponent.bryntumConfigsOnly = [
    'accept',
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'multiple',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weight'
];
BryntumFileFieldComponent.bryntumProps = BryntumFileFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumFileFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFileFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumFileFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumFileFieldComponent, selector: "bryntum-file-field", inputs: { accept: "accept", adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", multiple: "multiple", name: "name", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFileFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-file-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { accept: [{
                type: Input
            }], adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], multiple: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum FilePicker
 */
class BryntumFilePickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumFilePickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumFilePickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumFilePickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumFilePickerComponent.instanceClass = FilePicker;
BryntumFilePickerComponent.instanceName = 'FilePicker';
BryntumFilePickerComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumFilePickerComponent.bryntumFeatureNames = [];
BryntumFilePickerComponent.bryntumConfigs = BryntumFilePickerComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bubbleEvents',
    'buttonConfig',
    'callOnFunctions',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'fileFieldConfig',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showBadge',
    'showTooltipWhenDisabled',
    'strictRecordMapping',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumFilePickerComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bubbleEvents',
    'buttonConfig',
    'callOnFunctions',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'fileFieldConfig',
    'floating',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'ui',
    'weight'
];
BryntumFilePickerComponent.bryntumProps = BryntumFilePickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'showBadge',
    'strictRecordMapping',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumFilePickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFilePickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumFilePickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumFilePickerComponent, selector: "bryntum-file-picker", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bubbleEvents: "bubbleEvents", buttonConfig: "buttonConfig", callOnFunctions: "callOnFunctions", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", fileFieldConfig: "fileFieldConfig", floating: "floating", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", textContent: "textContent", title: "title", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", showBadge: "showBadge", strictRecordMapping: "strictRecordMapping", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFilePickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-file-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], buttonConfig: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], fileFieldConfig: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], showBadge: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum FilterField
 */
class BryntumFilterFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumFilterFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumFilterFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumFilterFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumFilterFieldComponent.instanceClass = FilterField;
BryntumFilterFieldComponent.instanceName = 'FilterField';
BryntumFilterFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumFilterFieldComponent.bryntumFeatureNames = [];
BryntumFilterFieldComponent.bryntumConfigs = BryntumFilterFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'field',
    'filterFunction',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumFilterFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'field',
    'filterFunction',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minLength',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weight'
];
BryntumFilterFieldComponent.bryntumProps = BryntumFilterFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumFilterFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFilterFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumFilterFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumFilterFieldComponent, selector: "bryntum-filter-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", field: "field", filterFunction: "filterFunction", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minLength: "minLength", monitorResize: "monitorResize", name: "name", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", store: "store", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumFilterFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-filter-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], field: [{
                type: Input
            }], filterFunction: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], store: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum GanttTaskEditor
 */
class BryntumGanttTaskEditorComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeClose = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumGanttTaskEditorComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumGanttTaskEditorComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumGanttTaskEditorComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumGanttTaskEditorComponent.instanceClass = GanttTaskEditor;
BryntumGanttTaskEditorComponent.instanceName = 'GanttTaskEditor';
BryntumGanttTaskEditorComponent.bryntumEvents = [
    'onBeforeClose',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onCollapse',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onToolClick'
];
BryntumGanttTaskEditorComponent.bryntumFeatureNames = [];
BryntumGanttTaskEditorComponent.bryntumConfigs = BryntumGanttTaskEditorComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoShow',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'calculateMask',
    'calculateMaskDelay',
    'callOnFunctions',
    'centered',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dependencyIdField',
    'disabled',
    'dock',
    'draggable',
    'durationDisplayPrecision',
    'extraData',
    'flex',
    'floating',
    'focusOnToFront',
    'footer',
    'forElement',
    'header',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'modal',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showOnClick',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumGanttTaskEditorComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoShow',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'calculateMask',
    'calculateMaskDelay',
    'callOnFunctions',
    'centered',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dependencyIdField',
    'dock',
    'draggable',
    'durationDisplayPrecision',
    'floating',
    'focusOnToFront',
    'footer',
    'forElement',
    'header',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maximizable',
    'modal',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showOnClick',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'trapFocus',
    'ui',
    'weight'
];
BryntumGanttTaskEditorComponent.bryntumProps = BryntumGanttTaskEditorComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximized',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumGanttTaskEditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGanttTaskEditorComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumGanttTaskEditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumGanttTaskEditorComponent, selector: "bryntum-gantt-task-editor", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoShow: "autoShow", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", calculateMask: "calculateMask", calculateMaskDelay: "calculateMaskDelay", callOnFunctions: "callOnFunctions", centered: "centered", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dependencyIdField: "dependencyIdField", dock: "dock", draggable: "draggable", durationDisplayPrecision: "durationDisplayPrecision", floating: "floating", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", header: "header", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", modal: "modal", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showOnClick: "showOnClick", showTooltipWhenDisabled: "showTooltipWhenDisabled", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", trapFocus: "trapFocus", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", state: "state", values: "values" }, outputs: { onBeforeClose: "onBeforeClose", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGanttTaskEditorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-gantt-task-editor',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], calculateMask: [{
                type: Input
            }], calculateMaskDelay: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dependencyIdField: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], durationDisplayPrecision: [{
                type: Input
            }], floating: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], header: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], state: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeClose: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Grid
 */
class BryntumGridComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumGridComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
        // Backwards compatibility for gridInstance, schedulerInstance etc.
        // @ts-ignore
        me[StringHelper.uncapitalize(instanceName) + 'Instance'] = me.instance;
        // 
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumGridComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumGridComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumGridComponent.instanceClass = Grid;
BryntumGridComponent.instanceName = 'Grid';
BryntumGridComponent.bryntumEvents = [
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDestroy',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDestroy',
    'onDragSelecting',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onMouseOut',
    'onMouseOver',
    'onPaint',
    'onPaste',
    'onPdfExport',
    'onPointerOver',
    'onRecompose',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResponsive',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onUnsplit'
];
BryntumGridComponent.bryntumFeatureNames = [
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'mergeCellsFeature',
    'pdfExportFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'searchFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stripeFeature',
    'summaryFeature',
    'treeFeature',
    'treeGroupFeature'
];
BryntumGridComponent.bryntumConfigs = BryntumGridComponent.bryntumFeatureNames.concat([
    'adopt',
    'alignSelf',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'data',
    'dataset',
    'defaultRegion',
    'destroyStore',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'emptyText',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'extraData',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getHtml',
    'getRowHeight',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hoverDelay',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxWidth',
    'minHeight',
    'minWidth',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'showDirty',
    'showOnClick',
    'showOnHover',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'store',
    'strips',
    'subGridConfigs',
    'syncMask',
    'tab',
    'tbar',
    'title',
    'tools',
    'trackMouse',
    'transitionDuration',
    'ui',
    'weight',
    'width'
]);
BryntumGridComponent.bryntumConfigsOnly = [
    'adopt',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'defaultRegion',
    'destroyStore',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'enableSticky',
    'enableTextSelection',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getHtml',
    'getRowHeight',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'ignoreParentReadOnly',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'maskDefaults',
    'masked',
    'maximizable',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'scrollerClass',
    'scrollManager',
    'showDirty',
    'showOnClick',
    'showOnHover',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'subGridConfigs',
    'syncMask',
    'tab',
    'tbar',
    'trackMouse',
    'ui',
    'weight'
];
BryntumGridComponent.bryntumProps = BryntumGridComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaLive',
    'cls',
    'columnLines',
    'columns',
    'data',
    'dataset',
    'disabled',
    'emptyText',
    'enableUndoRedoKeys',
    'extraData',
    'flex',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'minHeight',
    'minWidth',
    'preserveScroll',
    'readOnly',
    'rowHeight',
    'rowLines',
    'scrollable',
    'selectedCell',
    'selectedCells',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'state',
    'store',
    'title',
    'tools',
    'tooltip',
    'transitionDuration',
    'width'
]);
BryntumGridComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGridComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumGridComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumGridComponent, selector: "bryntum-grid", inputs: { adopt: "adopt", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", defaultRegion: "defaultRegion", destroyStore: "destroyStore", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", dock: "dock", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getHtml: "getHtml", getRowHeight: "getRowHeight", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", ignoreParentReadOnly: "ignoreParentReadOnly", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", modal: "modal", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", owner: "owner", plugins: "plugins", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", scrollerClass: "scrollerClass", scrollManager: "scrollManager", showDirty: "showDirty", showOnClick: "showOnClick", showOnHover: "showOnHover", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", subGridConfigs: "subGridConfigs", syncMask: "syncMask", tab: "tab", tbar: "tbar", trackMouse: "trackMouse", ui: "ui", weight: "weight", alignSelf: "alignSelf", animateTreeNodeToggle: "animateTreeNodeToggle", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", columnLines: "columnLines", columns: "columns", data: "data", dataset: "dataset", disabled: "disabled", emptyText: "emptyText", enableUndoRedoKeys: "enableUndoRedoKeys", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", preserveScroll: "preserveScroll", readOnly: "readOnly", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", store: "store", title: "title", tools: "tools", transitionDuration: "transitionDuration", width: "width", accessibility: "accessibility", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", tooltip: "tooltip", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", mergeCellsFeature: "mergeCellsFeature", pdfExportFeature: "pdfExportFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", searchFeature: "searchFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature" }, outputs: { onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDestroy: "onBeforeDestroy", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDestroy: "onDestroy", onDragSelecting: "onDragSelecting", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onPaint: "onPaint", onPaste: "onPaste", onPdfExport: "onPdfExport", onPointerOver: "onPointerOver", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResponsive: "onResponsive", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onUnsplit: "onUnsplit" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGridComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-grid',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], dock: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], owner: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], data: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], store: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], width: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum GridBase
 */
class BryntumGridBaseComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumGridBaseComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
        // Backwards compatibility for gridInstance, schedulerInstance etc.
        // @ts-ignore
        me[StringHelper.uncapitalize(instanceName) + 'Instance'] = me.instance;
        // 
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumGridBaseComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumGridBaseComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumGridBaseComponent.instanceClass = GridBase;
BryntumGridBaseComponent.instanceName = 'GridBase';
BryntumGridBaseComponent.bryntumEvents = [
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDestroy',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDestroy',
    'onDragSelecting',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onMouseOut',
    'onMouseOver',
    'onPaint',
    'onPaste',
    'onPdfExport',
    'onPointerOver',
    'onRecompose',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResponsive',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onUnsplit'
];
BryntumGridBaseComponent.bryntumFeatureNames = [
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'mergeCellsFeature',
    'pdfExportFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'searchFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stripeFeature',
    'summaryFeature',
    'treeFeature',
    'treeGroupFeature'
];
BryntumGridBaseComponent.bryntumConfigs = BryntumGridBaseComponent.bryntumFeatureNames.concat([
    'adopt',
    'alignSelf',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'data',
    'dataset',
    'defaultRegion',
    'destroyStore',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'emptyText',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'extraData',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getHtml',
    'getRowHeight',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hoverDelay',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxWidth',
    'minHeight',
    'minWidth',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'showDirty',
    'showOnClick',
    'showOnHover',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'store',
    'strips',
    'subGridConfigs',
    'syncMask',
    'tab',
    'tbar',
    'title',
    'tools',
    'trackMouse',
    'transitionDuration',
    'ui',
    'weight',
    'width'
]);
BryntumGridBaseComponent.bryntumConfigsOnly = [
    'adopt',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'defaultRegion',
    'destroyStore',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'enableSticky',
    'enableTextSelection',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getHtml',
    'getRowHeight',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'ignoreParentReadOnly',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'maskDefaults',
    'masked',
    'maximizable',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'scrollerClass',
    'scrollManager',
    'showDirty',
    'showOnClick',
    'showOnHover',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'subGridConfigs',
    'syncMask',
    'tab',
    'tbar',
    'trackMouse',
    'ui',
    'weight'
];
BryntumGridBaseComponent.bryntumProps = BryntumGridBaseComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaLive',
    'cls',
    'columnLines',
    'columns',
    'data',
    'dataset',
    'disabled',
    'emptyText',
    'enableUndoRedoKeys',
    'extraData',
    'flex',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'minHeight',
    'minWidth',
    'preserveScroll',
    'readOnly',
    'rowHeight',
    'rowLines',
    'scrollable',
    'selectedCell',
    'selectedCells',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'state',
    'store',
    'title',
    'tools',
    'tooltip',
    'transitionDuration',
    'width'
]);
BryntumGridBaseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGridBaseComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumGridBaseComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumGridBaseComponent, selector: "bryntum-grid-base", inputs: { adopt: "adopt", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", defaultRegion: "defaultRegion", destroyStore: "destroyStore", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", dock: "dock", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getHtml: "getHtml", getRowHeight: "getRowHeight", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", ignoreParentReadOnly: "ignoreParentReadOnly", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", modal: "modal", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", owner: "owner", plugins: "plugins", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", scrollerClass: "scrollerClass", scrollManager: "scrollManager", showDirty: "showDirty", showOnClick: "showOnClick", showOnHover: "showOnHover", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", subGridConfigs: "subGridConfigs", syncMask: "syncMask", tab: "tab", tbar: "tbar", trackMouse: "trackMouse", ui: "ui", weight: "weight", alignSelf: "alignSelf", animateTreeNodeToggle: "animateTreeNodeToggle", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", columnLines: "columnLines", columns: "columns", data: "data", dataset: "dataset", disabled: "disabled", emptyText: "emptyText", enableUndoRedoKeys: "enableUndoRedoKeys", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", preserveScroll: "preserveScroll", readOnly: "readOnly", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", store: "store", title: "title", tools: "tools", transitionDuration: "transitionDuration", width: "width", accessibility: "accessibility", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", tooltip: "tooltip", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", mergeCellsFeature: "mergeCellsFeature", pdfExportFeature: "pdfExportFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", searchFeature: "searchFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature" }, outputs: { onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDestroy: "onBeforeDestroy", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDestroy: "onDestroy", onDragSelecting: "onDragSelecting", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onPaint: "onPaint", onPaste: "onPaste", onPdfExport: "onPdfExport", onPointerOver: "onPointerOver", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResponsive: "onResponsive", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onUnsplit: "onUnsplit" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGridBaseComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-grid-base',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], dock: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], owner: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], data: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], store: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], width: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum GridFieldFilterPicker
 */
class BryntumGridFieldFilterPickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumGridFieldFilterPickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumGridFieldFilterPickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumGridFieldFilterPickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumGridFieldFilterPickerComponent.instanceClass = GridFieldFilterPicker;
BryntumGridFieldFilterPickerComponent.instanceName = 'GridFieldFilterPicker';
BryntumGridFieldFilterPickerComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumGridFieldFilterPickerComponent.bryntumFeatureNames = [];
BryntumGridFieldFilterPickerComponent.bryntumConfigs = BryntumGridFieldFilterPickerComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'allowedFieldNames',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'dateFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'fields',
    'filter',
    'flex',
    'floating',
    'getValueFieldConfig',
    'grid',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'operators',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'propertyFieldConfig',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'strictRecordMapping',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'tooltip',
    'triggerChangeOnInput',
    'ui',
    'valueFieldPlaceholders',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumGridFieldFilterPickerComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'allowedFieldNames',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'dateFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'fields',
    'filter',
    'floating',
    'getValueFieldConfig',
    'grid',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'operators',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'propertyFieldConfig',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'triggerChangeOnInput',
    'ui',
    'valueFieldPlaceholders',
    'weight'
];
BryntumGridFieldFilterPickerComponent.bryntumProps = BryntumGridFieldFilterPickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'strictRecordMapping',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumGridFieldFilterPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGridFieldFilterPickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumGridFieldFilterPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumGridFieldFilterPickerComponent, selector: "bryntum-grid-field-filter-picker", inputs: { adopt: "adopt", align: "align", allowedFieldNames: "allowedFieldNames", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", dateFormat: "dateFormat", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", fields: "fields", filter: "filter", floating: "floating", getValueFieldConfig: "getValueFieldConfig", grid: "grid", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", operators: "operators", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", propertyFieldConfig: "propertyFieldConfig", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", textContent: "textContent", title: "title", triggerChangeOnInput: "triggerChangeOnInput", ui: "ui", valueFieldPlaceholders: "valueFieldPlaceholders", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGridFieldFilterPickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-grid-field-filter-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], allowedFieldNames: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], dateFormat: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], fields: [{
                type: Input
            }], filter: [{
                type: Input
            }], floating: [{
                type: Input
            }], getValueFieldConfig: [{
                type: Input
            }], grid: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], operators: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], propertyFieldConfig: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerChangeOnInput: [{
                type: Input
            }], ui: [{
                type: Input
            }], valueFieldPlaceholders: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum GridFieldFilterPickerGroup
 */
class BryntumGridFieldFilterPickerGroupComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeAddFilter = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumGridFieldFilterPickerGroupComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumGridFieldFilterPickerGroupComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumGridFieldFilterPickerGroupComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumGridFieldFilterPickerGroupComponent.instanceClass = GridFieldFilterPickerGroup;
BryntumGridFieldFilterPickerGroupComponent.instanceName = 'GridFieldFilterPickerGroup';
BryntumGridFieldFilterPickerGroupComponent.bryntumEvents = [
    'onBeforeAddFilter',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumGridFieldFilterPickerGroupComponent.bryntumFeatureNames = [];
BryntumGridFieldFilterPickerGroupComponent.bryntumConfigs = BryntumGridFieldFilterPickerGroupComponent.bryntumFeatureNames.concat([
    'addFilterButtonText',
    'adopt',
    'align',
    'alignSelf',
    'allowedFieldNames',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'canDeleteFilter',
    'canManageFilter',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'dateFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'filters',
    'flex',
    'floating',
    'getFieldFilterPickerConfig',
    'grid',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'limitToProperty',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'operators',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAddFilterButton',
    'showAnimation',
    'showTooltipWhenDisabled',
    'strictRecordMapping',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'tooltip',
    'triggerChangeOnInput',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumGridFieldFilterPickerGroupComponent.bryntumConfigsOnly = [
    'addFilterButtonText',
    'adopt',
    'align',
    'allowedFieldNames',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'canDeleteFilter',
    'canManageFilter',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'dateFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'filters',
    'floating',
    'getFieldFilterPickerConfig',
    'grid',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'limitToProperty',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'operators',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAddFilterButton',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'triggerChangeOnInput',
    'ui',
    'weight'
];
BryntumGridFieldFilterPickerGroupComponent.bryntumProps = BryntumGridFieldFilterPickerGroupComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'strictRecordMapping',
    'tooltip',
    'value',
    'values',
    'width',
    'x',
    'y'
]);
BryntumGridFieldFilterPickerGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGridFieldFilterPickerGroupComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumGridFieldFilterPickerGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumGridFieldFilterPickerGroupComponent, selector: "bryntum-grid-field-filter-picker-group", inputs: { addFilterButtonText: "addFilterButtonText", adopt: "adopt", align: "align", allowedFieldNames: "allowedFieldNames", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", canDeleteFilter: "canDeleteFilter", canManageFilter: "canManageFilter", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", dateFormat: "dateFormat", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", filters: "filters", floating: "floating", getFieldFilterPickerConfig: "getFieldFilterPickerConfig", grid: "grid", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", limitToProperty: "limitToProperty", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", operators: "operators", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAddFilterButton: "showAddFilterButton", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", textContent: "textContent", title: "title", triggerChangeOnInput: "triggerChangeOnInput", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", value: "value", values: "values" }, outputs: { onBeforeAddFilter: "onBeforeAddFilter", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGridFieldFilterPickerGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-grid-field-filter-picker-group',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { addFilterButtonText: [{
                type: Input
            }], adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], allowedFieldNames: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], canDeleteFilter: [{
                type: Input
            }], canManageFilter: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], dateFormat: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], filters: [{
                type: Input
            }], floating: [{
                type: Input
            }], getFieldFilterPickerConfig: [{
                type: Input
            }], grid: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], limitToProperty: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], operators: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAddFilterButton: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerChangeOnInput: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], value: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeAddFilter: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum GroupBar
 */
class BryntumGroupBarComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeItem = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onItem = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumGroupBarComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumGroupBarComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumGroupBarComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumGroupBarComponent.instanceClass = GroupBar;
BryntumGroupBarComponent.instanceName = 'GroupBar';
BryntumGroupBarComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeItem',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onItem',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelectionChange',
    'onShow',
    'onToggleGroup'
];
BryntumGroupBarComponent.bryntumFeatureNames = [];
BryntumGroupBarComponent.bryntumConfigs = BryntumGroupBarComponent.bryntumFeatureNames.concat([
    'activateOnMouseover',
    'adopt',
    'align',
    'alignSelf',
    'allowGroupSelect',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'closable',
    'closeHandler',
    'cls',
    'collapsibleGroups',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'height',
    'hidden',
    'hideAnimation',
    'html',
    'htmlCls',
    'iconTpl',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'items',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'selectAllItem',
    'selected',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'tab',
    'tag',
    'textAlign',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumGroupBarComponent.bryntumConfigsOnly = [
    'activateOnMouseover',
    'adopt',
    'align',
    'allowGroupSelect',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'closable',
    'closeHandler',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'displayField',
    'dock',
    'draggable',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'hideAnimation',
    'htmlCls',
    'iconTpl',
    'ignoreParentReadOnly',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'selectAllItem',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'ui',
    'weight'
];
BryntumGroupBarComponent.bryntumProps = BryntumGroupBarComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'collapsibleGroups',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'items',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'multiSelect',
    'readOnly',
    'scrollable',
    'selected',
    'store',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'width',
    'x',
    'y'
]);
BryntumGroupBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGroupBarComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumGroupBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumGroupBarComponent, selector: "bryntum-group-bar", inputs: { activateOnMouseover: "activateOnMouseover", adopt: "adopt", align: "align", allowGroupSelect: "allowGroupSelect", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearSelectionOnEmptySpaceClick: "clearSelectionOnEmptySpaceClick", closable: "closable", closeHandler: "closeHandler", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", displayField: "displayField", dock: "dock", draggable: "draggable", floating: "floating", getItemCls: "getItemCls", getItemStyle: "getItemStyle", groupHeaderTpl: "groupHeaderTpl", hideAnimation: "hideAnimation", htmlCls: "htmlCls", iconTpl: "iconTpl", ignoreParentReadOnly: "ignoreParentReadOnly", itemTpl: "itemTpl", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", selectAllItem: "selectAllItem", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", collapsibleGroups: "collapsibleGroups", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", multiSelect: "multiSelect", readOnly: "readOnly", scrollable: "scrollable", selected: "selected", store: "store", title: "title", toggleAllIfCtrlPressed: "toggleAllIfCtrlPressed", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeItem: "onBeforeItem", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onItem: "onItem", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelectionChange: "onSelectionChange", onShow: "onShow", onToggleGroup: "onToggleGroup" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumGroupBarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-group-bar',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { activateOnMouseover: [{
                type: Input
            }], adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], allowGroupSelect: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearSelectionOnEmptySpaceClick: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeHandler: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], getItemCls: [{
                type: Input
            }], getItemStyle: [{
                type: Input
            }], groupHeaderTpl: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], iconTpl: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemTpl: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], selectAllItem: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], collapsibleGroups: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selected: [{
                type: Input
            }], store: [{
                type: Input
            }], title: [{
                type: Input
            }], toggleAllIfCtrlPressed: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeItem: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onItem: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Label
 */
class BryntumLabelComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumLabelComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumLabelComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumLabelComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumLabelComponent.instanceClass = Label;
BryntumLabelComponent.instanceName = 'Label';
BryntumLabelComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumLabelComponent.bryntumFeatureNames = [];
BryntumLabelComponent.bryntumConfigs = BryntumLabelComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'text',
    'textAlign',
    'title',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumLabelComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'htmlCls',
    'ignoreParentReadOnly',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'title',
    'ui',
    'weight'
];
BryntumLabelComponent.bryntumProps = BryntumLabelComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'text',
    'tooltip',
    'width',
    'x',
    'y'
]);
BryntumLabelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumLabelComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumLabelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumLabelComponent, selector: "bryntum-label", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", title: "title", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", scrollable: "scrollable", text: "text", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumLabelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-label',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], text: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum List
 */
class BryntumListComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeItem = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onItem = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumListComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumListComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumListComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumListComponent.instanceClass = List;
BryntumListComponent.instanceName = 'List';
BryntumListComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeItem',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onItem',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelectionChange',
    'onShow',
    'onToggleGroup'
];
BryntumListComponent.bryntumFeatureNames = [];
BryntumListComponent.bryntumConfigs = BryntumListComponent.bryntumFeatureNames.concat([
    'activateOnMouseover',
    'adopt',
    'align',
    'alignSelf',
    'allowGroupSelect',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'cls',
    'collapsibleGroups',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'height',
    'hidden',
    'hideAnimation',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'items',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'selectAllItem',
    'selected',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'tab',
    'tag',
    'textAlign',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumListComponent.bryntumConfigsOnly = [
    'activateOnMouseover',
    'adopt',
    'align',
    'allowGroupSelect',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'displayField',
    'dock',
    'draggable',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'hideAnimation',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'selectAllItem',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'ui',
    'weight'
];
BryntumListComponent.bryntumProps = BryntumListComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'collapsibleGroups',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'items',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'multiSelect',
    'readOnly',
    'scrollable',
    'selected',
    'store',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'width',
    'x',
    'y'
]);
BryntumListComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumListComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumListComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumListComponent, selector: "bryntum-list", inputs: { activateOnMouseover: "activateOnMouseover", adopt: "adopt", align: "align", allowGroupSelect: "allowGroupSelect", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearSelectionOnEmptySpaceClick: "clearSelectionOnEmptySpaceClick", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", displayField: "displayField", dock: "dock", draggable: "draggable", floating: "floating", getItemCls: "getItemCls", getItemStyle: "getItemStyle", groupHeaderTpl: "groupHeaderTpl", hideAnimation: "hideAnimation", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemTpl: "itemTpl", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", selectAllItem: "selectAllItem", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", collapsibleGroups: "collapsibleGroups", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", multiSelect: "multiSelect", readOnly: "readOnly", scrollable: "scrollable", selected: "selected", store: "store", title: "title", toggleAllIfCtrlPressed: "toggleAllIfCtrlPressed", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeItem: "onBeforeItem", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onItem: "onItem", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelectionChange: "onSelectionChange", onShow: "onShow", onToggleGroup: "onToggleGroup" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumListComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-list',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { activateOnMouseover: [{
                type: Input
            }], adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], allowGroupSelect: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearSelectionOnEmptySpaceClick: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], getItemCls: [{
                type: Input
            }], getItemStyle: [{
                type: Input
            }], groupHeaderTpl: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemTpl: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], selectAllItem: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], collapsibleGroups: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selected: [{
                type: Input
            }], store: [{
                type: Input
            }], title: [{
                type: Input
            }], toggleAllIfCtrlPressed: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeItem: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onItem: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Menu
 */
class BryntumMenuComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeClose = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onItem = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToggle = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumMenuComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumMenuComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumMenuComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumMenuComponent.instanceClass = Menu;
BryntumMenuComponent.instanceName = 'Menu';
BryntumMenuComponent.bryntumEvents = [
    'onBeforeClose',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onCollapse',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onItem',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onToggle',
    'onToolClick'
];
BryntumMenuComponent.bryntumFeatureNames = [];
BryntumMenuComponent.bryntumConfigs = BryntumMenuComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoShow',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'focusOnHover',
    'focusOnToFront',
    'footer',
    'forElement',
    'header',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'modal',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showOnClick',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumMenuComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoShow',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'focusOnHover',
    'focusOnToFront',
    'footer',
    'forElement',
    'header',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maximizable',
    'modal',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showOnClick',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'trapFocus',
    'ui',
    'weight'
];
BryntumMenuComponent.bryntumProps = BryntumMenuComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximized',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'parentMenu',
    'readOnly',
    'record',
    'selectedElement',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumMenuComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumMenuComponent, selector: "bryntum-menu", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoShow: "autoShow", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", focusOnHover: "focusOnHover", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", header: "header", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", modal: "modal", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showOnClick: "showOnClick", showTooltipWhenDisabled: "showTooltipWhenDisabled", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", trapFocus: "trapFocus", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", parentMenu: "parentMenu", selectedElement: "selectedElement", state: "state", values: "values" }, outputs: { onBeforeClose: "onBeforeClose", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onItem: "onItem", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onToggle: "onToggle", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumMenuComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-menu',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], focusOnHover: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], header: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], parentMenu: [{
                type: Input
            }], selectedElement: [{
                type: Input
            }], state: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeClose: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onItem: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToggle: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum ModelCombo
 */
class BryntumModelComboComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumModelComboComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumModelComboComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumModelComboComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumModelComboComponent.instanceClass = ModelCombo;
BryntumModelComboComponent.instanceName = 'ModelCombo';
BryntumModelComboComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onTrigger'
];
BryntumModelComboComponent.bryntumFeatureNames = [];
BryntumModelComboComponent.bryntumConfigs = BryntumModelComboComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'editable',
    'emptyText',
    'encodeFilterParams',
    'extraData',
    'filterOnEnter',
    'filterOperator',
    'filterParamName',
    'filterSelected',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minChars',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggerAction',
    'triggers',
    'ui',
    'validateFilter',
    'validateOnInput',
    'value',
    'valueField',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumModelComboComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'defaultBindProperty',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'emptyText',
    'encodeFilterParams',
    'filterOnEnter',
    'filterParamName',
    'filterSelected',
    'floating',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minChars',
    'minLength',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'triggerAction',
    'ui',
    'validateFilter',
    'validateOnInput',
    'valueField',
    'weight'
];
BryntumModelComboComponent.bryntumProps = BryntumModelComboComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'filterOperator',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'picker',
    'readOnly',
    'scrollable',
    'store',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumModelComboComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumModelComboComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumModelComboComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumModelComboComponent, selector: "bryntum-model-combo", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", caseSensitive: "caseSensitive", centered: "centered", chipView: "chipView", clearable: "clearable", clearTextOnPickerHide: "clearTextOnPickerHide", clearTextOnSelection: "clearTextOnSelection", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", createOnUnmatched: "createOnUnmatched", defaultBindProperty: "defaultBindProperty", displayField: "displayField", displayValueRenderer: "displayValueRenderer", dock: "dock", draggable: "draggable", emptyText: "emptyText", encodeFilterParams: "encodeFilterParams", filterOnEnter: "filterOnEnter", filterParamName: "filterParamName", filterSelected: "filterSelected", floating: "floating", hideAnimation: "hideAnimation", hidePickerOnSelect: "hidePickerOnSelect", hideTrigger: "hideTrigger", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inlinePicker: "inlinePicker", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", items: "items", keyStrokeChangeDelay: "keyStrokeChangeDelay", keyStrokeFilterDelay: "keyStrokeFilterDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listCls: "listCls", listeners: "listeners", listItemTpl: "listItemTpl", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minChars: "minChars", minLength: "minLength", monitorResize: "monitorResize", multiSelect: "multiSelect", multiValueSeparator: "multiValueSeparator", name: "name", overlayAnchor: "overlayAnchor", owner: "owner", pickerAlignElement: "pickerAlignElement", pickerWidth: "pickerWidth", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", primaryFilter: "primaryFilter", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", triggerAction: "triggerAction", ui: "ui", validateFilter: "validateFilter", validateOnInput: "validateOnInput", valueField: "valueField", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", filterOperator: "filterOperator", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", picker: "picker", readOnly: "readOnly", store: "store", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumModelComboComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-model-combo',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], caseSensitive: [{
                type: Input
            }], centered: [{
                type: Input
            }], chipView: [{
                type: Input
            }], clearable: [{
                type: Input
            }], clearTextOnPickerHide: [{
                type: Input
            }], clearTextOnSelection: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], createOnUnmatched: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], displayValueRenderer: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], encodeFilterParams: [{
                type: Input
            }], filterOnEnter: [{
                type: Input
            }], filterParamName: [{
                type: Input
            }], filterSelected: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hidePickerOnSelect: [{
                type: Input
            }], hideTrigger: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inlinePicker: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], items: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], keyStrokeFilterDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listCls: [{
                type: Input
            }], listeners: [{
                type: Input
            }], listItemTpl: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minChars: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], multiValueSeparator: [{
                type: Input
            }], name: [{
                type: Input
            }], overlayAnchor: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerWidth: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], primaryFilter: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerAction: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateFilter: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], valueField: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], filterOperator: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], picker: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], store: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum NumberField
 */
class BryntumNumberFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumNumberFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumNumberFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumNumberFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumNumberFieldComponent.instanceClass = NumberField;
BryntumNumberFieldComponent.instanceName = 'NumberField';
BryntumNumberFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumNumberFieldComponent.bryntumFeatureNames = [];
BryntumNumberFieldComponent.bryntumConfigs = BryntumNumberFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'changeOnSpin',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'decimalPrecision',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'format',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'largeStep',
    'leadingZeroes',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'min',
    'minHeight',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'step',
    'tab',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'wrapAround',
    'x',
    'y'
]);
BryntumNumberFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'changeOnSpin',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'decimalPrecision',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'format',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'largeStep',
    'leadingZeroes',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'max',
    'min',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weight',
    'wrapAround'
];
BryntumNumberFieldComponent.bryntumProps = BryntumNumberFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'step',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumNumberFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumNumberFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumNumberFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumNumberFieldComponent, selector: "bryntum-number-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", changeOnSpin: "changeOnSpin", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", decimalPrecision: "decimalPrecision", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", format: "format", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", largeStep: "largeStep", leadingZeroes: "leadingZeroes", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", max: "max", min: "min", monitorResize: "monitorResize", name: "name", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weight: "weight", wrapAround: "wrapAround", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", step: "step", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumNumberFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-number-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], changeOnSpin: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], decimalPrecision: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], format: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], largeStep: [{
                type: Input
            }], leadingZeroes: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], max: [{
                type: Input
            }], min: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], wrapAround: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], step: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum PagingToolbar
 */
class BryntumPagingToolbarComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumPagingToolbarComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumPagingToolbarComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumPagingToolbarComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumPagingToolbarComponent.instanceClass = PagingToolbar;
BryntumPagingToolbarComponent.instanceName = 'PagingToolbar';
BryntumPagingToolbarComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumPagingToolbarComponent.bryntumFeatureNames = [];
BryntumPagingToolbarComponent.bryntumConfigs = BryntumPagingToolbarComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'overflow',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'strictRecordMapping',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'ui',
    'weight',
    'widgetCls',
    'width',
    'x',
    'y'
]);
BryntumPagingToolbarComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'overflow',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'ui',
    'weight',
    'widgetCls'
];
BryntumPagingToolbarComponent.bryntumProps = BryntumPagingToolbarComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'strictRecordMapping',
    'tools',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumPagingToolbarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumPagingToolbarComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumPagingToolbarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumPagingToolbarComponent, selector: "bryntum-paging-toolbar", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", overflow: "overflow", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", store: "store", tab: "tab", tag: "tag", textAlign: "textAlign", textContent: "textContent", title: "title", ui: "ui", weight: "weight", widgetCls: "widgetCls", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumPagingToolbarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-paging-toolbar',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], overflow: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], store: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], widgetCls: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Panel
 */
class BryntumPanelComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumPanelComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumPanelComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumPanelComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumPanelComponent.instanceClass = Panel;
BryntumPanelComponent.instanceName = 'Panel';
BryntumPanelComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onCollapse',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onToolClick'
];
BryntumPanelComponent.bryntumFeatureNames = [];
BryntumPanelComponent.bryntumConfigs = BryntumPanelComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'footer',
    'header',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumPanelComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'footer',
    'header',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'trapFocus',
    'ui',
    'weight'
];
BryntumPanelComponent.bryntumProps = BryntumPanelComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumPanelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumPanelComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumPanelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumPanelComponent, selector: "bryntum-panel", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", footer: "footer", header: "header", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", trapFocus: "trapFocus", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", state: "state", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumPanelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-panel',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], footer: [{
                type: Input
            }], header: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], state: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum PasswordField
 */
class BryntumPasswordFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumPasswordFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumPasswordFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumPasswordFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumPasswordFieldComponent.instanceClass = PasswordField;
BryntumPasswordFieldComponent.instanceName = 'PasswordField';
BryntumPasswordFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumPasswordFieldComponent.bryntumFeatureNames = [];
BryntumPasswordFieldComponent.bryntumConfigs = BryntumPasswordFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumPasswordFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weight'
];
BryntumPasswordFieldComponent.bryntumProps = BryntumPasswordFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumPasswordFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumPasswordFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumPasswordFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumPasswordFieldComponent, selector: "bryntum-password-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", name: "name", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumPasswordFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-password-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum ProjectCombo
 */
class BryntumProjectComboComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumProjectComboComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumProjectComboComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumProjectComboComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumProjectComboComponent.instanceClass = ProjectCombo;
BryntumProjectComboComponent.instanceName = 'ProjectCombo';
BryntumProjectComboComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onTrigger'
];
BryntumProjectComboComponent.bryntumFeatureNames = [];
BryntumProjectComboComponent.bryntumConfigs = BryntumProjectComboComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'editable',
    'emptyText',
    'encodeFilterParams',
    'extraData',
    'filterOnEnter',
    'filterOperator',
    'filterParamName',
    'filterSelected',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minChars',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'project',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggerAction',
    'triggers',
    'ui',
    'validateFilter',
    'validateOnInput',
    'value',
    'valueField',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumProjectComboComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'defaultBindProperty',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'emptyText',
    'encodeFilterParams',
    'filterOnEnter',
    'filterParamName',
    'filterSelected',
    'floating',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minChars',
    'minLength',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'project',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'triggerAction',
    'ui',
    'validateFilter',
    'validateOnInput',
    'valueField',
    'weight'
];
BryntumProjectComboComponent.bryntumProps = BryntumProjectComboComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'filterOperator',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'picker',
    'readOnly',
    'scrollable',
    'store',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumProjectComboComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumProjectComboComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumProjectComboComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumProjectComboComponent, selector: "bryntum-project-combo", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", caseSensitive: "caseSensitive", centered: "centered", chipView: "chipView", clearable: "clearable", clearTextOnPickerHide: "clearTextOnPickerHide", clearTextOnSelection: "clearTextOnSelection", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", createOnUnmatched: "createOnUnmatched", defaultBindProperty: "defaultBindProperty", displayField: "displayField", displayValueRenderer: "displayValueRenderer", dock: "dock", draggable: "draggable", emptyText: "emptyText", encodeFilterParams: "encodeFilterParams", filterOnEnter: "filterOnEnter", filterParamName: "filterParamName", filterSelected: "filterSelected", floating: "floating", hideAnimation: "hideAnimation", hidePickerOnSelect: "hidePickerOnSelect", hideTrigger: "hideTrigger", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inlinePicker: "inlinePicker", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", items: "items", keyStrokeChangeDelay: "keyStrokeChangeDelay", keyStrokeFilterDelay: "keyStrokeFilterDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listCls: "listCls", listeners: "listeners", listItemTpl: "listItemTpl", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minChars: "minChars", minLength: "minLength", monitorResize: "monitorResize", multiSelect: "multiSelect", multiValueSeparator: "multiValueSeparator", name: "name", overlayAnchor: "overlayAnchor", owner: "owner", pickerAlignElement: "pickerAlignElement", pickerWidth: "pickerWidth", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", primaryFilter: "primaryFilter", project: "project", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", triggerAction: "triggerAction", ui: "ui", validateFilter: "validateFilter", validateOnInput: "validateOnInput", valueField: "valueField", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", filterOperator: "filterOperator", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", picker: "picker", readOnly: "readOnly", store: "store", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumProjectComboComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-project-combo',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], caseSensitive: [{
                type: Input
            }], centered: [{
                type: Input
            }], chipView: [{
                type: Input
            }], clearable: [{
                type: Input
            }], clearTextOnPickerHide: [{
                type: Input
            }], clearTextOnSelection: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], createOnUnmatched: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], displayValueRenderer: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], encodeFilterParams: [{
                type: Input
            }], filterOnEnter: [{
                type: Input
            }], filterParamName: [{
                type: Input
            }], filterSelected: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hidePickerOnSelect: [{
                type: Input
            }], hideTrigger: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inlinePicker: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], items: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], keyStrokeFilterDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listCls: [{
                type: Input
            }], listeners: [{
                type: Input
            }], listItemTpl: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minChars: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], multiValueSeparator: [{
                type: Input
            }], name: [{
                type: Input
            }], overlayAnchor: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerWidth: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], primaryFilter: [{
                type: Input
            }], project: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerAction: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateFilter: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], valueField: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], filterOperator: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], picker: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], store: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum ProjectModel
 */
class BryntumSchedulerProProjectModelComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeLoad = new EventEmitter();
        this.onBeforeLoadApply = new EventEmitter();
        this.onBeforeResponseApply = new EventEmitter();
        this.onBeforeSend = new EventEmitter();
        this.onBeforeSync = new EventEmitter();
        this.onBeforeSyncApply = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onCycle = new EventEmitter();
        this.onDataReady = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onEmptyCalendar = new EventEmitter();
        this.onHasChanges = new EventEmitter();
        this.onLoad = new EventEmitter();
        this.onLoadCanceled = new EventEmitter();
        this.onLoadFail = new EventEmitter();
        this.onNoChanges = new EventEmitter();
        this.onProgress = new EventEmitter();
        this.onRequestDone = new EventEmitter();
        this.onRequestFail = new EventEmitter();
        this.onSchedulingConflict = new EventEmitter();
        this.onSync = new EventEmitter();
        this.onSyncCanceled = new EventEmitter();
        this.onSyncDelayed = new EventEmitter();
        this.onSyncFail = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSchedulerProProjectModelComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSchedulerProProjectModelComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSchedulerProProjectModelComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSchedulerProProjectModelComponent.instanceClass = ProjectModel;
BryntumSchedulerProProjectModelComponent.instanceName = 'ProjectModel';
BryntumSchedulerProProjectModelComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeLoad',
    'onBeforeLoadApply',
    'onBeforeResponseApply',
    'onBeforeSend',
    'onBeforeSync',
    'onBeforeSyncApply',
    'onCatchAll',
    'onChange',
    'onCycle',
    'onDataReady',
    'onDestroy',
    'onEmptyCalendar',
    'onHasChanges',
    'onLoad',
    'onLoadCanceled',
    'onLoadFail',
    'onNoChanges',
    'onProgress',
    'onRequestDone',
    'onRequestFail',
    'onSchedulingConflict',
    'onSync',
    'onSyncCanceled',
    'onSyncDelayed',
    'onSyncFail'
];
BryntumSchedulerProProjectModelComponent.bryntumFeatureNames = [];
BryntumSchedulerProProjectModelComponent.bryntumConfigs = BryntumSchedulerProProjectModelComponent.bryntumFeatureNames.concat([
    'addConstraintOnDateSet',
    'adjustDurationToDST',
    'assignmentModelClass',
    'assignments',
    'assignmentsData',
    'assignmentStore',
    'assignmentStoreClass',
    'autoCalculatePercentDoneForParentTasks',
    'autoLoad',
    'autoMergeAdjacentSegments',
    'autoSync',
    'autoSyncTimeout',
    'bubbleEvents',
    'calendar',
    'calendarManagerStore',
    'calendarManagerStoreClass',
    'calendarModelClass',
    'calendars',
    'calendarsData',
    'callOnFunctions',
    'children',
    'crudStores',
    'daysPerMonth',
    'daysPerWeek',
    'delayCalculation',
    'dependencies',
    'dependenciesCalendar',
    'dependenciesData',
    'dependencyModelClass',
    'dependencyStore',
    'dependencyStoreClass',
    'enableProgressNotifications',
    'encoder',
    'eventModelClass',
    'events',
    'eventsData',
    'eventStore',
    'eventStoreClass',
    'expanded',
    'forceSync',
    'hoursPerDay',
    'id',
    'includeChildrenInRemoveRequest',
    'json',
    'listeners',
    'loadUrl',
    'maxCalendarRange',
    'orderedParentIndex',
    'parentId',
    'parentIndex',
    'phantomIdField',
    'phantomParentIdField',
    'readOnly',
    'resetIdsBeforeSync',
    'resetUndoRedoQueuesAfterLoad',
    'resourceAllocationInfoClass',
    'resourceModelClass',
    'resources',
    'resourcesData',
    'resourceStore',
    'resourceStoreClass',
    'resourceTimeRanges',
    'resourceTimeRangesData',
    'resourceTimeRangeStore',
    'resourceTimeRangeStoreClass',
    'shouldSyncDataOnLoad',
    'silenceInitialCommit',
    'skipNonWorkingTimeInDurationWhenSchedulingManually',
    'skipNonWorkingTimeWhenSchedulingManually',
    'skipSuccessProperty',
    'stm',
    'storeIdProperty',
    'supportShortSyncResponse',
    'syncApplySequence',
    'syncUrl',
    'taskStore',
    'timeRanges',
    'timeRangesData',
    'timeRangeStore',
    'timeRangeStoreClass',
    'timeZone',
    'toJSONResultFormat',
    'trackProjectModelChanges',
    'trackResponseType',
    'transport',
    'validateResponse',
    'writeAllFields'
]);
BryntumSchedulerProProjectModelComponent.bryntumConfigsOnly = [
    'adjustDurationToDST',
    'assignmentModelClass',
    'assignmentsData',
    'assignmentStoreClass',
    'autoLoad',
    'autoSync',
    'autoSyncTimeout',
    'bubbleEvents',
    'calendarManagerStoreClass',
    'calendarModelClass',
    'calendarsData',
    'callOnFunctions',
    'children',
    'delayCalculation',
    'dependenciesData',
    'dependencyModelClass',
    'dependencyStoreClass',
    'encoder',
    'eventModelClass',
    'eventsData',
    'eventStoreClass',
    'expanded',
    'includeChildrenInRemoveRequest',
    'listeners',
    'maxCalendarRange',
    'orderedParentIndex',
    'parentId',
    'parentIndex',
    'phantomIdField',
    'phantomParentIdField',
    'resetIdsBeforeSync',
    'resetUndoRedoQueuesAfterLoad',
    'resourceAllocationInfoClass',
    'resourceModelClass',
    'resourcesData',
    'resourceStoreClass',
    'resourceTimeRangesData',
    'resourceTimeRangeStoreClass',
    'silenceInitialCommit',
    'skipSuccessProperty',
    'storeIdProperty',
    'supportShortSyncResponse',
    'taskStore',
    'timeRangesData',
    'timeRangeStoreClass',
    'toJSONResultFormat',
    'trackResponseType',
    'transport',
    'validateResponse',
    'writeAllFields'
];
BryntumSchedulerProProjectModelComponent.bryntumProps = BryntumSchedulerProProjectModelComponent.bryntumFeatureNames.concat([
    'addConstraintOnDateSet',
    'allChildren',
    'assignments',
    'assignmentStore',
    'autoCalculatePercentDoneForParentTasks',
    'autoMergeAdjacentSegments',
    'calendar',
    'calendarManagerStore',
    'calendars',
    'crudStores',
    'daysPerMonth',
    'daysPerWeek',
    'dependencies',
    'dependenciesCalendar',
    'dependencyStore',
    'descendantCount',
    'enableProgressNotifications',
    'events',
    'eventStore',
    'forceSync',
    'hasGeneratedId',
    'hoursPerDay',
    'id',
    'inlineData',
    'internalId',
    'isCommitting',
    'isCreating',
    'isValid',
    'json',
    'loadUrl',
    'previousSiblingsTotalCount',
    'readOnly',
    'resources',
    'resourceStore',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'shouldSyncDataOnLoad',
    'skipNonWorkingTimeInDurationWhenSchedulingManually',
    'skipNonWorkingTimeWhenSchedulingManually',
    'stm',
    'syncApplySequence',
    'syncUrl',
    'timeRanges',
    'timeRangeStore',
    'timeZone',
    'trackProjectModelChanges',
    'visibleDescendantCount'
]);
BryntumSchedulerProProjectModelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerProProjectModelComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSchedulerProProjectModelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSchedulerProProjectModelComponent, selector: "bryntum-scheduler-pro-project-model", inputs: { adjustDurationToDST: "adjustDurationToDST", assignmentModelClass: "assignmentModelClass", assignmentsData: "assignmentsData", assignmentStoreClass: "assignmentStoreClass", autoLoad: "autoLoad", autoSync: "autoSync", autoSyncTimeout: "autoSyncTimeout", bubbleEvents: "bubbleEvents", calendarManagerStoreClass: "calendarManagerStoreClass", calendarModelClass: "calendarModelClass", calendarsData: "calendarsData", callOnFunctions: "callOnFunctions", children: "children", delayCalculation: "delayCalculation", dependenciesData: "dependenciesData", dependencyModelClass: "dependencyModelClass", dependencyStoreClass: "dependencyStoreClass", encoder: "encoder", eventModelClass: "eventModelClass", eventsData: "eventsData", eventStoreClass: "eventStoreClass", expanded: "expanded", includeChildrenInRemoveRequest: "includeChildrenInRemoveRequest", listeners: "listeners", maxCalendarRange: "maxCalendarRange", orderedParentIndex: "orderedParentIndex", parentId: "parentId", parentIndex: "parentIndex", phantomIdField: "phantomIdField", phantomParentIdField: "phantomParentIdField", resetIdsBeforeSync: "resetIdsBeforeSync", resetUndoRedoQueuesAfterLoad: "resetUndoRedoQueuesAfterLoad", resourceAllocationInfoClass: "resourceAllocationInfoClass", resourceModelClass: "resourceModelClass", resourcesData: "resourcesData", resourceStoreClass: "resourceStoreClass", resourceTimeRangesData: "resourceTimeRangesData", resourceTimeRangeStoreClass: "resourceTimeRangeStoreClass", silenceInitialCommit: "silenceInitialCommit", skipSuccessProperty: "skipSuccessProperty", storeIdProperty: "storeIdProperty", supportShortSyncResponse: "supportShortSyncResponse", taskStore: "taskStore", timeRangesData: "timeRangesData", timeRangeStoreClass: "timeRangeStoreClass", toJSONResultFormat: "toJSONResultFormat", trackResponseType: "trackResponseType", transport: "transport", validateResponse: "validateResponse", writeAllFields: "writeAllFields", addConstraintOnDateSet: "addConstraintOnDateSet", assignments: "assignments", assignmentStore: "assignmentStore", autoCalculatePercentDoneForParentTasks: "autoCalculatePercentDoneForParentTasks", autoMergeAdjacentSegments: "autoMergeAdjacentSegments", calendar: "calendar", calendarManagerStore: "calendarManagerStore", calendars: "calendars", crudStores: "crudStores", daysPerMonth: "daysPerMonth", daysPerWeek: "daysPerWeek", dependencies: "dependencies", dependenciesCalendar: "dependenciesCalendar", dependencyStore: "dependencyStore", enableProgressNotifications: "enableProgressNotifications", events: "events", eventStore: "eventStore", forceSync: "forceSync", hoursPerDay: "hoursPerDay", id: "id", json: "json", loadUrl: "loadUrl", readOnly: "readOnly", resources: "resources", resourceStore: "resourceStore", resourceTimeRanges: "resourceTimeRanges", resourceTimeRangeStore: "resourceTimeRangeStore", shouldSyncDataOnLoad: "shouldSyncDataOnLoad", skipNonWorkingTimeInDurationWhenSchedulingManually: "skipNonWorkingTimeInDurationWhenSchedulingManually", skipNonWorkingTimeWhenSchedulingManually: "skipNonWorkingTimeWhenSchedulingManually", stm: "stm", syncApplySequence: "syncApplySequence", syncUrl: "syncUrl", timeRanges: "timeRanges", timeRangeStore: "timeRangeStore", timeZone: "timeZone", trackProjectModelChanges: "trackProjectModelChanges", allChildren: "allChildren", descendantCount: "descendantCount", hasGeneratedId: "hasGeneratedId", inlineData: "inlineData", internalId: "internalId", isCommitting: "isCommitting", isCreating: "isCreating", isValid: "isValid", previousSiblingsTotalCount: "previousSiblingsTotalCount", visibleDescendantCount: "visibleDescendantCount" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeLoad: "onBeforeLoad", onBeforeLoadApply: "onBeforeLoadApply", onBeforeResponseApply: "onBeforeResponseApply", onBeforeSend: "onBeforeSend", onBeforeSync: "onBeforeSync", onBeforeSyncApply: "onBeforeSyncApply", onCatchAll: "onCatchAll", onChange: "onChange", onCycle: "onCycle", onDataReady: "onDataReady", onDestroy: "onDestroy", onEmptyCalendar: "onEmptyCalendar", onHasChanges: "onHasChanges", onLoad: "onLoad", onLoadCanceled: "onLoadCanceled", onLoadFail: "onLoadFail", onNoChanges: "onNoChanges", onProgress: "onProgress", onRequestDone: "onRequestDone", onRequestFail: "onRequestFail", onSchedulingConflict: "onSchedulingConflict", onSync: "onSync", onSyncCanceled: "onSyncCanceled", onSyncDelayed: "onSyncDelayed", onSyncFail: "onSyncFail" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerProProjectModelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-scheduler-pro-project-model',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adjustDurationToDST: [{
                type: Input
            }], assignmentModelClass: [{
                type: Input
            }], assignmentsData: [{
                type: Input
            }], assignmentStoreClass: [{
                type: Input
            }], autoLoad: [{
                type: Input
            }], autoSync: [{
                type: Input
            }], autoSyncTimeout: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], calendarManagerStoreClass: [{
                type: Input
            }], calendarModelClass: [{
                type: Input
            }], calendarsData: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], children: [{
                type: Input
            }], delayCalculation: [{
                type: Input
            }], dependenciesData: [{
                type: Input
            }], dependencyModelClass: [{
                type: Input
            }], dependencyStoreClass: [{
                type: Input
            }], encoder: [{
                type: Input
            }], eventModelClass: [{
                type: Input
            }], eventsData: [{
                type: Input
            }], eventStoreClass: [{
                type: Input
            }], expanded: [{
                type: Input
            }], includeChildrenInRemoveRequest: [{
                type: Input
            }], listeners: [{
                type: Input
            }], maxCalendarRange: [{
                type: Input
            }], orderedParentIndex: [{
                type: Input
            }], parentId: [{
                type: Input
            }], parentIndex: [{
                type: Input
            }], phantomIdField: [{
                type: Input
            }], phantomParentIdField: [{
                type: Input
            }], resetIdsBeforeSync: [{
                type: Input
            }], resetUndoRedoQueuesAfterLoad: [{
                type: Input
            }], resourceAllocationInfoClass: [{
                type: Input
            }], resourceModelClass: [{
                type: Input
            }], resourcesData: [{
                type: Input
            }], resourceStoreClass: [{
                type: Input
            }], resourceTimeRangesData: [{
                type: Input
            }], resourceTimeRangeStoreClass: [{
                type: Input
            }], silenceInitialCommit: [{
                type: Input
            }], skipSuccessProperty: [{
                type: Input
            }], storeIdProperty: [{
                type: Input
            }], supportShortSyncResponse: [{
                type: Input
            }], taskStore: [{
                type: Input
            }], timeRangesData: [{
                type: Input
            }], timeRangeStoreClass: [{
                type: Input
            }], toJSONResultFormat: [{
                type: Input
            }], trackResponseType: [{
                type: Input
            }], transport: [{
                type: Input
            }], validateResponse: [{
                type: Input
            }], writeAllFields: [{
                type: Input
            }], addConstraintOnDateSet: [{
                type: Input
            }], assignments: [{
                type: Input
            }], assignmentStore: [{
                type: Input
            }], autoCalculatePercentDoneForParentTasks: [{
                type: Input
            }], autoMergeAdjacentSegments: [{
                type: Input
            }], calendar: [{
                type: Input
            }], calendarManagerStore: [{
                type: Input
            }], calendars: [{
                type: Input
            }], crudStores: [{
                type: Input
            }], daysPerMonth: [{
                type: Input
            }], daysPerWeek: [{
                type: Input
            }], dependencies: [{
                type: Input
            }], dependenciesCalendar: [{
                type: Input
            }], dependencyStore: [{
                type: Input
            }], enableProgressNotifications: [{
                type: Input
            }], events: [{
                type: Input
            }], eventStore: [{
                type: Input
            }], forceSync: [{
                type: Input
            }], hoursPerDay: [{
                type: Input
            }], id: [{
                type: Input
            }], json: [{
                type: Input
            }], loadUrl: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], resources: [{
                type: Input
            }], resourceStore: [{
                type: Input
            }], resourceTimeRanges: [{
                type: Input
            }], resourceTimeRangeStore: [{
                type: Input
            }], shouldSyncDataOnLoad: [{
                type: Input
            }], skipNonWorkingTimeInDurationWhenSchedulingManually: [{
                type: Input
            }], skipNonWorkingTimeWhenSchedulingManually: [{
                type: Input
            }], stm: [{
                type: Input
            }], syncApplySequence: [{
                type: Input
            }], syncUrl: [{
                type: Input
            }], timeRanges: [{
                type: Input
            }], timeRangeStore: [{
                type: Input
            }], timeZone: [{
                type: Input
            }], trackProjectModelChanges: [{
                type: Input
            }], allChildren: [{
                type: Input
            }], descendantCount: [{
                type: Input
            }], hasGeneratedId: [{
                type: Input
            }], inlineData: [{
                type: Input
            }], internalId: [{
                type: Input
            }], isCommitting: [{
                type: Input
            }], isCreating: [{
                type: Input
            }], isValid: [{
                type: Input
            }], previousSiblingsTotalCount: [{
                type: Input
            }], visibleDescendantCount: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeLoad: [{
                type: Output
            }], onBeforeLoadApply: [{
                type: Output
            }], onBeforeResponseApply: [{
                type: Output
            }], onBeforeSend: [{
                type: Output
            }], onBeforeSync: [{
                type: Output
            }], onBeforeSyncApply: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onCycle: [{
                type: Output
            }], onDataReady: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onEmptyCalendar: [{
                type: Output
            }], onHasChanges: [{
                type: Output
            }], onLoad: [{
                type: Output
            }], onLoadCanceled: [{
                type: Output
            }], onLoadFail: [{
                type: Output
            }], onNoChanges: [{
                type: Output
            }], onProgress: [{
                type: Output
            }], onRequestDone: [{
                type: Output
            }], onRequestFail: [{
                type: Output
            }], onSchedulingConflict: [{
                type: Output
            }], onSync: [{
                type: Output
            }], onSyncCanceled: [{
                type: Output
            }], onSyncDelayed: [{
                type: Output
            }], onSyncFail: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum ProjectModel
 */
class BryntumProjectModelComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeLoad = new EventEmitter();
        this.onBeforeLoadApply = new EventEmitter();
        this.onBeforeResponseApply = new EventEmitter();
        this.onBeforeSend = new EventEmitter();
        this.onBeforeSync = new EventEmitter();
        this.onBeforeSyncApply = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onCycle = new EventEmitter();
        this.onDataReady = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onEmptyCalendar = new EventEmitter();
        this.onHasChanges = new EventEmitter();
        this.onLoad = new EventEmitter();
        this.onLoadCanceled = new EventEmitter();
        this.onLoadFail = new EventEmitter();
        this.onNoChanges = new EventEmitter();
        this.onProgress = new EventEmitter();
        this.onRequestDone = new EventEmitter();
        this.onRequestFail = new EventEmitter();
        this.onSchedulingConflict = new EventEmitter();
        this.onSync = new EventEmitter();
        this.onSyncCanceled = new EventEmitter();
        this.onSyncDelayed = new EventEmitter();
        this.onSyncFail = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumProjectModelComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumProjectModelComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumProjectModelComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumProjectModelComponent.instanceClass = ProjectModel;
BryntumProjectModelComponent.instanceName = 'ProjectModel';
BryntumProjectModelComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeLoad',
    'onBeforeLoadApply',
    'onBeforeResponseApply',
    'onBeforeSend',
    'onBeforeSync',
    'onBeforeSyncApply',
    'onCatchAll',
    'onChange',
    'onCycle',
    'onDataReady',
    'onDestroy',
    'onEmptyCalendar',
    'onHasChanges',
    'onLoad',
    'onLoadCanceled',
    'onLoadFail',
    'onNoChanges',
    'onProgress',
    'onRequestDone',
    'onRequestFail',
    'onSchedulingConflict',
    'onSync',
    'onSyncCanceled',
    'onSyncDelayed',
    'onSyncFail'
];
BryntumProjectModelComponent.bryntumFeatureNames = [];
BryntumProjectModelComponent.bryntumConfigs = BryntumProjectModelComponent.bryntumFeatureNames.concat([
    'addConstraintOnDateSet',
    'adjustDurationToDST',
    'assignmentModelClass',
    'assignments',
    'assignmentsData',
    'assignmentStore',
    'assignmentStoreClass',
    'autoCalculatePercentDoneForParentTasks',
    'autoLoad',
    'autoMergeAdjacentSegments',
    'autoSync',
    'autoSyncTimeout',
    'bubbleEvents',
    'calendar',
    'calendarManagerStore',
    'calendarManagerStoreClass',
    'calendarModelClass',
    'calendars',
    'calendarsData',
    'callOnFunctions',
    'children',
    'crudStores',
    'daysPerMonth',
    'daysPerWeek',
    'delayCalculation',
    'dependencies',
    'dependenciesCalendar',
    'dependenciesData',
    'dependencyModelClass',
    'dependencyStore',
    'dependencyStoreClass',
    'enableProgressNotifications',
    'encoder',
    'eventModelClass',
    'events',
    'eventsData',
    'eventStore',
    'eventStoreClass',
    'expanded',
    'forceSync',
    'hoursPerDay',
    'id',
    'includeChildrenInRemoveRequest',
    'json',
    'listeners',
    'loadUrl',
    'maxCalendarRange',
    'orderedParentIndex',
    'parentId',
    'parentIndex',
    'phantomIdField',
    'phantomParentIdField',
    'readOnly',
    'resetIdsBeforeSync',
    'resetUndoRedoQueuesAfterLoad',
    'resourceAllocationInfoClass',
    'resourceModelClass',
    'resources',
    'resourcesData',
    'resourceStore',
    'resourceStoreClass',
    'resourceTimeRanges',
    'resourceTimeRangesData',
    'resourceTimeRangeStore',
    'resourceTimeRangeStoreClass',
    'shouldSyncDataOnLoad',
    'silenceInitialCommit',
    'skipNonWorkingTimeInDurationWhenSchedulingManually',
    'skipNonWorkingTimeWhenSchedulingManually',
    'skipSuccessProperty',
    'stm',
    'storeIdProperty',
    'supportShortSyncResponse',
    'syncApplySequence',
    'syncUrl',
    'taskStore',
    'timeRanges',
    'timeRangesData',
    'timeRangeStore',
    'timeRangeStoreClass',
    'timeZone',
    'toJSONResultFormat',
    'trackProjectModelChanges',
    'trackResponseType',
    'transport',
    'validateResponse',
    'writeAllFields'
]);
BryntumProjectModelComponent.bryntumConfigsOnly = [
    'adjustDurationToDST',
    'assignmentModelClass',
    'assignmentsData',
    'assignmentStoreClass',
    'autoLoad',
    'autoSync',
    'autoSyncTimeout',
    'bubbleEvents',
    'calendarManagerStoreClass',
    'calendarModelClass',
    'calendarsData',
    'callOnFunctions',
    'children',
    'delayCalculation',
    'dependenciesData',
    'dependencyModelClass',
    'dependencyStoreClass',
    'encoder',
    'eventModelClass',
    'eventsData',
    'eventStoreClass',
    'expanded',
    'includeChildrenInRemoveRequest',
    'listeners',
    'maxCalendarRange',
    'orderedParentIndex',
    'parentId',
    'parentIndex',
    'phantomIdField',
    'phantomParentIdField',
    'resetIdsBeforeSync',
    'resetUndoRedoQueuesAfterLoad',
    'resourceAllocationInfoClass',
    'resourceModelClass',
    'resourcesData',
    'resourceStoreClass',
    'resourceTimeRangesData',
    'resourceTimeRangeStoreClass',
    'silenceInitialCommit',
    'skipSuccessProperty',
    'storeIdProperty',
    'supportShortSyncResponse',
    'taskStore',
    'timeRangesData',
    'timeRangeStoreClass',
    'toJSONResultFormat',
    'trackResponseType',
    'transport',
    'validateResponse',
    'writeAllFields'
];
BryntumProjectModelComponent.bryntumProps = BryntumProjectModelComponent.bryntumFeatureNames.concat([
    'addConstraintOnDateSet',
    'allChildren',
    'assignments',
    'assignmentStore',
    'autoCalculatePercentDoneForParentTasks',
    'autoMergeAdjacentSegments',
    'calendar',
    'calendarManagerStore',
    'calendars',
    'crudStores',
    'daysPerMonth',
    'daysPerWeek',
    'dependencies',
    'dependenciesCalendar',
    'dependencyStore',
    'descendantCount',
    'enableProgressNotifications',
    'events',
    'eventStore',
    'forceSync',
    'hasGeneratedId',
    'hoursPerDay',
    'id',
    'inlineData',
    'internalId',
    'isCommitting',
    'isCreating',
    'isValid',
    'json',
    'loadUrl',
    'previousSiblingsTotalCount',
    'readOnly',
    'resources',
    'resourceStore',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'shouldSyncDataOnLoad',
    'skipNonWorkingTimeInDurationWhenSchedulingManually',
    'skipNonWorkingTimeWhenSchedulingManually',
    'stm',
    'syncApplySequence',
    'syncUrl',
    'timeRanges',
    'timeRangeStore',
    'timeZone',
    'trackProjectModelChanges',
    'visibleDescendantCount'
]);
BryntumProjectModelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumProjectModelComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumProjectModelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumProjectModelComponent, selector: "bryntum-project-model", inputs: { adjustDurationToDST: "adjustDurationToDST", assignmentModelClass: "assignmentModelClass", assignmentsData: "assignmentsData", assignmentStoreClass: "assignmentStoreClass", autoLoad: "autoLoad", autoSync: "autoSync", autoSyncTimeout: "autoSyncTimeout", bubbleEvents: "bubbleEvents", calendarManagerStoreClass: "calendarManagerStoreClass", calendarModelClass: "calendarModelClass", calendarsData: "calendarsData", callOnFunctions: "callOnFunctions", children: "children", delayCalculation: "delayCalculation", dependenciesData: "dependenciesData", dependencyModelClass: "dependencyModelClass", dependencyStoreClass: "dependencyStoreClass", encoder: "encoder", eventModelClass: "eventModelClass", eventsData: "eventsData", eventStoreClass: "eventStoreClass", expanded: "expanded", includeChildrenInRemoveRequest: "includeChildrenInRemoveRequest", listeners: "listeners", maxCalendarRange: "maxCalendarRange", orderedParentIndex: "orderedParentIndex", parentId: "parentId", parentIndex: "parentIndex", phantomIdField: "phantomIdField", phantomParentIdField: "phantomParentIdField", resetIdsBeforeSync: "resetIdsBeforeSync", resetUndoRedoQueuesAfterLoad: "resetUndoRedoQueuesAfterLoad", resourceAllocationInfoClass: "resourceAllocationInfoClass", resourceModelClass: "resourceModelClass", resourcesData: "resourcesData", resourceStoreClass: "resourceStoreClass", resourceTimeRangesData: "resourceTimeRangesData", resourceTimeRangeStoreClass: "resourceTimeRangeStoreClass", silenceInitialCommit: "silenceInitialCommit", skipSuccessProperty: "skipSuccessProperty", storeIdProperty: "storeIdProperty", supportShortSyncResponse: "supportShortSyncResponse", taskStore: "taskStore", timeRangesData: "timeRangesData", timeRangeStoreClass: "timeRangeStoreClass", toJSONResultFormat: "toJSONResultFormat", trackResponseType: "trackResponseType", transport: "transport", validateResponse: "validateResponse", writeAllFields: "writeAllFields", addConstraintOnDateSet: "addConstraintOnDateSet", assignments: "assignments", assignmentStore: "assignmentStore", autoCalculatePercentDoneForParentTasks: "autoCalculatePercentDoneForParentTasks", autoMergeAdjacentSegments: "autoMergeAdjacentSegments", calendar: "calendar", calendarManagerStore: "calendarManagerStore", calendars: "calendars", crudStores: "crudStores", daysPerMonth: "daysPerMonth", daysPerWeek: "daysPerWeek", dependencies: "dependencies", dependenciesCalendar: "dependenciesCalendar", dependencyStore: "dependencyStore", enableProgressNotifications: "enableProgressNotifications", events: "events", eventStore: "eventStore", forceSync: "forceSync", hoursPerDay: "hoursPerDay", id: "id", json: "json", loadUrl: "loadUrl", readOnly: "readOnly", resources: "resources", resourceStore: "resourceStore", resourceTimeRanges: "resourceTimeRanges", resourceTimeRangeStore: "resourceTimeRangeStore", shouldSyncDataOnLoad: "shouldSyncDataOnLoad", skipNonWorkingTimeInDurationWhenSchedulingManually: "skipNonWorkingTimeInDurationWhenSchedulingManually", skipNonWorkingTimeWhenSchedulingManually: "skipNonWorkingTimeWhenSchedulingManually", stm: "stm", syncApplySequence: "syncApplySequence", syncUrl: "syncUrl", timeRanges: "timeRanges", timeRangeStore: "timeRangeStore", timeZone: "timeZone", trackProjectModelChanges: "trackProjectModelChanges", allChildren: "allChildren", descendantCount: "descendantCount", hasGeneratedId: "hasGeneratedId", inlineData: "inlineData", internalId: "internalId", isCommitting: "isCommitting", isCreating: "isCreating", isValid: "isValid", previousSiblingsTotalCount: "previousSiblingsTotalCount", visibleDescendantCount: "visibleDescendantCount" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeLoad: "onBeforeLoad", onBeforeLoadApply: "onBeforeLoadApply", onBeforeResponseApply: "onBeforeResponseApply", onBeforeSend: "onBeforeSend", onBeforeSync: "onBeforeSync", onBeforeSyncApply: "onBeforeSyncApply", onCatchAll: "onCatchAll", onChange: "onChange", onCycle: "onCycle", onDataReady: "onDataReady", onDestroy: "onDestroy", onEmptyCalendar: "onEmptyCalendar", onHasChanges: "onHasChanges", onLoad: "onLoad", onLoadCanceled: "onLoadCanceled", onLoadFail: "onLoadFail", onNoChanges: "onNoChanges", onProgress: "onProgress", onRequestDone: "onRequestDone", onRequestFail: "onRequestFail", onSchedulingConflict: "onSchedulingConflict", onSync: "onSync", onSyncCanceled: "onSyncCanceled", onSyncDelayed: "onSyncDelayed", onSyncFail: "onSyncFail" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumProjectModelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-project-model',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adjustDurationToDST: [{
                type: Input
            }], assignmentModelClass: [{
                type: Input
            }], assignmentsData: [{
                type: Input
            }], assignmentStoreClass: [{
                type: Input
            }], autoLoad: [{
                type: Input
            }], autoSync: [{
                type: Input
            }], autoSyncTimeout: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], calendarManagerStoreClass: [{
                type: Input
            }], calendarModelClass: [{
                type: Input
            }], calendarsData: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], children: [{
                type: Input
            }], delayCalculation: [{
                type: Input
            }], dependenciesData: [{
                type: Input
            }], dependencyModelClass: [{
                type: Input
            }], dependencyStoreClass: [{
                type: Input
            }], encoder: [{
                type: Input
            }], eventModelClass: [{
                type: Input
            }], eventsData: [{
                type: Input
            }], eventStoreClass: [{
                type: Input
            }], expanded: [{
                type: Input
            }], includeChildrenInRemoveRequest: [{
                type: Input
            }], listeners: [{
                type: Input
            }], maxCalendarRange: [{
                type: Input
            }], orderedParentIndex: [{
                type: Input
            }], parentId: [{
                type: Input
            }], parentIndex: [{
                type: Input
            }], phantomIdField: [{
                type: Input
            }], phantomParentIdField: [{
                type: Input
            }], resetIdsBeforeSync: [{
                type: Input
            }], resetUndoRedoQueuesAfterLoad: [{
                type: Input
            }], resourceAllocationInfoClass: [{
                type: Input
            }], resourceModelClass: [{
                type: Input
            }], resourcesData: [{
                type: Input
            }], resourceStoreClass: [{
                type: Input
            }], resourceTimeRangesData: [{
                type: Input
            }], resourceTimeRangeStoreClass: [{
                type: Input
            }], silenceInitialCommit: [{
                type: Input
            }], skipSuccessProperty: [{
                type: Input
            }], storeIdProperty: [{
                type: Input
            }], supportShortSyncResponse: [{
                type: Input
            }], taskStore: [{
                type: Input
            }], timeRangesData: [{
                type: Input
            }], timeRangeStoreClass: [{
                type: Input
            }], toJSONResultFormat: [{
                type: Input
            }], trackResponseType: [{
                type: Input
            }], transport: [{
                type: Input
            }], validateResponse: [{
                type: Input
            }], writeAllFields: [{
                type: Input
            }], addConstraintOnDateSet: [{
                type: Input
            }], assignments: [{
                type: Input
            }], assignmentStore: [{
                type: Input
            }], autoCalculatePercentDoneForParentTasks: [{
                type: Input
            }], autoMergeAdjacentSegments: [{
                type: Input
            }], calendar: [{
                type: Input
            }], calendarManagerStore: [{
                type: Input
            }], calendars: [{
                type: Input
            }], crudStores: [{
                type: Input
            }], daysPerMonth: [{
                type: Input
            }], daysPerWeek: [{
                type: Input
            }], dependencies: [{
                type: Input
            }], dependenciesCalendar: [{
                type: Input
            }], dependencyStore: [{
                type: Input
            }], enableProgressNotifications: [{
                type: Input
            }], events: [{
                type: Input
            }], eventStore: [{
                type: Input
            }], forceSync: [{
                type: Input
            }], hoursPerDay: [{
                type: Input
            }], id: [{
                type: Input
            }], json: [{
                type: Input
            }], loadUrl: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], resources: [{
                type: Input
            }], resourceStore: [{
                type: Input
            }], resourceTimeRanges: [{
                type: Input
            }], resourceTimeRangeStore: [{
                type: Input
            }], shouldSyncDataOnLoad: [{
                type: Input
            }], skipNonWorkingTimeInDurationWhenSchedulingManually: [{
                type: Input
            }], skipNonWorkingTimeWhenSchedulingManually: [{
                type: Input
            }], stm: [{
                type: Input
            }], syncApplySequence: [{
                type: Input
            }], syncUrl: [{
                type: Input
            }], timeRanges: [{
                type: Input
            }], timeRangeStore: [{
                type: Input
            }], timeZone: [{
                type: Input
            }], trackProjectModelChanges: [{
                type: Input
            }], allChildren: [{
                type: Input
            }], descendantCount: [{
                type: Input
            }], hasGeneratedId: [{
                type: Input
            }], inlineData: [{
                type: Input
            }], internalId: [{
                type: Input
            }], isCommitting: [{
                type: Input
            }], isCreating: [{
                type: Input
            }], isValid: [{
                type: Input
            }], previousSiblingsTotalCount: [{
                type: Input
            }], visibleDescendantCount: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeLoad: [{
                type: Output
            }], onBeforeLoadApply: [{
                type: Output
            }], onBeforeResponseApply: [{
                type: Output
            }], onBeforeSend: [{
                type: Output
            }], onBeforeSync: [{
                type: Output
            }], onBeforeSyncApply: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onCycle: [{
                type: Output
            }], onDataReady: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onEmptyCalendar: [{
                type: Output
            }], onHasChanges: [{
                type: Output
            }], onLoad: [{
                type: Output
            }], onLoadCanceled: [{
                type: Output
            }], onLoadFail: [{
                type: Output
            }], onNoChanges: [{
                type: Output
            }], onProgress: [{
                type: Output
            }], onRequestDone: [{
                type: Output
            }], onRequestFail: [{
                type: Output
            }], onSchedulingConflict: [{
                type: Output
            }], onSync: [{
                type: Output
            }], onSyncCanceled: [{
                type: Output
            }], onSyncDelayed: [{
                type: Output
            }], onSyncFail: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Radio
 */
class BryntumRadioComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeChange = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onClick = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumRadioComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumRadioComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumRadioComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumRadioComponent.instanceClass = Radio;
BryntumRadioComponent.instanceName = 'Radio';
BryntumRadioComponent.bryntumEvents = [
    'onAction',
    'onBeforeChange',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onClick',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumRadioComponent.bryntumFeatureNames = [];
BryntumRadioComponent.bryntumConfigs = BryntumRadioComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoCollapse',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'checked',
    'checkedValue',
    'clearable',
    'cls',
    'color',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'text',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'uncheckedValue',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumRadioComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoCollapse',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'checkedValue',
    'clearable',
    'color',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'text',
    'textAlign',
    'title',
    'ui',
    'uncheckedValue',
    'validateOnInput',
    'weight'
];
BryntumRadioComponent.bryntumProps = BryntumRadioComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'checked',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'name',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumRadioComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumRadioComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumRadioComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumRadioComponent, selector: "bryntum-radio", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoCollapse: "autoCollapse", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", checkedValue: "checkedValue", clearable: "clearable", color: "color", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", text: "text", textAlign: "textAlign", title: "title", ui: "ui", uncheckedValue: "uncheckedValue", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", checked: "checked", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", name: "name", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeChange: "onBeforeChange", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onClick: "onClick", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumRadioComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-radio',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoCollapse: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], checkedValue: [{
                type: Input
            }], clearable: [{
                type: Input
            }], color: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], text: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], uncheckedValue: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], checked: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], name: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeChange: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onClick: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum RadioGroup
 */
class BryntumRadioGroupComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumRadioGroupComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumRadioGroupComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumRadioGroupComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumRadioGroupComponent.instanceClass = RadioGroup;
BryntumRadioGroupComponent.instanceName = 'RadioGroup';
BryntumRadioGroupComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onCollapse',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onToolClick'
];
BryntumRadioGroupComponent.bryntumFeatureNames = [];
BryntumRadioGroupComponent.bryntumConfigs = BryntumRadioGroupComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'footer',
    'header',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'label',
    'labelCls',
    'labelPosition',
    'labelWidth',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'name',
    'namedItems',
    'options',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumRadioGroupComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'footer',
    'header',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'inline',
    'itemCls',
    'labelCls',
    'labelPosition',
    'labelWidth',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'name',
    'namedItems',
    'options',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'trapFocus',
    'ui',
    'weight'
];
BryntumRadioGroupComponent.bryntumProps = BryntumRadioGroupComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'label',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'value',
    'values',
    'width',
    'x',
    'y'
]);
BryntumRadioGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumRadioGroupComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumRadioGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumRadioGroupComponent, selector: "bryntum-radio-group", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", footer: "footer", header: "header", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", itemCls: "itemCls", labelCls: "labelCls", labelPosition: "labelPosition", labelWidth: "labelWidth", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", name: "name", namedItems: "namedItems", options: "options", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", trapFocus: "trapFocus", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", label: "label", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", state: "state", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumRadioGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-radio-group',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], footer: [{
                type: Input
            }], header: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], options: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], label: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], state: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum ResourceCombo
 */
class BryntumResourceComboComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumResourceComboComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumResourceComboComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumResourceComboComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumResourceComboComponent.instanceClass = ResourceCombo;
BryntumResourceComboComponent.instanceName = 'ResourceCombo';
BryntumResourceComboComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onTrigger'
];
BryntumResourceComboComponent.bryntumFeatureNames = [];
BryntumResourceComboComponent.bryntumConfigs = BryntumResourceComboComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'editable',
    'emptyText',
    'encodeFilterParams',
    'extraData',
    'filterOnEnter',
    'filterOperator',
    'filterParamName',
    'filterSelected',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minChars',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showEventColor',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggerAction',
    'triggers',
    'ui',
    'validateFilter',
    'validateOnInput',
    'value',
    'valueField',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumResourceComboComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'defaultBindProperty',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'emptyText',
    'encodeFilterParams',
    'filterOnEnter',
    'filterParamName',
    'filterSelected',
    'floating',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minChars',
    'minLength',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showEventColor',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'triggerAction',
    'ui',
    'validateFilter',
    'validateOnInput',
    'valueField',
    'weight'
];
BryntumResourceComboComponent.bryntumProps = BryntumResourceComboComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'filterOperator',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'picker',
    'readOnly',
    'scrollable',
    'store',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumResourceComboComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumResourceComboComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumResourceComboComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumResourceComboComponent, selector: "bryntum-resource-combo", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", caseSensitive: "caseSensitive", centered: "centered", chipView: "chipView", clearable: "clearable", clearTextOnPickerHide: "clearTextOnPickerHide", clearTextOnSelection: "clearTextOnSelection", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", createOnUnmatched: "createOnUnmatched", defaultBindProperty: "defaultBindProperty", displayField: "displayField", displayValueRenderer: "displayValueRenderer", dock: "dock", draggable: "draggable", emptyText: "emptyText", encodeFilterParams: "encodeFilterParams", filterOnEnter: "filterOnEnter", filterParamName: "filterParamName", filterSelected: "filterSelected", floating: "floating", hideAnimation: "hideAnimation", hidePickerOnSelect: "hidePickerOnSelect", hideTrigger: "hideTrigger", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inlinePicker: "inlinePicker", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", items: "items", keyStrokeChangeDelay: "keyStrokeChangeDelay", keyStrokeFilterDelay: "keyStrokeFilterDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listCls: "listCls", listeners: "listeners", listItemTpl: "listItemTpl", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minChars: "minChars", minLength: "minLength", monitorResize: "monitorResize", multiSelect: "multiSelect", multiValueSeparator: "multiValueSeparator", name: "name", overlayAnchor: "overlayAnchor", owner: "owner", pickerAlignElement: "pickerAlignElement", pickerWidth: "pickerWidth", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", primaryFilter: "primaryFilter", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showEventColor: "showEventColor", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", triggerAction: "triggerAction", ui: "ui", validateFilter: "validateFilter", validateOnInput: "validateOnInput", valueField: "valueField", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", filterOperator: "filterOperator", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", picker: "picker", readOnly: "readOnly", store: "store", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumResourceComboComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-resource-combo',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], caseSensitive: [{
                type: Input
            }], centered: [{
                type: Input
            }], chipView: [{
                type: Input
            }], clearable: [{
                type: Input
            }], clearTextOnPickerHide: [{
                type: Input
            }], clearTextOnSelection: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], createOnUnmatched: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], displayValueRenderer: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], encodeFilterParams: [{
                type: Input
            }], filterOnEnter: [{
                type: Input
            }], filterParamName: [{
                type: Input
            }], filterSelected: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hidePickerOnSelect: [{
                type: Input
            }], hideTrigger: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inlinePicker: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], items: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], keyStrokeFilterDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listCls: [{
                type: Input
            }], listeners: [{
                type: Input
            }], listItemTpl: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minChars: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], multiValueSeparator: [{
                type: Input
            }], name: [{
                type: Input
            }], overlayAnchor: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerWidth: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], primaryFilter: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showEventColor: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerAction: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateFilter: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], valueField: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], filterOperator: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], picker: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], store: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum ResourceFilter
 */
class BryntumResourceFilterComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeItem = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onItem = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumResourceFilterComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumResourceFilterComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumResourceFilterComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumResourceFilterComponent.instanceClass = ResourceFilter;
BryntumResourceFilterComponent.instanceName = 'ResourceFilter';
BryntumResourceFilterComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeItem',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onItem',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelectionChange',
    'onShow',
    'onToggleGroup'
];
BryntumResourceFilterComponent.bryntumFeatureNames = [];
BryntumResourceFilterComponent.bryntumConfigs = BryntumResourceFilterComponent.bryntumFeatureNames.concat([
    'activateOnMouseover',
    'adopt',
    'align',
    'alignSelf',
    'allowGroupSelect',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'cls',
    'collapsibleGroups',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'dock',
    'draggable',
    'eventStore',
    'extraData',
    'filterResources',
    'flex',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'height',
    'hidden',
    'hideAnimation',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'items',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'masterFilter',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'selectAllItem',
    'selected',
    'showAnimation',
    'showTooltipWhenDisabled',
    'store',
    'tab',
    'tag',
    'textAlign',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumResourceFilterComponent.bryntumConfigsOnly = [
    'activateOnMouseover',
    'adopt',
    'align',
    'allowGroupSelect',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearSelectionOnEmptySpaceClick',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'displayField',
    'dock',
    'draggable',
    'eventStore',
    'filterResources',
    'floating',
    'getItemCls',
    'getItemStyle',
    'groupHeaderTpl',
    'hideAnimation',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemTpl',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'masterFilter',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'selectAllItem',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'ui',
    'weight'
];
BryntumResourceFilterComponent.bryntumProps = BryntumResourceFilterComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'collapsibleGroups',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'items',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'multiSelect',
    'readOnly',
    'scrollable',
    'selected',
    'store',
    'title',
    'toggleAllIfCtrlPressed',
    'tooltip',
    'width',
    'x',
    'y'
]);
BryntumResourceFilterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumResourceFilterComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumResourceFilterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumResourceFilterComponent, selector: "bryntum-resource-filter", inputs: { activateOnMouseover: "activateOnMouseover", adopt: "adopt", align: "align", allowGroupSelect: "allowGroupSelect", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearSelectionOnEmptySpaceClick: "clearSelectionOnEmptySpaceClick", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", displayField: "displayField", dock: "dock", draggable: "draggable", eventStore: "eventStore", filterResources: "filterResources", floating: "floating", getItemCls: "getItemCls", getItemStyle: "getItemStyle", groupHeaderTpl: "groupHeaderTpl", hideAnimation: "hideAnimation", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemTpl: "itemTpl", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", masterFilter: "masterFilter", monitorResize: "monitorResize", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", selectAllItem: "selectAllItem", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", collapsibleGroups: "collapsibleGroups", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", multiSelect: "multiSelect", readOnly: "readOnly", scrollable: "scrollable", selected: "selected", store: "store", title: "title", toggleAllIfCtrlPressed: "toggleAllIfCtrlPressed", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeItem: "onBeforeItem", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onItem: "onItem", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelectionChange: "onSelectionChange", onShow: "onShow", onToggleGroup: "onToggleGroup" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumResourceFilterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-resource-filter',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { activateOnMouseover: [{
                type: Input
            }], adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], allowGroupSelect: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearSelectionOnEmptySpaceClick: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], eventStore: [{
                type: Input
            }], filterResources: [{
                type: Input
            }], floating: [{
                type: Input
            }], getItemCls: [{
                type: Input
            }], getItemStyle: [{
                type: Input
            }], groupHeaderTpl: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemTpl: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], masterFilter: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], selectAllItem: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], collapsibleGroups: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selected: [{
                type: Input
            }], store: [{
                type: Input
            }], title: [{
                type: Input
            }], toggleAllIfCtrlPressed: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeItem: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onItem: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum ResourceHistogram
 */
class BryntumResourceHistogramComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeAssignmentDelete = new EventEmitter();
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeEventDelete = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeHistogramDataCacheSet = new EventEmitter();
        this.onBeforePan = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforePresetChange = new EventEmitter();
        this.onBeforeRenderHistogramRow = new EventEmitter();
        this.onBeforeRenderRecordHistogram = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDateRangeChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGenerateScalePoints = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onHistogramDataCacheSet = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onPresetChange = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResourceMenuBeforeShow = new EventEmitter();
        this.onResourceMenuItem = new EventEmitter();
        this.onResourceMenuShow = new EventEmitter();
        this.onResourceMenuToggleItem = new EventEmitter();
        this.onResourceNonWorkingTimeClick = new EventEmitter();
        this.onResourceNonWorkingTimeContextMenu = new EventEmitter();
        this.onResourceNonWorkingTimeDblClick = new EventEmitter();
        this.onResourceNonWorkingTimeMouseDown = new EventEmitter();
        this.onResourceNonWorkingTimeMouseOut = new EventEmitter();
        this.onResourceNonWorkingTimeMouseOver = new EventEmitter();
        this.onResourceNonWorkingTimeMouseUp = new EventEmitter();
        this.onResourceTimeRangeClick = new EventEmitter();
        this.onResourceTimeRangeContextMenu = new EventEmitter();
        this.onResourceTimeRangeDblClick = new EventEmitter();
        this.onResourceTimeRangeMouseDown = new EventEmitter();
        this.onResourceTimeRangeMouseOut = new EventEmitter();
        this.onResourceTimeRangeMouseOver = new EventEmitter();
        this.onResourceTimeRangeMouseUp = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onTimeAxisChange = new EventEmitter();
        this.onTimeAxisHeaderClick = new EventEmitter();
        this.onTimeAxisHeaderContextMenu = new EventEmitter();
        this.onTimeAxisHeaderDblClick = new EventEmitter();
        this.onTimeAxisHeaderMenuBeforeShow = new EventEmitter();
        this.onTimeAxisHeaderMenuItem = new EventEmitter();
        this.onTimeAxisHeaderMenuShow = new EventEmitter();
        this.onTimelineContextChange = new EventEmitter();
        this.onTimelineViewportResize = new EventEmitter();
        this.onTimeRangeHeaderClick = new EventEmitter();
        this.onTimeRangeHeaderContextMenu = new EventEmitter();
        this.onTimeRangeHeaderDblClick = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.onVisibleDateRangeChange = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumResourceHistogramComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
        // Backwards compatibility for gridInstance, schedulerInstance etc.
        // @ts-ignore
        me[StringHelper.uncapitalize(instanceName) + 'Instance'] = me.instance;
        // 
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumResourceHistogramComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumResourceHistogramComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumResourceHistogramComponent.instanceClass = ResourceHistogram;
BryntumResourceHistogramComponent.instanceName = 'ResourceHistogram';
BryntumResourceHistogramComponent.bryntumEvents = [
    'onBeforeAssignmentDelete',
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDestroy',
    'onBeforeEventDelete',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforeHistogramDataCacheSet',
    'onBeforePan',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforePresetChange',
    'onBeforeRenderHistogramRow',
    'onBeforeRenderRecordHistogram',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDateRangeChange',
    'onDestroy',
    'onDragSelecting',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGenerateScalePoints',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onHistogramDataCacheSet',
    'onMouseOut',
    'onMouseOver',
    'onPaint',
    'onPaste',
    'onPdfExport',
    'onPointerOver',
    'onPresetChange',
    'onRecompose',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResourceMenuBeforeShow',
    'onResourceMenuItem',
    'onResourceMenuShow',
    'onResourceMenuToggleItem',
    'onResourceNonWorkingTimeClick',
    'onResourceNonWorkingTimeContextMenu',
    'onResourceNonWorkingTimeDblClick',
    'onResourceNonWorkingTimeMouseDown',
    'onResourceNonWorkingTimeMouseOut',
    'onResourceNonWorkingTimeMouseOver',
    'onResourceNonWorkingTimeMouseUp',
    'onResourceTimeRangeClick',
    'onResourceTimeRangeContextMenu',
    'onResourceTimeRangeDblClick',
    'onResourceTimeRangeMouseDown',
    'onResourceTimeRangeMouseOut',
    'onResourceTimeRangeMouseOver',
    'onResourceTimeRangeMouseUp',
    'onResponsive',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onTimeAxisChange',
    'onTimeAxisHeaderClick',
    'onTimeAxisHeaderContextMenu',
    'onTimeAxisHeaderDblClick',
    'onTimeAxisHeaderMenuBeforeShow',
    'onTimeAxisHeaderMenuItem',
    'onTimeAxisHeaderMenuShow',
    'onTimelineContextChange',
    'onTimelineViewportResize',
    'onTimeRangeHeaderClick',
    'onTimeRangeHeaderContextMenu',
    'onTimeRangeHeaderDblClick',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onUnsplit',
    'onVisibleDateRangeChange'
];
BryntumResourceHistogramComponent.bryntumFeatureNames = [
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnLinesFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'headerZoomFeature',
    'mergeCellsFeature',
    'nonWorkingTimeFeature',
    'panFeature',
    'pdfExportFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'resourceMenuFeature',
    'resourceNonWorkingTimeFeature',
    'resourceTimeRangesFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'scheduleTooltipFeature',
    'searchFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stripeFeature',
    'summaryFeature',
    'timeAxisHeaderMenuFeature',
    'timeRangesFeature',
    'timeSpanHighlightFeature',
    'treeFeature',
    'treeGroupFeature'
];
BryntumResourceHistogramComponent.bryntumConfigs = BryntumResourceHistogramComponent.bryntumFeatureNames.concat([
    'adopt',
    'aggregateDataEntry',
    'aggregateHistogramDataForGroups',
    'alignSelf',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'barMargin',
    'barTextEffortUnit',
    'barTipEffortUnit',
    'barTooltipTemplate',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'crudManager',
    'crudManagerClass',
    'data',
    'dataModelField',
    'dataset',
    'defaultRegion',
    'defaultResourceImageName',
    'destroyStore',
    'destroyStores',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'displayDateFormat',
    'dock',
    'effortFormat',
    'effortUnit',
    'emptyText',
    'enableEventAnimations',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'endDate',
    'endParamName',
    'extraData',
    'features',
    'fillLastColumn',
    'fillTicks',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forceFit',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getBarText',
    'getBarTip',
    'getDataEntryForAggregating',
    'getHtml',
    'getOutlineClass',
    'getRecordData',
    'getRectClass',
    'getRowHeight',
    'hardRefreshOnTimeAxisReconfigure',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hideRowHover',
    'histogramWidget',
    'histogramWidgetClass',
    'hoverDelay',
    'id',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'includeInactiveEvents',
    'infiniteScroll',
    'initAggregatedDataEntry',
    'insertBefore',
    'insertFirst',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'managedEventSizing',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxTimeAxisUnit',
    'maxWidth',
    'maxZoomLevel',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'presets',
    'preventTooltipOnTouch',
    'project',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceMargin',
    'resourceTimeRangeRenderer',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'scaleColumn',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'series',
    'showBarText',
    'showBarTip',
    'showDirty',
    'showEffortUnit',
    'showMaxEffort',
    'showOnClick',
    'showOnHover',
    'snap',
    'snapRelativeToEventStartDate',
    'startDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stickyHeaders',
    'store',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'tickSize',
    'timeAxis',
    'timeRanges',
    'timeRangeStore',
    'title',
    'tools',
    'trackMouse',
    'transitionDuration',
    'ui',
    'viewPreset',
    'visibleDate',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'width',
    'workingTime',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
]);
BryntumResourceHistogramComponent.bryntumConfigsOnly = [
    'adopt',
    'aggregateDataEntry',
    'aggregateHistogramDataForGroups',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'ariaDescription',
    'ariaLabel',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'barTextEffortUnit',
    'barTipEffortUnit',
    'barTooltipTemplate',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'crudManagerClass',
    'dataModelField',
    'defaultRegion',
    'defaultResourceImageName',
    'destroyStore',
    'destroyStores',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'effortFormat',
    'effortUnit',
    'enableSticky',
    'enableTextSelection',
    'endParamName',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getBarText',
    'getBarTip',
    'getDataEntryForAggregating',
    'getHtml',
    'getOutlineClass',
    'getRecordData',
    'getRectClass',
    'getRowHeight',
    'hardRefreshOnTimeAxisReconfigure',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'histogramWidgetClass',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'includeInactiveEvents',
    'infiniteScroll',
    'initAggregatedDataEntry',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'managedEventSizing',
    'maskDefaults',
    'masked',
    'maximizable',
    'maxTimeAxisUnit',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceTimeRangeRenderer',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'scaleColumn',
    'scrollerClass',
    'scrollManager',
    'series',
    'showBarText',
    'showDirty',
    'showEffortUnit',
    'showMaxEffort',
    'showOnClick',
    'showOnHover',
    'snapRelativeToEventStartDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stickyHeaders',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'timeAxis',
    'trackMouse',
    'ui',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
];
BryntumResourceHistogramComponent.bryntumProps = BryntumResourceHistogramComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'appendTo',
    'ariaLive',
    'barMargin',
    'cls',
    'columnLines',
    'columns',
    'crudManager',
    'data',
    'dataset',
    'disabled',
    'displayDateFormat',
    'emptyText',
    'enableEventAnimations',
    'enableUndoRedoKeys',
    'endDate',
    'eventColor',
    'eventStyle',
    'extraData',
    'fillTicks',
    'flex',
    'forceFit',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hideRowHover',
    'histogramWidget',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'isEngineReady',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'maxZoomLevel',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'preserveScroll',
    'presets',
    'project',
    'readOnly',
    'resourceMargin',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollLeft',
    'scrollTop',
    'scrollX',
    'selectedCell',
    'selectedCells',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'showBarTip',
    'snap',
    'startDate',
    'state',
    'store',
    'tickSize',
    'timeRanges',
    'timeRangeStore',
    'timeResolution',
    'timeZone',
    'title',
    'tools',
    'tooltip',
    'transitionDuration',
    'viewPreset',
    'visibleDate',
    'width',
    'workingTime',
    'zoomLevel'
]);
BryntumResourceHistogramComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumResourceHistogramComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumResourceHistogramComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumResourceHistogramComponent, selector: "bryntum-resource-histogram", inputs: { adopt: "adopt", aggregateDataEntry: "aggregateDataEntry", aggregateHistogramDataForGroups: "aggregateHistogramDataForGroups", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", animateTreeNodeToggle: "animateTreeNodeToggle", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoAdjustTimeAxis: "autoAdjustTimeAxis", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", barTextEffortUnit: "barTextEffortUnit", barTipEffortUnit: "barTipEffortUnit", barTooltipTemplate: "barTooltipTemplate", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", bufferCoef: "bufferCoef", bufferThreshold: "bufferThreshold", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", crudManagerClass: "crudManagerClass", dataModelField: "dataModelField", defaultRegion: "defaultRegion", defaultResourceImageName: "defaultResourceImageName", destroyStore: "destroyStore", destroyStores: "destroyStores", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", dock: "dock", effortFormat: "effortFormat", effortUnit: "effortUnit", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", endParamName: "endParamName", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getBarText: "getBarText", getBarTip: "getBarTip", getDataEntryForAggregating: "getDataEntryForAggregating", getHtml: "getHtml", getOutlineClass: "getOutlineClass", getRecordData: "getRecordData", getRectClass: "getRectClass", getRowHeight: "getRowHeight", hardRefreshOnTimeAxisReconfigure: "hardRefreshOnTimeAxisReconfigure", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", histogramWidgetClass: "histogramWidgetClass", ignoreDomEventsWhileScrolling: "ignoreDomEventsWhileScrolling", ignoreParentReadOnly: "ignoreParentReadOnly", includeInactiveEvents: "includeInactiveEvents", infiniteScroll: "infiniteScroll", initAggregatedDataEntry: "initAggregatedDataEntry", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", managedEventSizing: "managedEventSizing", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", maxTimeAxisUnit: "maxTimeAxisUnit", modal: "modal", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", owner: "owner", partner: "partner", passStartEndParameters: "passStartEndParameters", plugins: "plugins", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", resourceImageExtension: "resourceImageExtension", resourceImagePath: "resourceImagePath", resourceTimeRangeRenderer: "resourceTimeRangeRenderer", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", scaleColumn: "scaleColumn", scrollerClass: "scrollerClass", scrollManager: "scrollManager", series: "series", showBarText: "showBarText", showDirty: "showDirty", showEffortUnit: "showEffortUnit", showMaxEffort: "showMaxEffort", showOnClick: "showOnClick", showOnHover: "showOnHover", snapRelativeToEventStartDate: "snapRelativeToEventStartDate", startParamName: "startParamName", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", stickyHeaders: "stickyHeaders", strips: "strips", subGridConfigs: "subGridConfigs", suppressFit: "suppressFit", syncMask: "syncMask", tab: "tab", tbar: "tbar", timeAxis: "timeAxis", trackMouse: "trackMouse", ui: "ui", visibleZoomFactor: "visibleZoomFactor", weekStartDay: "weekStartDay", weight: "weight", zoomKeepsOriginalTimespan: "zoomKeepsOriginalTimespan", zoomOnMouseWheel: "zoomOnMouseWheel", zoomOnTimeAxisDoubleClick: "zoomOnTimeAxisDoubleClick", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", barMargin: "barMargin", cls: "cls", columnLines: "columnLines", columns: "columns", crudManager: "crudManager", data: "data", dataset: "dataset", disabled: "disabled", displayDateFormat: "displayDateFormat", emptyText: "emptyText", enableEventAnimations: "enableEventAnimations", enableUndoRedoKeys: "enableUndoRedoKeys", endDate: "endDate", extraData: "extraData", fillTicks: "fillTicks", flex: "flex", forceFit: "forceFit", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hideRowHover: "hideRowHover", histogramWidget: "histogramWidget", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", maxZoomLevel: "maxZoomLevel", minHeight: "minHeight", minWidth: "minWidth", minZoomLevel: "minZoomLevel", preserveScroll: "preserveScroll", presets: "presets", project: "project", readOnly: "readOnly", resourceMargin: "resourceMargin", resourceTimeRanges: "resourceTimeRanges", resourceTimeRangeStore: "resourceTimeRangeStore", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", showBarTip: "showBarTip", snap: "snap", startDate: "startDate", store: "store", tickSize: "tickSize", timeRanges: "timeRanges", timeRangeStore: "timeRangeStore", title: "title", tools: "tools", transitionDuration: "transitionDuration", viewPreset: "viewPreset", visibleDate: "visibleDate", width: "width", workingTime: "workingTime", accessibility: "accessibility", eventColor: "eventColor", eventStyle: "eventStyle", isEngineReady: "isEngineReady", scrollLeft: "scrollLeft", scrollTop: "scrollTop", scrollX: "scrollX", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", timeResolution: "timeResolution", timeZone: "timeZone", tooltip: "tooltip", zoomLevel: "zoomLevel", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnLinesFeature: "columnLinesFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", headerZoomFeature: "headerZoomFeature", mergeCellsFeature: "mergeCellsFeature", nonWorkingTimeFeature: "nonWorkingTimeFeature", panFeature: "panFeature", pdfExportFeature: "pdfExportFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", resourceMenuFeature: "resourceMenuFeature", resourceNonWorkingTimeFeature: "resourceNonWorkingTimeFeature", resourceTimeRangesFeature: "resourceTimeRangesFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", scheduleTooltipFeature: "scheduleTooltipFeature", searchFeature: "searchFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", timeAxisHeaderMenuFeature: "timeAxisHeaderMenuFeature", timeRangesFeature: "timeRangesFeature", timeSpanHighlightFeature: "timeSpanHighlightFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature" }, outputs: { onBeforeAssignmentDelete: "onBeforeAssignmentDelete", onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDestroy: "onBeforeDestroy", onBeforeEventDelete: "onBeforeEventDelete", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforeHistogramDataCacheSet: "onBeforeHistogramDataCacheSet", onBeforePan: "onBeforePan", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforePresetChange: "onBeforePresetChange", onBeforeRenderHistogramRow: "onBeforeRenderHistogramRow", onBeforeRenderRecordHistogram: "onBeforeRenderRecordHistogram", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDateRangeChange: "onDateRangeChange", onDestroy: "onDestroy", onDragSelecting: "onDragSelecting", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGenerateScalePoints: "onGenerateScalePoints", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onHistogramDataCacheSet: "onHistogramDataCacheSet", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onPaint: "onPaint", onPaste: "onPaste", onPdfExport: "onPdfExport", onPointerOver: "onPointerOver", onPresetChange: "onPresetChange", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResourceMenuBeforeShow: "onResourceMenuBeforeShow", onResourceMenuItem: "onResourceMenuItem", onResourceMenuShow: "onResourceMenuShow", onResourceMenuToggleItem: "onResourceMenuToggleItem", onResourceNonWorkingTimeClick: "onResourceNonWorkingTimeClick", onResourceNonWorkingTimeContextMenu: "onResourceNonWorkingTimeContextMenu", onResourceNonWorkingTimeDblClick: "onResourceNonWorkingTimeDblClick", onResourceNonWorkingTimeMouseDown: "onResourceNonWorkingTimeMouseDown", onResourceNonWorkingTimeMouseOut: "onResourceNonWorkingTimeMouseOut", onResourceNonWorkingTimeMouseOver: "onResourceNonWorkingTimeMouseOver", onResourceNonWorkingTimeMouseUp: "onResourceNonWorkingTimeMouseUp", onResourceTimeRangeClick: "onResourceTimeRangeClick", onResourceTimeRangeContextMenu: "onResourceTimeRangeContextMenu", onResourceTimeRangeDblClick: "onResourceTimeRangeDblClick", onResourceTimeRangeMouseDown: "onResourceTimeRangeMouseDown", onResourceTimeRangeMouseOut: "onResourceTimeRangeMouseOut", onResourceTimeRangeMouseOver: "onResourceTimeRangeMouseOver", onResourceTimeRangeMouseUp: "onResourceTimeRangeMouseUp", onResponsive: "onResponsive", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onTimeAxisChange: "onTimeAxisChange", onTimeAxisHeaderClick: "onTimeAxisHeaderClick", onTimeAxisHeaderContextMenu: "onTimeAxisHeaderContextMenu", onTimeAxisHeaderDblClick: "onTimeAxisHeaderDblClick", onTimeAxisHeaderMenuBeforeShow: "onTimeAxisHeaderMenuBeforeShow", onTimeAxisHeaderMenuItem: "onTimeAxisHeaderMenuItem", onTimeAxisHeaderMenuShow: "onTimeAxisHeaderMenuShow", onTimelineContextChange: "onTimelineContextChange", onTimelineViewportResize: "onTimelineViewportResize", onTimeRangeHeaderClick: "onTimeRangeHeaderClick", onTimeRangeHeaderContextMenu: "onTimeRangeHeaderContextMenu", onTimeRangeHeaderDblClick: "onTimeRangeHeaderDblClick", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onUnsplit: "onUnsplit", onVisibleDateRangeChange: "onVisibleDateRangeChange" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumResourceHistogramComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-resource-histogram',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], aggregateDataEntry: [{
                type: Input
            }], aggregateHistogramDataForGroups: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoAdjustTimeAxis: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], barTextEffortUnit: [{
                type: Input
            }], barTipEffortUnit: [{
                type: Input
            }], barTooltipTemplate: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], bufferCoef: [{
                type: Input
            }], bufferThreshold: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], crudManagerClass: [{
                type: Input
            }], dataModelField: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], defaultResourceImageName: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], destroyStores: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], dock: [{
                type: Input
            }], effortFormat: [{
                type: Input
            }], effortUnit: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], endParamName: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getBarText: [{
                type: Input
            }], getBarTip: [{
                type: Input
            }], getDataEntryForAggregating: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getOutlineClass: [{
                type: Input
            }], getRecordData: [{
                type: Input
            }], getRectClass: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], hardRefreshOnTimeAxisReconfigure: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], histogramWidgetClass: [{
                type: Input
            }], ignoreDomEventsWhileScrolling: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], includeInactiveEvents: [{
                type: Input
            }], infiniteScroll: [{
                type: Input
            }], initAggregatedDataEntry: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], managedEventSizing: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], maxTimeAxisUnit: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], owner: [{
                type: Input
            }], partner: [{
                type: Input
            }], passStartEndParameters: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], resourceImageExtension: [{
                type: Input
            }], resourceImagePath: [{
                type: Input
            }], resourceTimeRangeRenderer: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scaleColumn: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], series: [{
                type: Input
            }], showBarText: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showEffortUnit: [{
                type: Input
            }], showMaxEffort: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], snapRelativeToEventStartDate: [{
                type: Input
            }], startParamName: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], stickyHeaders: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], suppressFit: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], timeAxis: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], ui: [{
                type: Input
            }], visibleZoomFactor: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], zoomKeepsOriginalTimespan: [{
                type: Input
            }], zoomOnMouseWheel: [{
                type: Input
            }], zoomOnTimeAxisDoubleClick: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], barMargin: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], crudManager: [{
                type: Input
            }], data: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], displayDateFormat: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableEventAnimations: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], endDate: [{
                type: Input
            }], extraData: [{
                type: Input
            }], fillTicks: [{
                type: Input
            }], flex: [{
                type: Input
            }], forceFit: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hideRowHover: [{
                type: Input
            }], histogramWidget: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], maxZoomLevel: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], minZoomLevel: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], presets: [{
                type: Input
            }], project: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], resourceMargin: [{
                type: Input
            }], resourceTimeRanges: [{
                type: Input
            }], resourceTimeRangeStore: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], showBarTip: [{
                type: Input
            }], snap: [{
                type: Input
            }], startDate: [{
                type: Input
            }], store: [{
                type: Input
            }], tickSize: [{
                type: Input
            }], timeRanges: [{
                type: Input
            }], timeRangeStore: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], viewPreset: [{
                type: Input
            }], visibleDate: [{
                type: Input
            }], width: [{
                type: Input
            }], workingTime: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], eventColor: [{
                type: Input
            }], eventStyle: [{
                type: Input
            }], isEngineReady: [{
                type: Input
            }], scrollLeft: [{
                type: Input
            }], scrollTop: [{
                type: Input
            }], scrollX: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], timeResolution: [{
                type: Input
            }], timeZone: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], zoomLevel: [{
                type: Input
            }], onBeforeAssignmentDelete: [{
                type: Output
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeEventDelete: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeHistogramDataCacheSet: [{
                type: Output
            }], onBeforePan: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforePresetChange: [{
                type: Output
            }], onBeforeRenderHistogramRow: [{
                type: Output
            }], onBeforeRenderRecordHistogram: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDateRangeChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGenerateScalePoints: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onHistogramDataCacheSet: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onPresetChange: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResourceMenuBeforeShow: [{
                type: Output
            }], onResourceMenuItem: [{
                type: Output
            }], onResourceMenuShow: [{
                type: Output
            }], onResourceMenuToggleItem: [{
                type: Output
            }], onResourceNonWorkingTimeClick: [{
                type: Output
            }], onResourceNonWorkingTimeContextMenu: [{
                type: Output
            }], onResourceNonWorkingTimeDblClick: [{
                type: Output
            }], onResourceNonWorkingTimeMouseDown: [{
                type: Output
            }], onResourceNonWorkingTimeMouseOut: [{
                type: Output
            }], onResourceNonWorkingTimeMouseOver: [{
                type: Output
            }], onResourceNonWorkingTimeMouseUp: [{
                type: Output
            }], onResourceTimeRangeClick: [{
                type: Output
            }], onResourceTimeRangeContextMenu: [{
                type: Output
            }], onResourceTimeRangeDblClick: [{
                type: Output
            }], onResourceTimeRangeMouseDown: [{
                type: Output
            }], onResourceTimeRangeMouseOut: [{
                type: Output
            }], onResourceTimeRangeMouseOver: [{
                type: Output
            }], onResourceTimeRangeMouseUp: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onTimeAxisChange: [{
                type: Output
            }], onTimeAxisHeaderClick: [{
                type: Output
            }], onTimeAxisHeaderContextMenu: [{
                type: Output
            }], onTimeAxisHeaderDblClick: [{
                type: Output
            }], onTimeAxisHeaderMenuBeforeShow: [{
                type: Output
            }], onTimeAxisHeaderMenuItem: [{
                type: Output
            }], onTimeAxisHeaderMenuShow: [{
                type: Output
            }], onTimelineContextChange: [{
                type: Output
            }], onTimelineViewportResize: [{
                type: Output
            }], onTimeRangeHeaderClick: [{
                type: Output
            }], onTimeRangeHeaderContextMenu: [{
                type: Output
            }], onTimeRangeHeaderDblClick: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], onVisibleDateRangeChange: [{
                type: Output
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnLinesFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], headerZoomFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], nonWorkingTimeFeature: [{
                type: Input
            }], panFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], resourceMenuFeature: [{
                type: Input
            }], resourceNonWorkingTimeFeature: [{
                type: Input
            }], resourceTimeRangesFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], scheduleTooltipFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], timeAxisHeaderMenuFeature: [{
                type: Input
            }], timeRangesFeature: [{
                type: Input
            }], timeSpanHighlightFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum ResourceUtilization
 */
class BryntumResourceUtilizationComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeAssignmentDelete = new EventEmitter();
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeEventDelete = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeHistogramDataCacheSet = new EventEmitter();
        this.onBeforePan = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforePresetChange = new EventEmitter();
        this.onBeforeRenderHistogramRow = new EventEmitter();
        this.onBeforeRenderRecordHistogram = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDateRangeChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onHistogramDataCacheSet = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onPresetChange = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResourceMenuBeforeShow = new EventEmitter();
        this.onResourceMenuItem = new EventEmitter();
        this.onResourceMenuShow = new EventEmitter();
        this.onResourceMenuToggleItem = new EventEmitter();
        this.onResourceNonWorkingTimeClick = new EventEmitter();
        this.onResourceNonWorkingTimeContextMenu = new EventEmitter();
        this.onResourceNonWorkingTimeDblClick = new EventEmitter();
        this.onResourceNonWorkingTimeMouseDown = new EventEmitter();
        this.onResourceNonWorkingTimeMouseOut = new EventEmitter();
        this.onResourceNonWorkingTimeMouseOver = new EventEmitter();
        this.onResourceNonWorkingTimeMouseUp = new EventEmitter();
        this.onResourceTimeRangeClick = new EventEmitter();
        this.onResourceTimeRangeContextMenu = new EventEmitter();
        this.onResourceTimeRangeDblClick = new EventEmitter();
        this.onResourceTimeRangeMouseDown = new EventEmitter();
        this.onResourceTimeRangeMouseOut = new EventEmitter();
        this.onResourceTimeRangeMouseOver = new EventEmitter();
        this.onResourceTimeRangeMouseUp = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onTimeAxisChange = new EventEmitter();
        this.onTimeAxisHeaderClick = new EventEmitter();
        this.onTimeAxisHeaderContextMenu = new EventEmitter();
        this.onTimeAxisHeaderDblClick = new EventEmitter();
        this.onTimeAxisHeaderMenuBeforeShow = new EventEmitter();
        this.onTimeAxisHeaderMenuItem = new EventEmitter();
        this.onTimeAxisHeaderMenuShow = new EventEmitter();
        this.onTimelineContextChange = new EventEmitter();
        this.onTimelineViewportResize = new EventEmitter();
        this.onTimeRangeHeaderClick = new EventEmitter();
        this.onTimeRangeHeaderContextMenu = new EventEmitter();
        this.onTimeRangeHeaderDblClick = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.onVisibleDateRangeChange = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumResourceUtilizationComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
        // Backwards compatibility for gridInstance, schedulerInstance etc.
        // @ts-ignore
        me[StringHelper.uncapitalize(instanceName) + 'Instance'] = me.instance;
        // 
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumResourceUtilizationComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumResourceUtilizationComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumResourceUtilizationComponent.instanceClass = ResourceUtilization;
BryntumResourceUtilizationComponent.instanceName = 'ResourceUtilization';
BryntumResourceUtilizationComponent.bryntumEvents = [
    'onBeforeAssignmentDelete',
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDestroy',
    'onBeforeEventDelete',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforeHistogramDataCacheSet',
    'onBeforePan',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforePresetChange',
    'onBeforeRenderHistogramRow',
    'onBeforeRenderRecordHistogram',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDateRangeChange',
    'onDestroy',
    'onDragSelecting',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onHistogramDataCacheSet',
    'onMouseOut',
    'onMouseOver',
    'onPaint',
    'onPaste',
    'onPdfExport',
    'onPointerOver',
    'onPresetChange',
    'onRecompose',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResourceMenuBeforeShow',
    'onResourceMenuItem',
    'onResourceMenuShow',
    'onResourceMenuToggleItem',
    'onResourceNonWorkingTimeClick',
    'onResourceNonWorkingTimeContextMenu',
    'onResourceNonWorkingTimeDblClick',
    'onResourceNonWorkingTimeMouseDown',
    'onResourceNonWorkingTimeMouseOut',
    'onResourceNonWorkingTimeMouseOver',
    'onResourceNonWorkingTimeMouseUp',
    'onResourceTimeRangeClick',
    'onResourceTimeRangeContextMenu',
    'onResourceTimeRangeDblClick',
    'onResourceTimeRangeMouseDown',
    'onResourceTimeRangeMouseOut',
    'onResourceTimeRangeMouseOver',
    'onResourceTimeRangeMouseUp',
    'onResponsive',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onTimeAxisChange',
    'onTimeAxisHeaderClick',
    'onTimeAxisHeaderContextMenu',
    'onTimeAxisHeaderDblClick',
    'onTimeAxisHeaderMenuBeforeShow',
    'onTimeAxisHeaderMenuItem',
    'onTimeAxisHeaderMenuShow',
    'onTimelineContextChange',
    'onTimelineViewportResize',
    'onTimeRangeHeaderClick',
    'onTimeRangeHeaderContextMenu',
    'onTimeRangeHeaderDblClick',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onUnsplit',
    'onVisibleDateRangeChange'
];
BryntumResourceUtilizationComponent.bryntumFeatureNames = [
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnLinesFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'headerZoomFeature',
    'mergeCellsFeature',
    'nonWorkingTimeFeature',
    'panFeature',
    'pdfExportFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'resourceMenuFeature',
    'resourceNonWorkingTimeFeature',
    'resourceTimeRangesFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'scheduleTooltipFeature',
    'searchFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stripeFeature',
    'summaryFeature',
    'timeAxisHeaderMenuFeature',
    'timeRangesFeature',
    'timeSpanHighlightFeature',
    'treeFeature',
    'treeGroupFeature'
];
BryntumResourceUtilizationComponent.bryntumConfigs = BryntumResourceUtilizationComponent.bryntumFeatureNames.concat([
    'adopt',
    'aggregateDataEntry',
    'aggregateHistogramDataForGroups',
    'alignSelf',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'barMargin',
    'barTextEffortUnit',
    'barTipEffortUnit',
    'barTooltipTemplate',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'dataModelField',
    'dataset',
    'defaultRegion',
    'defaultResourceImageName',
    'destroyStore',
    'destroyStores',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'displayDateFormat',
    'dock',
    'effortFormat',
    'effortUnit',
    'emptyText',
    'enableEventAnimations',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'endDate',
    'endParamName',
    'extraData',
    'features',
    'fillLastColumn',
    'fillTicks',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forceFit',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getBarText',
    'getBarTip',
    'getDataEntryForAggregating',
    'getHtml',
    'getOutlineClass',
    'getRecordData',
    'getRectClass',
    'getRowHeight',
    'hardRefreshOnTimeAxisReconfigure',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hideRowHover',
    'histogramWidget',
    'histogramWidgetClass',
    'hoverDelay',
    'id',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'includeInactiveEvents',
    'infiniteScroll',
    'initAggregatedDataEntry',
    'insertBefore',
    'insertFirst',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'managedEventSizing',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxTimeAxisUnit',
    'maxWidth',
    'maxZoomLevel',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'presets',
    'preventTooltipOnTouch',
    'project',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceMargin',
    'resourceTimeRangeRenderer',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'scaleColumn',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'series',
    'showBarText',
    'showBarTip',
    'showDirty',
    'showEffortUnit',
    'showOnClick',
    'showOnHover',
    'snap',
    'snapRelativeToEventStartDate',
    'startDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stickyHeaders',
    'store',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'tickSize',
    'timeAxis',
    'timeRanges',
    'timeRangeStore',
    'title',
    'tools',
    'trackMouse',
    'transitionDuration',
    'ui',
    'viewPreset',
    'visibleDate',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'width',
    'workingTime',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
]);
BryntumResourceUtilizationComponent.bryntumConfigsOnly = [
    'adopt',
    'aggregateDataEntry',
    'aggregateHistogramDataForGroups',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'ariaDescription',
    'ariaLabel',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'barTextEffortUnit',
    'barTipEffortUnit',
    'barTooltipTemplate',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'dataModelField',
    'defaultRegion',
    'defaultResourceImageName',
    'destroyStore',
    'destroyStores',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'effortFormat',
    'effortUnit',
    'enableSticky',
    'enableTextSelection',
    'endParamName',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getBarText',
    'getBarTip',
    'getDataEntryForAggregating',
    'getHtml',
    'getOutlineClass',
    'getRecordData',
    'getRectClass',
    'getRowHeight',
    'hardRefreshOnTimeAxisReconfigure',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'histogramWidgetClass',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'includeInactiveEvents',
    'infiniteScroll',
    'initAggregatedDataEntry',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'managedEventSizing',
    'maskDefaults',
    'masked',
    'maximizable',
    'maxTimeAxisUnit',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceTimeRangeRenderer',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'scaleColumn',
    'scrollerClass',
    'scrollManager',
    'series',
    'showBarText',
    'showDirty',
    'showEffortUnit',
    'showOnClick',
    'showOnHover',
    'snapRelativeToEventStartDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stickyHeaders',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'timeAxis',
    'trackMouse',
    'ui',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
];
BryntumResourceUtilizationComponent.bryntumProps = BryntumResourceUtilizationComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'appendTo',
    'ariaLive',
    'barMargin',
    'cls',
    'columnLines',
    'columns',
    'crudManager',
    'data',
    'dataset',
    'disabled',
    'displayDateFormat',
    'emptyText',
    'enableEventAnimations',
    'enableUndoRedoKeys',
    'endDate',
    'eventColor',
    'eventStyle',
    'extraData',
    'fillTicks',
    'flex',
    'forceFit',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hideRowHover',
    'histogramWidget',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'isEngineReady',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'maxZoomLevel',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'preserveScroll',
    'presets',
    'project',
    'readOnly',
    'resourceMargin',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollLeft',
    'scrollTop',
    'scrollX',
    'selectedCell',
    'selectedCells',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'showBarTip',
    'snap',
    'startDate',
    'state',
    'store',
    'tickSize',
    'timeRanges',
    'timeRangeStore',
    'timeResolution',
    'timeZone',
    'title',
    'tools',
    'tooltip',
    'transitionDuration',
    'viewPreset',
    'visibleDate',
    'width',
    'workingTime',
    'zoomLevel'
]);
BryntumResourceUtilizationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumResourceUtilizationComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumResourceUtilizationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumResourceUtilizationComponent, selector: "bryntum-resource-utilization", inputs: { adopt: "adopt", aggregateDataEntry: "aggregateDataEntry", aggregateHistogramDataForGroups: "aggregateHistogramDataForGroups", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", animateTreeNodeToggle: "animateTreeNodeToggle", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoAdjustTimeAxis: "autoAdjustTimeAxis", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", barTextEffortUnit: "barTextEffortUnit", barTipEffortUnit: "barTipEffortUnit", barTooltipTemplate: "barTooltipTemplate", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", bufferCoef: "bufferCoef", bufferThreshold: "bufferThreshold", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", dataModelField: "dataModelField", defaultRegion: "defaultRegion", defaultResourceImageName: "defaultResourceImageName", destroyStore: "destroyStore", destroyStores: "destroyStores", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", dock: "dock", effortFormat: "effortFormat", effortUnit: "effortUnit", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", endParamName: "endParamName", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getBarText: "getBarText", getBarTip: "getBarTip", getDataEntryForAggregating: "getDataEntryForAggregating", getHtml: "getHtml", getOutlineClass: "getOutlineClass", getRecordData: "getRecordData", getRectClass: "getRectClass", getRowHeight: "getRowHeight", hardRefreshOnTimeAxisReconfigure: "hardRefreshOnTimeAxisReconfigure", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", histogramWidgetClass: "histogramWidgetClass", ignoreDomEventsWhileScrolling: "ignoreDomEventsWhileScrolling", ignoreParentReadOnly: "ignoreParentReadOnly", includeInactiveEvents: "includeInactiveEvents", infiniteScroll: "infiniteScroll", initAggregatedDataEntry: "initAggregatedDataEntry", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", managedEventSizing: "managedEventSizing", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", maxTimeAxisUnit: "maxTimeAxisUnit", modal: "modal", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", owner: "owner", partner: "partner", passStartEndParameters: "passStartEndParameters", plugins: "plugins", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", resourceImageExtension: "resourceImageExtension", resourceImagePath: "resourceImagePath", resourceTimeRangeRenderer: "resourceTimeRangeRenderer", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", scaleColumn: "scaleColumn", scrollerClass: "scrollerClass", scrollManager: "scrollManager", series: "series", showBarText: "showBarText", showDirty: "showDirty", showEffortUnit: "showEffortUnit", showOnClick: "showOnClick", showOnHover: "showOnHover", snapRelativeToEventStartDate: "snapRelativeToEventStartDate", startParamName: "startParamName", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", stickyHeaders: "stickyHeaders", strips: "strips", subGridConfigs: "subGridConfigs", suppressFit: "suppressFit", syncMask: "syncMask", tab: "tab", tbar: "tbar", timeAxis: "timeAxis", trackMouse: "trackMouse", ui: "ui", visibleZoomFactor: "visibleZoomFactor", weekStartDay: "weekStartDay", weight: "weight", zoomKeepsOriginalTimespan: "zoomKeepsOriginalTimespan", zoomOnMouseWheel: "zoomOnMouseWheel", zoomOnTimeAxisDoubleClick: "zoomOnTimeAxisDoubleClick", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", barMargin: "barMargin", cls: "cls", columnLines: "columnLines", columns: "columns", dataset: "dataset", disabled: "disabled", displayDateFormat: "displayDateFormat", emptyText: "emptyText", enableEventAnimations: "enableEventAnimations", enableUndoRedoKeys: "enableUndoRedoKeys", endDate: "endDate", extraData: "extraData", fillTicks: "fillTicks", flex: "flex", forceFit: "forceFit", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hideRowHover: "hideRowHover", histogramWidget: "histogramWidget", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", maxZoomLevel: "maxZoomLevel", minHeight: "minHeight", minWidth: "minWidth", minZoomLevel: "minZoomLevel", preserveScroll: "preserveScroll", presets: "presets", project: "project", readOnly: "readOnly", resourceMargin: "resourceMargin", resourceTimeRanges: "resourceTimeRanges", resourceTimeRangeStore: "resourceTimeRangeStore", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", showBarTip: "showBarTip", snap: "snap", startDate: "startDate", store: "store", tickSize: "tickSize", timeRanges: "timeRanges", timeRangeStore: "timeRangeStore", title: "title", tools: "tools", transitionDuration: "transitionDuration", viewPreset: "viewPreset", visibleDate: "visibleDate", width: "width", workingTime: "workingTime", accessibility: "accessibility", crudManager: "crudManager", data: "data", eventColor: "eventColor", eventStyle: "eventStyle", isEngineReady: "isEngineReady", scrollLeft: "scrollLeft", scrollTop: "scrollTop", scrollX: "scrollX", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", timeResolution: "timeResolution", timeZone: "timeZone", tooltip: "tooltip", zoomLevel: "zoomLevel", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnLinesFeature: "columnLinesFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", headerZoomFeature: "headerZoomFeature", mergeCellsFeature: "mergeCellsFeature", nonWorkingTimeFeature: "nonWorkingTimeFeature", panFeature: "panFeature", pdfExportFeature: "pdfExportFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", resourceMenuFeature: "resourceMenuFeature", resourceNonWorkingTimeFeature: "resourceNonWorkingTimeFeature", resourceTimeRangesFeature: "resourceTimeRangesFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", scheduleTooltipFeature: "scheduleTooltipFeature", searchFeature: "searchFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", timeAxisHeaderMenuFeature: "timeAxisHeaderMenuFeature", timeRangesFeature: "timeRangesFeature", timeSpanHighlightFeature: "timeSpanHighlightFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature" }, outputs: { onBeforeAssignmentDelete: "onBeforeAssignmentDelete", onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDestroy: "onBeforeDestroy", onBeforeEventDelete: "onBeforeEventDelete", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforeHistogramDataCacheSet: "onBeforeHistogramDataCacheSet", onBeforePan: "onBeforePan", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforePresetChange: "onBeforePresetChange", onBeforeRenderHistogramRow: "onBeforeRenderHistogramRow", onBeforeRenderRecordHistogram: "onBeforeRenderRecordHistogram", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDateRangeChange: "onDateRangeChange", onDestroy: "onDestroy", onDragSelecting: "onDragSelecting", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onHistogramDataCacheSet: "onHistogramDataCacheSet", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onPaint: "onPaint", onPaste: "onPaste", onPdfExport: "onPdfExport", onPointerOver: "onPointerOver", onPresetChange: "onPresetChange", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResourceMenuBeforeShow: "onResourceMenuBeforeShow", onResourceMenuItem: "onResourceMenuItem", onResourceMenuShow: "onResourceMenuShow", onResourceMenuToggleItem: "onResourceMenuToggleItem", onResourceNonWorkingTimeClick: "onResourceNonWorkingTimeClick", onResourceNonWorkingTimeContextMenu: "onResourceNonWorkingTimeContextMenu", onResourceNonWorkingTimeDblClick: "onResourceNonWorkingTimeDblClick", onResourceNonWorkingTimeMouseDown: "onResourceNonWorkingTimeMouseDown", onResourceNonWorkingTimeMouseOut: "onResourceNonWorkingTimeMouseOut", onResourceNonWorkingTimeMouseOver: "onResourceNonWorkingTimeMouseOver", onResourceNonWorkingTimeMouseUp: "onResourceNonWorkingTimeMouseUp", onResourceTimeRangeClick: "onResourceTimeRangeClick", onResourceTimeRangeContextMenu: "onResourceTimeRangeContextMenu", onResourceTimeRangeDblClick: "onResourceTimeRangeDblClick", onResourceTimeRangeMouseDown: "onResourceTimeRangeMouseDown", onResourceTimeRangeMouseOut: "onResourceTimeRangeMouseOut", onResourceTimeRangeMouseOver: "onResourceTimeRangeMouseOver", onResourceTimeRangeMouseUp: "onResourceTimeRangeMouseUp", onResponsive: "onResponsive", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onTimeAxisChange: "onTimeAxisChange", onTimeAxisHeaderClick: "onTimeAxisHeaderClick", onTimeAxisHeaderContextMenu: "onTimeAxisHeaderContextMenu", onTimeAxisHeaderDblClick: "onTimeAxisHeaderDblClick", onTimeAxisHeaderMenuBeforeShow: "onTimeAxisHeaderMenuBeforeShow", onTimeAxisHeaderMenuItem: "onTimeAxisHeaderMenuItem", onTimeAxisHeaderMenuShow: "onTimeAxisHeaderMenuShow", onTimelineContextChange: "onTimelineContextChange", onTimelineViewportResize: "onTimelineViewportResize", onTimeRangeHeaderClick: "onTimeRangeHeaderClick", onTimeRangeHeaderContextMenu: "onTimeRangeHeaderContextMenu", onTimeRangeHeaderDblClick: "onTimeRangeHeaderDblClick", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onUnsplit: "onUnsplit", onVisibleDateRangeChange: "onVisibleDateRangeChange" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumResourceUtilizationComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-resource-utilization',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], aggregateDataEntry: [{
                type: Input
            }], aggregateHistogramDataForGroups: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoAdjustTimeAxis: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], barTextEffortUnit: [{
                type: Input
            }], barTipEffortUnit: [{
                type: Input
            }], barTooltipTemplate: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], bufferCoef: [{
                type: Input
            }], bufferThreshold: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], dataModelField: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], defaultResourceImageName: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], destroyStores: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], dock: [{
                type: Input
            }], effortFormat: [{
                type: Input
            }], effortUnit: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], endParamName: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getBarText: [{
                type: Input
            }], getBarTip: [{
                type: Input
            }], getDataEntryForAggregating: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getOutlineClass: [{
                type: Input
            }], getRecordData: [{
                type: Input
            }], getRectClass: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], hardRefreshOnTimeAxisReconfigure: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], histogramWidgetClass: [{
                type: Input
            }], ignoreDomEventsWhileScrolling: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], includeInactiveEvents: [{
                type: Input
            }], infiniteScroll: [{
                type: Input
            }], initAggregatedDataEntry: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], managedEventSizing: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], maxTimeAxisUnit: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], owner: [{
                type: Input
            }], partner: [{
                type: Input
            }], passStartEndParameters: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], resourceImageExtension: [{
                type: Input
            }], resourceImagePath: [{
                type: Input
            }], resourceTimeRangeRenderer: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scaleColumn: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], series: [{
                type: Input
            }], showBarText: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showEffortUnit: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], snapRelativeToEventStartDate: [{
                type: Input
            }], startParamName: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], stickyHeaders: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], suppressFit: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], timeAxis: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], ui: [{
                type: Input
            }], visibleZoomFactor: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], zoomKeepsOriginalTimespan: [{
                type: Input
            }], zoomOnMouseWheel: [{
                type: Input
            }], zoomOnTimeAxisDoubleClick: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], barMargin: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], displayDateFormat: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableEventAnimations: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], endDate: [{
                type: Input
            }], extraData: [{
                type: Input
            }], fillTicks: [{
                type: Input
            }], flex: [{
                type: Input
            }], forceFit: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hideRowHover: [{
                type: Input
            }], histogramWidget: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], maxZoomLevel: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], minZoomLevel: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], presets: [{
                type: Input
            }], project: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], resourceMargin: [{
                type: Input
            }], resourceTimeRanges: [{
                type: Input
            }], resourceTimeRangeStore: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], showBarTip: [{
                type: Input
            }], snap: [{
                type: Input
            }], startDate: [{
                type: Input
            }], store: [{
                type: Input
            }], tickSize: [{
                type: Input
            }], timeRanges: [{
                type: Input
            }], timeRangeStore: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], viewPreset: [{
                type: Input
            }], visibleDate: [{
                type: Input
            }], width: [{
                type: Input
            }], workingTime: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], crudManager: [{
                type: Input
            }], data: [{
                type: Input
            }], eventColor: [{
                type: Input
            }], eventStyle: [{
                type: Input
            }], isEngineReady: [{
                type: Input
            }], scrollLeft: [{
                type: Input
            }], scrollTop: [{
                type: Input
            }], scrollX: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], timeResolution: [{
                type: Input
            }], timeZone: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], zoomLevel: [{
                type: Input
            }], onBeforeAssignmentDelete: [{
                type: Output
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeEventDelete: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeHistogramDataCacheSet: [{
                type: Output
            }], onBeforePan: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforePresetChange: [{
                type: Output
            }], onBeforeRenderHistogramRow: [{
                type: Output
            }], onBeforeRenderRecordHistogram: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDateRangeChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onHistogramDataCacheSet: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onPresetChange: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResourceMenuBeforeShow: [{
                type: Output
            }], onResourceMenuItem: [{
                type: Output
            }], onResourceMenuShow: [{
                type: Output
            }], onResourceMenuToggleItem: [{
                type: Output
            }], onResourceNonWorkingTimeClick: [{
                type: Output
            }], onResourceNonWorkingTimeContextMenu: [{
                type: Output
            }], onResourceNonWorkingTimeDblClick: [{
                type: Output
            }], onResourceNonWorkingTimeMouseDown: [{
                type: Output
            }], onResourceNonWorkingTimeMouseOut: [{
                type: Output
            }], onResourceNonWorkingTimeMouseOver: [{
                type: Output
            }], onResourceNonWorkingTimeMouseUp: [{
                type: Output
            }], onResourceTimeRangeClick: [{
                type: Output
            }], onResourceTimeRangeContextMenu: [{
                type: Output
            }], onResourceTimeRangeDblClick: [{
                type: Output
            }], onResourceTimeRangeMouseDown: [{
                type: Output
            }], onResourceTimeRangeMouseOut: [{
                type: Output
            }], onResourceTimeRangeMouseOver: [{
                type: Output
            }], onResourceTimeRangeMouseUp: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onTimeAxisChange: [{
                type: Output
            }], onTimeAxisHeaderClick: [{
                type: Output
            }], onTimeAxisHeaderContextMenu: [{
                type: Output
            }], onTimeAxisHeaderDblClick: [{
                type: Output
            }], onTimeAxisHeaderMenuBeforeShow: [{
                type: Output
            }], onTimeAxisHeaderMenuItem: [{
                type: Output
            }], onTimeAxisHeaderMenuShow: [{
                type: Output
            }], onTimelineContextChange: [{
                type: Output
            }], onTimelineViewportResize: [{
                type: Output
            }], onTimeRangeHeaderClick: [{
                type: Output
            }], onTimeRangeHeaderContextMenu: [{
                type: Output
            }], onTimeRangeHeaderDblClick: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], onVisibleDateRangeChange: [{
                type: Output
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnLinesFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], headerZoomFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], nonWorkingTimeFeature: [{
                type: Input
            }], panFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], resourceMenuFeature: [{
                type: Input
            }], resourceNonWorkingTimeFeature: [{
                type: Input
            }], resourceTimeRangesFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], scheduleTooltipFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], timeAxisHeaderMenuFeature: [{
                type: Input
            }], timeRangesFeature: [{
                type: Input
            }], timeSpanHighlightFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum Scheduler
 */
class BryntumSchedulerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAfterDependencyCreateDrop = new EventEmitter();
        this.onAfterDependencySave = new EventEmitter();
        this.onAfterDragCreate = new EventEmitter();
        this.onAfterEventDrop = new EventEmitter();
        this.onAfterEventEdit = new EventEmitter();
        this.onAfterEventSave = new EventEmitter();
        this.onAssignmentSelectionChange = new EventEmitter();
        this.onBeforeAssignmentDelete = new EventEmitter();
        this.onBeforeAssignmentSelectionChange = new EventEmitter();
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDependencyAdd = new EventEmitter();
        this.onBeforeDependencyCreateDrag = new EventEmitter();
        this.onBeforeDependencyCreateFinalize = new EventEmitter();
        this.onBeforeDependencyDelete = new EventEmitter();
        this.onBeforeDependencyEdit = new EventEmitter();
        this.onBeforeDependencyEditShow = new EventEmitter();
        this.onBeforeDependencySave = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeDragCreate = new EventEmitter();
        this.onBeforeDragCreateFinalize = new EventEmitter();
        this.onBeforeEventAdd = new EventEmitter();
        this.onBeforeEventDelete = new EventEmitter();
        this.onBeforeEventDrag = new EventEmitter();
        this.onBeforeEventDragSelect = new EventEmitter();
        this.onBeforeEventDropFinalize = new EventEmitter();
        this.onBeforeEventEdit = new EventEmitter();
        this.onBeforeEventEditShow = new EventEmitter();
        this.onBeforeEventResize = new EventEmitter();
        this.onBeforeEventResizeFinalize = new EventEmitter();
        this.onBeforeEventSave = new EventEmitter();
        this.onBeforeEventSelectionChange = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforePan = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforePresetChange = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeShowTerminals = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDateRangeChange = new EventEmitter();
        this.onDependenciesDrawn = new EventEmitter();
        this.onDependencyClick = new EventEmitter();
        this.onDependencyContextMenu = new EventEmitter();
        this.onDependencyCreateDragStart = new EventEmitter();
        this.onDependencyCreateDrop = new EventEmitter();
        this.onDependencyDblClick = new EventEmitter();
        this.onDependencyMouseOut = new EventEmitter();
        this.onDependencyMouseOver = new EventEmitter();
        this.onDependencyValidationComplete = new EventEmitter();
        this.onDependencyValidationStart = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragCreateEnd = new EventEmitter();
        this.onDragCreateStart = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onEventAutoCreated = new EventEmitter();
        this.onEventClick = new EventEmitter();
        this.onEventContextMenu = new EventEmitter();
        this.onEventDblClick = new EventEmitter();
        this.onEventDrag = new EventEmitter();
        this.onEventDragAbort = new EventEmitter();
        this.onEventDragModeChange = new EventEmitter();
        this.onEventDragReset = new EventEmitter();
        this.onEventDragStart = new EventEmitter();
        this.onEventDrop = new EventEmitter();
        this.onEventEditBeforeSetRecord = new EventEmitter();
        this.onEventKeyDown = new EventEmitter();
        this.onEventKeyUp = new EventEmitter();
        this.onEventMenuBeforeShow = new EventEmitter();
        this.onEventMenuItem = new EventEmitter();
        this.onEventMenuShow = new EventEmitter();
        this.onEventMouseDown = new EventEmitter();
        this.onEventMouseEnter = new EventEmitter();
        this.onEventMouseLeave = new EventEmitter();
        this.onEventMouseOut = new EventEmitter();
        this.onEventMouseOver = new EventEmitter();
        this.onEventMouseUp = new EventEmitter();
        this.onEventPartialResize = new EventEmitter();
        this.onEventResizeEnd = new EventEmitter();
        this.onEventResizeStart = new EventEmitter();
        this.onEventSelectionChange = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onNavigate = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPasteNotAllowed = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onPresetChange = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onReleaseEvent = new EventEmitter();
        this.onRenderEvent = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResourceHeaderClick = new EventEmitter();
        this.onResourceHeaderContextmenu = new EventEmitter();
        this.onResourceHeaderDblclick = new EventEmitter();
        this.onResourceMenuBeforeShow = new EventEmitter();
        this.onResourceMenuItem = new EventEmitter();
        this.onResourceMenuShow = new EventEmitter();
        this.onResourceMenuToggleItem = new EventEmitter();
        this.onResourceTimeRangeClick = new EventEmitter();
        this.onResourceTimeRangeContextMenu = new EventEmitter();
        this.onResourceTimeRangeDblClick = new EventEmitter();
        this.onResourceTimeRangeMouseDown = new EventEmitter();
        this.onResourceTimeRangeMouseOut = new EventEmitter();
        this.onResourceTimeRangeMouseOver = new EventEmitter();
        this.onResourceTimeRangeMouseUp = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScheduleClick = new EventEmitter();
        this.onScheduleContextMenu = new EventEmitter();
        this.onScheduleDblClick = new EventEmitter();
        this.onScheduleMenuBeforeShow = new EventEmitter();
        this.onScheduleMenuItem = new EventEmitter();
        this.onScheduleMenuShow = new EventEmitter();
        this.onScheduleMouseDown = new EventEmitter();
        this.onScheduleMouseEnter = new EventEmitter();
        this.onScheduleMouseLeave = new EventEmitter();
        this.onScheduleMouseMove = new EventEmitter();
        this.onScheduleMouseUp = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onTimeAxisChange = new EventEmitter();
        this.onTimeAxisHeaderClick = new EventEmitter();
        this.onTimeAxisHeaderContextMenu = new EventEmitter();
        this.onTimeAxisHeaderDblClick = new EventEmitter();
        this.onTimeAxisHeaderMenuBeforeShow = new EventEmitter();
        this.onTimeAxisHeaderMenuItem = new EventEmitter();
        this.onTimeAxisHeaderMenuShow = new EventEmitter();
        this.onTimelineContextChange = new EventEmitter();
        this.onTimelineViewportResize = new EventEmitter();
        this.onTimeRangeHeaderClick = new EventEmitter();
        this.onTimeRangeHeaderContextMenu = new EventEmitter();
        this.onTimeRangeHeaderDblClick = new EventEmitter();
        this.onTimeSelectionChange = new EventEmitter();
        this.onTimeSelectionElementClick = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.onVisibleDateRangeChange = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSchedulerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
        // Backwards compatibility for gridInstance, schedulerInstance etc.
        // @ts-ignore
        me[StringHelper.uncapitalize(instanceName) + 'Instance'] = me.instance;
        // 
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSchedulerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSchedulerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSchedulerComponent.instanceClass = Scheduler;
BryntumSchedulerComponent.instanceName = 'Scheduler';
BryntumSchedulerComponent.bryntumEvents = [
    'onAfterDependencyCreateDrop',
    'onAfterDependencySave',
    'onAfterDragCreate',
    'onAfterEventDrop',
    'onAfterEventEdit',
    'onAfterEventSave',
    'onAssignmentSelectionChange',
    'onBeforeAssignmentDelete',
    'onBeforeAssignmentSelectionChange',
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDependencyAdd',
    'onBeforeDependencyCreateDrag',
    'onBeforeDependencyCreateFinalize',
    'onBeforeDependencyDelete',
    'onBeforeDependencyEdit',
    'onBeforeDependencyEditShow',
    'onBeforeDependencySave',
    'onBeforeDestroy',
    'onBeforeDragCreate',
    'onBeforeDragCreateFinalize',
    'onBeforeEventAdd',
    'onBeforeEventDelete',
    'onBeforeEventDrag',
    'onBeforeEventDragSelect',
    'onBeforeEventDropFinalize',
    'onBeforeEventEdit',
    'onBeforeEventEditShow',
    'onBeforeEventResize',
    'onBeforeEventResizeFinalize',
    'onBeforeEventSave',
    'onBeforeEventSelectionChange',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforePan',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforePresetChange',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeShowTerminals',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDateRangeChange',
    'onDependenciesDrawn',
    'onDependencyClick',
    'onDependencyContextMenu',
    'onDependencyCreateDragStart',
    'onDependencyCreateDrop',
    'onDependencyDblClick',
    'onDependencyMouseOut',
    'onDependencyMouseOver',
    'onDependencyValidationComplete',
    'onDependencyValidationStart',
    'onDestroy',
    'onDragCreateEnd',
    'onDragCreateStart',
    'onDragSelecting',
    'onEventAutoCreated',
    'onEventClick',
    'onEventContextMenu',
    'onEventDblClick',
    'onEventDrag',
    'onEventDragAbort',
    'onEventDragModeChange',
    'onEventDragReset',
    'onEventDragStart',
    'onEventDrop',
    'onEventEditBeforeSetRecord',
    'onEventKeyDown',
    'onEventKeyUp',
    'onEventMenuBeforeShow',
    'onEventMenuItem',
    'onEventMenuShow',
    'onEventMouseDown',
    'onEventMouseEnter',
    'onEventMouseLeave',
    'onEventMouseOut',
    'onEventMouseOver',
    'onEventMouseUp',
    'onEventPartialResize',
    'onEventResizeEnd',
    'onEventResizeStart',
    'onEventSelectionChange',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onMouseOut',
    'onMouseOver',
    'onNavigate',
    'onPaint',
    'onPaste',
    'onPasteNotAllowed',
    'onPdfExport',
    'onPointerOver',
    'onPresetChange',
    'onRecompose',
    'onReleaseEvent',
    'onRenderEvent',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResourceHeaderClick',
    'onResourceHeaderContextmenu',
    'onResourceHeaderDblclick',
    'onResourceMenuBeforeShow',
    'onResourceMenuItem',
    'onResourceMenuShow',
    'onResourceMenuToggleItem',
    'onResourceTimeRangeClick',
    'onResourceTimeRangeContextMenu',
    'onResourceTimeRangeDblClick',
    'onResourceTimeRangeMouseDown',
    'onResourceTimeRangeMouseOut',
    'onResourceTimeRangeMouseOver',
    'onResourceTimeRangeMouseUp',
    'onResponsive',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScheduleClick',
    'onScheduleContextMenu',
    'onScheduleDblClick',
    'onScheduleMenuBeforeShow',
    'onScheduleMenuItem',
    'onScheduleMenuShow',
    'onScheduleMouseDown',
    'onScheduleMouseEnter',
    'onScheduleMouseLeave',
    'onScheduleMouseMove',
    'onScheduleMouseUp',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onTimeAxisChange',
    'onTimeAxisHeaderClick',
    'onTimeAxisHeaderContextMenu',
    'onTimeAxisHeaderDblClick',
    'onTimeAxisHeaderMenuBeforeShow',
    'onTimeAxisHeaderMenuItem',
    'onTimeAxisHeaderMenuShow',
    'onTimelineContextChange',
    'onTimelineViewportResize',
    'onTimeRangeHeaderClick',
    'onTimeRangeHeaderContextMenu',
    'onTimeRangeHeaderDblClick',
    'onTimeSelectionChange',
    'onTimeSelectionElementClick',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onUnsplit',
    'onVisibleDateRangeChange'
];
BryntumSchedulerComponent.bryntumFeatureNames = [
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnLinesFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'dependenciesFeature',
    'dependencyEditFeature',
    'eventCopyPasteFeature',
    'eventDragFeature',
    'eventDragCreateFeature',
    'eventDragSelectFeature',
    'eventEditFeature',
    'eventFilterFeature',
    'eventMenuFeature',
    'eventNonWorkingTimeFeature',
    'eventResizeFeature',
    'eventTooltipFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'headerZoomFeature',
    'labelsFeature',
    'mergeCellsFeature',
    'nonWorkingTimeFeature',
    'panFeature',
    'pdfExportFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'resourceMenuFeature',
    'resourceTimeRangesFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'scheduleContextFeature',
    'scheduleMenuFeature',
    'scheduleTooltipFeature',
    'searchFeature',
    'simpleEventEditFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stickyEventsFeature',
    'stripeFeature',
    'summaryFeature',
    'timeAxisHeaderMenuFeature',
    'timeRangesFeature',
    'timeSelectionFeature',
    'treeFeature',
    'treeGroupFeature'
];
BryntumSchedulerComponent.bryntumConfigs = BryntumSchedulerComponent.bryntumFeatureNames.concat([
    'adopt',
    'alignSelf',
    'allowCreate',
    'allowDropOnEventBar',
    'allowOver',
    'allowOverlap',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'assignments',
    'assignmentStore',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'barMargin',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'createEventOnDblClick',
    'creationTooltip',
    'creationTooltipTemplate',
    'crudManager',
    'crudManagerClass',
    'data',
    'dataset',
    'date',
    'dateFormat',
    'dateSeparator',
    'defaultRegion',
    'defaultResourceImageName',
    'dependencies',
    'dependencyStore',
    'descriptionFormat',
    'descriptionRenderer',
    'deselectAllOnScheduleClick',
    'deselectOnClick',
    'destroyStore',
    'destroyStores',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'displayDateFormat',
    'dock',
    'durationDisplayPrecision',
    'emptyText',
    'enableDeleteKey',
    'enableEventAnimations',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'endDate',
    'endParamName',
    'eventBarTextField',
    'eventBodyTemplate',
    'eventColor',
    'eventLayout',
    'eventRenderer',
    'eventRendererThisObj',
    'events',
    'eventSelectionDisabled',
    'eventStore',
    'eventStyle',
    'extraData',
    'features',
    'fillLastColumn',
    'fillTicks',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forceFit',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getDateConstraints',
    'getHtml',
    'getRowHeight',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hideRowHover',
    'highlightPredecessors',
    'highlightSuccessors',
    'horizontalEventSorterFn',
    'hoverDelay',
    'id',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'insertBefore',
    'insertFirst',
    'isEventSelectable',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'maintainSelectionOnDatasetChange',
    'managedEventSizing',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxTimeAxisUnit',
    'maxWidth',
    'maxZoomLevel',
    'milestoneAlign',
    'milestoneCharWidth',
    'milestoneLayoutMode',
    'milestoneTextPosition',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'modal',
    'mode',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'multiEventSelect',
    'narrowEventWidth',
    'overlappingEventSorter',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preCalculateHeightLimit',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'presets',
    'preventTooltipOnTouch',
    'project',
    'range',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceColumns',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceMargin',
    'resources',
    'resourceStore',
    'resourceTimeRangeRenderer',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'selectResourceOnEventNavigate',
    'selectResourceOnScheduleClick',
    'showCreationTooltip',
    'showDirty',
    'showEventColorPickers',
    'showLagInTooltip',
    'showOnClick',
    'showOnHover',
    'showRecurringUI',
    'showTooltip',
    'snap',
    'snapRelativeToEventStartDate',
    'startDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stepUnit',
    'stickyHeaders',
    'store',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'terminalCls',
    'terminalHideDelay',
    'terminalOffset',
    'terminalShowDelay',
    'terminalSides',
    'terminalSize',
    'tickSize',
    'timeAxis',
    'timeRanges',
    'timeRangeStore',
    'timeZone',
    'title',
    'tools',
    'tooltipTemplate',
    'trackMouse',
    'transitionDuration',
    'triggerSelectionChangeOnRemove',
    'ui',
    'updateTimelineContextOnScroll',
    'useInitialAnimation',
    'verticalTimeAxisColumn',
    'viewPreset',
    'visibleDate',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'width',
    'workingTime',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
]);
BryntumSchedulerComponent.bryntumConfigsOnly = [
    'adopt',
    'allowCreate',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'ariaDescription',
    'ariaLabel',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'createEventOnDblClick',
    'creationTooltip',
    'crudManagerClass',
    'date',
    'defaultRegion',
    'defaultResourceImageName',
    'descriptionRenderer',
    'deselectAllOnScheduleClick',
    'deselectOnClick',
    'destroyStore',
    'destroyStores',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'durationDisplayPrecision',
    'enableDeleteKey',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'endParamName',
    'eventBarTextField',
    'eventBodyTemplate',
    'eventRenderer',
    'eventRendererThisObj',
    'eventSelectionDisabled',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getDateConstraints',
    'getHtml',
    'getRowHeight',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'highlightPredecessors',
    'highlightSuccessors',
    'horizontalEventSorterFn',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'maintainSelectionOnDatasetChange',
    'managedEventSizing',
    'maskDefaults',
    'masked',
    'maximizable',
    'maxTimeAxisUnit',
    'modal',
    'mode',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'multiEventSelect',
    'narrowEventWidth',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preCalculateHeightLimit',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'range',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceColumns',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceTimeRangeRenderer',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'scrollerClass',
    'scrollManager',
    'showCreationTooltip',
    'showDirty',
    'showEventColorPickers',
    'showLagInTooltip',
    'showOnClick',
    'showOnHover',
    'showRecurringUI',
    'showTooltip',
    'snapRelativeToEventStartDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stepUnit',
    'stickyHeaders',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'terminalCls',
    'terminalSides',
    'timeAxis',
    'trackMouse',
    'triggerSelectionChangeOnRemove',
    'ui',
    'verticalTimeAxisColumn',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
];
BryntumSchedulerComponent.bryntumProps = BryntumSchedulerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'allowDropOnEventBar',
    'allowOverlap',
    'appendTo',
    'ariaLive',
    'assignments',
    'assignmentStore',
    'barMargin',
    'cls',
    'columnLines',
    'columns',
    'creationTooltipTemplate',
    'crudManager',
    'data',
    'dataset',
    'dateFormat',
    'dateSeparator',
    'dependencies',
    'dependencyStore',
    'descriptionFormat',
    'disabled',
    'displayDateFormat',
    'emptyText',
    'enableEventAnimations',
    'enableUndoRedoKeys',
    'endDate',
    'eventColor',
    'eventLayout',
    'events',
    'eventStore',
    'eventStyle',
    'extraData',
    'fillTicks',
    'flex',
    'forceFit',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hideRowHover',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'isEngineReady',
    'isEventSelectable',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'maxZoomLevel',
    'milestoneAlign',
    'milestoneCharWidth',
    'milestoneLayoutMode',
    'milestoneTextPosition',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'overlappingEventSorter',
    'preserveScroll',
    'presets',
    'project',
    'readOnly',
    'resourceMargin',
    'resources',
    'resourceStore',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollLeft',
    'scrollTop',
    'scrollX',
    'selectedAssignments',
    'selectedCell',
    'selectedCells',
    'selectedEvents',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'selectResourceOnEventNavigate',
    'selectResourceOnScheduleClick',
    'snap',
    'startDate',
    'state',
    'store',
    'terminalHideDelay',
    'terminalOffset',
    'terminalShowDelay',
    'terminalSize',
    'tickSize',
    'timeRanges',
    'timeRangeStore',
    'timeResolution',
    'timeZone',
    'title',
    'tools',
    'tooltip',
    'tooltipTemplate',
    'transitionDuration',
    'updateTimelineContextOnScroll',
    'useInitialAnimation',
    'viewPreset',
    'visibleDate',
    'width',
    'workingTime',
    'zoomLevel'
]);
BryntumSchedulerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSchedulerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSchedulerComponent, selector: "bryntum-scheduler", inputs: { adopt: "adopt", allowCreate: "allowCreate", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", animateTreeNodeToggle: "animateTreeNodeToggle", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoAdjustTimeAxis: "autoAdjustTimeAxis", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", bufferCoef: "bufferCoef", bufferThreshold: "bufferThreshold", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", createEventOnDblClick: "createEventOnDblClick", creationTooltip: "creationTooltip", crudManagerClass: "crudManagerClass", date: "date", defaultRegion: "defaultRegion", defaultResourceImageName: "defaultResourceImageName", descriptionRenderer: "descriptionRenderer", deselectAllOnScheduleClick: "deselectAllOnScheduleClick", deselectOnClick: "deselectOnClick", destroyStore: "destroyStore", destroyStores: "destroyStores", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", dock: "dock", durationDisplayPrecision: "durationDisplayPrecision", enableDeleteKey: "enableDeleteKey", enableRecurringEvents: "enableRecurringEvents", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", endParamName: "endParamName", eventBarTextField: "eventBarTextField", eventBodyTemplate: "eventBodyTemplate", eventRenderer: "eventRenderer", eventRendererThisObj: "eventRendererThisObj", eventSelectionDisabled: "eventSelectionDisabled", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getDateConstraints: "getDateConstraints", getHtml: "getHtml", getRowHeight: "getRowHeight", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", highlightPredecessors: "highlightPredecessors", highlightSuccessors: "highlightSuccessors", horizontalEventSorterFn: "horizontalEventSorterFn", ignoreDomEventsWhileScrolling: "ignoreDomEventsWhileScrolling", ignoreParentReadOnly: "ignoreParentReadOnly", infiniteScroll: "infiniteScroll", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", maintainSelectionOnDatasetChange: "maintainSelectionOnDatasetChange", managedEventSizing: "managedEventSizing", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", maxTimeAxisUnit: "maxTimeAxisUnit", modal: "modal", mode: "mode", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", multiEventSelect: "multiEventSelect", narrowEventWidth: "narrowEventWidth", owner: "owner", partner: "partner", passStartEndParameters: "passStartEndParameters", plugins: "plugins", preCalculateHeightLimit: "preCalculateHeightLimit", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", range: "range", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", resourceColumns: "resourceColumns", resourceImageExtension: "resourceImageExtension", resourceImagePath: "resourceImagePath", resourceTimeRangeRenderer: "resourceTimeRangeRenderer", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", scrollerClass: "scrollerClass", scrollManager: "scrollManager", showCreationTooltip: "showCreationTooltip", showDirty: "showDirty", showEventColorPickers: "showEventColorPickers", showLagInTooltip: "showLagInTooltip", showOnClick: "showOnClick", showOnHover: "showOnHover", showRecurringUI: "showRecurringUI", showTooltip: "showTooltip", snapRelativeToEventStartDate: "snapRelativeToEventStartDate", startParamName: "startParamName", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", stepUnit: "stepUnit", stickyHeaders: "stickyHeaders", strips: "strips", subGridConfigs: "subGridConfigs", suppressFit: "suppressFit", syncMask: "syncMask", tab: "tab", tbar: "tbar", terminalCls: "terminalCls", terminalSides: "terminalSides", timeAxis: "timeAxis", trackMouse: "trackMouse", triggerSelectionChangeOnRemove: "triggerSelectionChangeOnRemove", ui: "ui", verticalTimeAxisColumn: "verticalTimeAxisColumn", visibleZoomFactor: "visibleZoomFactor", weekStartDay: "weekStartDay", weight: "weight", zoomKeepsOriginalTimespan: "zoomKeepsOriginalTimespan", zoomOnMouseWheel: "zoomOnMouseWheel", zoomOnTimeAxisDoubleClick: "zoomOnTimeAxisDoubleClick", alignSelf: "alignSelf", allowDropOnEventBar: "allowDropOnEventBar", allowOverlap: "allowOverlap", appendTo: "appendTo", ariaLive: "ariaLive", assignments: "assignments", assignmentStore: "assignmentStore", barMargin: "barMargin", cls: "cls", columnLines: "columnLines", columns: "columns", creationTooltipTemplate: "creationTooltipTemplate", crudManager: "crudManager", data: "data", dataset: "dataset", dateFormat: "dateFormat", dateSeparator: "dateSeparator", dependencies: "dependencies", dependencyStore: "dependencyStore", descriptionFormat: "descriptionFormat", disabled: "disabled", displayDateFormat: "displayDateFormat", emptyText: "emptyText", enableEventAnimations: "enableEventAnimations", enableUndoRedoKeys: "enableUndoRedoKeys", endDate: "endDate", eventColor: "eventColor", eventLayout: "eventLayout", events: "events", eventStore: "eventStore", eventStyle: "eventStyle", extraData: "extraData", fillTicks: "fillTicks", flex: "flex", forceFit: "forceFit", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hideRowHover: "hideRowHover", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", isEventSelectable: "isEventSelectable", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", maxZoomLevel: "maxZoomLevel", milestoneAlign: "milestoneAlign", milestoneCharWidth: "milestoneCharWidth", milestoneLayoutMode: "milestoneLayoutMode", milestoneTextPosition: "milestoneTextPosition", minHeight: "minHeight", minWidth: "minWidth", minZoomLevel: "minZoomLevel", overlappingEventSorter: "overlappingEventSorter", preserveScroll: "preserveScroll", presets: "presets", project: "project", readOnly: "readOnly", resourceMargin: "resourceMargin", resources: "resources", resourceStore: "resourceStore", resourceTimeRanges: "resourceTimeRanges", resourceTimeRangeStore: "resourceTimeRangeStore", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", selectResourceOnEventNavigate: "selectResourceOnEventNavigate", selectResourceOnScheduleClick: "selectResourceOnScheduleClick", snap: "snap", startDate: "startDate", store: "store", terminalHideDelay: "terminalHideDelay", terminalOffset: "terminalOffset", terminalShowDelay: "terminalShowDelay", terminalSize: "terminalSize", tickSize: "tickSize", timeRanges: "timeRanges", timeRangeStore: "timeRangeStore", timeZone: "timeZone", title: "title", tools: "tools", tooltipTemplate: "tooltipTemplate", transitionDuration: "transitionDuration", updateTimelineContextOnScroll: "updateTimelineContextOnScroll", useInitialAnimation: "useInitialAnimation", viewPreset: "viewPreset", visibleDate: "visibleDate", width: "width", workingTime: "workingTime", accessibility: "accessibility", isEngineReady: "isEngineReady", scrollLeft: "scrollLeft", scrollTop: "scrollTop", scrollX: "scrollX", selectedAssignments: "selectedAssignments", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedEvents: "selectedEvents", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", timeResolution: "timeResolution", tooltip: "tooltip", zoomLevel: "zoomLevel", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnLinesFeature: "columnLinesFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", dependenciesFeature: "dependenciesFeature", dependencyEditFeature: "dependencyEditFeature", eventCopyPasteFeature: "eventCopyPasteFeature", eventDragFeature: "eventDragFeature", eventDragCreateFeature: "eventDragCreateFeature", eventDragSelectFeature: "eventDragSelectFeature", eventEditFeature: "eventEditFeature", eventFilterFeature: "eventFilterFeature", eventMenuFeature: "eventMenuFeature", eventNonWorkingTimeFeature: "eventNonWorkingTimeFeature", eventResizeFeature: "eventResizeFeature", eventTooltipFeature: "eventTooltipFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", headerZoomFeature: "headerZoomFeature", labelsFeature: "labelsFeature", mergeCellsFeature: "mergeCellsFeature", nonWorkingTimeFeature: "nonWorkingTimeFeature", panFeature: "panFeature", pdfExportFeature: "pdfExportFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", resourceMenuFeature: "resourceMenuFeature", resourceTimeRangesFeature: "resourceTimeRangesFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", scheduleContextFeature: "scheduleContextFeature", scheduleMenuFeature: "scheduleMenuFeature", scheduleTooltipFeature: "scheduleTooltipFeature", searchFeature: "searchFeature", simpleEventEditFeature: "simpleEventEditFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stickyEventsFeature: "stickyEventsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", timeAxisHeaderMenuFeature: "timeAxisHeaderMenuFeature", timeRangesFeature: "timeRangesFeature", timeSelectionFeature: "timeSelectionFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature" }, outputs: { onAfterDependencyCreateDrop: "onAfterDependencyCreateDrop", onAfterDependencySave: "onAfterDependencySave", onAfterDragCreate: "onAfterDragCreate", onAfterEventDrop: "onAfterEventDrop", onAfterEventEdit: "onAfterEventEdit", onAfterEventSave: "onAfterEventSave", onAssignmentSelectionChange: "onAssignmentSelectionChange", onBeforeAssignmentDelete: "onBeforeAssignmentDelete", onBeforeAssignmentSelectionChange: "onBeforeAssignmentSelectionChange", onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDependencyAdd: "onBeforeDependencyAdd", onBeforeDependencyCreateDrag: "onBeforeDependencyCreateDrag", onBeforeDependencyCreateFinalize: "onBeforeDependencyCreateFinalize", onBeforeDependencyDelete: "onBeforeDependencyDelete", onBeforeDependencyEdit: "onBeforeDependencyEdit", onBeforeDependencyEditShow: "onBeforeDependencyEditShow", onBeforeDependencySave: "onBeforeDependencySave", onBeforeDestroy: "onBeforeDestroy", onBeforeDragCreate: "onBeforeDragCreate", onBeforeDragCreateFinalize: "onBeforeDragCreateFinalize", onBeforeEventAdd: "onBeforeEventAdd", onBeforeEventDelete: "onBeforeEventDelete", onBeforeEventDrag: "onBeforeEventDrag", onBeforeEventDragSelect: "onBeforeEventDragSelect", onBeforeEventDropFinalize: "onBeforeEventDropFinalize", onBeforeEventEdit: "onBeforeEventEdit", onBeforeEventEditShow: "onBeforeEventEditShow", onBeforeEventResize: "onBeforeEventResize", onBeforeEventResizeFinalize: "onBeforeEventResizeFinalize", onBeforeEventSave: "onBeforeEventSave", onBeforeEventSelectionChange: "onBeforeEventSelectionChange", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforePan: "onBeforePan", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforePresetChange: "onBeforePresetChange", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeShowTerminals: "onBeforeShowTerminals", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDateRangeChange: "onDateRangeChange", onDependenciesDrawn: "onDependenciesDrawn", onDependencyClick: "onDependencyClick", onDependencyContextMenu: "onDependencyContextMenu", onDependencyCreateDragStart: "onDependencyCreateDragStart", onDependencyCreateDrop: "onDependencyCreateDrop", onDependencyDblClick: "onDependencyDblClick", onDependencyMouseOut: "onDependencyMouseOut", onDependencyMouseOver: "onDependencyMouseOver", onDependencyValidationComplete: "onDependencyValidationComplete", onDependencyValidationStart: "onDependencyValidationStart", onDestroy: "onDestroy", onDragCreateEnd: "onDragCreateEnd", onDragCreateStart: "onDragCreateStart", onDragSelecting: "onDragSelecting", onEventAutoCreated: "onEventAutoCreated", onEventClick: "onEventClick", onEventContextMenu: "onEventContextMenu", onEventDblClick: "onEventDblClick", onEventDrag: "onEventDrag", onEventDragAbort: "onEventDragAbort", onEventDragModeChange: "onEventDragModeChange", onEventDragReset: "onEventDragReset", onEventDragStart: "onEventDragStart", onEventDrop: "onEventDrop", onEventEditBeforeSetRecord: "onEventEditBeforeSetRecord", onEventKeyDown: "onEventKeyDown", onEventKeyUp: "onEventKeyUp", onEventMenuBeforeShow: "onEventMenuBeforeShow", onEventMenuItem: "onEventMenuItem", onEventMenuShow: "onEventMenuShow", onEventMouseDown: "onEventMouseDown", onEventMouseEnter: "onEventMouseEnter", onEventMouseLeave: "onEventMouseLeave", onEventMouseOut: "onEventMouseOut", onEventMouseOver: "onEventMouseOver", onEventMouseUp: "onEventMouseUp", onEventPartialResize: "onEventPartialResize", onEventResizeEnd: "onEventResizeEnd", onEventResizeStart: "onEventResizeStart", onEventSelectionChange: "onEventSelectionChange", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onNavigate: "onNavigate", onPaint: "onPaint", onPaste: "onPaste", onPasteNotAllowed: "onPasteNotAllowed", onPdfExport: "onPdfExport", onPointerOver: "onPointerOver", onPresetChange: "onPresetChange", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onReleaseEvent: "onReleaseEvent", onRenderEvent: "onRenderEvent", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResourceHeaderClick: "onResourceHeaderClick", onResourceHeaderContextmenu: "onResourceHeaderContextmenu", onResourceHeaderDblclick: "onResourceHeaderDblclick", onResourceMenuBeforeShow: "onResourceMenuBeforeShow", onResourceMenuItem: "onResourceMenuItem", onResourceMenuShow: "onResourceMenuShow", onResourceMenuToggleItem: "onResourceMenuToggleItem", onResourceTimeRangeClick: "onResourceTimeRangeClick", onResourceTimeRangeContextMenu: "onResourceTimeRangeContextMenu", onResourceTimeRangeDblClick: "onResourceTimeRangeDblClick", onResourceTimeRangeMouseDown: "onResourceTimeRangeMouseDown", onResourceTimeRangeMouseOut: "onResourceTimeRangeMouseOut", onResourceTimeRangeMouseOver: "onResourceTimeRangeMouseOver", onResourceTimeRangeMouseUp: "onResourceTimeRangeMouseUp", onResponsive: "onResponsive", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScheduleClick: "onScheduleClick", onScheduleContextMenu: "onScheduleContextMenu", onScheduleDblClick: "onScheduleDblClick", onScheduleMenuBeforeShow: "onScheduleMenuBeforeShow", onScheduleMenuItem: "onScheduleMenuItem", onScheduleMenuShow: "onScheduleMenuShow", onScheduleMouseDown: "onScheduleMouseDown", onScheduleMouseEnter: "onScheduleMouseEnter", onScheduleMouseLeave: "onScheduleMouseLeave", onScheduleMouseMove: "onScheduleMouseMove", onScheduleMouseUp: "onScheduleMouseUp", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onTimeAxisChange: "onTimeAxisChange", onTimeAxisHeaderClick: "onTimeAxisHeaderClick", onTimeAxisHeaderContextMenu: "onTimeAxisHeaderContextMenu", onTimeAxisHeaderDblClick: "onTimeAxisHeaderDblClick", onTimeAxisHeaderMenuBeforeShow: "onTimeAxisHeaderMenuBeforeShow", onTimeAxisHeaderMenuItem: "onTimeAxisHeaderMenuItem", onTimeAxisHeaderMenuShow: "onTimeAxisHeaderMenuShow", onTimelineContextChange: "onTimelineContextChange", onTimelineViewportResize: "onTimelineViewportResize", onTimeRangeHeaderClick: "onTimeRangeHeaderClick", onTimeRangeHeaderContextMenu: "onTimeRangeHeaderContextMenu", onTimeRangeHeaderDblClick: "onTimeRangeHeaderDblClick", onTimeSelectionChange: "onTimeSelectionChange", onTimeSelectionElementClick: "onTimeSelectionElementClick", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onUnsplit: "onUnsplit", onVisibleDateRangeChange: "onVisibleDateRangeChange" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-scheduler',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], allowCreate: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoAdjustTimeAxis: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], bufferCoef: [{
                type: Input
            }], bufferThreshold: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], createEventOnDblClick: [{
                type: Input
            }], creationTooltip: [{
                type: Input
            }], crudManagerClass: [{
                type: Input
            }], date: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], defaultResourceImageName: [{
                type: Input
            }], descriptionRenderer: [{
                type: Input
            }], deselectAllOnScheduleClick: [{
                type: Input
            }], deselectOnClick: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], destroyStores: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], dock: [{
                type: Input
            }], durationDisplayPrecision: [{
                type: Input
            }], enableDeleteKey: [{
                type: Input
            }], enableRecurringEvents: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], endParamName: [{
                type: Input
            }], eventBarTextField: [{
                type: Input
            }], eventBodyTemplate: [{
                type: Input
            }], eventRenderer: [{
                type: Input
            }], eventRendererThisObj: [{
                type: Input
            }], eventSelectionDisabled: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getDateConstraints: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], highlightPredecessors: [{
                type: Input
            }], highlightSuccessors: [{
                type: Input
            }], horizontalEventSorterFn: [{
                type: Input
            }], ignoreDomEventsWhileScrolling: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], infiniteScroll: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], maintainSelectionOnDatasetChange: [{
                type: Input
            }], managedEventSizing: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], maxTimeAxisUnit: [{
                type: Input
            }], modal: [{
                type: Input
            }], mode: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], multiEventSelect: [{
                type: Input
            }], narrowEventWidth: [{
                type: Input
            }], owner: [{
                type: Input
            }], partner: [{
                type: Input
            }], passStartEndParameters: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preCalculateHeightLimit: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], range: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], resourceColumns: [{
                type: Input
            }], resourceImageExtension: [{
                type: Input
            }], resourceImagePath: [{
                type: Input
            }], resourceTimeRangeRenderer: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], showCreationTooltip: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showEventColorPickers: [{
                type: Input
            }], showLagInTooltip: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], showRecurringUI: [{
                type: Input
            }], showTooltip: [{
                type: Input
            }], snapRelativeToEventStartDate: [{
                type: Input
            }], startParamName: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], stepUnit: [{
                type: Input
            }], stickyHeaders: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], suppressFit: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], terminalCls: [{
                type: Input
            }], terminalSides: [{
                type: Input
            }], timeAxis: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], triggerSelectionChangeOnRemove: [{
                type: Input
            }], ui: [{
                type: Input
            }], verticalTimeAxisColumn: [{
                type: Input
            }], visibleZoomFactor: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], zoomKeepsOriginalTimespan: [{
                type: Input
            }], zoomOnMouseWheel: [{
                type: Input
            }], zoomOnTimeAxisDoubleClick: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], allowDropOnEventBar: [{
                type: Input
            }], allowOverlap: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], assignments: [{
                type: Input
            }], assignmentStore: [{
                type: Input
            }], barMargin: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], creationTooltipTemplate: [{
                type: Input
            }], crudManager: [{
                type: Input
            }], data: [{
                type: Input
            }], dataset: [{
                type: Input
            }], dateFormat: [{
                type: Input
            }], dateSeparator: [{
                type: Input
            }], dependencies: [{
                type: Input
            }], dependencyStore: [{
                type: Input
            }], descriptionFormat: [{
                type: Input
            }], disabled: [{
                type: Input
            }], displayDateFormat: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableEventAnimations: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], endDate: [{
                type: Input
            }], eventColor: [{
                type: Input
            }], eventLayout: [{
                type: Input
            }], events: [{
                type: Input
            }], eventStore: [{
                type: Input
            }], eventStyle: [{
                type: Input
            }], extraData: [{
                type: Input
            }], fillTicks: [{
                type: Input
            }], flex: [{
                type: Input
            }], forceFit: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hideRowHover: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], isEventSelectable: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], maxZoomLevel: [{
                type: Input
            }], milestoneAlign: [{
                type: Input
            }], milestoneCharWidth: [{
                type: Input
            }], milestoneLayoutMode: [{
                type: Input
            }], milestoneTextPosition: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], minZoomLevel: [{
                type: Input
            }], overlappingEventSorter: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], presets: [{
                type: Input
            }], project: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], resourceMargin: [{
                type: Input
            }], resources: [{
                type: Input
            }], resourceStore: [{
                type: Input
            }], resourceTimeRanges: [{
                type: Input
            }], resourceTimeRangeStore: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], selectResourceOnEventNavigate: [{
                type: Input
            }], selectResourceOnScheduleClick: [{
                type: Input
            }], snap: [{
                type: Input
            }], startDate: [{
                type: Input
            }], store: [{
                type: Input
            }], terminalHideDelay: [{
                type: Input
            }], terminalOffset: [{
                type: Input
            }], terminalShowDelay: [{
                type: Input
            }], terminalSize: [{
                type: Input
            }], tickSize: [{
                type: Input
            }], timeRanges: [{
                type: Input
            }], timeRangeStore: [{
                type: Input
            }], timeZone: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltipTemplate: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], updateTimelineContextOnScroll: [{
                type: Input
            }], useInitialAnimation: [{
                type: Input
            }], viewPreset: [{
                type: Input
            }], visibleDate: [{
                type: Input
            }], width: [{
                type: Input
            }], workingTime: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], isEngineReady: [{
                type: Input
            }], scrollLeft: [{
                type: Input
            }], scrollTop: [{
                type: Input
            }], scrollX: [{
                type: Input
            }], selectedAssignments: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedEvents: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], timeResolution: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], zoomLevel: [{
                type: Input
            }], onAfterDependencyCreateDrop: [{
                type: Output
            }], onAfterDependencySave: [{
                type: Output
            }], onAfterDragCreate: [{
                type: Output
            }], onAfterEventDrop: [{
                type: Output
            }], onAfterEventEdit: [{
                type: Output
            }], onAfterEventSave: [{
                type: Output
            }], onAssignmentSelectionChange: [{
                type: Output
            }], onBeforeAssignmentDelete: [{
                type: Output
            }], onBeforeAssignmentSelectionChange: [{
                type: Output
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDependencyAdd: [{
                type: Output
            }], onBeforeDependencyCreateDrag: [{
                type: Output
            }], onBeforeDependencyCreateFinalize: [{
                type: Output
            }], onBeforeDependencyDelete: [{
                type: Output
            }], onBeforeDependencyEdit: [{
                type: Output
            }], onBeforeDependencyEditShow: [{
                type: Output
            }], onBeforeDependencySave: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeDragCreate: [{
                type: Output
            }], onBeforeDragCreateFinalize: [{
                type: Output
            }], onBeforeEventAdd: [{
                type: Output
            }], onBeforeEventDelete: [{
                type: Output
            }], onBeforeEventDrag: [{
                type: Output
            }], onBeforeEventDragSelect: [{
                type: Output
            }], onBeforeEventDropFinalize: [{
                type: Output
            }], onBeforeEventEdit: [{
                type: Output
            }], onBeforeEventEditShow: [{
                type: Output
            }], onBeforeEventResize: [{
                type: Output
            }], onBeforeEventResizeFinalize: [{
                type: Output
            }], onBeforeEventSave: [{
                type: Output
            }], onBeforeEventSelectionChange: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforePan: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforePresetChange: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeShowTerminals: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDateRangeChange: [{
                type: Output
            }], onDependenciesDrawn: [{
                type: Output
            }], onDependencyClick: [{
                type: Output
            }], onDependencyContextMenu: [{
                type: Output
            }], onDependencyCreateDragStart: [{
                type: Output
            }], onDependencyCreateDrop: [{
                type: Output
            }], onDependencyDblClick: [{
                type: Output
            }], onDependencyMouseOut: [{
                type: Output
            }], onDependencyMouseOver: [{
                type: Output
            }], onDependencyValidationComplete: [{
                type: Output
            }], onDependencyValidationStart: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragCreateEnd: [{
                type: Output
            }], onDragCreateStart: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onEventAutoCreated: [{
                type: Output
            }], onEventClick: [{
                type: Output
            }], onEventContextMenu: [{
                type: Output
            }], onEventDblClick: [{
                type: Output
            }], onEventDrag: [{
                type: Output
            }], onEventDragAbort: [{
                type: Output
            }], onEventDragModeChange: [{
                type: Output
            }], onEventDragReset: [{
                type: Output
            }], onEventDragStart: [{
                type: Output
            }], onEventDrop: [{
                type: Output
            }], onEventEditBeforeSetRecord: [{
                type: Output
            }], onEventKeyDown: [{
                type: Output
            }], onEventKeyUp: [{
                type: Output
            }], onEventMenuBeforeShow: [{
                type: Output
            }], onEventMenuItem: [{
                type: Output
            }], onEventMenuShow: [{
                type: Output
            }], onEventMouseDown: [{
                type: Output
            }], onEventMouseEnter: [{
                type: Output
            }], onEventMouseLeave: [{
                type: Output
            }], onEventMouseOut: [{
                type: Output
            }], onEventMouseOver: [{
                type: Output
            }], onEventMouseUp: [{
                type: Output
            }], onEventPartialResize: [{
                type: Output
            }], onEventResizeEnd: [{
                type: Output
            }], onEventResizeStart: [{
                type: Output
            }], onEventSelectionChange: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onNavigate: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPasteNotAllowed: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onPresetChange: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onReleaseEvent: [{
                type: Output
            }], onRenderEvent: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResourceHeaderClick: [{
                type: Output
            }], onResourceHeaderContextmenu: [{
                type: Output
            }], onResourceHeaderDblclick: [{
                type: Output
            }], onResourceMenuBeforeShow: [{
                type: Output
            }], onResourceMenuItem: [{
                type: Output
            }], onResourceMenuShow: [{
                type: Output
            }], onResourceMenuToggleItem: [{
                type: Output
            }], onResourceTimeRangeClick: [{
                type: Output
            }], onResourceTimeRangeContextMenu: [{
                type: Output
            }], onResourceTimeRangeDblClick: [{
                type: Output
            }], onResourceTimeRangeMouseDown: [{
                type: Output
            }], onResourceTimeRangeMouseOut: [{
                type: Output
            }], onResourceTimeRangeMouseOver: [{
                type: Output
            }], onResourceTimeRangeMouseUp: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScheduleClick: [{
                type: Output
            }], onScheduleContextMenu: [{
                type: Output
            }], onScheduleDblClick: [{
                type: Output
            }], onScheduleMenuBeforeShow: [{
                type: Output
            }], onScheduleMenuItem: [{
                type: Output
            }], onScheduleMenuShow: [{
                type: Output
            }], onScheduleMouseDown: [{
                type: Output
            }], onScheduleMouseEnter: [{
                type: Output
            }], onScheduleMouseLeave: [{
                type: Output
            }], onScheduleMouseMove: [{
                type: Output
            }], onScheduleMouseUp: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onTimeAxisChange: [{
                type: Output
            }], onTimeAxisHeaderClick: [{
                type: Output
            }], onTimeAxisHeaderContextMenu: [{
                type: Output
            }], onTimeAxisHeaderDblClick: [{
                type: Output
            }], onTimeAxisHeaderMenuBeforeShow: [{
                type: Output
            }], onTimeAxisHeaderMenuItem: [{
                type: Output
            }], onTimeAxisHeaderMenuShow: [{
                type: Output
            }], onTimelineContextChange: [{
                type: Output
            }], onTimelineViewportResize: [{
                type: Output
            }], onTimeRangeHeaderClick: [{
                type: Output
            }], onTimeRangeHeaderContextMenu: [{
                type: Output
            }], onTimeRangeHeaderDblClick: [{
                type: Output
            }], onTimeSelectionChange: [{
                type: Output
            }], onTimeSelectionElementClick: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], onVisibleDateRangeChange: [{
                type: Output
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnLinesFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], dependenciesFeature: [{
                type: Input
            }], dependencyEditFeature: [{
                type: Input
            }], eventCopyPasteFeature: [{
                type: Input
            }], eventDragFeature: [{
                type: Input
            }], eventDragCreateFeature: [{
                type: Input
            }], eventDragSelectFeature: [{
                type: Input
            }], eventEditFeature: [{
                type: Input
            }], eventFilterFeature: [{
                type: Input
            }], eventMenuFeature: [{
                type: Input
            }], eventNonWorkingTimeFeature: [{
                type: Input
            }], eventResizeFeature: [{
                type: Input
            }], eventTooltipFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], headerZoomFeature: [{
                type: Input
            }], labelsFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], nonWorkingTimeFeature: [{
                type: Input
            }], panFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], resourceMenuFeature: [{
                type: Input
            }], resourceTimeRangesFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], scheduleContextFeature: [{
                type: Input
            }], scheduleMenuFeature: [{
                type: Input
            }], scheduleTooltipFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], simpleEventEditFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stickyEventsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], timeAxisHeaderMenuFeature: [{
                type: Input
            }], timeRangesFeature: [{
                type: Input
            }], timeSelectionFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum SchedulerBase
 */
class BryntumSchedulerBaseComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAfterDependencyCreateDrop = new EventEmitter();
        this.onAfterDependencySave = new EventEmitter();
        this.onAfterDragCreate = new EventEmitter();
        this.onAfterEventDrop = new EventEmitter();
        this.onAfterEventEdit = new EventEmitter();
        this.onAfterEventSave = new EventEmitter();
        this.onAssignmentSelectionChange = new EventEmitter();
        this.onBeforeAssignmentDelete = new EventEmitter();
        this.onBeforeAssignmentSelectionChange = new EventEmitter();
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDependencyAdd = new EventEmitter();
        this.onBeforeDependencyCreateDrag = new EventEmitter();
        this.onBeforeDependencyCreateFinalize = new EventEmitter();
        this.onBeforeDependencyDelete = new EventEmitter();
        this.onBeforeDependencyEdit = new EventEmitter();
        this.onBeforeDependencyEditShow = new EventEmitter();
        this.onBeforeDependencySave = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeDragCreate = new EventEmitter();
        this.onBeforeDragCreateFinalize = new EventEmitter();
        this.onBeforeEventAdd = new EventEmitter();
        this.onBeforeEventDelete = new EventEmitter();
        this.onBeforeEventDrag = new EventEmitter();
        this.onBeforeEventDragSelect = new EventEmitter();
        this.onBeforeEventDropFinalize = new EventEmitter();
        this.onBeforeEventEdit = new EventEmitter();
        this.onBeforeEventEditShow = new EventEmitter();
        this.onBeforeEventResize = new EventEmitter();
        this.onBeforeEventResizeFinalize = new EventEmitter();
        this.onBeforeEventSave = new EventEmitter();
        this.onBeforeEventSelectionChange = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforePan = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforePresetChange = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeShowTerminals = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDateRangeChange = new EventEmitter();
        this.onDependenciesDrawn = new EventEmitter();
        this.onDependencyClick = new EventEmitter();
        this.onDependencyContextMenu = new EventEmitter();
        this.onDependencyCreateDragStart = new EventEmitter();
        this.onDependencyCreateDrop = new EventEmitter();
        this.onDependencyDblClick = new EventEmitter();
        this.onDependencyMouseOut = new EventEmitter();
        this.onDependencyMouseOver = new EventEmitter();
        this.onDependencyValidationComplete = new EventEmitter();
        this.onDependencyValidationStart = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragCreateEnd = new EventEmitter();
        this.onDragCreateStart = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onEventAutoCreated = new EventEmitter();
        this.onEventClick = new EventEmitter();
        this.onEventContextMenu = new EventEmitter();
        this.onEventDblClick = new EventEmitter();
        this.onEventDrag = new EventEmitter();
        this.onEventDragAbort = new EventEmitter();
        this.onEventDragModeChange = new EventEmitter();
        this.onEventDragReset = new EventEmitter();
        this.onEventDragStart = new EventEmitter();
        this.onEventDrop = new EventEmitter();
        this.onEventEditBeforeSetRecord = new EventEmitter();
        this.onEventKeyDown = new EventEmitter();
        this.onEventKeyUp = new EventEmitter();
        this.onEventMenuBeforeShow = new EventEmitter();
        this.onEventMenuItem = new EventEmitter();
        this.onEventMenuShow = new EventEmitter();
        this.onEventMouseDown = new EventEmitter();
        this.onEventMouseEnter = new EventEmitter();
        this.onEventMouseLeave = new EventEmitter();
        this.onEventMouseOut = new EventEmitter();
        this.onEventMouseOver = new EventEmitter();
        this.onEventMouseUp = new EventEmitter();
        this.onEventPartialResize = new EventEmitter();
        this.onEventResizeEnd = new EventEmitter();
        this.onEventResizeStart = new EventEmitter();
        this.onEventSelectionChange = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onNavigate = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPasteNotAllowed = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onPresetChange = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onReleaseEvent = new EventEmitter();
        this.onRenderEvent = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResourceHeaderClick = new EventEmitter();
        this.onResourceHeaderContextmenu = new EventEmitter();
        this.onResourceHeaderDblclick = new EventEmitter();
        this.onResourceMenuBeforeShow = new EventEmitter();
        this.onResourceMenuItem = new EventEmitter();
        this.onResourceMenuShow = new EventEmitter();
        this.onResourceMenuToggleItem = new EventEmitter();
        this.onResourceTimeRangeClick = new EventEmitter();
        this.onResourceTimeRangeContextMenu = new EventEmitter();
        this.onResourceTimeRangeDblClick = new EventEmitter();
        this.onResourceTimeRangeMouseDown = new EventEmitter();
        this.onResourceTimeRangeMouseOut = new EventEmitter();
        this.onResourceTimeRangeMouseOver = new EventEmitter();
        this.onResourceTimeRangeMouseUp = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScheduleClick = new EventEmitter();
        this.onScheduleContextMenu = new EventEmitter();
        this.onScheduleDblClick = new EventEmitter();
        this.onScheduleMenuBeforeShow = new EventEmitter();
        this.onScheduleMenuItem = new EventEmitter();
        this.onScheduleMenuShow = new EventEmitter();
        this.onScheduleMouseDown = new EventEmitter();
        this.onScheduleMouseEnter = new EventEmitter();
        this.onScheduleMouseLeave = new EventEmitter();
        this.onScheduleMouseMove = new EventEmitter();
        this.onScheduleMouseUp = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onTimeAxisChange = new EventEmitter();
        this.onTimeAxisHeaderClick = new EventEmitter();
        this.onTimeAxisHeaderContextMenu = new EventEmitter();
        this.onTimeAxisHeaderDblClick = new EventEmitter();
        this.onTimeAxisHeaderMenuBeforeShow = new EventEmitter();
        this.onTimeAxisHeaderMenuItem = new EventEmitter();
        this.onTimeAxisHeaderMenuShow = new EventEmitter();
        this.onTimelineContextChange = new EventEmitter();
        this.onTimelineViewportResize = new EventEmitter();
        this.onTimeRangeHeaderClick = new EventEmitter();
        this.onTimeRangeHeaderContextMenu = new EventEmitter();
        this.onTimeRangeHeaderDblClick = new EventEmitter();
        this.onTimeSelectionChange = new EventEmitter();
        this.onTimeSelectionElementClick = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.onVisibleDateRangeChange = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSchedulerBaseComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
        // Backwards compatibility for gridInstance, schedulerInstance etc.
        // @ts-ignore
        me[StringHelper.uncapitalize(instanceName) + 'Instance'] = me.instance;
        // 
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSchedulerBaseComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSchedulerBaseComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSchedulerBaseComponent.instanceClass = SchedulerBase;
BryntumSchedulerBaseComponent.instanceName = 'SchedulerBase';
BryntumSchedulerBaseComponent.bryntumEvents = [
    'onAfterDependencyCreateDrop',
    'onAfterDependencySave',
    'onAfterDragCreate',
    'onAfterEventDrop',
    'onAfterEventEdit',
    'onAfterEventSave',
    'onAssignmentSelectionChange',
    'onBeforeAssignmentDelete',
    'onBeforeAssignmentSelectionChange',
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDependencyAdd',
    'onBeforeDependencyCreateDrag',
    'onBeforeDependencyCreateFinalize',
    'onBeforeDependencyDelete',
    'onBeforeDependencyEdit',
    'onBeforeDependencyEditShow',
    'onBeforeDependencySave',
    'onBeforeDestroy',
    'onBeforeDragCreate',
    'onBeforeDragCreateFinalize',
    'onBeforeEventAdd',
    'onBeforeEventDelete',
    'onBeforeEventDrag',
    'onBeforeEventDragSelect',
    'onBeforeEventDropFinalize',
    'onBeforeEventEdit',
    'onBeforeEventEditShow',
    'onBeforeEventResize',
    'onBeforeEventResizeFinalize',
    'onBeforeEventSave',
    'onBeforeEventSelectionChange',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforePan',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforePresetChange',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeShowTerminals',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDateRangeChange',
    'onDependenciesDrawn',
    'onDependencyClick',
    'onDependencyContextMenu',
    'onDependencyCreateDragStart',
    'onDependencyCreateDrop',
    'onDependencyDblClick',
    'onDependencyMouseOut',
    'onDependencyMouseOver',
    'onDependencyValidationComplete',
    'onDependencyValidationStart',
    'onDestroy',
    'onDragCreateEnd',
    'onDragCreateStart',
    'onDragSelecting',
    'onEventAutoCreated',
    'onEventClick',
    'onEventContextMenu',
    'onEventDblClick',
    'onEventDrag',
    'onEventDragAbort',
    'onEventDragModeChange',
    'onEventDragReset',
    'onEventDragStart',
    'onEventDrop',
    'onEventEditBeforeSetRecord',
    'onEventKeyDown',
    'onEventKeyUp',
    'onEventMenuBeforeShow',
    'onEventMenuItem',
    'onEventMenuShow',
    'onEventMouseDown',
    'onEventMouseEnter',
    'onEventMouseLeave',
    'onEventMouseOut',
    'onEventMouseOver',
    'onEventMouseUp',
    'onEventPartialResize',
    'onEventResizeEnd',
    'onEventResizeStart',
    'onEventSelectionChange',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onMouseOut',
    'onMouseOver',
    'onNavigate',
    'onPaint',
    'onPaste',
    'onPasteNotAllowed',
    'onPdfExport',
    'onPointerOver',
    'onPresetChange',
    'onRecompose',
    'onReleaseEvent',
    'onRenderEvent',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResourceHeaderClick',
    'onResourceHeaderContextmenu',
    'onResourceHeaderDblclick',
    'onResourceMenuBeforeShow',
    'onResourceMenuItem',
    'onResourceMenuShow',
    'onResourceMenuToggleItem',
    'onResourceTimeRangeClick',
    'onResourceTimeRangeContextMenu',
    'onResourceTimeRangeDblClick',
    'onResourceTimeRangeMouseDown',
    'onResourceTimeRangeMouseOut',
    'onResourceTimeRangeMouseOver',
    'onResourceTimeRangeMouseUp',
    'onResponsive',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScheduleClick',
    'onScheduleContextMenu',
    'onScheduleDblClick',
    'onScheduleMenuBeforeShow',
    'onScheduleMenuItem',
    'onScheduleMenuShow',
    'onScheduleMouseDown',
    'onScheduleMouseEnter',
    'onScheduleMouseLeave',
    'onScheduleMouseMove',
    'onScheduleMouseUp',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onTimeAxisChange',
    'onTimeAxisHeaderClick',
    'onTimeAxisHeaderContextMenu',
    'onTimeAxisHeaderDblClick',
    'onTimeAxisHeaderMenuBeforeShow',
    'onTimeAxisHeaderMenuItem',
    'onTimeAxisHeaderMenuShow',
    'onTimelineContextChange',
    'onTimelineViewportResize',
    'onTimeRangeHeaderClick',
    'onTimeRangeHeaderContextMenu',
    'onTimeRangeHeaderDblClick',
    'onTimeSelectionChange',
    'onTimeSelectionElementClick',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onUnsplit',
    'onVisibleDateRangeChange'
];
BryntumSchedulerBaseComponent.bryntumFeatureNames = [
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnLinesFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'dependenciesFeature',
    'dependencyEditFeature',
    'eventCopyPasteFeature',
    'eventDragFeature',
    'eventDragCreateFeature',
    'eventDragSelectFeature',
    'eventEditFeature',
    'eventFilterFeature',
    'eventMenuFeature',
    'eventNonWorkingTimeFeature',
    'eventResizeFeature',
    'eventTooltipFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'headerZoomFeature',
    'labelsFeature',
    'mergeCellsFeature',
    'nonWorkingTimeFeature',
    'panFeature',
    'pdfExportFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'resourceMenuFeature',
    'resourceTimeRangesFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'scheduleContextFeature',
    'scheduleMenuFeature',
    'scheduleTooltipFeature',
    'searchFeature',
    'simpleEventEditFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stickyEventsFeature',
    'stripeFeature',
    'summaryFeature',
    'timeAxisHeaderMenuFeature',
    'timeRangesFeature',
    'timeSelectionFeature',
    'treeFeature',
    'treeGroupFeature'
];
BryntumSchedulerBaseComponent.bryntumConfigs = BryntumSchedulerBaseComponent.bryntumFeatureNames.concat([
    'adopt',
    'alignSelf',
    'allowCreate',
    'allowDropOnEventBar',
    'allowOver',
    'allowOverlap',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'assignments',
    'assignmentStore',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'barMargin',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'createEventOnDblClick',
    'creationTooltip',
    'creationTooltipTemplate',
    'crudManager',
    'crudManagerClass',
    'data',
    'dataset',
    'date',
    'dateFormat',
    'dateSeparator',
    'defaultRegion',
    'defaultResourceImageName',
    'dependencies',
    'dependencyStore',
    'descriptionFormat',
    'descriptionRenderer',
    'deselectAllOnScheduleClick',
    'deselectOnClick',
    'destroyStore',
    'destroyStores',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'displayDateFormat',
    'dock',
    'durationDisplayPrecision',
    'emptyText',
    'enableDeleteKey',
    'enableEventAnimations',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'endDate',
    'endParamName',
    'eventBarTextField',
    'eventBodyTemplate',
    'eventColor',
    'eventLayout',
    'eventRenderer',
    'eventRendererThisObj',
    'events',
    'eventSelectionDisabled',
    'eventStore',
    'eventStyle',
    'extraData',
    'features',
    'fillLastColumn',
    'fillTicks',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forceFit',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getDateConstraints',
    'getHtml',
    'getRowHeight',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hideRowHover',
    'highlightPredecessors',
    'highlightSuccessors',
    'horizontalEventSorterFn',
    'hoverDelay',
    'id',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'insertBefore',
    'insertFirst',
    'isEventSelectable',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'maintainSelectionOnDatasetChange',
    'managedEventSizing',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxTimeAxisUnit',
    'maxWidth',
    'maxZoomLevel',
    'milestoneAlign',
    'milestoneCharWidth',
    'milestoneLayoutMode',
    'milestoneTextPosition',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'modal',
    'mode',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'multiEventSelect',
    'narrowEventWidth',
    'overlappingEventSorter',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preCalculateHeightLimit',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'presets',
    'preventTooltipOnTouch',
    'project',
    'range',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceColumns',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceMargin',
    'resources',
    'resourceStore',
    'resourceTimeRangeRenderer',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'selectResourceOnEventNavigate',
    'selectResourceOnScheduleClick',
    'showCreationTooltip',
    'showDirty',
    'showEventColorPickers',
    'showLagInTooltip',
    'showOnClick',
    'showOnHover',
    'showRecurringUI',
    'showTooltip',
    'snap',
    'snapRelativeToEventStartDate',
    'startDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stepUnit',
    'stickyHeaders',
    'store',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'terminalCls',
    'terminalHideDelay',
    'terminalOffset',
    'terminalShowDelay',
    'terminalSides',
    'terminalSize',
    'tickSize',
    'timeAxis',
    'timeRanges',
    'timeRangeStore',
    'timeZone',
    'title',
    'tools',
    'tooltipTemplate',
    'trackMouse',
    'transitionDuration',
    'triggerSelectionChangeOnRemove',
    'ui',
    'updateTimelineContextOnScroll',
    'useInitialAnimation',
    'verticalTimeAxisColumn',
    'viewPreset',
    'visibleDate',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'width',
    'workingTime',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
]);
BryntumSchedulerBaseComponent.bryntumConfigsOnly = [
    'adopt',
    'allowCreate',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'ariaDescription',
    'ariaLabel',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'createEventOnDblClick',
    'creationTooltip',
    'crudManagerClass',
    'date',
    'defaultRegion',
    'defaultResourceImageName',
    'descriptionRenderer',
    'deselectAllOnScheduleClick',
    'deselectOnClick',
    'destroyStore',
    'destroyStores',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'durationDisplayPrecision',
    'enableDeleteKey',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'endParamName',
    'eventBarTextField',
    'eventBodyTemplate',
    'eventRenderer',
    'eventRendererThisObj',
    'eventSelectionDisabled',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getDateConstraints',
    'getHtml',
    'getRowHeight',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'highlightPredecessors',
    'highlightSuccessors',
    'horizontalEventSorterFn',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'maintainSelectionOnDatasetChange',
    'managedEventSizing',
    'maskDefaults',
    'masked',
    'maximizable',
    'maxTimeAxisUnit',
    'modal',
    'mode',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'multiEventSelect',
    'narrowEventWidth',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preCalculateHeightLimit',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'range',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceColumns',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceTimeRangeRenderer',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'scrollerClass',
    'scrollManager',
    'showCreationTooltip',
    'showDirty',
    'showEventColorPickers',
    'showLagInTooltip',
    'showOnClick',
    'showOnHover',
    'showRecurringUI',
    'showTooltip',
    'snapRelativeToEventStartDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stepUnit',
    'stickyHeaders',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'terminalCls',
    'terminalSides',
    'timeAxis',
    'trackMouse',
    'triggerSelectionChangeOnRemove',
    'ui',
    'verticalTimeAxisColumn',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
];
BryntumSchedulerBaseComponent.bryntumProps = BryntumSchedulerBaseComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'allowDropOnEventBar',
    'allowOverlap',
    'appendTo',
    'ariaLive',
    'assignments',
    'assignmentStore',
    'barMargin',
    'cls',
    'columnLines',
    'columns',
    'creationTooltipTemplate',
    'crudManager',
    'data',
    'dataset',
    'dateFormat',
    'dateSeparator',
    'dependencies',
    'dependencyStore',
    'descriptionFormat',
    'disabled',
    'displayDateFormat',
    'emptyText',
    'enableEventAnimations',
    'enableUndoRedoKeys',
    'endDate',
    'eventColor',
    'eventLayout',
    'events',
    'eventStore',
    'eventStyle',
    'extraData',
    'fillTicks',
    'flex',
    'forceFit',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hideRowHover',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'isEngineReady',
    'isEventSelectable',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'maxZoomLevel',
    'milestoneAlign',
    'milestoneCharWidth',
    'milestoneLayoutMode',
    'milestoneTextPosition',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'overlappingEventSorter',
    'preserveScroll',
    'presets',
    'project',
    'readOnly',
    'resourceMargin',
    'resources',
    'resourceStore',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollLeft',
    'scrollTop',
    'scrollX',
    'selectedAssignments',
    'selectedCell',
    'selectedCells',
    'selectedEvents',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'selectResourceOnEventNavigate',
    'selectResourceOnScheduleClick',
    'snap',
    'startDate',
    'state',
    'store',
    'terminalHideDelay',
    'terminalOffset',
    'terminalShowDelay',
    'terminalSize',
    'tickSize',
    'timeRanges',
    'timeRangeStore',
    'timeResolution',
    'timeZone',
    'title',
    'tools',
    'tooltip',
    'tooltipTemplate',
    'transitionDuration',
    'updateTimelineContextOnScroll',
    'useInitialAnimation',
    'viewPreset',
    'visibleDate',
    'width',
    'workingTime',
    'zoomLevel'
]);
BryntumSchedulerBaseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerBaseComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSchedulerBaseComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSchedulerBaseComponent, selector: "bryntum-scheduler-base", inputs: { adopt: "adopt", allowCreate: "allowCreate", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", animateTreeNodeToggle: "animateTreeNodeToggle", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoAdjustTimeAxis: "autoAdjustTimeAxis", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", bufferCoef: "bufferCoef", bufferThreshold: "bufferThreshold", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", createEventOnDblClick: "createEventOnDblClick", creationTooltip: "creationTooltip", crudManagerClass: "crudManagerClass", date: "date", defaultRegion: "defaultRegion", defaultResourceImageName: "defaultResourceImageName", descriptionRenderer: "descriptionRenderer", deselectAllOnScheduleClick: "deselectAllOnScheduleClick", deselectOnClick: "deselectOnClick", destroyStore: "destroyStore", destroyStores: "destroyStores", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", dock: "dock", durationDisplayPrecision: "durationDisplayPrecision", enableDeleteKey: "enableDeleteKey", enableRecurringEvents: "enableRecurringEvents", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", endParamName: "endParamName", eventBarTextField: "eventBarTextField", eventBodyTemplate: "eventBodyTemplate", eventRenderer: "eventRenderer", eventRendererThisObj: "eventRendererThisObj", eventSelectionDisabled: "eventSelectionDisabled", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getDateConstraints: "getDateConstraints", getHtml: "getHtml", getRowHeight: "getRowHeight", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", highlightPredecessors: "highlightPredecessors", highlightSuccessors: "highlightSuccessors", horizontalEventSorterFn: "horizontalEventSorterFn", ignoreDomEventsWhileScrolling: "ignoreDomEventsWhileScrolling", ignoreParentReadOnly: "ignoreParentReadOnly", infiniteScroll: "infiniteScroll", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", maintainSelectionOnDatasetChange: "maintainSelectionOnDatasetChange", managedEventSizing: "managedEventSizing", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", maxTimeAxisUnit: "maxTimeAxisUnit", modal: "modal", mode: "mode", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", multiEventSelect: "multiEventSelect", narrowEventWidth: "narrowEventWidth", owner: "owner", partner: "partner", passStartEndParameters: "passStartEndParameters", plugins: "plugins", preCalculateHeightLimit: "preCalculateHeightLimit", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", range: "range", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", resourceColumns: "resourceColumns", resourceImageExtension: "resourceImageExtension", resourceImagePath: "resourceImagePath", resourceTimeRangeRenderer: "resourceTimeRangeRenderer", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", scrollerClass: "scrollerClass", scrollManager: "scrollManager", showCreationTooltip: "showCreationTooltip", showDirty: "showDirty", showEventColorPickers: "showEventColorPickers", showLagInTooltip: "showLagInTooltip", showOnClick: "showOnClick", showOnHover: "showOnHover", showRecurringUI: "showRecurringUI", showTooltip: "showTooltip", snapRelativeToEventStartDate: "snapRelativeToEventStartDate", startParamName: "startParamName", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", stepUnit: "stepUnit", stickyHeaders: "stickyHeaders", strips: "strips", subGridConfigs: "subGridConfigs", suppressFit: "suppressFit", syncMask: "syncMask", tab: "tab", tbar: "tbar", terminalCls: "terminalCls", terminalSides: "terminalSides", timeAxis: "timeAxis", trackMouse: "trackMouse", triggerSelectionChangeOnRemove: "triggerSelectionChangeOnRemove", ui: "ui", verticalTimeAxisColumn: "verticalTimeAxisColumn", visibleZoomFactor: "visibleZoomFactor", weekStartDay: "weekStartDay", weight: "weight", zoomKeepsOriginalTimespan: "zoomKeepsOriginalTimespan", zoomOnMouseWheel: "zoomOnMouseWheel", zoomOnTimeAxisDoubleClick: "zoomOnTimeAxisDoubleClick", alignSelf: "alignSelf", allowDropOnEventBar: "allowDropOnEventBar", allowOverlap: "allowOverlap", appendTo: "appendTo", ariaLive: "ariaLive", assignments: "assignments", assignmentStore: "assignmentStore", barMargin: "barMargin", cls: "cls", columnLines: "columnLines", columns: "columns", creationTooltipTemplate: "creationTooltipTemplate", crudManager: "crudManager", data: "data", dataset: "dataset", dateFormat: "dateFormat", dateSeparator: "dateSeparator", dependencies: "dependencies", dependencyStore: "dependencyStore", descriptionFormat: "descriptionFormat", disabled: "disabled", displayDateFormat: "displayDateFormat", emptyText: "emptyText", enableEventAnimations: "enableEventAnimations", enableUndoRedoKeys: "enableUndoRedoKeys", endDate: "endDate", eventColor: "eventColor", eventLayout: "eventLayout", events: "events", eventStore: "eventStore", eventStyle: "eventStyle", extraData: "extraData", fillTicks: "fillTicks", flex: "flex", forceFit: "forceFit", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hideRowHover: "hideRowHover", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", isEventSelectable: "isEventSelectable", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", maxZoomLevel: "maxZoomLevel", milestoneAlign: "milestoneAlign", milestoneCharWidth: "milestoneCharWidth", milestoneLayoutMode: "milestoneLayoutMode", milestoneTextPosition: "milestoneTextPosition", minHeight: "minHeight", minWidth: "minWidth", minZoomLevel: "minZoomLevel", overlappingEventSorter: "overlappingEventSorter", preserveScroll: "preserveScroll", presets: "presets", project: "project", readOnly: "readOnly", resourceMargin: "resourceMargin", resources: "resources", resourceStore: "resourceStore", resourceTimeRanges: "resourceTimeRanges", resourceTimeRangeStore: "resourceTimeRangeStore", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", selectResourceOnEventNavigate: "selectResourceOnEventNavigate", selectResourceOnScheduleClick: "selectResourceOnScheduleClick", snap: "snap", startDate: "startDate", store: "store", terminalHideDelay: "terminalHideDelay", terminalOffset: "terminalOffset", terminalShowDelay: "terminalShowDelay", terminalSize: "terminalSize", tickSize: "tickSize", timeRanges: "timeRanges", timeRangeStore: "timeRangeStore", timeZone: "timeZone", title: "title", tools: "tools", tooltipTemplate: "tooltipTemplate", transitionDuration: "transitionDuration", updateTimelineContextOnScroll: "updateTimelineContextOnScroll", useInitialAnimation: "useInitialAnimation", viewPreset: "viewPreset", visibleDate: "visibleDate", width: "width", workingTime: "workingTime", accessibility: "accessibility", isEngineReady: "isEngineReady", scrollLeft: "scrollLeft", scrollTop: "scrollTop", scrollX: "scrollX", selectedAssignments: "selectedAssignments", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedEvents: "selectedEvents", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", timeResolution: "timeResolution", tooltip: "tooltip", zoomLevel: "zoomLevel", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnLinesFeature: "columnLinesFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", dependenciesFeature: "dependenciesFeature", dependencyEditFeature: "dependencyEditFeature", eventCopyPasteFeature: "eventCopyPasteFeature", eventDragFeature: "eventDragFeature", eventDragCreateFeature: "eventDragCreateFeature", eventDragSelectFeature: "eventDragSelectFeature", eventEditFeature: "eventEditFeature", eventFilterFeature: "eventFilterFeature", eventMenuFeature: "eventMenuFeature", eventNonWorkingTimeFeature: "eventNonWorkingTimeFeature", eventResizeFeature: "eventResizeFeature", eventTooltipFeature: "eventTooltipFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", headerZoomFeature: "headerZoomFeature", labelsFeature: "labelsFeature", mergeCellsFeature: "mergeCellsFeature", nonWorkingTimeFeature: "nonWorkingTimeFeature", panFeature: "panFeature", pdfExportFeature: "pdfExportFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", resourceMenuFeature: "resourceMenuFeature", resourceTimeRangesFeature: "resourceTimeRangesFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", scheduleContextFeature: "scheduleContextFeature", scheduleMenuFeature: "scheduleMenuFeature", scheduleTooltipFeature: "scheduleTooltipFeature", searchFeature: "searchFeature", simpleEventEditFeature: "simpleEventEditFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stickyEventsFeature: "stickyEventsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", timeAxisHeaderMenuFeature: "timeAxisHeaderMenuFeature", timeRangesFeature: "timeRangesFeature", timeSelectionFeature: "timeSelectionFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature" }, outputs: { onAfterDependencyCreateDrop: "onAfterDependencyCreateDrop", onAfterDependencySave: "onAfterDependencySave", onAfterDragCreate: "onAfterDragCreate", onAfterEventDrop: "onAfterEventDrop", onAfterEventEdit: "onAfterEventEdit", onAfterEventSave: "onAfterEventSave", onAssignmentSelectionChange: "onAssignmentSelectionChange", onBeforeAssignmentDelete: "onBeforeAssignmentDelete", onBeforeAssignmentSelectionChange: "onBeforeAssignmentSelectionChange", onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDependencyAdd: "onBeforeDependencyAdd", onBeforeDependencyCreateDrag: "onBeforeDependencyCreateDrag", onBeforeDependencyCreateFinalize: "onBeforeDependencyCreateFinalize", onBeforeDependencyDelete: "onBeforeDependencyDelete", onBeforeDependencyEdit: "onBeforeDependencyEdit", onBeforeDependencyEditShow: "onBeforeDependencyEditShow", onBeforeDependencySave: "onBeforeDependencySave", onBeforeDestroy: "onBeforeDestroy", onBeforeDragCreate: "onBeforeDragCreate", onBeforeDragCreateFinalize: "onBeforeDragCreateFinalize", onBeforeEventAdd: "onBeforeEventAdd", onBeforeEventDelete: "onBeforeEventDelete", onBeforeEventDrag: "onBeforeEventDrag", onBeforeEventDragSelect: "onBeforeEventDragSelect", onBeforeEventDropFinalize: "onBeforeEventDropFinalize", onBeforeEventEdit: "onBeforeEventEdit", onBeforeEventEditShow: "onBeforeEventEditShow", onBeforeEventResize: "onBeforeEventResize", onBeforeEventResizeFinalize: "onBeforeEventResizeFinalize", onBeforeEventSave: "onBeforeEventSave", onBeforeEventSelectionChange: "onBeforeEventSelectionChange", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforePan: "onBeforePan", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforePresetChange: "onBeforePresetChange", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeShowTerminals: "onBeforeShowTerminals", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDateRangeChange: "onDateRangeChange", onDependenciesDrawn: "onDependenciesDrawn", onDependencyClick: "onDependencyClick", onDependencyContextMenu: "onDependencyContextMenu", onDependencyCreateDragStart: "onDependencyCreateDragStart", onDependencyCreateDrop: "onDependencyCreateDrop", onDependencyDblClick: "onDependencyDblClick", onDependencyMouseOut: "onDependencyMouseOut", onDependencyMouseOver: "onDependencyMouseOver", onDependencyValidationComplete: "onDependencyValidationComplete", onDependencyValidationStart: "onDependencyValidationStart", onDestroy: "onDestroy", onDragCreateEnd: "onDragCreateEnd", onDragCreateStart: "onDragCreateStart", onDragSelecting: "onDragSelecting", onEventAutoCreated: "onEventAutoCreated", onEventClick: "onEventClick", onEventContextMenu: "onEventContextMenu", onEventDblClick: "onEventDblClick", onEventDrag: "onEventDrag", onEventDragAbort: "onEventDragAbort", onEventDragModeChange: "onEventDragModeChange", onEventDragReset: "onEventDragReset", onEventDragStart: "onEventDragStart", onEventDrop: "onEventDrop", onEventEditBeforeSetRecord: "onEventEditBeforeSetRecord", onEventKeyDown: "onEventKeyDown", onEventKeyUp: "onEventKeyUp", onEventMenuBeforeShow: "onEventMenuBeforeShow", onEventMenuItem: "onEventMenuItem", onEventMenuShow: "onEventMenuShow", onEventMouseDown: "onEventMouseDown", onEventMouseEnter: "onEventMouseEnter", onEventMouseLeave: "onEventMouseLeave", onEventMouseOut: "onEventMouseOut", onEventMouseOver: "onEventMouseOver", onEventMouseUp: "onEventMouseUp", onEventPartialResize: "onEventPartialResize", onEventResizeEnd: "onEventResizeEnd", onEventResizeStart: "onEventResizeStart", onEventSelectionChange: "onEventSelectionChange", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onNavigate: "onNavigate", onPaint: "onPaint", onPaste: "onPaste", onPasteNotAllowed: "onPasteNotAllowed", onPdfExport: "onPdfExport", onPointerOver: "onPointerOver", onPresetChange: "onPresetChange", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onReleaseEvent: "onReleaseEvent", onRenderEvent: "onRenderEvent", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResourceHeaderClick: "onResourceHeaderClick", onResourceHeaderContextmenu: "onResourceHeaderContextmenu", onResourceHeaderDblclick: "onResourceHeaderDblclick", onResourceMenuBeforeShow: "onResourceMenuBeforeShow", onResourceMenuItem: "onResourceMenuItem", onResourceMenuShow: "onResourceMenuShow", onResourceMenuToggleItem: "onResourceMenuToggleItem", onResourceTimeRangeClick: "onResourceTimeRangeClick", onResourceTimeRangeContextMenu: "onResourceTimeRangeContextMenu", onResourceTimeRangeDblClick: "onResourceTimeRangeDblClick", onResourceTimeRangeMouseDown: "onResourceTimeRangeMouseDown", onResourceTimeRangeMouseOut: "onResourceTimeRangeMouseOut", onResourceTimeRangeMouseOver: "onResourceTimeRangeMouseOver", onResourceTimeRangeMouseUp: "onResourceTimeRangeMouseUp", onResponsive: "onResponsive", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScheduleClick: "onScheduleClick", onScheduleContextMenu: "onScheduleContextMenu", onScheduleDblClick: "onScheduleDblClick", onScheduleMenuBeforeShow: "onScheduleMenuBeforeShow", onScheduleMenuItem: "onScheduleMenuItem", onScheduleMenuShow: "onScheduleMenuShow", onScheduleMouseDown: "onScheduleMouseDown", onScheduleMouseEnter: "onScheduleMouseEnter", onScheduleMouseLeave: "onScheduleMouseLeave", onScheduleMouseMove: "onScheduleMouseMove", onScheduleMouseUp: "onScheduleMouseUp", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onTimeAxisChange: "onTimeAxisChange", onTimeAxisHeaderClick: "onTimeAxisHeaderClick", onTimeAxisHeaderContextMenu: "onTimeAxisHeaderContextMenu", onTimeAxisHeaderDblClick: "onTimeAxisHeaderDblClick", onTimeAxisHeaderMenuBeforeShow: "onTimeAxisHeaderMenuBeforeShow", onTimeAxisHeaderMenuItem: "onTimeAxisHeaderMenuItem", onTimeAxisHeaderMenuShow: "onTimeAxisHeaderMenuShow", onTimelineContextChange: "onTimelineContextChange", onTimelineViewportResize: "onTimelineViewportResize", onTimeRangeHeaderClick: "onTimeRangeHeaderClick", onTimeRangeHeaderContextMenu: "onTimeRangeHeaderContextMenu", onTimeRangeHeaderDblClick: "onTimeRangeHeaderDblClick", onTimeSelectionChange: "onTimeSelectionChange", onTimeSelectionElementClick: "onTimeSelectionElementClick", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onUnsplit: "onUnsplit", onVisibleDateRangeChange: "onVisibleDateRangeChange" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerBaseComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-scheduler-base',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], allowCreate: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoAdjustTimeAxis: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], bufferCoef: [{
                type: Input
            }], bufferThreshold: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], createEventOnDblClick: [{
                type: Input
            }], creationTooltip: [{
                type: Input
            }], crudManagerClass: [{
                type: Input
            }], date: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], defaultResourceImageName: [{
                type: Input
            }], descriptionRenderer: [{
                type: Input
            }], deselectAllOnScheduleClick: [{
                type: Input
            }], deselectOnClick: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], destroyStores: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], dock: [{
                type: Input
            }], durationDisplayPrecision: [{
                type: Input
            }], enableDeleteKey: [{
                type: Input
            }], enableRecurringEvents: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], endParamName: [{
                type: Input
            }], eventBarTextField: [{
                type: Input
            }], eventBodyTemplate: [{
                type: Input
            }], eventRenderer: [{
                type: Input
            }], eventRendererThisObj: [{
                type: Input
            }], eventSelectionDisabled: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getDateConstraints: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], highlightPredecessors: [{
                type: Input
            }], highlightSuccessors: [{
                type: Input
            }], horizontalEventSorterFn: [{
                type: Input
            }], ignoreDomEventsWhileScrolling: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], infiniteScroll: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], maintainSelectionOnDatasetChange: [{
                type: Input
            }], managedEventSizing: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], maxTimeAxisUnit: [{
                type: Input
            }], modal: [{
                type: Input
            }], mode: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], multiEventSelect: [{
                type: Input
            }], narrowEventWidth: [{
                type: Input
            }], owner: [{
                type: Input
            }], partner: [{
                type: Input
            }], passStartEndParameters: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preCalculateHeightLimit: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], range: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], resourceColumns: [{
                type: Input
            }], resourceImageExtension: [{
                type: Input
            }], resourceImagePath: [{
                type: Input
            }], resourceTimeRangeRenderer: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], showCreationTooltip: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showEventColorPickers: [{
                type: Input
            }], showLagInTooltip: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], showRecurringUI: [{
                type: Input
            }], showTooltip: [{
                type: Input
            }], snapRelativeToEventStartDate: [{
                type: Input
            }], startParamName: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], stepUnit: [{
                type: Input
            }], stickyHeaders: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], suppressFit: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], terminalCls: [{
                type: Input
            }], terminalSides: [{
                type: Input
            }], timeAxis: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], triggerSelectionChangeOnRemove: [{
                type: Input
            }], ui: [{
                type: Input
            }], verticalTimeAxisColumn: [{
                type: Input
            }], visibleZoomFactor: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], zoomKeepsOriginalTimespan: [{
                type: Input
            }], zoomOnMouseWheel: [{
                type: Input
            }], zoomOnTimeAxisDoubleClick: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], allowDropOnEventBar: [{
                type: Input
            }], allowOverlap: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], assignments: [{
                type: Input
            }], assignmentStore: [{
                type: Input
            }], barMargin: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], creationTooltipTemplate: [{
                type: Input
            }], crudManager: [{
                type: Input
            }], data: [{
                type: Input
            }], dataset: [{
                type: Input
            }], dateFormat: [{
                type: Input
            }], dateSeparator: [{
                type: Input
            }], dependencies: [{
                type: Input
            }], dependencyStore: [{
                type: Input
            }], descriptionFormat: [{
                type: Input
            }], disabled: [{
                type: Input
            }], displayDateFormat: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableEventAnimations: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], endDate: [{
                type: Input
            }], eventColor: [{
                type: Input
            }], eventLayout: [{
                type: Input
            }], events: [{
                type: Input
            }], eventStore: [{
                type: Input
            }], eventStyle: [{
                type: Input
            }], extraData: [{
                type: Input
            }], fillTicks: [{
                type: Input
            }], flex: [{
                type: Input
            }], forceFit: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hideRowHover: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], isEventSelectable: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], maxZoomLevel: [{
                type: Input
            }], milestoneAlign: [{
                type: Input
            }], milestoneCharWidth: [{
                type: Input
            }], milestoneLayoutMode: [{
                type: Input
            }], milestoneTextPosition: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], minZoomLevel: [{
                type: Input
            }], overlappingEventSorter: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], presets: [{
                type: Input
            }], project: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], resourceMargin: [{
                type: Input
            }], resources: [{
                type: Input
            }], resourceStore: [{
                type: Input
            }], resourceTimeRanges: [{
                type: Input
            }], resourceTimeRangeStore: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], selectResourceOnEventNavigate: [{
                type: Input
            }], selectResourceOnScheduleClick: [{
                type: Input
            }], snap: [{
                type: Input
            }], startDate: [{
                type: Input
            }], store: [{
                type: Input
            }], terminalHideDelay: [{
                type: Input
            }], terminalOffset: [{
                type: Input
            }], terminalShowDelay: [{
                type: Input
            }], terminalSize: [{
                type: Input
            }], tickSize: [{
                type: Input
            }], timeRanges: [{
                type: Input
            }], timeRangeStore: [{
                type: Input
            }], timeZone: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltipTemplate: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], updateTimelineContextOnScroll: [{
                type: Input
            }], useInitialAnimation: [{
                type: Input
            }], viewPreset: [{
                type: Input
            }], visibleDate: [{
                type: Input
            }], width: [{
                type: Input
            }], workingTime: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], isEngineReady: [{
                type: Input
            }], scrollLeft: [{
                type: Input
            }], scrollTop: [{
                type: Input
            }], scrollX: [{
                type: Input
            }], selectedAssignments: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedEvents: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], timeResolution: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], zoomLevel: [{
                type: Input
            }], onAfterDependencyCreateDrop: [{
                type: Output
            }], onAfterDependencySave: [{
                type: Output
            }], onAfterDragCreate: [{
                type: Output
            }], onAfterEventDrop: [{
                type: Output
            }], onAfterEventEdit: [{
                type: Output
            }], onAfterEventSave: [{
                type: Output
            }], onAssignmentSelectionChange: [{
                type: Output
            }], onBeforeAssignmentDelete: [{
                type: Output
            }], onBeforeAssignmentSelectionChange: [{
                type: Output
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDependencyAdd: [{
                type: Output
            }], onBeforeDependencyCreateDrag: [{
                type: Output
            }], onBeforeDependencyCreateFinalize: [{
                type: Output
            }], onBeforeDependencyDelete: [{
                type: Output
            }], onBeforeDependencyEdit: [{
                type: Output
            }], onBeforeDependencyEditShow: [{
                type: Output
            }], onBeforeDependencySave: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeDragCreate: [{
                type: Output
            }], onBeforeDragCreateFinalize: [{
                type: Output
            }], onBeforeEventAdd: [{
                type: Output
            }], onBeforeEventDelete: [{
                type: Output
            }], onBeforeEventDrag: [{
                type: Output
            }], onBeforeEventDragSelect: [{
                type: Output
            }], onBeforeEventDropFinalize: [{
                type: Output
            }], onBeforeEventEdit: [{
                type: Output
            }], onBeforeEventEditShow: [{
                type: Output
            }], onBeforeEventResize: [{
                type: Output
            }], onBeforeEventResizeFinalize: [{
                type: Output
            }], onBeforeEventSave: [{
                type: Output
            }], onBeforeEventSelectionChange: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforePan: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforePresetChange: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeShowTerminals: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDateRangeChange: [{
                type: Output
            }], onDependenciesDrawn: [{
                type: Output
            }], onDependencyClick: [{
                type: Output
            }], onDependencyContextMenu: [{
                type: Output
            }], onDependencyCreateDragStart: [{
                type: Output
            }], onDependencyCreateDrop: [{
                type: Output
            }], onDependencyDblClick: [{
                type: Output
            }], onDependencyMouseOut: [{
                type: Output
            }], onDependencyMouseOver: [{
                type: Output
            }], onDependencyValidationComplete: [{
                type: Output
            }], onDependencyValidationStart: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragCreateEnd: [{
                type: Output
            }], onDragCreateStart: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onEventAutoCreated: [{
                type: Output
            }], onEventClick: [{
                type: Output
            }], onEventContextMenu: [{
                type: Output
            }], onEventDblClick: [{
                type: Output
            }], onEventDrag: [{
                type: Output
            }], onEventDragAbort: [{
                type: Output
            }], onEventDragModeChange: [{
                type: Output
            }], onEventDragReset: [{
                type: Output
            }], onEventDragStart: [{
                type: Output
            }], onEventDrop: [{
                type: Output
            }], onEventEditBeforeSetRecord: [{
                type: Output
            }], onEventKeyDown: [{
                type: Output
            }], onEventKeyUp: [{
                type: Output
            }], onEventMenuBeforeShow: [{
                type: Output
            }], onEventMenuItem: [{
                type: Output
            }], onEventMenuShow: [{
                type: Output
            }], onEventMouseDown: [{
                type: Output
            }], onEventMouseEnter: [{
                type: Output
            }], onEventMouseLeave: [{
                type: Output
            }], onEventMouseOut: [{
                type: Output
            }], onEventMouseOver: [{
                type: Output
            }], onEventMouseUp: [{
                type: Output
            }], onEventPartialResize: [{
                type: Output
            }], onEventResizeEnd: [{
                type: Output
            }], onEventResizeStart: [{
                type: Output
            }], onEventSelectionChange: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onNavigate: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPasteNotAllowed: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onPresetChange: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onReleaseEvent: [{
                type: Output
            }], onRenderEvent: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResourceHeaderClick: [{
                type: Output
            }], onResourceHeaderContextmenu: [{
                type: Output
            }], onResourceHeaderDblclick: [{
                type: Output
            }], onResourceMenuBeforeShow: [{
                type: Output
            }], onResourceMenuItem: [{
                type: Output
            }], onResourceMenuShow: [{
                type: Output
            }], onResourceMenuToggleItem: [{
                type: Output
            }], onResourceTimeRangeClick: [{
                type: Output
            }], onResourceTimeRangeContextMenu: [{
                type: Output
            }], onResourceTimeRangeDblClick: [{
                type: Output
            }], onResourceTimeRangeMouseDown: [{
                type: Output
            }], onResourceTimeRangeMouseOut: [{
                type: Output
            }], onResourceTimeRangeMouseOver: [{
                type: Output
            }], onResourceTimeRangeMouseUp: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScheduleClick: [{
                type: Output
            }], onScheduleContextMenu: [{
                type: Output
            }], onScheduleDblClick: [{
                type: Output
            }], onScheduleMenuBeforeShow: [{
                type: Output
            }], onScheduleMenuItem: [{
                type: Output
            }], onScheduleMenuShow: [{
                type: Output
            }], onScheduleMouseDown: [{
                type: Output
            }], onScheduleMouseEnter: [{
                type: Output
            }], onScheduleMouseLeave: [{
                type: Output
            }], onScheduleMouseMove: [{
                type: Output
            }], onScheduleMouseUp: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onTimeAxisChange: [{
                type: Output
            }], onTimeAxisHeaderClick: [{
                type: Output
            }], onTimeAxisHeaderContextMenu: [{
                type: Output
            }], onTimeAxisHeaderDblClick: [{
                type: Output
            }], onTimeAxisHeaderMenuBeforeShow: [{
                type: Output
            }], onTimeAxisHeaderMenuItem: [{
                type: Output
            }], onTimeAxisHeaderMenuShow: [{
                type: Output
            }], onTimelineContextChange: [{
                type: Output
            }], onTimelineViewportResize: [{
                type: Output
            }], onTimeRangeHeaderClick: [{
                type: Output
            }], onTimeRangeHeaderContextMenu: [{
                type: Output
            }], onTimeRangeHeaderDblClick: [{
                type: Output
            }], onTimeSelectionChange: [{
                type: Output
            }], onTimeSelectionElementClick: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], onVisibleDateRangeChange: [{
                type: Output
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnLinesFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], dependenciesFeature: [{
                type: Input
            }], dependencyEditFeature: [{
                type: Input
            }], eventCopyPasteFeature: [{
                type: Input
            }], eventDragFeature: [{
                type: Input
            }], eventDragCreateFeature: [{
                type: Input
            }], eventDragSelectFeature: [{
                type: Input
            }], eventEditFeature: [{
                type: Input
            }], eventFilterFeature: [{
                type: Input
            }], eventMenuFeature: [{
                type: Input
            }], eventNonWorkingTimeFeature: [{
                type: Input
            }], eventResizeFeature: [{
                type: Input
            }], eventTooltipFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], headerZoomFeature: [{
                type: Input
            }], labelsFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], nonWorkingTimeFeature: [{
                type: Input
            }], panFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], resourceMenuFeature: [{
                type: Input
            }], resourceTimeRangesFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], scheduleContextFeature: [{
                type: Input
            }], scheduleMenuFeature: [{
                type: Input
            }], scheduleTooltipFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], simpleEventEditFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stickyEventsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], timeAxisHeaderMenuFeature: [{
                type: Input
            }], timeRangesFeature: [{
                type: Input
            }], timeSelectionFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum SchedulerDatePicker
 */
class BryntumSchedulerDatePickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeRefresh = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDateChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onRefresh = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSchedulerDatePickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSchedulerDatePickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSchedulerDatePickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSchedulerDatePickerComponent.instanceClass = SchedulerDatePicker;
BryntumSchedulerDatePickerComponent.instanceName = 'SchedulerDatePicker';
BryntumSchedulerDatePickerComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeRefresh',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onCollapse',
    'onDateChange',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onRefresh',
    'onResize',
    'onSelectionChange',
    'onShow',
    'onToolClick'
];
BryntumSchedulerDatePickerComponent.bryntumFeatureNames = [];
BryntumSchedulerDatePickerComponent.bryntumConfigs = BryntumSchedulerDatePickerComponent.bryntumFeatureNames.concat([
    'activeDate',
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'animateTimeShift',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'cellRenderer',
    'centered',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'date',
    'dayNameFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'disabledCls',
    'disabledDates',
    'disableNonWorkingDays',
    'disableOtherMonthCells',
    'disableWeekends',
    'dock',
    'draggable',
    'editMonth',
    'eventFilter',
    'eventStore',
    'extraData',
    'flex',
    'floating',
    'focusDisabledDates',
    'footer',
    'header',
    'headerRenderer',
    'height',
    'hidden',
    'hideAnimation',
    'hideOtherMonthCells',
    'hideWhenEmpty',
    'highlightSelectedWeek',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxDate',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minColumnWidth',
    'minDate',
    'minHeight',
    'minRowHeight',
    'minWidth',
    'monitorResize',
    'month',
    'multiSelect',
    'namedItems',
    'nonWorkingDayCls',
    'nonWorkingDays',
    'otherMonthCls',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'selection',
    'showAnimation',
    'showEvents',
    'showTooltipWhenDisabled',
    'showWeekColumn',
    'showWeekNumber',
    'sixWeeks',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'tip',
    'title',
    'todayCls',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'weekendCls',
    'weekRenderer',
    'weekStartDay',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumSchedulerDatePickerComponent.bryntumConfigsOnly = [
    'activeDate',
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'cellRenderer',
    'centered',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'dayNameFormat',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabledCls',
    'disabledDates',
    'disableNonWorkingDays',
    'disableOtherMonthCells',
    'disableWeekends',
    'dock',
    'draggable',
    'editMonth',
    'eventFilter',
    'eventStore',
    'floating',
    'focusDisabledDates',
    'footer',
    'header',
    'headerRenderer',
    'hideAnimation',
    'hideOtherMonthCells',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxDate',
    'minColumnWidth',
    'minDate',
    'minRowHeight',
    'monitorResize',
    'month',
    'multiSelect',
    'namedItems',
    'nonWorkingDayCls',
    'nonWorkingDays',
    'otherMonthCls',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showEvents',
    'showTooltipWhenDisabled',
    'showWeekColumn',
    'showWeekNumber',
    'sixWeeks',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'tip',
    'todayCls',
    'trapFocus',
    'ui',
    'weekendCls',
    'weekRenderer',
    'weekStartDay',
    'weight'
];
BryntumSchedulerDatePickerComponent.bryntumProps = BryntumSchedulerDatePickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'animateTimeShift',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'date',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'highlightSelectedWeek',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'selection',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumSchedulerDatePickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerDatePickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSchedulerDatePickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSchedulerDatePickerComponent, selector: "bryntum-scheduler-date-picker", inputs: { activeDate: "activeDate", adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", cellRenderer: "cellRenderer", centered: "centered", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", dayNameFormat: "dayNameFormat", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", disabledCls: "disabledCls", disabledDates: "disabledDates", disableNonWorkingDays: "disableNonWorkingDays", disableOtherMonthCells: "disableOtherMonthCells", disableWeekends: "disableWeekends", dock: "dock", draggable: "draggable", editMonth: "editMonth", eventFilter: "eventFilter", eventStore: "eventStore", floating: "floating", focusDisabledDates: "focusDisabledDates", footer: "footer", header: "header", headerRenderer: "headerRenderer", hideAnimation: "hideAnimation", hideOtherMonthCells: "hideOtherMonthCells", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxDate: "maxDate", minColumnWidth: "minColumnWidth", minDate: "minDate", minRowHeight: "minRowHeight", monitorResize: "monitorResize", month: "month", multiSelect: "multiSelect", namedItems: "namedItems", nonWorkingDayCls: "nonWorkingDayCls", nonWorkingDays: "nonWorkingDays", otherMonthCls: "otherMonthCls", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showEvents: "showEvents", showTooltipWhenDisabled: "showTooltipWhenDisabled", showWeekColumn: "showWeekColumn", showWeekNumber: "showWeekNumber", sixWeeks: "sixWeeks", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", tip: "tip", todayCls: "todayCls", trapFocus: "trapFocus", ui: "ui", weekendCls: "weekendCls", weekRenderer: "weekRenderer", weekStartDay: "weekStartDay", weight: "weight", alignSelf: "alignSelf", animateTimeShift: "animateTimeShift", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", date: "date", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", highlightSelectedWeek: "highlightSelectedWeek", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", selection: "selection", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", state: "state", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeRefresh: "onBeforeRefresh", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDateChange: "onDateChange", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onRefresh: "onRefresh", onResize: "onResize", onSelectionChange: "onSelectionChange", onShow: "onShow", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerDatePickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-scheduler-date-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { activeDate: [{
                type: Input
            }], adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], cellRenderer: [{
                type: Input
            }], centered: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], dayNameFormat: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], disabledCls: [{
                type: Input
            }], disabledDates: [{
                type: Input
            }], disableNonWorkingDays: [{
                type: Input
            }], disableOtherMonthCells: [{
                type: Input
            }], disableWeekends: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], editMonth: [{
                type: Input
            }], eventFilter: [{
                type: Input
            }], eventStore: [{
                type: Input
            }], floating: [{
                type: Input
            }], focusDisabledDates: [{
                type: Input
            }], footer: [{
                type: Input
            }], header: [{
                type: Input
            }], headerRenderer: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideOtherMonthCells: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], minColumnWidth: [{
                type: Input
            }], minDate: [{
                type: Input
            }], minRowHeight: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], month: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], nonWorkingDayCls: [{
                type: Input
            }], nonWorkingDays: [{
                type: Input
            }], otherMonthCls: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showEvents: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], showWeekColumn: [{
                type: Input
            }], showWeekNumber: [{
                type: Input
            }], sixWeeks: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], tip: [{
                type: Input
            }], todayCls: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weekendCls: [{
                type: Input
            }], weekRenderer: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], animateTimeShift: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], date: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], highlightSelectedWeek: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selection: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], state: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeRefresh: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDateChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onRefresh: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum SchedulerPro
 */
class BryntumSchedulerProComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAfterDependencyCreateDrop = new EventEmitter();
        this.onAfterDependencySave = new EventEmitter();
        this.onAfterDragCreate = new EventEmitter();
        this.onAfterEventDrop = new EventEmitter();
        this.onAfterEventEdit = new EventEmitter();
        this.onAfterEventSave = new EventEmitter();
        this.onAfterEventSegmentDrop = new EventEmitter();
        this.onAfterTaskEdit = new EventEmitter();
        this.onAfterTaskSave = new EventEmitter();
        this.onAssignmentSelectionChange = new EventEmitter();
        this.onBeforeAssignmentDelete = new EventEmitter();
        this.onBeforeAssignmentSelectionChange = new EventEmitter();
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDependencyAdd = new EventEmitter();
        this.onBeforeDependencyCreateDrag = new EventEmitter();
        this.onBeforeDependencyCreateFinalize = new EventEmitter();
        this.onBeforeDependencyDelete = new EventEmitter();
        this.onBeforeDependencyEdit = new EventEmitter();
        this.onBeforeDependencyEditShow = new EventEmitter();
        this.onBeforeDependencySave = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeDragCreate = new EventEmitter();
        this.onBeforeDragCreateFinalize = new EventEmitter();
        this.onBeforeEventAdd = new EventEmitter();
        this.onBeforeEventDelete = new EventEmitter();
        this.onBeforeEventDrag = new EventEmitter();
        this.onBeforeEventDragSelect = new EventEmitter();
        this.onBeforeEventDropFinalize = new EventEmitter();
        this.onBeforeEventEdit = new EventEmitter();
        this.onBeforeEventEditShow = new EventEmitter();
        this.onBeforeEventResize = new EventEmitter();
        this.onBeforeEventResizeFinalize = new EventEmitter();
        this.onBeforeEventSave = new EventEmitter();
        this.onBeforeEventSegmentDrag = new EventEmitter();
        this.onBeforeEventSegmentDropFinalize = new EventEmitter();
        this.onBeforeEventSegmentResize = new EventEmitter();
        this.onBeforeEventSegmentResizeFinalize = new EventEmitter();
        this.onBeforeEventSelectionChange = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforePan = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforePresetChange = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeShowTerminals = new EventEmitter();
        this.onBeforeTaskDelete = new EventEmitter();
        this.onBeforeTaskEdit = new EventEmitter();
        this.onBeforeTaskEditShow = new EventEmitter();
        this.onBeforeTaskSave = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDateRangeChange = new EventEmitter();
        this.onDependenciesDrawn = new EventEmitter();
        this.onDependencyClick = new EventEmitter();
        this.onDependencyContextMenu = new EventEmitter();
        this.onDependencyCreateDragStart = new EventEmitter();
        this.onDependencyCreateDrop = new EventEmitter();
        this.onDependencyDblClick = new EventEmitter();
        this.onDependencyMouseOut = new EventEmitter();
        this.onDependencyMouseOver = new EventEmitter();
        this.onDependencyValidationComplete = new EventEmitter();
        this.onDependencyValidationStart = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragCreateEnd = new EventEmitter();
        this.onDragCreateStart = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onEventAutoCreated = new EventEmitter();
        this.onEventClick = new EventEmitter();
        this.onEventContextMenu = new EventEmitter();
        this.onEventDblClick = new EventEmitter();
        this.onEventDrag = new EventEmitter();
        this.onEventDragAbort = new EventEmitter();
        this.onEventDragModeChange = new EventEmitter();
        this.onEventDragReset = new EventEmitter();
        this.onEventDragStart = new EventEmitter();
        this.onEventDrop = new EventEmitter();
        this.onEventEditBeforeSetRecord = new EventEmitter();
        this.onEventKeyDown = new EventEmitter();
        this.onEventKeyUp = new EventEmitter();
        this.onEventMenuBeforeShow = new EventEmitter();
        this.onEventMenuItem = new EventEmitter();
        this.onEventMenuShow = new EventEmitter();
        this.onEventMouseDown = new EventEmitter();
        this.onEventMouseEnter = new EventEmitter();
        this.onEventMouseLeave = new EventEmitter();
        this.onEventMouseOut = new EventEmitter();
        this.onEventMouseOver = new EventEmitter();
        this.onEventMouseUp = new EventEmitter();
        this.onEventPartialResize = new EventEmitter();
        this.onEventResizeEnd = new EventEmitter();
        this.onEventResizeStart = new EventEmitter();
        this.onEventSegmentDrag = new EventEmitter();
        this.onEventSegmentDragAbort = new EventEmitter();
        this.onEventSegmentDragReset = new EventEmitter();
        this.onEventSegmentDragStart = new EventEmitter();
        this.onEventSegmentDrop = new EventEmitter();
        this.onEventSegmentPartialResize = new EventEmitter();
        this.onEventSegmentResizeEnd = new EventEmitter();
        this.onEventSegmentResizeStart = new EventEmitter();
        this.onEventSelectionChange = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onNavigate = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPasteNotAllowed = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPercentBarDrag = new EventEmitter();
        this.onPercentBarDragAbort = new EventEmitter();
        this.onPercentBarDragStart = new EventEmitter();
        this.onPercentBarDrop = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onPresetChange = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onReleaseEvent = new EventEmitter();
        this.onRenderEvent = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResourceHeaderClick = new EventEmitter();
        this.onResourceHeaderContextmenu = new EventEmitter();
        this.onResourceHeaderDblclick = new EventEmitter();
        this.onResourceMenuBeforeShow = new EventEmitter();
        this.onResourceMenuItem = new EventEmitter();
        this.onResourceMenuShow = new EventEmitter();
        this.onResourceMenuToggleItem = new EventEmitter();
        this.onResourceNonWorkingTimeClick = new EventEmitter();
        this.onResourceNonWorkingTimeContextMenu = new EventEmitter();
        this.onResourceNonWorkingTimeDblClick = new EventEmitter();
        this.onResourceNonWorkingTimeMouseDown = new EventEmitter();
        this.onResourceNonWorkingTimeMouseOut = new EventEmitter();
        this.onResourceNonWorkingTimeMouseOver = new EventEmitter();
        this.onResourceNonWorkingTimeMouseUp = new EventEmitter();
        this.onResourceTimeRangeClick = new EventEmitter();
        this.onResourceTimeRangeContextMenu = new EventEmitter();
        this.onResourceTimeRangeDblClick = new EventEmitter();
        this.onResourceTimeRangeMouseDown = new EventEmitter();
        this.onResourceTimeRangeMouseOut = new EventEmitter();
        this.onResourceTimeRangeMouseOver = new EventEmitter();
        this.onResourceTimeRangeMouseUp = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScheduleClick = new EventEmitter();
        this.onScheduleContextMenu = new EventEmitter();
        this.onScheduleDblClick = new EventEmitter();
        this.onScheduleMenuBeforeShow = new EventEmitter();
        this.onScheduleMenuItem = new EventEmitter();
        this.onScheduleMenuShow = new EventEmitter();
        this.onScheduleMouseDown = new EventEmitter();
        this.onScheduleMouseEnter = new EventEmitter();
        this.onScheduleMouseLeave = new EventEmitter();
        this.onScheduleMouseMove = new EventEmitter();
        this.onScheduleMouseUp = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onTaskEditCanceled = new EventEmitter();
        this.onTimeAxisChange = new EventEmitter();
        this.onTimeAxisHeaderClick = new EventEmitter();
        this.onTimeAxisHeaderContextMenu = new EventEmitter();
        this.onTimeAxisHeaderDblClick = new EventEmitter();
        this.onTimeAxisHeaderMenuBeforeShow = new EventEmitter();
        this.onTimeAxisHeaderMenuItem = new EventEmitter();
        this.onTimeAxisHeaderMenuShow = new EventEmitter();
        this.onTimelineContextChange = new EventEmitter();
        this.onTimelineViewportResize = new EventEmitter();
        this.onTimeRangeHeaderClick = new EventEmitter();
        this.onTimeRangeHeaderContextMenu = new EventEmitter();
        this.onTimeRangeHeaderDblClick = new EventEmitter();
        this.onTimeSelectionChange = new EventEmitter();
        this.onTimeSelectionElementClick = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onTransactionChange = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.onVisibleDateRangeChange = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSchedulerProComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
        // Backwards compatibility for gridInstance, schedulerInstance etc.
        // @ts-ignore
        me[StringHelper.uncapitalize(instanceName) + 'Instance'] = me.instance;
        // 
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSchedulerProComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSchedulerProComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSchedulerProComponent.instanceClass = SchedulerPro;
BryntumSchedulerProComponent.instanceName = 'SchedulerPro';
BryntumSchedulerProComponent.bryntumEvents = [
    'onAfterDependencyCreateDrop',
    'onAfterDependencySave',
    'onAfterDragCreate',
    'onAfterEventDrop',
    'onAfterEventEdit',
    'onAfterEventSave',
    'onAfterEventSegmentDrop',
    'onAfterTaskEdit',
    'onAfterTaskSave',
    'onAssignmentSelectionChange',
    'onBeforeAssignmentDelete',
    'onBeforeAssignmentSelectionChange',
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDependencyAdd',
    'onBeforeDependencyCreateDrag',
    'onBeforeDependencyCreateFinalize',
    'onBeforeDependencyDelete',
    'onBeforeDependencyEdit',
    'onBeforeDependencyEditShow',
    'onBeforeDependencySave',
    'onBeforeDestroy',
    'onBeforeDragCreate',
    'onBeforeDragCreateFinalize',
    'onBeforeEventAdd',
    'onBeforeEventDelete',
    'onBeforeEventDrag',
    'onBeforeEventDragSelect',
    'onBeforeEventDropFinalize',
    'onBeforeEventEdit',
    'onBeforeEventEditShow',
    'onBeforeEventResize',
    'onBeforeEventResizeFinalize',
    'onBeforeEventSave',
    'onBeforeEventSegmentDrag',
    'onBeforeEventSegmentDropFinalize',
    'onBeforeEventSegmentResize',
    'onBeforeEventSegmentResizeFinalize',
    'onBeforeEventSelectionChange',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforePan',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforePresetChange',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeShowTerminals',
    'onBeforeTaskDelete',
    'onBeforeTaskEdit',
    'onBeforeTaskEditShow',
    'onBeforeTaskSave',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDateRangeChange',
    'onDependenciesDrawn',
    'onDependencyClick',
    'onDependencyContextMenu',
    'onDependencyCreateDragStart',
    'onDependencyCreateDrop',
    'onDependencyDblClick',
    'onDependencyMouseOut',
    'onDependencyMouseOver',
    'onDependencyValidationComplete',
    'onDependencyValidationStart',
    'onDestroy',
    'onDragCreateEnd',
    'onDragCreateStart',
    'onDragSelecting',
    'onEventAutoCreated',
    'onEventClick',
    'onEventContextMenu',
    'onEventDblClick',
    'onEventDrag',
    'onEventDragAbort',
    'onEventDragModeChange',
    'onEventDragReset',
    'onEventDragStart',
    'onEventDrop',
    'onEventEditBeforeSetRecord',
    'onEventKeyDown',
    'onEventKeyUp',
    'onEventMenuBeforeShow',
    'onEventMenuItem',
    'onEventMenuShow',
    'onEventMouseDown',
    'onEventMouseEnter',
    'onEventMouseLeave',
    'onEventMouseOut',
    'onEventMouseOver',
    'onEventMouseUp',
    'onEventPartialResize',
    'onEventResizeEnd',
    'onEventResizeStart',
    'onEventSegmentDrag',
    'onEventSegmentDragAbort',
    'onEventSegmentDragReset',
    'onEventSegmentDragStart',
    'onEventSegmentDrop',
    'onEventSegmentPartialResize',
    'onEventSegmentResizeEnd',
    'onEventSegmentResizeStart',
    'onEventSelectionChange',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onMouseOut',
    'onMouseOver',
    'onNavigate',
    'onPaint',
    'onPaste',
    'onPasteNotAllowed',
    'onPdfExport',
    'onPercentBarDrag',
    'onPercentBarDragAbort',
    'onPercentBarDragStart',
    'onPercentBarDrop',
    'onPointerOver',
    'onPresetChange',
    'onRecompose',
    'onReleaseEvent',
    'onRenderEvent',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResourceHeaderClick',
    'onResourceHeaderContextmenu',
    'onResourceHeaderDblclick',
    'onResourceMenuBeforeShow',
    'onResourceMenuItem',
    'onResourceMenuShow',
    'onResourceMenuToggleItem',
    'onResourceNonWorkingTimeClick',
    'onResourceNonWorkingTimeContextMenu',
    'onResourceNonWorkingTimeDblClick',
    'onResourceNonWorkingTimeMouseDown',
    'onResourceNonWorkingTimeMouseOut',
    'onResourceNonWorkingTimeMouseOver',
    'onResourceNonWorkingTimeMouseUp',
    'onResourceTimeRangeClick',
    'onResourceTimeRangeContextMenu',
    'onResourceTimeRangeDblClick',
    'onResourceTimeRangeMouseDown',
    'onResourceTimeRangeMouseOut',
    'onResourceTimeRangeMouseOver',
    'onResourceTimeRangeMouseUp',
    'onResponsive',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScheduleClick',
    'onScheduleContextMenu',
    'onScheduleDblClick',
    'onScheduleMenuBeforeShow',
    'onScheduleMenuItem',
    'onScheduleMenuShow',
    'onScheduleMouseDown',
    'onScheduleMouseEnter',
    'onScheduleMouseLeave',
    'onScheduleMouseMove',
    'onScheduleMouseUp',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onTaskEditCanceled',
    'onTimeAxisChange',
    'onTimeAxisHeaderClick',
    'onTimeAxisHeaderContextMenu',
    'onTimeAxisHeaderDblClick',
    'onTimeAxisHeaderMenuBeforeShow',
    'onTimeAxisHeaderMenuItem',
    'onTimeAxisHeaderMenuShow',
    'onTimelineContextChange',
    'onTimelineViewportResize',
    'onTimeRangeHeaderClick',
    'onTimeRangeHeaderContextMenu',
    'onTimeRangeHeaderDblClick',
    'onTimeSelectionChange',
    'onTimeSelectionElementClick',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onTransactionChange',
    'onUnsplit',
    'onVisibleDateRangeChange'
];
BryntumSchedulerProComponent.bryntumFeatureNames = [
    'calendarHighlightFeature',
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnLinesFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'dependenciesFeature',
    'dependencyEditFeature',
    'eventBufferFeature',
    'eventCopyPasteFeature',
    'eventDragFeature',
    'eventDragCreateFeature',
    'eventDragSelectFeature',
    'eventEditFeature',
    'eventFilterFeature',
    'eventMenuFeature',
    'eventNonWorkingTimeFeature',
    'eventResizeFeature',
    'eventSegmentDragFeature',
    'eventSegmentResizeFeature',
    'eventSegmentsFeature',
    'eventTooltipFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'headerZoomFeature',
    'labelsFeature',
    'mergeCellsFeature',
    'nestedEventsFeature',
    'nonWorkingTimeFeature',
    'panFeature',
    'pdfExportFeature',
    'percentBarFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'resourceMenuFeature',
    'resourceNonWorkingTimeFeature',
    'resourceTimeRangesFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'scheduleContextFeature',
    'scheduleMenuFeature',
    'scheduleTooltipFeature',
    'searchFeature',
    'simpleEventEditFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stickyEventsFeature',
    'stripeFeature',
    'summaryFeature',
    'taskEditFeature',
    'timeAxisHeaderMenuFeature',
    'timeRangesFeature',
    'timeSelectionFeature',
    'timeSpanHighlightFeature',
    'treeFeature',
    'treeGroupFeature',
    'versionsFeature'
];
BryntumSchedulerProComponent.bryntumConfigs = BryntumSchedulerProComponent.bryntumFeatureNames.concat([
    'adopt',
    'alignSelf',
    'allowCreate',
    'allowDropOnEventBar',
    'allowOver',
    'allowOverlap',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'assignments',
    'assignmentStore',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'barMargin',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'calendars',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'createEventOnDblClick',
    'creationTooltip',
    'creationTooltipTemplate',
    'crudManager',
    'crudManagerClass',
    'cycleResolutionPopupClass',
    'data',
    'dataset',
    'date',
    'dateFormat',
    'dateSeparator',
    'defaultRegion',
    'defaultResourceImageName',
    'dependencies',
    'dependencyIdField',
    'dependencyStore',
    'descriptionFormat',
    'descriptionRenderer',
    'deselectAllOnScheduleClick',
    'deselectOnClick',
    'destroyStore',
    'destroyStores',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'displayDateFormat',
    'displaySchedulingIssueResolutionPopup',
    'dock',
    'durationDisplayPrecision',
    'emptyText',
    'enableDeleteKey',
    'enableEventAnimations',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'endDate',
    'endParamName',
    'eventBarTextField',
    'eventBodyTemplate',
    'eventColor',
    'eventLayout',
    'eventRenderer',
    'eventRendererThisObj',
    'events',
    'eventSelectionDisabled',
    'eventStore',
    'eventStyle',
    'extraData',
    'features',
    'fillLastColumn',
    'fillTicks',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forceFit',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getDateConstraints',
    'getHtml',
    'getRowHeight',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hideRowHover',
    'highlightPredecessors',
    'highlightSuccessors',
    'horizontalEventSorterFn',
    'hoverDelay',
    'id',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'insertBefore',
    'insertFirst',
    'isEventSelectable',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'maintainSelectionOnDatasetChange',
    'managedEventSizing',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxTimeAxisUnit',
    'maxWidth',
    'maxZoomLevel',
    'milestoneAlign',
    'milestoneCharWidth',
    'milestoneLayoutMode',
    'milestoneTextPosition',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'modal',
    'mode',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'multiEventSelect',
    'narrowEventWidth',
    'overlappingEventSorter',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preCalculateHeightLimit',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'presets',
    'preventTooltipOnTouch',
    'project',
    'projectProgressReporting',
    'range',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceColumns',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceMargin',
    'resources',
    'resourceStore',
    'resourceTimeRangeRenderer',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'schedulingIssueResolutionPopupClass',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'selectResourceOnEventNavigate',
    'selectResourceOnScheduleClick',
    'showCreationTooltip',
    'showDirty',
    'showEventColorPickers',
    'showLagInTooltip',
    'showOnClick',
    'showOnHover',
    'showRecurringUI',
    'showTaskColorPickers',
    'showTooltip',
    'snap',
    'snapRelativeToEventStartDate',
    'startDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stepUnit',
    'stickyHeaders',
    'store',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'terminalCls',
    'terminalHideDelay',
    'terminalOffset',
    'terminalShowDelay',
    'terminalSides',
    'terminalSize',
    'tickSize',
    'timeAxis',
    'timeRanges',
    'timeRangeStore',
    'timeZone',
    'title',
    'tools',
    'tooltipTemplate',
    'trackMouse',
    'transitionDuration',
    'triggerSelectionChangeOnRemove',
    'ui',
    'updateTimelineContextOnScroll',
    'useInitialAnimation',
    'verticalTimeAxisColumn',
    'viewPreset',
    'visibleDate',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'width',
    'workingTime',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
]);
BryntumSchedulerProComponent.bryntumConfigsOnly = [
    'adopt',
    'allowCreate',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'ariaDescription',
    'ariaLabel',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'createEventOnDblClick',
    'creationTooltip',
    'crudManagerClass',
    'cycleResolutionPopupClass',
    'date',
    'defaultRegion',
    'defaultResourceImageName',
    'dependencyIdField',
    'descriptionRenderer',
    'deselectAllOnScheduleClick',
    'deselectOnClick',
    'destroyStore',
    'destroyStores',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'displaySchedulingIssueResolutionPopup',
    'dock',
    'durationDisplayPrecision',
    'enableDeleteKey',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'endParamName',
    'eventBarTextField',
    'eventBodyTemplate',
    'eventRenderer',
    'eventRendererThisObj',
    'eventSelectionDisabled',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getDateConstraints',
    'getHtml',
    'getRowHeight',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'highlightPredecessors',
    'highlightSuccessors',
    'horizontalEventSorterFn',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'maintainSelectionOnDatasetChange',
    'managedEventSizing',
    'maskDefaults',
    'masked',
    'maximizable',
    'maxTimeAxisUnit',
    'modal',
    'mode',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'multiEventSelect',
    'narrowEventWidth',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preCalculateHeightLimit',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'projectProgressReporting',
    'range',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceColumns',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceTimeRangeRenderer',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'schedulingIssueResolutionPopupClass',
    'scrollerClass',
    'scrollManager',
    'showCreationTooltip',
    'showDirty',
    'showEventColorPickers',
    'showLagInTooltip',
    'showOnClick',
    'showOnHover',
    'showRecurringUI',
    'showTaskColorPickers',
    'showTooltip',
    'snapRelativeToEventStartDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stepUnit',
    'stickyHeaders',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'terminalCls',
    'terminalSides',
    'timeAxis',
    'trackMouse',
    'triggerSelectionChangeOnRemove',
    'ui',
    'verticalTimeAxisColumn',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
];
BryntumSchedulerProComponent.bryntumProps = BryntumSchedulerProComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'allowDropOnEventBar',
    'allowOverlap',
    'appendTo',
    'ariaLive',
    'assignments',
    'assignmentStore',
    'barMargin',
    'calendars',
    'cls',
    'columnLines',
    'columns',
    'creationTooltipTemplate',
    'crudManager',
    'data',
    'dataset',
    'dateFormat',
    'dateSeparator',
    'dependencies',
    'dependencyStore',
    'descriptionFormat',
    'disabled',
    'displayDateFormat',
    'emptyText',
    'enableEventAnimations',
    'enableUndoRedoKeys',
    'endDate',
    'eventColor',
    'eventLayout',
    'events',
    'eventStore',
    'eventStyle',
    'extraData',
    'fillTicks',
    'flex',
    'forceFit',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hideRowHover',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'isEngineReady',
    'isEventSelectable',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'maxZoomLevel',
    'milestoneAlign',
    'milestoneCharWidth',
    'milestoneLayoutMode',
    'milestoneTextPosition',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'overlappingEventSorter',
    'preserveScroll',
    'presets',
    'project',
    'readOnly',
    'resourceMargin',
    'resources',
    'resourceStore',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollLeft',
    'scrollTop',
    'scrollX',
    'selectedAssignments',
    'selectedCell',
    'selectedCells',
    'selectedEvents',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'selectResourceOnEventNavigate',
    'selectResourceOnScheduleClick',
    'snap',
    'startDate',
    'state',
    'store',
    'terminalHideDelay',
    'terminalOffset',
    'terminalShowDelay',
    'terminalSize',
    'tickSize',
    'timeRanges',
    'timeRangeStore',
    'timeResolution',
    'timeZone',
    'title',
    'tools',
    'tooltip',
    'tooltipTemplate',
    'transitionDuration',
    'updateTimelineContextOnScroll',
    'useInitialAnimation',
    'viewPreset',
    'visibleDate',
    'width',
    'workingTime',
    'zoomLevel'
]);
BryntumSchedulerProComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerProComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSchedulerProComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSchedulerProComponent, selector: "bryntum-scheduler-pro", inputs: { adopt: "adopt", allowCreate: "allowCreate", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", animateTreeNodeToggle: "animateTreeNodeToggle", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoAdjustTimeAxis: "autoAdjustTimeAxis", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", bufferCoef: "bufferCoef", bufferThreshold: "bufferThreshold", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", createEventOnDblClick: "createEventOnDblClick", creationTooltip: "creationTooltip", crudManagerClass: "crudManagerClass", cycleResolutionPopupClass: "cycleResolutionPopupClass", date: "date", defaultRegion: "defaultRegion", defaultResourceImageName: "defaultResourceImageName", dependencyIdField: "dependencyIdField", descriptionRenderer: "descriptionRenderer", deselectAllOnScheduleClick: "deselectAllOnScheduleClick", deselectOnClick: "deselectOnClick", destroyStore: "destroyStore", destroyStores: "destroyStores", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", displaySchedulingIssueResolutionPopup: "displaySchedulingIssueResolutionPopup", dock: "dock", durationDisplayPrecision: "durationDisplayPrecision", enableDeleteKey: "enableDeleteKey", enableRecurringEvents: "enableRecurringEvents", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", endParamName: "endParamName", eventBarTextField: "eventBarTextField", eventBodyTemplate: "eventBodyTemplate", eventRenderer: "eventRenderer", eventRendererThisObj: "eventRendererThisObj", eventSelectionDisabled: "eventSelectionDisabled", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getDateConstraints: "getDateConstraints", getHtml: "getHtml", getRowHeight: "getRowHeight", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", highlightPredecessors: "highlightPredecessors", highlightSuccessors: "highlightSuccessors", horizontalEventSorterFn: "horizontalEventSorterFn", ignoreDomEventsWhileScrolling: "ignoreDomEventsWhileScrolling", ignoreParentReadOnly: "ignoreParentReadOnly", infiniteScroll: "infiniteScroll", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", maintainSelectionOnDatasetChange: "maintainSelectionOnDatasetChange", managedEventSizing: "managedEventSizing", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", maxTimeAxisUnit: "maxTimeAxisUnit", modal: "modal", mode: "mode", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", multiEventSelect: "multiEventSelect", narrowEventWidth: "narrowEventWidth", owner: "owner", partner: "partner", passStartEndParameters: "passStartEndParameters", plugins: "plugins", preCalculateHeightLimit: "preCalculateHeightLimit", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", projectProgressReporting: "projectProgressReporting", range: "range", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", resourceColumns: "resourceColumns", resourceImageExtension: "resourceImageExtension", resourceImagePath: "resourceImagePath", resourceTimeRangeRenderer: "resourceTimeRangeRenderer", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", schedulingIssueResolutionPopupClass: "schedulingIssueResolutionPopupClass", scrollerClass: "scrollerClass", scrollManager: "scrollManager", showCreationTooltip: "showCreationTooltip", showDirty: "showDirty", showEventColorPickers: "showEventColorPickers", showLagInTooltip: "showLagInTooltip", showOnClick: "showOnClick", showOnHover: "showOnHover", showRecurringUI: "showRecurringUI", showTaskColorPickers: "showTaskColorPickers", showTooltip: "showTooltip", snapRelativeToEventStartDate: "snapRelativeToEventStartDate", startParamName: "startParamName", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", stepUnit: "stepUnit", stickyHeaders: "stickyHeaders", strips: "strips", subGridConfigs: "subGridConfigs", suppressFit: "suppressFit", syncMask: "syncMask", tab: "tab", tbar: "tbar", terminalCls: "terminalCls", terminalSides: "terminalSides", timeAxis: "timeAxis", trackMouse: "trackMouse", triggerSelectionChangeOnRemove: "triggerSelectionChangeOnRemove", ui: "ui", verticalTimeAxisColumn: "verticalTimeAxisColumn", visibleZoomFactor: "visibleZoomFactor", weekStartDay: "weekStartDay", weight: "weight", zoomKeepsOriginalTimespan: "zoomKeepsOriginalTimespan", zoomOnMouseWheel: "zoomOnMouseWheel", zoomOnTimeAxisDoubleClick: "zoomOnTimeAxisDoubleClick", alignSelf: "alignSelf", allowDropOnEventBar: "allowDropOnEventBar", allowOverlap: "allowOverlap", appendTo: "appendTo", ariaLive: "ariaLive", assignments: "assignments", assignmentStore: "assignmentStore", barMargin: "barMargin", calendars: "calendars", cls: "cls", columnLines: "columnLines", columns: "columns", creationTooltipTemplate: "creationTooltipTemplate", crudManager: "crudManager", data: "data", dataset: "dataset", dateFormat: "dateFormat", dateSeparator: "dateSeparator", dependencies: "dependencies", dependencyStore: "dependencyStore", descriptionFormat: "descriptionFormat", disabled: "disabled", displayDateFormat: "displayDateFormat", emptyText: "emptyText", enableEventAnimations: "enableEventAnimations", enableUndoRedoKeys: "enableUndoRedoKeys", endDate: "endDate", eventColor: "eventColor", eventLayout: "eventLayout", events: "events", eventStore: "eventStore", eventStyle: "eventStyle", extraData: "extraData", fillTicks: "fillTicks", flex: "flex", forceFit: "forceFit", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hideRowHover: "hideRowHover", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", isEventSelectable: "isEventSelectable", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", maxZoomLevel: "maxZoomLevel", milestoneAlign: "milestoneAlign", milestoneCharWidth: "milestoneCharWidth", milestoneLayoutMode: "milestoneLayoutMode", milestoneTextPosition: "milestoneTextPosition", minHeight: "minHeight", minWidth: "minWidth", minZoomLevel: "minZoomLevel", overlappingEventSorter: "overlappingEventSorter", preserveScroll: "preserveScroll", presets: "presets", project: "project", readOnly: "readOnly", resourceMargin: "resourceMargin", resources: "resources", resourceStore: "resourceStore", resourceTimeRanges: "resourceTimeRanges", resourceTimeRangeStore: "resourceTimeRangeStore", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", selectResourceOnEventNavigate: "selectResourceOnEventNavigate", selectResourceOnScheduleClick: "selectResourceOnScheduleClick", snap: "snap", startDate: "startDate", store: "store", terminalHideDelay: "terminalHideDelay", terminalOffset: "terminalOffset", terminalShowDelay: "terminalShowDelay", terminalSize: "terminalSize", tickSize: "tickSize", timeRanges: "timeRanges", timeRangeStore: "timeRangeStore", timeZone: "timeZone", title: "title", tools: "tools", tooltipTemplate: "tooltipTemplate", transitionDuration: "transitionDuration", updateTimelineContextOnScroll: "updateTimelineContextOnScroll", useInitialAnimation: "useInitialAnimation", viewPreset: "viewPreset", visibleDate: "visibleDate", width: "width", workingTime: "workingTime", accessibility: "accessibility", isEngineReady: "isEngineReady", scrollLeft: "scrollLeft", scrollTop: "scrollTop", scrollX: "scrollX", selectedAssignments: "selectedAssignments", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedEvents: "selectedEvents", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", timeResolution: "timeResolution", tooltip: "tooltip", zoomLevel: "zoomLevel", calendarHighlightFeature: "calendarHighlightFeature", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnLinesFeature: "columnLinesFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", dependenciesFeature: "dependenciesFeature", dependencyEditFeature: "dependencyEditFeature", eventBufferFeature: "eventBufferFeature", eventCopyPasteFeature: "eventCopyPasteFeature", eventDragFeature: "eventDragFeature", eventDragCreateFeature: "eventDragCreateFeature", eventDragSelectFeature: "eventDragSelectFeature", eventEditFeature: "eventEditFeature", eventFilterFeature: "eventFilterFeature", eventMenuFeature: "eventMenuFeature", eventNonWorkingTimeFeature: "eventNonWorkingTimeFeature", eventResizeFeature: "eventResizeFeature", eventSegmentDragFeature: "eventSegmentDragFeature", eventSegmentResizeFeature: "eventSegmentResizeFeature", eventSegmentsFeature: "eventSegmentsFeature", eventTooltipFeature: "eventTooltipFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", headerZoomFeature: "headerZoomFeature", labelsFeature: "labelsFeature", mergeCellsFeature: "mergeCellsFeature", nestedEventsFeature: "nestedEventsFeature", nonWorkingTimeFeature: "nonWorkingTimeFeature", panFeature: "panFeature", pdfExportFeature: "pdfExportFeature", percentBarFeature: "percentBarFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", resourceMenuFeature: "resourceMenuFeature", resourceNonWorkingTimeFeature: "resourceNonWorkingTimeFeature", resourceTimeRangesFeature: "resourceTimeRangesFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", scheduleContextFeature: "scheduleContextFeature", scheduleMenuFeature: "scheduleMenuFeature", scheduleTooltipFeature: "scheduleTooltipFeature", searchFeature: "searchFeature", simpleEventEditFeature: "simpleEventEditFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stickyEventsFeature: "stickyEventsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", taskEditFeature: "taskEditFeature", timeAxisHeaderMenuFeature: "timeAxisHeaderMenuFeature", timeRangesFeature: "timeRangesFeature", timeSelectionFeature: "timeSelectionFeature", timeSpanHighlightFeature: "timeSpanHighlightFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature", versionsFeature: "versionsFeature" }, outputs: { onAfterDependencyCreateDrop: "onAfterDependencyCreateDrop", onAfterDependencySave: "onAfterDependencySave", onAfterDragCreate: "onAfterDragCreate", onAfterEventDrop: "onAfterEventDrop", onAfterEventEdit: "onAfterEventEdit", onAfterEventSave: "onAfterEventSave", onAfterEventSegmentDrop: "onAfterEventSegmentDrop", onAfterTaskEdit: "onAfterTaskEdit", onAfterTaskSave: "onAfterTaskSave", onAssignmentSelectionChange: "onAssignmentSelectionChange", onBeforeAssignmentDelete: "onBeforeAssignmentDelete", onBeforeAssignmentSelectionChange: "onBeforeAssignmentSelectionChange", onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDependencyAdd: "onBeforeDependencyAdd", onBeforeDependencyCreateDrag: "onBeforeDependencyCreateDrag", onBeforeDependencyCreateFinalize: "onBeforeDependencyCreateFinalize", onBeforeDependencyDelete: "onBeforeDependencyDelete", onBeforeDependencyEdit: "onBeforeDependencyEdit", onBeforeDependencyEditShow: "onBeforeDependencyEditShow", onBeforeDependencySave: "onBeforeDependencySave", onBeforeDestroy: "onBeforeDestroy", onBeforeDragCreate: "onBeforeDragCreate", onBeforeDragCreateFinalize: "onBeforeDragCreateFinalize", onBeforeEventAdd: "onBeforeEventAdd", onBeforeEventDelete: "onBeforeEventDelete", onBeforeEventDrag: "onBeforeEventDrag", onBeforeEventDragSelect: "onBeforeEventDragSelect", onBeforeEventDropFinalize: "onBeforeEventDropFinalize", onBeforeEventEdit: "onBeforeEventEdit", onBeforeEventEditShow: "onBeforeEventEditShow", onBeforeEventResize: "onBeforeEventResize", onBeforeEventResizeFinalize: "onBeforeEventResizeFinalize", onBeforeEventSave: "onBeforeEventSave", onBeforeEventSegmentDrag: "onBeforeEventSegmentDrag", onBeforeEventSegmentDropFinalize: "onBeforeEventSegmentDropFinalize", onBeforeEventSegmentResize: "onBeforeEventSegmentResize", onBeforeEventSegmentResizeFinalize: "onBeforeEventSegmentResizeFinalize", onBeforeEventSelectionChange: "onBeforeEventSelectionChange", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforePan: "onBeforePan", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforePresetChange: "onBeforePresetChange", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeShowTerminals: "onBeforeShowTerminals", onBeforeTaskDelete: "onBeforeTaskDelete", onBeforeTaskEdit: "onBeforeTaskEdit", onBeforeTaskEditShow: "onBeforeTaskEditShow", onBeforeTaskSave: "onBeforeTaskSave", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDateRangeChange: "onDateRangeChange", onDependenciesDrawn: "onDependenciesDrawn", onDependencyClick: "onDependencyClick", onDependencyContextMenu: "onDependencyContextMenu", onDependencyCreateDragStart: "onDependencyCreateDragStart", onDependencyCreateDrop: "onDependencyCreateDrop", onDependencyDblClick: "onDependencyDblClick", onDependencyMouseOut: "onDependencyMouseOut", onDependencyMouseOver: "onDependencyMouseOver", onDependencyValidationComplete: "onDependencyValidationComplete", onDependencyValidationStart: "onDependencyValidationStart", onDestroy: "onDestroy", onDragCreateEnd: "onDragCreateEnd", onDragCreateStart: "onDragCreateStart", onDragSelecting: "onDragSelecting", onEventAutoCreated: "onEventAutoCreated", onEventClick: "onEventClick", onEventContextMenu: "onEventContextMenu", onEventDblClick: "onEventDblClick", onEventDrag: "onEventDrag", onEventDragAbort: "onEventDragAbort", onEventDragModeChange: "onEventDragModeChange", onEventDragReset: "onEventDragReset", onEventDragStart: "onEventDragStart", onEventDrop: "onEventDrop", onEventEditBeforeSetRecord: "onEventEditBeforeSetRecord", onEventKeyDown: "onEventKeyDown", onEventKeyUp: "onEventKeyUp", onEventMenuBeforeShow: "onEventMenuBeforeShow", onEventMenuItem: "onEventMenuItem", onEventMenuShow: "onEventMenuShow", onEventMouseDown: "onEventMouseDown", onEventMouseEnter: "onEventMouseEnter", onEventMouseLeave: "onEventMouseLeave", onEventMouseOut: "onEventMouseOut", onEventMouseOver: "onEventMouseOver", onEventMouseUp: "onEventMouseUp", onEventPartialResize: "onEventPartialResize", onEventResizeEnd: "onEventResizeEnd", onEventResizeStart: "onEventResizeStart", onEventSegmentDrag: "onEventSegmentDrag", onEventSegmentDragAbort: "onEventSegmentDragAbort", onEventSegmentDragReset: "onEventSegmentDragReset", onEventSegmentDragStart: "onEventSegmentDragStart", onEventSegmentDrop: "onEventSegmentDrop", onEventSegmentPartialResize: "onEventSegmentPartialResize", onEventSegmentResizeEnd: "onEventSegmentResizeEnd", onEventSegmentResizeStart: "onEventSegmentResizeStart", onEventSelectionChange: "onEventSelectionChange", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onNavigate: "onNavigate", onPaint: "onPaint", onPaste: "onPaste", onPasteNotAllowed: "onPasteNotAllowed", onPdfExport: "onPdfExport", onPercentBarDrag: "onPercentBarDrag", onPercentBarDragAbort: "onPercentBarDragAbort", onPercentBarDragStart: "onPercentBarDragStart", onPercentBarDrop: "onPercentBarDrop", onPointerOver: "onPointerOver", onPresetChange: "onPresetChange", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onReleaseEvent: "onReleaseEvent", onRenderEvent: "onRenderEvent", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResourceHeaderClick: "onResourceHeaderClick", onResourceHeaderContextmenu: "onResourceHeaderContextmenu", onResourceHeaderDblclick: "onResourceHeaderDblclick", onResourceMenuBeforeShow: "onResourceMenuBeforeShow", onResourceMenuItem: "onResourceMenuItem", onResourceMenuShow: "onResourceMenuShow", onResourceMenuToggleItem: "onResourceMenuToggleItem", onResourceNonWorkingTimeClick: "onResourceNonWorkingTimeClick", onResourceNonWorkingTimeContextMenu: "onResourceNonWorkingTimeContextMenu", onResourceNonWorkingTimeDblClick: "onResourceNonWorkingTimeDblClick", onResourceNonWorkingTimeMouseDown: "onResourceNonWorkingTimeMouseDown", onResourceNonWorkingTimeMouseOut: "onResourceNonWorkingTimeMouseOut", onResourceNonWorkingTimeMouseOver: "onResourceNonWorkingTimeMouseOver", onResourceNonWorkingTimeMouseUp: "onResourceNonWorkingTimeMouseUp", onResourceTimeRangeClick: "onResourceTimeRangeClick", onResourceTimeRangeContextMenu: "onResourceTimeRangeContextMenu", onResourceTimeRangeDblClick: "onResourceTimeRangeDblClick", onResourceTimeRangeMouseDown: "onResourceTimeRangeMouseDown", onResourceTimeRangeMouseOut: "onResourceTimeRangeMouseOut", onResourceTimeRangeMouseOver: "onResourceTimeRangeMouseOver", onResourceTimeRangeMouseUp: "onResourceTimeRangeMouseUp", onResponsive: "onResponsive", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScheduleClick: "onScheduleClick", onScheduleContextMenu: "onScheduleContextMenu", onScheduleDblClick: "onScheduleDblClick", onScheduleMenuBeforeShow: "onScheduleMenuBeforeShow", onScheduleMenuItem: "onScheduleMenuItem", onScheduleMenuShow: "onScheduleMenuShow", onScheduleMouseDown: "onScheduleMouseDown", onScheduleMouseEnter: "onScheduleMouseEnter", onScheduleMouseLeave: "onScheduleMouseLeave", onScheduleMouseMove: "onScheduleMouseMove", onScheduleMouseUp: "onScheduleMouseUp", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onTaskEditCanceled: "onTaskEditCanceled", onTimeAxisChange: "onTimeAxisChange", onTimeAxisHeaderClick: "onTimeAxisHeaderClick", onTimeAxisHeaderContextMenu: "onTimeAxisHeaderContextMenu", onTimeAxisHeaderDblClick: "onTimeAxisHeaderDblClick", onTimeAxisHeaderMenuBeforeShow: "onTimeAxisHeaderMenuBeforeShow", onTimeAxisHeaderMenuItem: "onTimeAxisHeaderMenuItem", onTimeAxisHeaderMenuShow: "onTimeAxisHeaderMenuShow", onTimelineContextChange: "onTimelineContextChange", onTimelineViewportResize: "onTimelineViewportResize", onTimeRangeHeaderClick: "onTimeRangeHeaderClick", onTimeRangeHeaderContextMenu: "onTimeRangeHeaderContextMenu", onTimeRangeHeaderDblClick: "onTimeRangeHeaderDblClick", onTimeSelectionChange: "onTimeSelectionChange", onTimeSelectionElementClick: "onTimeSelectionElementClick", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onTransactionChange: "onTransactionChange", onUnsplit: "onUnsplit", onVisibleDateRangeChange: "onVisibleDateRangeChange" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerProComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-scheduler-pro',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], allowCreate: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoAdjustTimeAxis: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], bufferCoef: [{
                type: Input
            }], bufferThreshold: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], createEventOnDblClick: [{
                type: Input
            }], creationTooltip: [{
                type: Input
            }], crudManagerClass: [{
                type: Input
            }], cycleResolutionPopupClass: [{
                type: Input
            }], date: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], defaultResourceImageName: [{
                type: Input
            }], dependencyIdField: [{
                type: Input
            }], descriptionRenderer: [{
                type: Input
            }], deselectAllOnScheduleClick: [{
                type: Input
            }], deselectOnClick: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], destroyStores: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], displaySchedulingIssueResolutionPopup: [{
                type: Input
            }], dock: [{
                type: Input
            }], durationDisplayPrecision: [{
                type: Input
            }], enableDeleteKey: [{
                type: Input
            }], enableRecurringEvents: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], endParamName: [{
                type: Input
            }], eventBarTextField: [{
                type: Input
            }], eventBodyTemplate: [{
                type: Input
            }], eventRenderer: [{
                type: Input
            }], eventRendererThisObj: [{
                type: Input
            }], eventSelectionDisabled: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getDateConstraints: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], highlightPredecessors: [{
                type: Input
            }], highlightSuccessors: [{
                type: Input
            }], horizontalEventSorterFn: [{
                type: Input
            }], ignoreDomEventsWhileScrolling: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], infiniteScroll: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], maintainSelectionOnDatasetChange: [{
                type: Input
            }], managedEventSizing: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], maxTimeAxisUnit: [{
                type: Input
            }], modal: [{
                type: Input
            }], mode: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], multiEventSelect: [{
                type: Input
            }], narrowEventWidth: [{
                type: Input
            }], owner: [{
                type: Input
            }], partner: [{
                type: Input
            }], passStartEndParameters: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preCalculateHeightLimit: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], projectProgressReporting: [{
                type: Input
            }], range: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], resourceColumns: [{
                type: Input
            }], resourceImageExtension: [{
                type: Input
            }], resourceImagePath: [{
                type: Input
            }], resourceTimeRangeRenderer: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], schedulingIssueResolutionPopupClass: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], showCreationTooltip: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showEventColorPickers: [{
                type: Input
            }], showLagInTooltip: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], showRecurringUI: [{
                type: Input
            }], showTaskColorPickers: [{
                type: Input
            }], showTooltip: [{
                type: Input
            }], snapRelativeToEventStartDate: [{
                type: Input
            }], startParamName: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], stepUnit: [{
                type: Input
            }], stickyHeaders: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], suppressFit: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], terminalCls: [{
                type: Input
            }], terminalSides: [{
                type: Input
            }], timeAxis: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], triggerSelectionChangeOnRemove: [{
                type: Input
            }], ui: [{
                type: Input
            }], verticalTimeAxisColumn: [{
                type: Input
            }], visibleZoomFactor: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], zoomKeepsOriginalTimespan: [{
                type: Input
            }], zoomOnMouseWheel: [{
                type: Input
            }], zoomOnTimeAxisDoubleClick: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], allowDropOnEventBar: [{
                type: Input
            }], allowOverlap: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], assignments: [{
                type: Input
            }], assignmentStore: [{
                type: Input
            }], barMargin: [{
                type: Input
            }], calendars: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], creationTooltipTemplate: [{
                type: Input
            }], crudManager: [{
                type: Input
            }], data: [{
                type: Input
            }], dataset: [{
                type: Input
            }], dateFormat: [{
                type: Input
            }], dateSeparator: [{
                type: Input
            }], dependencies: [{
                type: Input
            }], dependencyStore: [{
                type: Input
            }], descriptionFormat: [{
                type: Input
            }], disabled: [{
                type: Input
            }], displayDateFormat: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableEventAnimations: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], endDate: [{
                type: Input
            }], eventColor: [{
                type: Input
            }], eventLayout: [{
                type: Input
            }], events: [{
                type: Input
            }], eventStore: [{
                type: Input
            }], eventStyle: [{
                type: Input
            }], extraData: [{
                type: Input
            }], fillTicks: [{
                type: Input
            }], flex: [{
                type: Input
            }], forceFit: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hideRowHover: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], isEventSelectable: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], maxZoomLevel: [{
                type: Input
            }], milestoneAlign: [{
                type: Input
            }], milestoneCharWidth: [{
                type: Input
            }], milestoneLayoutMode: [{
                type: Input
            }], milestoneTextPosition: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], minZoomLevel: [{
                type: Input
            }], overlappingEventSorter: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], presets: [{
                type: Input
            }], project: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], resourceMargin: [{
                type: Input
            }], resources: [{
                type: Input
            }], resourceStore: [{
                type: Input
            }], resourceTimeRanges: [{
                type: Input
            }], resourceTimeRangeStore: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], selectResourceOnEventNavigate: [{
                type: Input
            }], selectResourceOnScheduleClick: [{
                type: Input
            }], snap: [{
                type: Input
            }], startDate: [{
                type: Input
            }], store: [{
                type: Input
            }], terminalHideDelay: [{
                type: Input
            }], terminalOffset: [{
                type: Input
            }], terminalShowDelay: [{
                type: Input
            }], terminalSize: [{
                type: Input
            }], tickSize: [{
                type: Input
            }], timeRanges: [{
                type: Input
            }], timeRangeStore: [{
                type: Input
            }], timeZone: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltipTemplate: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], updateTimelineContextOnScroll: [{
                type: Input
            }], useInitialAnimation: [{
                type: Input
            }], viewPreset: [{
                type: Input
            }], visibleDate: [{
                type: Input
            }], width: [{
                type: Input
            }], workingTime: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], isEngineReady: [{
                type: Input
            }], scrollLeft: [{
                type: Input
            }], scrollTop: [{
                type: Input
            }], scrollX: [{
                type: Input
            }], selectedAssignments: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedEvents: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], timeResolution: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], zoomLevel: [{
                type: Input
            }], onAfterDependencyCreateDrop: [{
                type: Output
            }], onAfterDependencySave: [{
                type: Output
            }], onAfterDragCreate: [{
                type: Output
            }], onAfterEventDrop: [{
                type: Output
            }], onAfterEventEdit: [{
                type: Output
            }], onAfterEventSave: [{
                type: Output
            }], onAfterEventSegmentDrop: [{
                type: Output
            }], onAfterTaskEdit: [{
                type: Output
            }], onAfterTaskSave: [{
                type: Output
            }], onAssignmentSelectionChange: [{
                type: Output
            }], onBeforeAssignmentDelete: [{
                type: Output
            }], onBeforeAssignmentSelectionChange: [{
                type: Output
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDependencyAdd: [{
                type: Output
            }], onBeforeDependencyCreateDrag: [{
                type: Output
            }], onBeforeDependencyCreateFinalize: [{
                type: Output
            }], onBeforeDependencyDelete: [{
                type: Output
            }], onBeforeDependencyEdit: [{
                type: Output
            }], onBeforeDependencyEditShow: [{
                type: Output
            }], onBeforeDependencySave: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeDragCreate: [{
                type: Output
            }], onBeforeDragCreateFinalize: [{
                type: Output
            }], onBeforeEventAdd: [{
                type: Output
            }], onBeforeEventDelete: [{
                type: Output
            }], onBeforeEventDrag: [{
                type: Output
            }], onBeforeEventDragSelect: [{
                type: Output
            }], onBeforeEventDropFinalize: [{
                type: Output
            }], onBeforeEventEdit: [{
                type: Output
            }], onBeforeEventEditShow: [{
                type: Output
            }], onBeforeEventResize: [{
                type: Output
            }], onBeforeEventResizeFinalize: [{
                type: Output
            }], onBeforeEventSave: [{
                type: Output
            }], onBeforeEventSegmentDrag: [{
                type: Output
            }], onBeforeEventSegmentDropFinalize: [{
                type: Output
            }], onBeforeEventSegmentResize: [{
                type: Output
            }], onBeforeEventSegmentResizeFinalize: [{
                type: Output
            }], onBeforeEventSelectionChange: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforePan: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforePresetChange: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeShowTerminals: [{
                type: Output
            }], onBeforeTaskDelete: [{
                type: Output
            }], onBeforeTaskEdit: [{
                type: Output
            }], onBeforeTaskEditShow: [{
                type: Output
            }], onBeforeTaskSave: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDateRangeChange: [{
                type: Output
            }], onDependenciesDrawn: [{
                type: Output
            }], onDependencyClick: [{
                type: Output
            }], onDependencyContextMenu: [{
                type: Output
            }], onDependencyCreateDragStart: [{
                type: Output
            }], onDependencyCreateDrop: [{
                type: Output
            }], onDependencyDblClick: [{
                type: Output
            }], onDependencyMouseOut: [{
                type: Output
            }], onDependencyMouseOver: [{
                type: Output
            }], onDependencyValidationComplete: [{
                type: Output
            }], onDependencyValidationStart: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragCreateEnd: [{
                type: Output
            }], onDragCreateStart: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onEventAutoCreated: [{
                type: Output
            }], onEventClick: [{
                type: Output
            }], onEventContextMenu: [{
                type: Output
            }], onEventDblClick: [{
                type: Output
            }], onEventDrag: [{
                type: Output
            }], onEventDragAbort: [{
                type: Output
            }], onEventDragModeChange: [{
                type: Output
            }], onEventDragReset: [{
                type: Output
            }], onEventDragStart: [{
                type: Output
            }], onEventDrop: [{
                type: Output
            }], onEventEditBeforeSetRecord: [{
                type: Output
            }], onEventKeyDown: [{
                type: Output
            }], onEventKeyUp: [{
                type: Output
            }], onEventMenuBeforeShow: [{
                type: Output
            }], onEventMenuItem: [{
                type: Output
            }], onEventMenuShow: [{
                type: Output
            }], onEventMouseDown: [{
                type: Output
            }], onEventMouseEnter: [{
                type: Output
            }], onEventMouseLeave: [{
                type: Output
            }], onEventMouseOut: [{
                type: Output
            }], onEventMouseOver: [{
                type: Output
            }], onEventMouseUp: [{
                type: Output
            }], onEventPartialResize: [{
                type: Output
            }], onEventResizeEnd: [{
                type: Output
            }], onEventResizeStart: [{
                type: Output
            }], onEventSegmentDrag: [{
                type: Output
            }], onEventSegmentDragAbort: [{
                type: Output
            }], onEventSegmentDragReset: [{
                type: Output
            }], onEventSegmentDragStart: [{
                type: Output
            }], onEventSegmentDrop: [{
                type: Output
            }], onEventSegmentPartialResize: [{
                type: Output
            }], onEventSegmentResizeEnd: [{
                type: Output
            }], onEventSegmentResizeStart: [{
                type: Output
            }], onEventSelectionChange: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onNavigate: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPasteNotAllowed: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPercentBarDrag: [{
                type: Output
            }], onPercentBarDragAbort: [{
                type: Output
            }], onPercentBarDragStart: [{
                type: Output
            }], onPercentBarDrop: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onPresetChange: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onReleaseEvent: [{
                type: Output
            }], onRenderEvent: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResourceHeaderClick: [{
                type: Output
            }], onResourceHeaderContextmenu: [{
                type: Output
            }], onResourceHeaderDblclick: [{
                type: Output
            }], onResourceMenuBeforeShow: [{
                type: Output
            }], onResourceMenuItem: [{
                type: Output
            }], onResourceMenuShow: [{
                type: Output
            }], onResourceMenuToggleItem: [{
                type: Output
            }], onResourceNonWorkingTimeClick: [{
                type: Output
            }], onResourceNonWorkingTimeContextMenu: [{
                type: Output
            }], onResourceNonWorkingTimeDblClick: [{
                type: Output
            }], onResourceNonWorkingTimeMouseDown: [{
                type: Output
            }], onResourceNonWorkingTimeMouseOut: [{
                type: Output
            }], onResourceNonWorkingTimeMouseOver: [{
                type: Output
            }], onResourceNonWorkingTimeMouseUp: [{
                type: Output
            }], onResourceTimeRangeClick: [{
                type: Output
            }], onResourceTimeRangeContextMenu: [{
                type: Output
            }], onResourceTimeRangeDblClick: [{
                type: Output
            }], onResourceTimeRangeMouseDown: [{
                type: Output
            }], onResourceTimeRangeMouseOut: [{
                type: Output
            }], onResourceTimeRangeMouseOver: [{
                type: Output
            }], onResourceTimeRangeMouseUp: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScheduleClick: [{
                type: Output
            }], onScheduleContextMenu: [{
                type: Output
            }], onScheduleDblClick: [{
                type: Output
            }], onScheduleMenuBeforeShow: [{
                type: Output
            }], onScheduleMenuItem: [{
                type: Output
            }], onScheduleMenuShow: [{
                type: Output
            }], onScheduleMouseDown: [{
                type: Output
            }], onScheduleMouseEnter: [{
                type: Output
            }], onScheduleMouseLeave: [{
                type: Output
            }], onScheduleMouseMove: [{
                type: Output
            }], onScheduleMouseUp: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onTaskEditCanceled: [{
                type: Output
            }], onTimeAxisChange: [{
                type: Output
            }], onTimeAxisHeaderClick: [{
                type: Output
            }], onTimeAxisHeaderContextMenu: [{
                type: Output
            }], onTimeAxisHeaderDblClick: [{
                type: Output
            }], onTimeAxisHeaderMenuBeforeShow: [{
                type: Output
            }], onTimeAxisHeaderMenuItem: [{
                type: Output
            }], onTimeAxisHeaderMenuShow: [{
                type: Output
            }], onTimelineContextChange: [{
                type: Output
            }], onTimelineViewportResize: [{
                type: Output
            }], onTimeRangeHeaderClick: [{
                type: Output
            }], onTimeRangeHeaderContextMenu: [{
                type: Output
            }], onTimeRangeHeaderDblClick: [{
                type: Output
            }], onTimeSelectionChange: [{
                type: Output
            }], onTimeSelectionElementClick: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onTransactionChange: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], onVisibleDateRangeChange: [{
                type: Output
            }], calendarHighlightFeature: [{
                type: Input
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnLinesFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], dependenciesFeature: [{
                type: Input
            }], dependencyEditFeature: [{
                type: Input
            }], eventBufferFeature: [{
                type: Input
            }], eventCopyPasteFeature: [{
                type: Input
            }], eventDragFeature: [{
                type: Input
            }], eventDragCreateFeature: [{
                type: Input
            }], eventDragSelectFeature: [{
                type: Input
            }], eventEditFeature: [{
                type: Input
            }], eventFilterFeature: [{
                type: Input
            }], eventMenuFeature: [{
                type: Input
            }], eventNonWorkingTimeFeature: [{
                type: Input
            }], eventResizeFeature: [{
                type: Input
            }], eventSegmentDragFeature: [{
                type: Input
            }], eventSegmentResizeFeature: [{
                type: Input
            }], eventSegmentsFeature: [{
                type: Input
            }], eventTooltipFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], headerZoomFeature: [{
                type: Input
            }], labelsFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], nestedEventsFeature: [{
                type: Input
            }], nonWorkingTimeFeature: [{
                type: Input
            }], panFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], percentBarFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], resourceMenuFeature: [{
                type: Input
            }], resourceNonWorkingTimeFeature: [{
                type: Input
            }], resourceTimeRangesFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], scheduleContextFeature: [{
                type: Input
            }], scheduleMenuFeature: [{
                type: Input
            }], scheduleTooltipFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], simpleEventEditFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stickyEventsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], taskEditFeature: [{
                type: Input
            }], timeAxisHeaderMenuFeature: [{
                type: Input
            }], timeRangesFeature: [{
                type: Input
            }], timeSelectionFeature: [{
                type: Input
            }], timeSpanHighlightFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }], versionsFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum SchedulerProBase
 */
class BryntumSchedulerProBaseComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAfterDependencyCreateDrop = new EventEmitter();
        this.onAfterDependencySave = new EventEmitter();
        this.onAfterDragCreate = new EventEmitter();
        this.onAfterEventDrop = new EventEmitter();
        this.onAfterEventEdit = new EventEmitter();
        this.onAfterEventSave = new EventEmitter();
        this.onAfterEventSegmentDrop = new EventEmitter();
        this.onAfterTaskEdit = new EventEmitter();
        this.onAfterTaskSave = new EventEmitter();
        this.onAssignmentSelectionChange = new EventEmitter();
        this.onBeforeAssignmentDelete = new EventEmitter();
        this.onBeforeAssignmentSelectionChange = new EventEmitter();
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDependencyAdd = new EventEmitter();
        this.onBeforeDependencyCreateDrag = new EventEmitter();
        this.onBeforeDependencyCreateFinalize = new EventEmitter();
        this.onBeforeDependencyDelete = new EventEmitter();
        this.onBeforeDependencyEdit = new EventEmitter();
        this.onBeforeDependencyEditShow = new EventEmitter();
        this.onBeforeDependencySave = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeDragCreate = new EventEmitter();
        this.onBeforeDragCreateFinalize = new EventEmitter();
        this.onBeforeEventAdd = new EventEmitter();
        this.onBeforeEventDelete = new EventEmitter();
        this.onBeforeEventDrag = new EventEmitter();
        this.onBeforeEventDragSelect = new EventEmitter();
        this.onBeforeEventDropFinalize = new EventEmitter();
        this.onBeforeEventEdit = new EventEmitter();
        this.onBeforeEventEditShow = new EventEmitter();
        this.onBeforeEventResize = new EventEmitter();
        this.onBeforeEventResizeFinalize = new EventEmitter();
        this.onBeforeEventSave = new EventEmitter();
        this.onBeforeEventSegmentDrag = new EventEmitter();
        this.onBeforeEventSegmentDropFinalize = new EventEmitter();
        this.onBeforeEventSegmentResize = new EventEmitter();
        this.onBeforeEventSegmentResizeFinalize = new EventEmitter();
        this.onBeforeEventSelectionChange = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforePan = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforePresetChange = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeShowTerminals = new EventEmitter();
        this.onBeforeTaskDelete = new EventEmitter();
        this.onBeforeTaskEdit = new EventEmitter();
        this.onBeforeTaskEditShow = new EventEmitter();
        this.onBeforeTaskSave = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDateRangeChange = new EventEmitter();
        this.onDependenciesDrawn = new EventEmitter();
        this.onDependencyClick = new EventEmitter();
        this.onDependencyContextMenu = new EventEmitter();
        this.onDependencyCreateDragStart = new EventEmitter();
        this.onDependencyCreateDrop = new EventEmitter();
        this.onDependencyDblClick = new EventEmitter();
        this.onDependencyMouseOut = new EventEmitter();
        this.onDependencyMouseOver = new EventEmitter();
        this.onDependencyValidationComplete = new EventEmitter();
        this.onDependencyValidationStart = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragCreateEnd = new EventEmitter();
        this.onDragCreateStart = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onEventAutoCreated = new EventEmitter();
        this.onEventClick = new EventEmitter();
        this.onEventContextMenu = new EventEmitter();
        this.onEventDblClick = new EventEmitter();
        this.onEventDrag = new EventEmitter();
        this.onEventDragAbort = new EventEmitter();
        this.onEventDragModeChange = new EventEmitter();
        this.onEventDragReset = new EventEmitter();
        this.onEventDragStart = new EventEmitter();
        this.onEventDrop = new EventEmitter();
        this.onEventEditBeforeSetRecord = new EventEmitter();
        this.onEventKeyDown = new EventEmitter();
        this.onEventKeyUp = new EventEmitter();
        this.onEventMenuBeforeShow = new EventEmitter();
        this.onEventMenuItem = new EventEmitter();
        this.onEventMenuShow = new EventEmitter();
        this.onEventMouseDown = new EventEmitter();
        this.onEventMouseEnter = new EventEmitter();
        this.onEventMouseLeave = new EventEmitter();
        this.onEventMouseOut = new EventEmitter();
        this.onEventMouseOver = new EventEmitter();
        this.onEventMouseUp = new EventEmitter();
        this.onEventPartialResize = new EventEmitter();
        this.onEventResizeEnd = new EventEmitter();
        this.onEventResizeStart = new EventEmitter();
        this.onEventSegmentDrag = new EventEmitter();
        this.onEventSegmentDragAbort = new EventEmitter();
        this.onEventSegmentDragReset = new EventEmitter();
        this.onEventSegmentDragStart = new EventEmitter();
        this.onEventSegmentDrop = new EventEmitter();
        this.onEventSegmentPartialResize = new EventEmitter();
        this.onEventSegmentResizeEnd = new EventEmitter();
        this.onEventSegmentResizeStart = new EventEmitter();
        this.onEventSelectionChange = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onNavigate = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPasteNotAllowed = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPercentBarDrag = new EventEmitter();
        this.onPercentBarDragAbort = new EventEmitter();
        this.onPercentBarDragStart = new EventEmitter();
        this.onPercentBarDrop = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onPresetChange = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onReleaseEvent = new EventEmitter();
        this.onRenderEvent = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResourceHeaderClick = new EventEmitter();
        this.onResourceHeaderContextmenu = new EventEmitter();
        this.onResourceHeaderDblclick = new EventEmitter();
        this.onResourceMenuBeforeShow = new EventEmitter();
        this.onResourceMenuItem = new EventEmitter();
        this.onResourceMenuShow = new EventEmitter();
        this.onResourceMenuToggleItem = new EventEmitter();
        this.onResourceNonWorkingTimeClick = new EventEmitter();
        this.onResourceNonWorkingTimeContextMenu = new EventEmitter();
        this.onResourceNonWorkingTimeDblClick = new EventEmitter();
        this.onResourceNonWorkingTimeMouseDown = new EventEmitter();
        this.onResourceNonWorkingTimeMouseOut = new EventEmitter();
        this.onResourceNonWorkingTimeMouseOver = new EventEmitter();
        this.onResourceNonWorkingTimeMouseUp = new EventEmitter();
        this.onResourceTimeRangeClick = new EventEmitter();
        this.onResourceTimeRangeContextMenu = new EventEmitter();
        this.onResourceTimeRangeDblClick = new EventEmitter();
        this.onResourceTimeRangeMouseDown = new EventEmitter();
        this.onResourceTimeRangeMouseOut = new EventEmitter();
        this.onResourceTimeRangeMouseOver = new EventEmitter();
        this.onResourceTimeRangeMouseUp = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScheduleClick = new EventEmitter();
        this.onScheduleContextMenu = new EventEmitter();
        this.onScheduleDblClick = new EventEmitter();
        this.onScheduleMenuBeforeShow = new EventEmitter();
        this.onScheduleMenuItem = new EventEmitter();
        this.onScheduleMenuShow = new EventEmitter();
        this.onScheduleMouseDown = new EventEmitter();
        this.onScheduleMouseEnter = new EventEmitter();
        this.onScheduleMouseLeave = new EventEmitter();
        this.onScheduleMouseMove = new EventEmitter();
        this.onScheduleMouseUp = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onTaskEditCanceled = new EventEmitter();
        this.onTimeAxisChange = new EventEmitter();
        this.onTimeAxisHeaderClick = new EventEmitter();
        this.onTimeAxisHeaderContextMenu = new EventEmitter();
        this.onTimeAxisHeaderDblClick = new EventEmitter();
        this.onTimeAxisHeaderMenuBeforeShow = new EventEmitter();
        this.onTimeAxisHeaderMenuItem = new EventEmitter();
        this.onTimeAxisHeaderMenuShow = new EventEmitter();
        this.onTimelineContextChange = new EventEmitter();
        this.onTimelineViewportResize = new EventEmitter();
        this.onTimeRangeHeaderClick = new EventEmitter();
        this.onTimeRangeHeaderContextMenu = new EventEmitter();
        this.onTimeRangeHeaderDblClick = new EventEmitter();
        this.onTimeSelectionChange = new EventEmitter();
        this.onTimeSelectionElementClick = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onTransactionChange = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.onVisibleDateRangeChange = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSchedulerProBaseComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
        // Backwards compatibility for gridInstance, schedulerInstance etc.
        // @ts-ignore
        me[StringHelper.uncapitalize(instanceName) + 'Instance'] = me.instance;
        // 
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSchedulerProBaseComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSchedulerProBaseComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSchedulerProBaseComponent.instanceClass = SchedulerProBase;
BryntumSchedulerProBaseComponent.instanceName = 'SchedulerProBase';
BryntumSchedulerProBaseComponent.bryntumEvents = [
    'onAfterDependencyCreateDrop',
    'onAfterDependencySave',
    'onAfterDragCreate',
    'onAfterEventDrop',
    'onAfterEventEdit',
    'onAfterEventSave',
    'onAfterEventSegmentDrop',
    'onAfterTaskEdit',
    'onAfterTaskSave',
    'onAssignmentSelectionChange',
    'onBeforeAssignmentDelete',
    'onBeforeAssignmentSelectionChange',
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDependencyAdd',
    'onBeforeDependencyCreateDrag',
    'onBeforeDependencyCreateFinalize',
    'onBeforeDependencyDelete',
    'onBeforeDependencyEdit',
    'onBeforeDependencyEditShow',
    'onBeforeDependencySave',
    'onBeforeDestroy',
    'onBeforeDragCreate',
    'onBeforeDragCreateFinalize',
    'onBeforeEventAdd',
    'onBeforeEventDelete',
    'onBeforeEventDrag',
    'onBeforeEventDragSelect',
    'onBeforeEventDropFinalize',
    'onBeforeEventEdit',
    'onBeforeEventEditShow',
    'onBeforeEventResize',
    'onBeforeEventResizeFinalize',
    'onBeforeEventSave',
    'onBeforeEventSegmentDrag',
    'onBeforeEventSegmentDropFinalize',
    'onBeforeEventSegmentResize',
    'onBeforeEventSegmentResizeFinalize',
    'onBeforeEventSelectionChange',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforePan',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforePresetChange',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeShowTerminals',
    'onBeforeTaskDelete',
    'onBeforeTaskEdit',
    'onBeforeTaskEditShow',
    'onBeforeTaskSave',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDateRangeChange',
    'onDependenciesDrawn',
    'onDependencyClick',
    'onDependencyContextMenu',
    'onDependencyCreateDragStart',
    'onDependencyCreateDrop',
    'onDependencyDblClick',
    'onDependencyMouseOut',
    'onDependencyMouseOver',
    'onDependencyValidationComplete',
    'onDependencyValidationStart',
    'onDestroy',
    'onDragCreateEnd',
    'onDragCreateStart',
    'onDragSelecting',
    'onEventAutoCreated',
    'onEventClick',
    'onEventContextMenu',
    'onEventDblClick',
    'onEventDrag',
    'onEventDragAbort',
    'onEventDragModeChange',
    'onEventDragReset',
    'onEventDragStart',
    'onEventDrop',
    'onEventEditBeforeSetRecord',
    'onEventKeyDown',
    'onEventKeyUp',
    'onEventMenuBeforeShow',
    'onEventMenuItem',
    'onEventMenuShow',
    'onEventMouseDown',
    'onEventMouseEnter',
    'onEventMouseLeave',
    'onEventMouseOut',
    'onEventMouseOver',
    'onEventMouseUp',
    'onEventPartialResize',
    'onEventResizeEnd',
    'onEventResizeStart',
    'onEventSegmentDrag',
    'onEventSegmentDragAbort',
    'onEventSegmentDragReset',
    'onEventSegmentDragStart',
    'onEventSegmentDrop',
    'onEventSegmentPartialResize',
    'onEventSegmentResizeEnd',
    'onEventSegmentResizeStart',
    'onEventSelectionChange',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onMouseOut',
    'onMouseOver',
    'onNavigate',
    'onPaint',
    'onPaste',
    'onPasteNotAllowed',
    'onPdfExport',
    'onPercentBarDrag',
    'onPercentBarDragAbort',
    'onPercentBarDragStart',
    'onPercentBarDrop',
    'onPointerOver',
    'onPresetChange',
    'onRecompose',
    'onReleaseEvent',
    'onRenderEvent',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResourceHeaderClick',
    'onResourceHeaderContextmenu',
    'onResourceHeaderDblclick',
    'onResourceMenuBeforeShow',
    'onResourceMenuItem',
    'onResourceMenuShow',
    'onResourceMenuToggleItem',
    'onResourceNonWorkingTimeClick',
    'onResourceNonWorkingTimeContextMenu',
    'onResourceNonWorkingTimeDblClick',
    'onResourceNonWorkingTimeMouseDown',
    'onResourceNonWorkingTimeMouseOut',
    'onResourceNonWorkingTimeMouseOver',
    'onResourceNonWorkingTimeMouseUp',
    'onResourceTimeRangeClick',
    'onResourceTimeRangeContextMenu',
    'onResourceTimeRangeDblClick',
    'onResourceTimeRangeMouseDown',
    'onResourceTimeRangeMouseOut',
    'onResourceTimeRangeMouseOver',
    'onResourceTimeRangeMouseUp',
    'onResponsive',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScheduleClick',
    'onScheduleContextMenu',
    'onScheduleDblClick',
    'onScheduleMenuBeforeShow',
    'onScheduleMenuItem',
    'onScheduleMenuShow',
    'onScheduleMouseDown',
    'onScheduleMouseEnter',
    'onScheduleMouseLeave',
    'onScheduleMouseMove',
    'onScheduleMouseUp',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onTaskEditCanceled',
    'onTimeAxisChange',
    'onTimeAxisHeaderClick',
    'onTimeAxisHeaderContextMenu',
    'onTimeAxisHeaderDblClick',
    'onTimeAxisHeaderMenuBeforeShow',
    'onTimeAxisHeaderMenuItem',
    'onTimeAxisHeaderMenuShow',
    'onTimelineContextChange',
    'onTimelineViewportResize',
    'onTimeRangeHeaderClick',
    'onTimeRangeHeaderContextMenu',
    'onTimeRangeHeaderDblClick',
    'onTimeSelectionChange',
    'onTimeSelectionElementClick',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onTransactionChange',
    'onUnsplit',
    'onVisibleDateRangeChange'
];
BryntumSchedulerProBaseComponent.bryntumFeatureNames = [
    'calendarHighlightFeature',
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnLinesFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'dependenciesFeature',
    'dependencyEditFeature',
    'eventBufferFeature',
    'eventCopyPasteFeature',
    'eventDragFeature',
    'eventDragCreateFeature',
    'eventDragSelectFeature',
    'eventEditFeature',
    'eventFilterFeature',
    'eventMenuFeature',
    'eventNonWorkingTimeFeature',
    'eventResizeFeature',
    'eventSegmentDragFeature',
    'eventSegmentResizeFeature',
    'eventSegmentsFeature',
    'eventTooltipFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'headerZoomFeature',
    'labelsFeature',
    'mergeCellsFeature',
    'nestedEventsFeature',
    'nonWorkingTimeFeature',
    'panFeature',
    'pdfExportFeature',
    'percentBarFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'resourceMenuFeature',
    'resourceNonWorkingTimeFeature',
    'resourceTimeRangesFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'scheduleContextFeature',
    'scheduleMenuFeature',
    'scheduleTooltipFeature',
    'searchFeature',
    'simpleEventEditFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stickyEventsFeature',
    'stripeFeature',
    'summaryFeature',
    'taskEditFeature',
    'timeAxisHeaderMenuFeature',
    'timeRangesFeature',
    'timeSelectionFeature',
    'timeSpanHighlightFeature',
    'treeFeature',
    'treeGroupFeature',
    'versionsFeature'
];
BryntumSchedulerProBaseComponent.bryntumConfigs = BryntumSchedulerProBaseComponent.bryntumFeatureNames.concat([
    'adopt',
    'alignSelf',
    'allowCreate',
    'allowDropOnEventBar',
    'allowOver',
    'allowOverlap',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'assignments',
    'assignmentStore',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'barMargin',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'calendars',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'createEventOnDblClick',
    'creationTooltip',
    'creationTooltipTemplate',
    'crudManager',
    'crudManagerClass',
    'cycleResolutionPopupClass',
    'data',
    'dataset',
    'date',
    'dateFormat',
    'dateSeparator',
    'defaultRegion',
    'defaultResourceImageName',
    'dependencies',
    'dependencyIdField',
    'dependencyStore',
    'descriptionFormat',
    'descriptionRenderer',
    'deselectAllOnScheduleClick',
    'deselectOnClick',
    'destroyStore',
    'destroyStores',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'displayDateFormat',
    'displaySchedulingIssueResolutionPopup',
    'dock',
    'durationDisplayPrecision',
    'emptyText',
    'enableDeleteKey',
    'enableEventAnimations',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'endDate',
    'endParamName',
    'eventBarTextField',
    'eventBodyTemplate',
    'eventColor',
    'eventLayout',
    'eventRenderer',
    'eventRendererThisObj',
    'events',
    'eventSelectionDisabled',
    'eventStore',
    'eventStyle',
    'extraData',
    'features',
    'fillLastColumn',
    'fillTicks',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forceFit',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getDateConstraints',
    'getHtml',
    'getRowHeight',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hideRowHover',
    'highlightPredecessors',
    'highlightSuccessors',
    'horizontalEventSorterFn',
    'hoverDelay',
    'id',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'insertBefore',
    'insertFirst',
    'isEventSelectable',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'maintainSelectionOnDatasetChange',
    'managedEventSizing',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxTimeAxisUnit',
    'maxWidth',
    'maxZoomLevel',
    'milestoneAlign',
    'milestoneCharWidth',
    'milestoneLayoutMode',
    'milestoneTextPosition',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'modal',
    'mode',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'multiEventSelect',
    'narrowEventWidth',
    'overlappingEventSorter',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preCalculateHeightLimit',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'presets',
    'preventTooltipOnTouch',
    'project',
    'projectProgressReporting',
    'range',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceColumns',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceMargin',
    'resources',
    'resourceStore',
    'resourceTimeRangeRenderer',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'schedulingIssueResolutionPopupClass',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'selectResourceOnEventNavigate',
    'selectResourceOnScheduleClick',
    'showCreationTooltip',
    'showDirty',
    'showEventColorPickers',
    'showLagInTooltip',
    'showOnClick',
    'showOnHover',
    'showRecurringUI',
    'showTaskColorPickers',
    'showTooltip',
    'snap',
    'snapRelativeToEventStartDate',
    'startDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stepUnit',
    'stickyHeaders',
    'store',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'terminalCls',
    'terminalHideDelay',
    'terminalOffset',
    'terminalShowDelay',
    'terminalSides',
    'terminalSize',
    'tickSize',
    'timeAxis',
    'timeRanges',
    'timeRangeStore',
    'timeZone',
    'title',
    'tools',
    'tooltipTemplate',
    'trackMouse',
    'transitionDuration',
    'triggerSelectionChangeOnRemove',
    'ui',
    'updateTimelineContextOnScroll',
    'useInitialAnimation',
    'verticalTimeAxisColumn',
    'viewPreset',
    'visibleDate',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'width',
    'workingTime',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
]);
BryntumSchedulerProBaseComponent.bryntumConfigsOnly = [
    'adopt',
    'allowCreate',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'ariaDescription',
    'ariaLabel',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'createEventOnDblClick',
    'creationTooltip',
    'crudManagerClass',
    'cycleResolutionPopupClass',
    'date',
    'defaultRegion',
    'defaultResourceImageName',
    'dependencyIdField',
    'descriptionRenderer',
    'deselectAllOnScheduleClick',
    'deselectOnClick',
    'destroyStore',
    'destroyStores',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'displaySchedulingIssueResolutionPopup',
    'dock',
    'durationDisplayPrecision',
    'enableDeleteKey',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'endParamName',
    'eventBarTextField',
    'eventBodyTemplate',
    'eventRenderer',
    'eventRendererThisObj',
    'eventSelectionDisabled',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getDateConstraints',
    'getHtml',
    'getRowHeight',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'highlightPredecessors',
    'highlightSuccessors',
    'horizontalEventSorterFn',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'maintainSelectionOnDatasetChange',
    'managedEventSizing',
    'maskDefaults',
    'masked',
    'maximizable',
    'maxTimeAxisUnit',
    'modal',
    'mode',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'multiEventSelect',
    'narrowEventWidth',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preCalculateHeightLimit',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'projectProgressReporting',
    'range',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceColumns',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceTimeRangeRenderer',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'schedulingIssueResolutionPopupClass',
    'scrollerClass',
    'scrollManager',
    'showCreationTooltip',
    'showDirty',
    'showEventColorPickers',
    'showLagInTooltip',
    'showOnClick',
    'showOnHover',
    'showRecurringUI',
    'showTaskColorPickers',
    'showTooltip',
    'snapRelativeToEventStartDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stepUnit',
    'stickyHeaders',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'terminalCls',
    'terminalSides',
    'timeAxis',
    'trackMouse',
    'triggerSelectionChangeOnRemove',
    'ui',
    'verticalTimeAxisColumn',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
];
BryntumSchedulerProBaseComponent.bryntumProps = BryntumSchedulerProBaseComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'allowDropOnEventBar',
    'allowOverlap',
    'appendTo',
    'ariaLive',
    'assignments',
    'assignmentStore',
    'barMargin',
    'calendars',
    'cls',
    'columnLines',
    'columns',
    'creationTooltipTemplate',
    'crudManager',
    'data',
    'dataset',
    'dateFormat',
    'dateSeparator',
    'dependencies',
    'dependencyStore',
    'descriptionFormat',
    'disabled',
    'displayDateFormat',
    'emptyText',
    'enableEventAnimations',
    'enableUndoRedoKeys',
    'endDate',
    'eventColor',
    'eventLayout',
    'events',
    'eventStore',
    'eventStyle',
    'extraData',
    'fillTicks',
    'flex',
    'forceFit',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hideRowHover',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'isEngineReady',
    'isEventSelectable',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'maxZoomLevel',
    'milestoneAlign',
    'milestoneCharWidth',
    'milestoneLayoutMode',
    'milestoneTextPosition',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'overlappingEventSorter',
    'preserveScroll',
    'presets',
    'project',
    'readOnly',
    'resourceMargin',
    'resources',
    'resourceStore',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollLeft',
    'scrollTop',
    'scrollX',
    'selectedAssignments',
    'selectedCell',
    'selectedCells',
    'selectedEvents',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'selectResourceOnEventNavigate',
    'selectResourceOnScheduleClick',
    'snap',
    'startDate',
    'state',
    'store',
    'terminalHideDelay',
    'terminalOffset',
    'terminalShowDelay',
    'terminalSize',
    'tickSize',
    'timeRanges',
    'timeRangeStore',
    'timeResolution',
    'timeZone',
    'title',
    'tools',
    'tooltip',
    'tooltipTemplate',
    'transitionDuration',
    'updateTimelineContextOnScroll',
    'useInitialAnimation',
    'viewPreset',
    'visibleDate',
    'width',
    'workingTime',
    'zoomLevel'
]);
BryntumSchedulerProBaseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerProBaseComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSchedulerProBaseComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSchedulerProBaseComponent, selector: "bryntum-scheduler-pro-base", inputs: { adopt: "adopt", allowCreate: "allowCreate", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", animateTreeNodeToggle: "animateTreeNodeToggle", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoAdjustTimeAxis: "autoAdjustTimeAxis", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", bufferCoef: "bufferCoef", bufferThreshold: "bufferThreshold", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", createEventOnDblClick: "createEventOnDblClick", creationTooltip: "creationTooltip", crudManagerClass: "crudManagerClass", cycleResolutionPopupClass: "cycleResolutionPopupClass", date: "date", defaultRegion: "defaultRegion", defaultResourceImageName: "defaultResourceImageName", dependencyIdField: "dependencyIdField", descriptionRenderer: "descriptionRenderer", deselectAllOnScheduleClick: "deselectAllOnScheduleClick", deselectOnClick: "deselectOnClick", destroyStore: "destroyStore", destroyStores: "destroyStores", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", displaySchedulingIssueResolutionPopup: "displaySchedulingIssueResolutionPopup", dock: "dock", durationDisplayPrecision: "durationDisplayPrecision", enableDeleteKey: "enableDeleteKey", enableRecurringEvents: "enableRecurringEvents", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", endParamName: "endParamName", eventBarTextField: "eventBarTextField", eventBodyTemplate: "eventBodyTemplate", eventRenderer: "eventRenderer", eventRendererThisObj: "eventRendererThisObj", eventSelectionDisabled: "eventSelectionDisabled", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getDateConstraints: "getDateConstraints", getHtml: "getHtml", getRowHeight: "getRowHeight", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", highlightPredecessors: "highlightPredecessors", highlightSuccessors: "highlightSuccessors", horizontalEventSorterFn: "horizontalEventSorterFn", ignoreDomEventsWhileScrolling: "ignoreDomEventsWhileScrolling", ignoreParentReadOnly: "ignoreParentReadOnly", infiniteScroll: "infiniteScroll", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", maintainSelectionOnDatasetChange: "maintainSelectionOnDatasetChange", managedEventSizing: "managedEventSizing", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", maxTimeAxisUnit: "maxTimeAxisUnit", modal: "modal", mode: "mode", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", multiEventSelect: "multiEventSelect", narrowEventWidth: "narrowEventWidth", owner: "owner", partner: "partner", passStartEndParameters: "passStartEndParameters", plugins: "plugins", preCalculateHeightLimit: "preCalculateHeightLimit", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", projectProgressReporting: "projectProgressReporting", range: "range", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", resourceColumns: "resourceColumns", resourceImageExtension: "resourceImageExtension", resourceImagePath: "resourceImagePath", resourceTimeRangeRenderer: "resourceTimeRangeRenderer", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", schedulingIssueResolutionPopupClass: "schedulingIssueResolutionPopupClass", scrollerClass: "scrollerClass", scrollManager: "scrollManager", showCreationTooltip: "showCreationTooltip", showDirty: "showDirty", showEventColorPickers: "showEventColorPickers", showLagInTooltip: "showLagInTooltip", showOnClick: "showOnClick", showOnHover: "showOnHover", showRecurringUI: "showRecurringUI", showTaskColorPickers: "showTaskColorPickers", showTooltip: "showTooltip", snapRelativeToEventStartDate: "snapRelativeToEventStartDate", startParamName: "startParamName", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", stepUnit: "stepUnit", stickyHeaders: "stickyHeaders", strips: "strips", subGridConfigs: "subGridConfigs", suppressFit: "suppressFit", syncMask: "syncMask", tab: "tab", tbar: "tbar", terminalCls: "terminalCls", terminalSides: "terminalSides", timeAxis: "timeAxis", trackMouse: "trackMouse", triggerSelectionChangeOnRemove: "triggerSelectionChangeOnRemove", ui: "ui", verticalTimeAxisColumn: "verticalTimeAxisColumn", visibleZoomFactor: "visibleZoomFactor", weekStartDay: "weekStartDay", weight: "weight", zoomKeepsOriginalTimespan: "zoomKeepsOriginalTimespan", zoomOnMouseWheel: "zoomOnMouseWheel", zoomOnTimeAxisDoubleClick: "zoomOnTimeAxisDoubleClick", alignSelf: "alignSelf", allowDropOnEventBar: "allowDropOnEventBar", allowOverlap: "allowOverlap", appendTo: "appendTo", ariaLive: "ariaLive", assignments: "assignments", assignmentStore: "assignmentStore", barMargin: "barMargin", calendars: "calendars", cls: "cls", columnLines: "columnLines", columns: "columns", creationTooltipTemplate: "creationTooltipTemplate", crudManager: "crudManager", data: "data", dataset: "dataset", dateFormat: "dateFormat", dateSeparator: "dateSeparator", dependencies: "dependencies", dependencyStore: "dependencyStore", descriptionFormat: "descriptionFormat", disabled: "disabled", displayDateFormat: "displayDateFormat", emptyText: "emptyText", enableEventAnimations: "enableEventAnimations", enableUndoRedoKeys: "enableUndoRedoKeys", endDate: "endDate", eventColor: "eventColor", eventLayout: "eventLayout", events: "events", eventStore: "eventStore", eventStyle: "eventStyle", extraData: "extraData", fillTicks: "fillTicks", flex: "flex", forceFit: "forceFit", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hideRowHover: "hideRowHover", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", isEventSelectable: "isEventSelectable", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", maxZoomLevel: "maxZoomLevel", milestoneAlign: "milestoneAlign", milestoneCharWidth: "milestoneCharWidth", milestoneLayoutMode: "milestoneLayoutMode", milestoneTextPosition: "milestoneTextPosition", minHeight: "minHeight", minWidth: "minWidth", minZoomLevel: "minZoomLevel", overlappingEventSorter: "overlappingEventSorter", preserveScroll: "preserveScroll", presets: "presets", project: "project", readOnly: "readOnly", resourceMargin: "resourceMargin", resources: "resources", resourceStore: "resourceStore", resourceTimeRanges: "resourceTimeRanges", resourceTimeRangeStore: "resourceTimeRangeStore", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", selectResourceOnEventNavigate: "selectResourceOnEventNavigate", selectResourceOnScheduleClick: "selectResourceOnScheduleClick", snap: "snap", startDate: "startDate", store: "store", terminalHideDelay: "terminalHideDelay", terminalOffset: "terminalOffset", terminalShowDelay: "terminalShowDelay", terminalSize: "terminalSize", tickSize: "tickSize", timeRanges: "timeRanges", timeRangeStore: "timeRangeStore", timeZone: "timeZone", title: "title", tools: "tools", tooltipTemplate: "tooltipTemplate", transitionDuration: "transitionDuration", updateTimelineContextOnScroll: "updateTimelineContextOnScroll", useInitialAnimation: "useInitialAnimation", viewPreset: "viewPreset", visibleDate: "visibleDate", width: "width", workingTime: "workingTime", accessibility: "accessibility", isEngineReady: "isEngineReady", scrollLeft: "scrollLeft", scrollTop: "scrollTop", scrollX: "scrollX", selectedAssignments: "selectedAssignments", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedEvents: "selectedEvents", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", timeResolution: "timeResolution", tooltip: "tooltip", zoomLevel: "zoomLevel", calendarHighlightFeature: "calendarHighlightFeature", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnLinesFeature: "columnLinesFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", dependenciesFeature: "dependenciesFeature", dependencyEditFeature: "dependencyEditFeature", eventBufferFeature: "eventBufferFeature", eventCopyPasteFeature: "eventCopyPasteFeature", eventDragFeature: "eventDragFeature", eventDragCreateFeature: "eventDragCreateFeature", eventDragSelectFeature: "eventDragSelectFeature", eventEditFeature: "eventEditFeature", eventFilterFeature: "eventFilterFeature", eventMenuFeature: "eventMenuFeature", eventNonWorkingTimeFeature: "eventNonWorkingTimeFeature", eventResizeFeature: "eventResizeFeature", eventSegmentDragFeature: "eventSegmentDragFeature", eventSegmentResizeFeature: "eventSegmentResizeFeature", eventSegmentsFeature: "eventSegmentsFeature", eventTooltipFeature: "eventTooltipFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", headerZoomFeature: "headerZoomFeature", labelsFeature: "labelsFeature", mergeCellsFeature: "mergeCellsFeature", nestedEventsFeature: "nestedEventsFeature", nonWorkingTimeFeature: "nonWorkingTimeFeature", panFeature: "panFeature", pdfExportFeature: "pdfExportFeature", percentBarFeature: "percentBarFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", resourceMenuFeature: "resourceMenuFeature", resourceNonWorkingTimeFeature: "resourceNonWorkingTimeFeature", resourceTimeRangesFeature: "resourceTimeRangesFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", scheduleContextFeature: "scheduleContextFeature", scheduleMenuFeature: "scheduleMenuFeature", scheduleTooltipFeature: "scheduleTooltipFeature", searchFeature: "searchFeature", simpleEventEditFeature: "simpleEventEditFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stickyEventsFeature: "stickyEventsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", taskEditFeature: "taskEditFeature", timeAxisHeaderMenuFeature: "timeAxisHeaderMenuFeature", timeRangesFeature: "timeRangesFeature", timeSelectionFeature: "timeSelectionFeature", timeSpanHighlightFeature: "timeSpanHighlightFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature", versionsFeature: "versionsFeature" }, outputs: { onAfterDependencyCreateDrop: "onAfterDependencyCreateDrop", onAfterDependencySave: "onAfterDependencySave", onAfterDragCreate: "onAfterDragCreate", onAfterEventDrop: "onAfterEventDrop", onAfterEventEdit: "onAfterEventEdit", onAfterEventSave: "onAfterEventSave", onAfterEventSegmentDrop: "onAfterEventSegmentDrop", onAfterTaskEdit: "onAfterTaskEdit", onAfterTaskSave: "onAfterTaskSave", onAssignmentSelectionChange: "onAssignmentSelectionChange", onBeforeAssignmentDelete: "onBeforeAssignmentDelete", onBeforeAssignmentSelectionChange: "onBeforeAssignmentSelectionChange", onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDependencyAdd: "onBeforeDependencyAdd", onBeforeDependencyCreateDrag: "onBeforeDependencyCreateDrag", onBeforeDependencyCreateFinalize: "onBeforeDependencyCreateFinalize", onBeforeDependencyDelete: "onBeforeDependencyDelete", onBeforeDependencyEdit: "onBeforeDependencyEdit", onBeforeDependencyEditShow: "onBeforeDependencyEditShow", onBeforeDependencySave: "onBeforeDependencySave", onBeforeDestroy: "onBeforeDestroy", onBeforeDragCreate: "onBeforeDragCreate", onBeforeDragCreateFinalize: "onBeforeDragCreateFinalize", onBeforeEventAdd: "onBeforeEventAdd", onBeforeEventDelete: "onBeforeEventDelete", onBeforeEventDrag: "onBeforeEventDrag", onBeforeEventDragSelect: "onBeforeEventDragSelect", onBeforeEventDropFinalize: "onBeforeEventDropFinalize", onBeforeEventEdit: "onBeforeEventEdit", onBeforeEventEditShow: "onBeforeEventEditShow", onBeforeEventResize: "onBeforeEventResize", onBeforeEventResizeFinalize: "onBeforeEventResizeFinalize", onBeforeEventSave: "onBeforeEventSave", onBeforeEventSegmentDrag: "onBeforeEventSegmentDrag", onBeforeEventSegmentDropFinalize: "onBeforeEventSegmentDropFinalize", onBeforeEventSegmentResize: "onBeforeEventSegmentResize", onBeforeEventSegmentResizeFinalize: "onBeforeEventSegmentResizeFinalize", onBeforeEventSelectionChange: "onBeforeEventSelectionChange", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforePan: "onBeforePan", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforePresetChange: "onBeforePresetChange", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeShowTerminals: "onBeforeShowTerminals", onBeforeTaskDelete: "onBeforeTaskDelete", onBeforeTaskEdit: "onBeforeTaskEdit", onBeforeTaskEditShow: "onBeforeTaskEditShow", onBeforeTaskSave: "onBeforeTaskSave", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDateRangeChange: "onDateRangeChange", onDependenciesDrawn: "onDependenciesDrawn", onDependencyClick: "onDependencyClick", onDependencyContextMenu: "onDependencyContextMenu", onDependencyCreateDragStart: "onDependencyCreateDragStart", onDependencyCreateDrop: "onDependencyCreateDrop", onDependencyDblClick: "onDependencyDblClick", onDependencyMouseOut: "onDependencyMouseOut", onDependencyMouseOver: "onDependencyMouseOver", onDependencyValidationComplete: "onDependencyValidationComplete", onDependencyValidationStart: "onDependencyValidationStart", onDestroy: "onDestroy", onDragCreateEnd: "onDragCreateEnd", onDragCreateStart: "onDragCreateStart", onDragSelecting: "onDragSelecting", onEventAutoCreated: "onEventAutoCreated", onEventClick: "onEventClick", onEventContextMenu: "onEventContextMenu", onEventDblClick: "onEventDblClick", onEventDrag: "onEventDrag", onEventDragAbort: "onEventDragAbort", onEventDragModeChange: "onEventDragModeChange", onEventDragReset: "onEventDragReset", onEventDragStart: "onEventDragStart", onEventDrop: "onEventDrop", onEventEditBeforeSetRecord: "onEventEditBeforeSetRecord", onEventKeyDown: "onEventKeyDown", onEventKeyUp: "onEventKeyUp", onEventMenuBeforeShow: "onEventMenuBeforeShow", onEventMenuItem: "onEventMenuItem", onEventMenuShow: "onEventMenuShow", onEventMouseDown: "onEventMouseDown", onEventMouseEnter: "onEventMouseEnter", onEventMouseLeave: "onEventMouseLeave", onEventMouseOut: "onEventMouseOut", onEventMouseOver: "onEventMouseOver", onEventMouseUp: "onEventMouseUp", onEventPartialResize: "onEventPartialResize", onEventResizeEnd: "onEventResizeEnd", onEventResizeStart: "onEventResizeStart", onEventSegmentDrag: "onEventSegmentDrag", onEventSegmentDragAbort: "onEventSegmentDragAbort", onEventSegmentDragReset: "onEventSegmentDragReset", onEventSegmentDragStart: "onEventSegmentDragStart", onEventSegmentDrop: "onEventSegmentDrop", onEventSegmentPartialResize: "onEventSegmentPartialResize", onEventSegmentResizeEnd: "onEventSegmentResizeEnd", onEventSegmentResizeStart: "onEventSegmentResizeStart", onEventSelectionChange: "onEventSelectionChange", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onNavigate: "onNavigate", onPaint: "onPaint", onPaste: "onPaste", onPasteNotAllowed: "onPasteNotAllowed", onPdfExport: "onPdfExport", onPercentBarDrag: "onPercentBarDrag", onPercentBarDragAbort: "onPercentBarDragAbort", onPercentBarDragStart: "onPercentBarDragStart", onPercentBarDrop: "onPercentBarDrop", onPointerOver: "onPointerOver", onPresetChange: "onPresetChange", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onReleaseEvent: "onReleaseEvent", onRenderEvent: "onRenderEvent", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResourceHeaderClick: "onResourceHeaderClick", onResourceHeaderContextmenu: "onResourceHeaderContextmenu", onResourceHeaderDblclick: "onResourceHeaderDblclick", onResourceMenuBeforeShow: "onResourceMenuBeforeShow", onResourceMenuItem: "onResourceMenuItem", onResourceMenuShow: "onResourceMenuShow", onResourceMenuToggleItem: "onResourceMenuToggleItem", onResourceNonWorkingTimeClick: "onResourceNonWorkingTimeClick", onResourceNonWorkingTimeContextMenu: "onResourceNonWorkingTimeContextMenu", onResourceNonWorkingTimeDblClick: "onResourceNonWorkingTimeDblClick", onResourceNonWorkingTimeMouseDown: "onResourceNonWorkingTimeMouseDown", onResourceNonWorkingTimeMouseOut: "onResourceNonWorkingTimeMouseOut", onResourceNonWorkingTimeMouseOver: "onResourceNonWorkingTimeMouseOver", onResourceNonWorkingTimeMouseUp: "onResourceNonWorkingTimeMouseUp", onResourceTimeRangeClick: "onResourceTimeRangeClick", onResourceTimeRangeContextMenu: "onResourceTimeRangeContextMenu", onResourceTimeRangeDblClick: "onResourceTimeRangeDblClick", onResourceTimeRangeMouseDown: "onResourceTimeRangeMouseDown", onResourceTimeRangeMouseOut: "onResourceTimeRangeMouseOut", onResourceTimeRangeMouseOver: "onResourceTimeRangeMouseOver", onResourceTimeRangeMouseUp: "onResourceTimeRangeMouseUp", onResponsive: "onResponsive", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScheduleClick: "onScheduleClick", onScheduleContextMenu: "onScheduleContextMenu", onScheduleDblClick: "onScheduleDblClick", onScheduleMenuBeforeShow: "onScheduleMenuBeforeShow", onScheduleMenuItem: "onScheduleMenuItem", onScheduleMenuShow: "onScheduleMenuShow", onScheduleMouseDown: "onScheduleMouseDown", onScheduleMouseEnter: "onScheduleMouseEnter", onScheduleMouseLeave: "onScheduleMouseLeave", onScheduleMouseMove: "onScheduleMouseMove", onScheduleMouseUp: "onScheduleMouseUp", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onTaskEditCanceled: "onTaskEditCanceled", onTimeAxisChange: "onTimeAxisChange", onTimeAxisHeaderClick: "onTimeAxisHeaderClick", onTimeAxisHeaderContextMenu: "onTimeAxisHeaderContextMenu", onTimeAxisHeaderDblClick: "onTimeAxisHeaderDblClick", onTimeAxisHeaderMenuBeforeShow: "onTimeAxisHeaderMenuBeforeShow", onTimeAxisHeaderMenuItem: "onTimeAxisHeaderMenuItem", onTimeAxisHeaderMenuShow: "onTimeAxisHeaderMenuShow", onTimelineContextChange: "onTimelineContextChange", onTimelineViewportResize: "onTimelineViewportResize", onTimeRangeHeaderClick: "onTimeRangeHeaderClick", onTimeRangeHeaderContextMenu: "onTimeRangeHeaderContextMenu", onTimeRangeHeaderDblClick: "onTimeRangeHeaderDblClick", onTimeSelectionChange: "onTimeSelectionChange", onTimeSelectionElementClick: "onTimeSelectionElementClick", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onTransactionChange: "onTransactionChange", onUnsplit: "onUnsplit", onVisibleDateRangeChange: "onVisibleDateRangeChange" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerProBaseComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-scheduler-pro-base',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], allowCreate: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoAdjustTimeAxis: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], bufferCoef: [{
                type: Input
            }], bufferThreshold: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], createEventOnDblClick: [{
                type: Input
            }], creationTooltip: [{
                type: Input
            }], crudManagerClass: [{
                type: Input
            }], cycleResolutionPopupClass: [{
                type: Input
            }], date: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], defaultResourceImageName: [{
                type: Input
            }], dependencyIdField: [{
                type: Input
            }], descriptionRenderer: [{
                type: Input
            }], deselectAllOnScheduleClick: [{
                type: Input
            }], deselectOnClick: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], destroyStores: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], displaySchedulingIssueResolutionPopup: [{
                type: Input
            }], dock: [{
                type: Input
            }], durationDisplayPrecision: [{
                type: Input
            }], enableDeleteKey: [{
                type: Input
            }], enableRecurringEvents: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], endParamName: [{
                type: Input
            }], eventBarTextField: [{
                type: Input
            }], eventBodyTemplate: [{
                type: Input
            }], eventRenderer: [{
                type: Input
            }], eventRendererThisObj: [{
                type: Input
            }], eventSelectionDisabled: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getDateConstraints: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], highlightPredecessors: [{
                type: Input
            }], highlightSuccessors: [{
                type: Input
            }], horizontalEventSorterFn: [{
                type: Input
            }], ignoreDomEventsWhileScrolling: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], infiniteScroll: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], maintainSelectionOnDatasetChange: [{
                type: Input
            }], managedEventSizing: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], maxTimeAxisUnit: [{
                type: Input
            }], modal: [{
                type: Input
            }], mode: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], multiEventSelect: [{
                type: Input
            }], narrowEventWidth: [{
                type: Input
            }], owner: [{
                type: Input
            }], partner: [{
                type: Input
            }], passStartEndParameters: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preCalculateHeightLimit: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], projectProgressReporting: [{
                type: Input
            }], range: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], resourceColumns: [{
                type: Input
            }], resourceImageExtension: [{
                type: Input
            }], resourceImagePath: [{
                type: Input
            }], resourceTimeRangeRenderer: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], schedulingIssueResolutionPopupClass: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], showCreationTooltip: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showEventColorPickers: [{
                type: Input
            }], showLagInTooltip: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], showRecurringUI: [{
                type: Input
            }], showTaskColorPickers: [{
                type: Input
            }], showTooltip: [{
                type: Input
            }], snapRelativeToEventStartDate: [{
                type: Input
            }], startParamName: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], stepUnit: [{
                type: Input
            }], stickyHeaders: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], suppressFit: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], terminalCls: [{
                type: Input
            }], terminalSides: [{
                type: Input
            }], timeAxis: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], triggerSelectionChangeOnRemove: [{
                type: Input
            }], ui: [{
                type: Input
            }], verticalTimeAxisColumn: [{
                type: Input
            }], visibleZoomFactor: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], zoomKeepsOriginalTimespan: [{
                type: Input
            }], zoomOnMouseWheel: [{
                type: Input
            }], zoomOnTimeAxisDoubleClick: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], allowDropOnEventBar: [{
                type: Input
            }], allowOverlap: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], assignments: [{
                type: Input
            }], assignmentStore: [{
                type: Input
            }], barMargin: [{
                type: Input
            }], calendars: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], creationTooltipTemplate: [{
                type: Input
            }], crudManager: [{
                type: Input
            }], data: [{
                type: Input
            }], dataset: [{
                type: Input
            }], dateFormat: [{
                type: Input
            }], dateSeparator: [{
                type: Input
            }], dependencies: [{
                type: Input
            }], dependencyStore: [{
                type: Input
            }], descriptionFormat: [{
                type: Input
            }], disabled: [{
                type: Input
            }], displayDateFormat: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableEventAnimations: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], endDate: [{
                type: Input
            }], eventColor: [{
                type: Input
            }], eventLayout: [{
                type: Input
            }], events: [{
                type: Input
            }], eventStore: [{
                type: Input
            }], eventStyle: [{
                type: Input
            }], extraData: [{
                type: Input
            }], fillTicks: [{
                type: Input
            }], flex: [{
                type: Input
            }], forceFit: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hideRowHover: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], isEventSelectable: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], maxZoomLevel: [{
                type: Input
            }], milestoneAlign: [{
                type: Input
            }], milestoneCharWidth: [{
                type: Input
            }], milestoneLayoutMode: [{
                type: Input
            }], milestoneTextPosition: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], minZoomLevel: [{
                type: Input
            }], overlappingEventSorter: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], presets: [{
                type: Input
            }], project: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], resourceMargin: [{
                type: Input
            }], resources: [{
                type: Input
            }], resourceStore: [{
                type: Input
            }], resourceTimeRanges: [{
                type: Input
            }], resourceTimeRangeStore: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], selectResourceOnEventNavigate: [{
                type: Input
            }], selectResourceOnScheduleClick: [{
                type: Input
            }], snap: [{
                type: Input
            }], startDate: [{
                type: Input
            }], store: [{
                type: Input
            }], terminalHideDelay: [{
                type: Input
            }], terminalOffset: [{
                type: Input
            }], terminalShowDelay: [{
                type: Input
            }], terminalSize: [{
                type: Input
            }], tickSize: [{
                type: Input
            }], timeRanges: [{
                type: Input
            }], timeRangeStore: [{
                type: Input
            }], timeZone: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltipTemplate: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], updateTimelineContextOnScroll: [{
                type: Input
            }], useInitialAnimation: [{
                type: Input
            }], viewPreset: [{
                type: Input
            }], visibleDate: [{
                type: Input
            }], width: [{
                type: Input
            }], workingTime: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], isEngineReady: [{
                type: Input
            }], scrollLeft: [{
                type: Input
            }], scrollTop: [{
                type: Input
            }], scrollX: [{
                type: Input
            }], selectedAssignments: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedEvents: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], timeResolution: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], zoomLevel: [{
                type: Input
            }], onAfterDependencyCreateDrop: [{
                type: Output
            }], onAfterDependencySave: [{
                type: Output
            }], onAfterDragCreate: [{
                type: Output
            }], onAfterEventDrop: [{
                type: Output
            }], onAfterEventEdit: [{
                type: Output
            }], onAfterEventSave: [{
                type: Output
            }], onAfterEventSegmentDrop: [{
                type: Output
            }], onAfterTaskEdit: [{
                type: Output
            }], onAfterTaskSave: [{
                type: Output
            }], onAssignmentSelectionChange: [{
                type: Output
            }], onBeforeAssignmentDelete: [{
                type: Output
            }], onBeforeAssignmentSelectionChange: [{
                type: Output
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDependencyAdd: [{
                type: Output
            }], onBeforeDependencyCreateDrag: [{
                type: Output
            }], onBeforeDependencyCreateFinalize: [{
                type: Output
            }], onBeforeDependencyDelete: [{
                type: Output
            }], onBeforeDependencyEdit: [{
                type: Output
            }], onBeforeDependencyEditShow: [{
                type: Output
            }], onBeforeDependencySave: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeDragCreate: [{
                type: Output
            }], onBeforeDragCreateFinalize: [{
                type: Output
            }], onBeforeEventAdd: [{
                type: Output
            }], onBeforeEventDelete: [{
                type: Output
            }], onBeforeEventDrag: [{
                type: Output
            }], onBeforeEventDragSelect: [{
                type: Output
            }], onBeforeEventDropFinalize: [{
                type: Output
            }], onBeforeEventEdit: [{
                type: Output
            }], onBeforeEventEditShow: [{
                type: Output
            }], onBeforeEventResize: [{
                type: Output
            }], onBeforeEventResizeFinalize: [{
                type: Output
            }], onBeforeEventSave: [{
                type: Output
            }], onBeforeEventSegmentDrag: [{
                type: Output
            }], onBeforeEventSegmentDropFinalize: [{
                type: Output
            }], onBeforeEventSegmentResize: [{
                type: Output
            }], onBeforeEventSegmentResizeFinalize: [{
                type: Output
            }], onBeforeEventSelectionChange: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforePan: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforePresetChange: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeShowTerminals: [{
                type: Output
            }], onBeforeTaskDelete: [{
                type: Output
            }], onBeforeTaskEdit: [{
                type: Output
            }], onBeforeTaskEditShow: [{
                type: Output
            }], onBeforeTaskSave: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDateRangeChange: [{
                type: Output
            }], onDependenciesDrawn: [{
                type: Output
            }], onDependencyClick: [{
                type: Output
            }], onDependencyContextMenu: [{
                type: Output
            }], onDependencyCreateDragStart: [{
                type: Output
            }], onDependencyCreateDrop: [{
                type: Output
            }], onDependencyDblClick: [{
                type: Output
            }], onDependencyMouseOut: [{
                type: Output
            }], onDependencyMouseOver: [{
                type: Output
            }], onDependencyValidationComplete: [{
                type: Output
            }], onDependencyValidationStart: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragCreateEnd: [{
                type: Output
            }], onDragCreateStart: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onEventAutoCreated: [{
                type: Output
            }], onEventClick: [{
                type: Output
            }], onEventContextMenu: [{
                type: Output
            }], onEventDblClick: [{
                type: Output
            }], onEventDrag: [{
                type: Output
            }], onEventDragAbort: [{
                type: Output
            }], onEventDragModeChange: [{
                type: Output
            }], onEventDragReset: [{
                type: Output
            }], onEventDragStart: [{
                type: Output
            }], onEventDrop: [{
                type: Output
            }], onEventEditBeforeSetRecord: [{
                type: Output
            }], onEventKeyDown: [{
                type: Output
            }], onEventKeyUp: [{
                type: Output
            }], onEventMenuBeforeShow: [{
                type: Output
            }], onEventMenuItem: [{
                type: Output
            }], onEventMenuShow: [{
                type: Output
            }], onEventMouseDown: [{
                type: Output
            }], onEventMouseEnter: [{
                type: Output
            }], onEventMouseLeave: [{
                type: Output
            }], onEventMouseOut: [{
                type: Output
            }], onEventMouseOver: [{
                type: Output
            }], onEventMouseUp: [{
                type: Output
            }], onEventPartialResize: [{
                type: Output
            }], onEventResizeEnd: [{
                type: Output
            }], onEventResizeStart: [{
                type: Output
            }], onEventSegmentDrag: [{
                type: Output
            }], onEventSegmentDragAbort: [{
                type: Output
            }], onEventSegmentDragReset: [{
                type: Output
            }], onEventSegmentDragStart: [{
                type: Output
            }], onEventSegmentDrop: [{
                type: Output
            }], onEventSegmentPartialResize: [{
                type: Output
            }], onEventSegmentResizeEnd: [{
                type: Output
            }], onEventSegmentResizeStart: [{
                type: Output
            }], onEventSelectionChange: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onNavigate: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPasteNotAllowed: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPercentBarDrag: [{
                type: Output
            }], onPercentBarDragAbort: [{
                type: Output
            }], onPercentBarDragStart: [{
                type: Output
            }], onPercentBarDrop: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onPresetChange: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onReleaseEvent: [{
                type: Output
            }], onRenderEvent: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResourceHeaderClick: [{
                type: Output
            }], onResourceHeaderContextmenu: [{
                type: Output
            }], onResourceHeaderDblclick: [{
                type: Output
            }], onResourceMenuBeforeShow: [{
                type: Output
            }], onResourceMenuItem: [{
                type: Output
            }], onResourceMenuShow: [{
                type: Output
            }], onResourceMenuToggleItem: [{
                type: Output
            }], onResourceNonWorkingTimeClick: [{
                type: Output
            }], onResourceNonWorkingTimeContextMenu: [{
                type: Output
            }], onResourceNonWorkingTimeDblClick: [{
                type: Output
            }], onResourceNonWorkingTimeMouseDown: [{
                type: Output
            }], onResourceNonWorkingTimeMouseOut: [{
                type: Output
            }], onResourceNonWorkingTimeMouseOver: [{
                type: Output
            }], onResourceNonWorkingTimeMouseUp: [{
                type: Output
            }], onResourceTimeRangeClick: [{
                type: Output
            }], onResourceTimeRangeContextMenu: [{
                type: Output
            }], onResourceTimeRangeDblClick: [{
                type: Output
            }], onResourceTimeRangeMouseDown: [{
                type: Output
            }], onResourceTimeRangeMouseOut: [{
                type: Output
            }], onResourceTimeRangeMouseOver: [{
                type: Output
            }], onResourceTimeRangeMouseUp: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScheduleClick: [{
                type: Output
            }], onScheduleContextMenu: [{
                type: Output
            }], onScheduleDblClick: [{
                type: Output
            }], onScheduleMenuBeforeShow: [{
                type: Output
            }], onScheduleMenuItem: [{
                type: Output
            }], onScheduleMenuShow: [{
                type: Output
            }], onScheduleMouseDown: [{
                type: Output
            }], onScheduleMouseEnter: [{
                type: Output
            }], onScheduleMouseLeave: [{
                type: Output
            }], onScheduleMouseMove: [{
                type: Output
            }], onScheduleMouseUp: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onTaskEditCanceled: [{
                type: Output
            }], onTimeAxisChange: [{
                type: Output
            }], onTimeAxisHeaderClick: [{
                type: Output
            }], onTimeAxisHeaderContextMenu: [{
                type: Output
            }], onTimeAxisHeaderDblClick: [{
                type: Output
            }], onTimeAxisHeaderMenuBeforeShow: [{
                type: Output
            }], onTimeAxisHeaderMenuItem: [{
                type: Output
            }], onTimeAxisHeaderMenuShow: [{
                type: Output
            }], onTimelineContextChange: [{
                type: Output
            }], onTimelineViewportResize: [{
                type: Output
            }], onTimeRangeHeaderClick: [{
                type: Output
            }], onTimeRangeHeaderContextMenu: [{
                type: Output
            }], onTimeRangeHeaderDblClick: [{
                type: Output
            }], onTimeSelectionChange: [{
                type: Output
            }], onTimeSelectionElementClick: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onTransactionChange: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], onVisibleDateRangeChange: [{
                type: Output
            }], calendarHighlightFeature: [{
                type: Input
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnLinesFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], dependenciesFeature: [{
                type: Input
            }], dependencyEditFeature: [{
                type: Input
            }], eventBufferFeature: [{
                type: Input
            }], eventCopyPasteFeature: [{
                type: Input
            }], eventDragFeature: [{
                type: Input
            }], eventDragCreateFeature: [{
                type: Input
            }], eventDragSelectFeature: [{
                type: Input
            }], eventEditFeature: [{
                type: Input
            }], eventFilterFeature: [{
                type: Input
            }], eventMenuFeature: [{
                type: Input
            }], eventNonWorkingTimeFeature: [{
                type: Input
            }], eventResizeFeature: [{
                type: Input
            }], eventSegmentDragFeature: [{
                type: Input
            }], eventSegmentResizeFeature: [{
                type: Input
            }], eventSegmentsFeature: [{
                type: Input
            }], eventTooltipFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], headerZoomFeature: [{
                type: Input
            }], labelsFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], nestedEventsFeature: [{
                type: Input
            }], nonWorkingTimeFeature: [{
                type: Input
            }], panFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], percentBarFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], resourceMenuFeature: [{
                type: Input
            }], resourceNonWorkingTimeFeature: [{
                type: Input
            }], resourceTimeRangesFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], scheduleContextFeature: [{
                type: Input
            }], scheduleMenuFeature: [{
                type: Input
            }], scheduleTooltipFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], simpleEventEditFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stickyEventsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], taskEditFeature: [{
                type: Input
            }], timeAxisHeaderMenuFeature: [{
                type: Input
            }], timeRangesFeature: [{
                type: Input
            }], timeSelectionFeature: [{
                type: Input
            }], timeSpanHighlightFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }], versionsFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum SchedulerTaskEditor
 */
class BryntumSchedulerTaskEditorComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeClose = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSchedulerTaskEditorComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSchedulerTaskEditorComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSchedulerTaskEditorComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSchedulerTaskEditorComponent.instanceClass = SchedulerTaskEditor;
BryntumSchedulerTaskEditorComponent.instanceName = 'SchedulerTaskEditor';
BryntumSchedulerTaskEditorComponent.bryntumEvents = [
    'onBeforeClose',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onCollapse',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onToolClick'
];
BryntumSchedulerTaskEditorComponent.bryntumFeatureNames = [];
BryntumSchedulerTaskEditorComponent.bryntumConfigs = BryntumSchedulerTaskEditorComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoShow',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'calculateMask',
    'calculateMaskDelay',
    'callOnFunctions',
    'centered',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dependencyIdField',
    'disabled',
    'dock',
    'draggable',
    'durationDisplayPrecision',
    'extraData',
    'flex',
    'floating',
    'focusOnToFront',
    'footer',
    'forElement',
    'header',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'modal',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showOnClick',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumSchedulerTaskEditorComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoShow',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'calculateMask',
    'calculateMaskDelay',
    'callOnFunctions',
    'centered',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dependencyIdField',
    'dock',
    'draggable',
    'durationDisplayPrecision',
    'floating',
    'focusOnToFront',
    'footer',
    'forElement',
    'header',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maximizable',
    'modal',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showOnClick',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'trapFocus',
    'ui',
    'weight'
];
BryntumSchedulerTaskEditorComponent.bryntumProps = BryntumSchedulerTaskEditorComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximized',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumSchedulerTaskEditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerTaskEditorComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSchedulerTaskEditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSchedulerTaskEditorComponent, selector: "bryntum-scheduler-task-editor", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoShow: "autoShow", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", calculateMask: "calculateMask", calculateMaskDelay: "calculateMaskDelay", callOnFunctions: "callOnFunctions", centered: "centered", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dependencyIdField: "dependencyIdField", dock: "dock", draggable: "draggable", durationDisplayPrecision: "durationDisplayPrecision", floating: "floating", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", header: "header", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", modal: "modal", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showOnClick: "showOnClick", showTooltipWhenDisabled: "showTooltipWhenDisabled", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", trapFocus: "trapFocus", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", state: "state", values: "values" }, outputs: { onBeforeClose: "onBeforeClose", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerTaskEditorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-scheduler-task-editor',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], calculateMask: [{
                type: Input
            }], calculateMaskDelay: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dependencyIdField: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], durationDisplayPrecision: [{
                type: Input
            }], floating: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], header: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], state: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeClose: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum SchedulingDirectionPicker
 */
class BryntumSchedulingDirectionPickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSchedulingDirectionPickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSchedulingDirectionPickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSchedulingDirectionPickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSchedulingDirectionPickerComponent.instanceClass = SchedulingDirectionPicker;
BryntumSchedulingDirectionPickerComponent.instanceName = 'SchedulingDirectionPicker';
BryntumSchedulingDirectionPickerComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onTrigger'
];
BryntumSchedulingDirectionPickerComponent.bryntumFeatureNames = [];
BryntumSchedulingDirectionPickerComponent.bryntumConfigs = BryntumSchedulingDirectionPickerComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'editable',
    'emptyText',
    'encodeFilterParams',
    'extraData',
    'filterOnEnter',
    'filterOperator',
    'filterParamName',
    'filterSelected',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minChars',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggerAction',
    'triggers',
    'ui',
    'validateFilter',
    'validateOnInput',
    'value',
    'valueField',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumSchedulingDirectionPickerComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'defaultBindProperty',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'emptyText',
    'encodeFilterParams',
    'filterOnEnter',
    'filterParamName',
    'filterSelected',
    'floating',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minChars',
    'minLength',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'triggerAction',
    'ui',
    'validateFilter',
    'validateOnInput',
    'valueField',
    'weight'
];
BryntumSchedulingDirectionPickerComponent.bryntumProps = BryntumSchedulingDirectionPickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'filterOperator',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'picker',
    'readOnly',
    'scrollable',
    'store',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumSchedulingDirectionPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulingDirectionPickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSchedulingDirectionPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSchedulingDirectionPickerComponent, selector: "bryntum-scheduling-direction-picker", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", caseSensitive: "caseSensitive", centered: "centered", chipView: "chipView", clearable: "clearable", clearTextOnPickerHide: "clearTextOnPickerHide", clearTextOnSelection: "clearTextOnSelection", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", createOnUnmatched: "createOnUnmatched", defaultBindProperty: "defaultBindProperty", displayField: "displayField", displayValueRenderer: "displayValueRenderer", dock: "dock", draggable: "draggable", emptyText: "emptyText", encodeFilterParams: "encodeFilterParams", filterOnEnter: "filterOnEnter", filterParamName: "filterParamName", filterSelected: "filterSelected", floating: "floating", hideAnimation: "hideAnimation", hidePickerOnSelect: "hidePickerOnSelect", hideTrigger: "hideTrigger", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inlinePicker: "inlinePicker", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", items: "items", keyStrokeChangeDelay: "keyStrokeChangeDelay", keyStrokeFilterDelay: "keyStrokeFilterDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listCls: "listCls", listeners: "listeners", listItemTpl: "listItemTpl", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minChars: "minChars", minLength: "minLength", monitorResize: "monitorResize", multiSelect: "multiSelect", multiValueSeparator: "multiValueSeparator", name: "name", overlayAnchor: "overlayAnchor", owner: "owner", pickerAlignElement: "pickerAlignElement", pickerWidth: "pickerWidth", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", primaryFilter: "primaryFilter", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", triggerAction: "triggerAction", ui: "ui", validateFilter: "validateFilter", validateOnInput: "validateOnInput", valueField: "valueField", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", filterOperator: "filterOperator", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", picker: "picker", readOnly: "readOnly", store: "store", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulingDirectionPickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-scheduling-direction-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], caseSensitive: [{
                type: Input
            }], centered: [{
                type: Input
            }], chipView: [{
                type: Input
            }], clearable: [{
                type: Input
            }], clearTextOnPickerHide: [{
                type: Input
            }], clearTextOnSelection: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], createOnUnmatched: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], displayValueRenderer: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], encodeFilterParams: [{
                type: Input
            }], filterOnEnter: [{
                type: Input
            }], filterParamName: [{
                type: Input
            }], filterSelected: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hidePickerOnSelect: [{
                type: Input
            }], hideTrigger: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inlinePicker: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], items: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], keyStrokeFilterDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listCls: [{
                type: Input
            }], listeners: [{
                type: Input
            }], listItemTpl: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minChars: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], multiValueSeparator: [{
                type: Input
            }], name: [{
                type: Input
            }], overlayAnchor: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerWidth: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], primaryFilter: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerAction: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateFilter: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], valueField: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], filterOperator: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], picker: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], store: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum SchedulingIssueResolutionPopup
 */
class BryntumSchedulingIssueResolutionPopupComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeClose = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSchedulingIssueResolutionPopupComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSchedulingIssueResolutionPopupComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSchedulingIssueResolutionPopupComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSchedulingIssueResolutionPopupComponent.instanceClass = SchedulingIssueResolutionPopup;
BryntumSchedulingIssueResolutionPopupComponent.instanceName = 'SchedulingIssueResolutionPopup';
BryntumSchedulingIssueResolutionPopupComponent.bryntumEvents = [
    'onBeforeClose',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onCollapse',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onToolClick'
];
BryntumSchedulingIssueResolutionPopupComponent.bryntumFeatureNames = [];
BryntumSchedulingIssueResolutionPopupComponent.bryntumConfigs = BryntumSchedulingIssueResolutionPopupComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoShow',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'focusOnToFront',
    'footer',
    'forElement',
    'header',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'modal',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showOnClick',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumSchedulingIssueResolutionPopupComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoShow',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'focusOnToFront',
    'footer',
    'forElement',
    'header',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maximizable',
    'modal',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showOnClick',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'trapFocus',
    'ui',
    'weight'
];
BryntumSchedulingIssueResolutionPopupComponent.bryntumProps = BryntumSchedulingIssueResolutionPopupComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximized',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumSchedulingIssueResolutionPopupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulingIssueResolutionPopupComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSchedulingIssueResolutionPopupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSchedulingIssueResolutionPopupComponent, selector: "bryntum-scheduling-issue-resolution-popup", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoShow: "autoShow", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", header: "header", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", modal: "modal", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showOnClick: "showOnClick", showTooltipWhenDisabled: "showTooltipWhenDisabled", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", trapFocus: "trapFocus", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", state: "state", values: "values" }, outputs: { onBeforeClose: "onBeforeClose", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulingIssueResolutionPopupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-scheduling-issue-resolution-popup',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], header: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], state: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeClose: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum SchedulingModePicker
 */
class BryntumSchedulingModePickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSchedulingModePickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSchedulingModePickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSchedulingModePickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSchedulingModePickerComponent.instanceClass = SchedulingModePicker;
BryntumSchedulingModePickerComponent.instanceName = 'SchedulingModePicker';
BryntumSchedulingModePickerComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onTrigger'
];
BryntumSchedulingModePickerComponent.bryntumFeatureNames = [];
BryntumSchedulingModePickerComponent.bryntumConfigs = BryntumSchedulingModePickerComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'allowedModes',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'editable',
    'emptyText',
    'encodeFilterParams',
    'extraData',
    'filterOnEnter',
    'filterOperator',
    'filterParamName',
    'filterSelected',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minChars',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggerAction',
    'triggers',
    'ui',
    'validateFilter',
    'validateOnInput',
    'value',
    'valueField',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumSchedulingModePickerComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'allowedModes',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'defaultBindProperty',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'emptyText',
    'encodeFilterParams',
    'filterOnEnter',
    'filterParamName',
    'filterSelected',
    'floating',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minChars',
    'minLength',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'triggerAction',
    'ui',
    'validateFilter',
    'validateOnInput',
    'valueField',
    'weight'
];
BryntumSchedulingModePickerComponent.bryntumProps = BryntumSchedulingModePickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'filterOperator',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'picker',
    'readOnly',
    'scrollable',
    'store',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumSchedulingModePickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulingModePickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSchedulingModePickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSchedulingModePickerComponent, selector: "bryntum-scheduling-mode-picker", inputs: { adopt: "adopt", align: "align", allowedModes: "allowedModes", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", caseSensitive: "caseSensitive", centered: "centered", chipView: "chipView", clearable: "clearable", clearTextOnPickerHide: "clearTextOnPickerHide", clearTextOnSelection: "clearTextOnSelection", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", createOnUnmatched: "createOnUnmatched", defaultBindProperty: "defaultBindProperty", displayField: "displayField", displayValueRenderer: "displayValueRenderer", dock: "dock", draggable: "draggable", emptyText: "emptyText", encodeFilterParams: "encodeFilterParams", filterOnEnter: "filterOnEnter", filterParamName: "filterParamName", filterSelected: "filterSelected", floating: "floating", hideAnimation: "hideAnimation", hidePickerOnSelect: "hidePickerOnSelect", hideTrigger: "hideTrigger", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inlinePicker: "inlinePicker", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", items: "items", keyStrokeChangeDelay: "keyStrokeChangeDelay", keyStrokeFilterDelay: "keyStrokeFilterDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listCls: "listCls", listeners: "listeners", listItemTpl: "listItemTpl", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minChars: "minChars", minLength: "minLength", monitorResize: "monitorResize", multiSelect: "multiSelect", multiValueSeparator: "multiValueSeparator", name: "name", overlayAnchor: "overlayAnchor", owner: "owner", pickerAlignElement: "pickerAlignElement", pickerWidth: "pickerWidth", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", primaryFilter: "primaryFilter", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", triggerAction: "triggerAction", ui: "ui", validateFilter: "validateFilter", validateOnInput: "validateOnInput", valueField: "valueField", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", filterOperator: "filterOperator", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", picker: "picker", readOnly: "readOnly", store: "store", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulingModePickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-scheduling-mode-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], allowedModes: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], caseSensitive: [{
                type: Input
            }], centered: [{
                type: Input
            }], chipView: [{
                type: Input
            }], clearable: [{
                type: Input
            }], clearTextOnPickerHide: [{
                type: Input
            }], clearTextOnSelection: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], createOnUnmatched: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], displayValueRenderer: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], encodeFilterParams: [{
                type: Input
            }], filterOnEnter: [{
                type: Input
            }], filterParamName: [{
                type: Input
            }], filterSelected: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hidePickerOnSelect: [{
                type: Input
            }], hideTrigger: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inlinePicker: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], items: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], keyStrokeFilterDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listCls: [{
                type: Input
            }], listeners: [{
                type: Input
            }], listItemTpl: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minChars: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], multiValueSeparator: [{
                type: Input
            }], name: [{
                type: Input
            }], overlayAnchor: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerWidth: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], primaryFilter: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerAction: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateFilter: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], valueField: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], filterOperator: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], picker: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], store: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Slider
 */
class BryntumSliderComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSliderComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSliderComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSliderComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSliderComponent.instanceClass = Slider;
BryntumSliderComponent.instanceName = 'Slider';
BryntumSliderComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onInput',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumSliderComponent.bryntumFeatureNames = [];
BryntumSliderComponent.bryntumConfigs = BryntumSliderComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'min',
    'minHeight',
    'minWidth',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltip',
    'showTooltipWhenDisabled',
    'showValue',
    'step',
    'tab',
    'tag',
    'text',
    'textAlign',
    'title',
    'tooltip',
    'triggerChangeOnInput',
    'ui',
    'unit',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumSliderComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'htmlCls',
    'ignoreParentReadOnly',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltip',
    'showTooltipWhenDisabled',
    'showValue',
    'tab',
    'tag',
    'textAlign',
    'title',
    'ui',
    'unit',
    'weight'
];
BryntumSliderComponent.bryntumProps = BryntumSliderComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'margin',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'min',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'step',
    'text',
    'tooltip',
    'triggerChangeOnInput',
    'value',
    'width',
    'x',
    'y'
]);
BryntumSliderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSliderComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSliderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSliderComponent, selector: "bryntum-slider", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltip: "showTooltip", showTooltipWhenDisabled: "showTooltipWhenDisabled", showValue: "showValue", tab: "tab", tag: "tag", textAlign: "textAlign", title: "title", ui: "ui", unit: "unit", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", margin: "margin", max: "max", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", min: "min", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", scrollable: "scrollable", step: "step", text: "text", tooltip: "tooltip", triggerChangeOnInput: "triggerChangeOnInput", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSliderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-slider',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltip: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], showValue: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], unit: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], margin: [{
                type: Input
            }], max: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], min: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], step: [{
                type: Input
            }], text: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggerChangeOnInput: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum SlideToggle
 */
class BryntumSlideToggleComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeChange = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onClick = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSlideToggleComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSlideToggleComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSlideToggleComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSlideToggleComponent.instanceClass = SlideToggle;
BryntumSlideToggleComponent.instanceName = 'SlideToggle';
BryntumSlideToggleComponent.bryntumEvents = [
    'onAction',
    'onBeforeChange',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onClick',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumSlideToggleComponent.bryntumFeatureNames = [];
BryntumSlideToggleComponent.bryntumConfigs = BryntumSlideToggleComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoCollapse',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'checked',
    'checkedValue',
    'clearable',
    'cls',
    'color',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'text',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'uncheckedValue',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumSlideToggleComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoCollapse',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'checkedValue',
    'clearable',
    'color',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'text',
    'textAlign',
    'title',
    'ui',
    'uncheckedValue',
    'validateOnInput',
    'weight'
];
BryntumSlideToggleComponent.bryntumProps = BryntumSlideToggleComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'checked',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'name',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumSlideToggleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSlideToggleComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSlideToggleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSlideToggleComponent, selector: "bryntum-slide-toggle", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoCollapse: "autoCollapse", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", checkedValue: "checkedValue", clearable: "clearable", color: "color", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", text: "text", textAlign: "textAlign", title: "title", ui: "ui", uncheckedValue: "uncheckedValue", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", checked: "checked", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", name: "name", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeChange: "onBeforeChange", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onClick: "onClick", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSlideToggleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-slide-toggle',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoCollapse: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], checkedValue: [{
                type: Input
            }], clearable: [{
                type: Input
            }], color: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], text: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], uncheckedValue: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], checked: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], name: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeChange: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onClick: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Splitter
 */
class BryntumSplitterComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDrag = new EventEmitter();
        this.onDragStart = new EventEmitter();
        this.onDrop = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumSplitterComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumSplitterComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumSplitterComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumSplitterComponent.instanceClass = Splitter;
BryntumSplitterComponent.instanceName = 'Splitter';
BryntumSplitterComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onDrag',
    'onDragStart',
    'onDrop',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumSplitterComponent.bryntumFeatureNames = [];
BryntumSplitterComponent.bryntumConfigs = BryntumSplitterComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'orientation',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'title',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumSplitterComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'htmlCls',
    'ignoreParentReadOnly',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'orientation',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'title',
    'ui',
    'weight'
];
BryntumSplitterComponent.bryntumProps = BryntumSplitterComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'width',
    'x',
    'y'
]);
BryntumSplitterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSplitterComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumSplitterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumSplitterComponent, selector: "bryntum-splitter", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", orientation: "orientation", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", title: "title", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", scrollable: "scrollable", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onDrag: "onDrag", onDragStart: "onDragStart", onDrop: "onDrop", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSplitterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-splitter',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], orientation: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDrag: [{
                type: Output
            }], onDragStart: [{
                type: Output
            }], onDrop: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum StartDateField
 */
class BryntumStartDateFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumStartDateFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumStartDateFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumStartDateFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumStartDateFieldComponent.instanceClass = StartDateField;
BryntumStartDateFieldComponent.instanceName = 'StartDateField';
BryntumStartDateFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumStartDateFieldComponent.bryntumFeatureNames = [];
BryntumStartDateFieldComponent.bryntumConfigs = BryntumStartDateFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'eventRecord',
    'extraData',
    'flex',
    'floating',
    'format',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keepTime',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'max',
    'maxDateDelta',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'min',
    'minDateDelta',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerFormat',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'project',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'step',
    'strictParsing',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weekStartDay',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumStartDateFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'eventRecord',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keepTime',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxDateDelta',
    'maxLength',
    'minDateDelta',
    'minLength',
    'monitorResize',
    'name',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerFormat',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'project',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'strictParsing',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weekStartDay',
    'weight'
];
BryntumStartDateFieldComponent.bryntumProps = BryntumStartDateFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'format',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'min',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'step',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumStartDateFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumStartDateFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumStartDateFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumStartDateFieldComponent, selector: "bryntum-start-date-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", eventRecord: "eventRecord", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keepTime: "keepTime", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxDateDelta: "maxDateDelta", maxLength: "maxLength", minDateDelta: "minDateDelta", minLength: "minLength", monitorResize: "monitorResize", name: "name", owner: "owner", picker: "picker", pickerAlignElement: "pickerAlignElement", pickerFormat: "pickerFormat", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", project: "project", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", strictParsing: "strictParsing", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weekStartDay: "weekStartDay", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", format: "format", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", max: "max", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", min: "min", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", step: "step", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumStartDateFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-start-date-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], eventRecord: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keepTime: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxDateDelta: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minDateDelta: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], picker: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerFormat: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], project: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], strictParsing: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], format: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], max: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], min: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], step: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum TabPanel
 */
class BryntumTabPanelComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeTabChange = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTabChange = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumTabPanelComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumTabPanelComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumTabPanelComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumTabPanelComponent.instanceClass = TabPanel;
BryntumTabPanelComponent.instanceName = 'TabPanel';
BryntumTabPanelComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeTabChange',
    'onCatchAll',
    'onCollapse',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTabChange',
    'onToolClick'
];
BryntumTabPanelComponent.bryntumFeatureNames = [];
BryntumTabPanelComponent.bryntumConfigs = BryntumTabPanelComponent.bryntumFeatureNames.concat([
    'activeTab',
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'animateTabChange',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoHeight',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'footer',
    'header',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tabMaxWidth',
    'tabMinWidth',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumTabPanelComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'animateTabChange',
    'ariaDescription',
    'ariaLabel',
    'autoHeight',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'footer',
    'header',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tabMaxWidth',
    'tabMinWidth',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'trapFocus',
    'ui',
    'weight'
];
BryntumTabPanelComponent.bryntumProps = BryntumTabPanelComponent.bryntumFeatureNames.concat([
    'accessibility',
    'activeTab',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumTabPanelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTabPanelComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumTabPanelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumTabPanelComponent, selector: "bryntum-tab-panel", inputs: { adopt: "adopt", align: "align", anchor: "anchor", animateTabChange: "animateTabChange", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoHeight: "autoHeight", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", footer: "footer", header: "header", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tabMaxWidth: "tabMaxWidth", tabMinWidth: "tabMinWidth", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", trapFocus: "trapFocus", ui: "ui", weight: "weight", activeTab: "activeTab", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", state: "state", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeTabChange: "onBeforeTabChange", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTabChange: "onTabChange", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTabPanelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-tab-panel',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], animateTabChange: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], footer: [{
                type: Input
            }], header: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabMaxWidth: [{
                type: Input
            }], tabMinWidth: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], activeTab: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], state: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeTabChange: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTabChange: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum TextAreaField
 */
class BryntumTextAreaFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumTextAreaFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumTextAreaFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumTextAreaFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumTextAreaFieldComponent.instanceClass = TextAreaField;
BryntumTextAreaFieldComponent.instanceName = 'TextAreaField';
BryntumTextAreaFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onShow',
    'onTrigger'
];
BryntumTextAreaFieldComponent.bryntumFeatureNames = [];
BryntumTextAreaFieldComponent.bryntumConfigs = BryntumTextAreaFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'resize',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumTextAreaFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'resize',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weight'
];
BryntumTextAreaFieldComponent.bryntumProps = BryntumTextAreaFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumTextAreaFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTextAreaFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumTextAreaFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumTextAreaFieldComponent, selector: "bryntum-text-area-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", name: "name", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", resize: "resize", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTextAreaFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-text-area-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], resize: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum TextAreaPickerField
 */
class BryntumTextAreaPickerFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumTextAreaPickerFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumTextAreaPickerFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumTextAreaPickerFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumTextAreaPickerFieldComponent.instanceClass = TextAreaPickerField;
BryntumTextAreaPickerFieldComponent.instanceName = 'TextAreaPickerField';
BryntumTextAreaPickerFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onShow',
    'onTrigger'
];
BryntumTextAreaPickerFieldComponent.bryntumFeatureNames = [];
BryntumTextAreaPickerFieldComponent.bryntumConfigs = BryntumTextAreaPickerFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'picker',
    'pickerAlignElement',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'resize',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumTextAreaPickerFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minLength',
    'monitorResize',
    'name',
    'owner',
    'picker',
    'pickerAlignElement',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'resize',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weight'
];
BryntumTextAreaPickerFieldComponent.bryntumProps = BryntumTextAreaPickerFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumTextAreaPickerFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTextAreaPickerFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumTextAreaPickerFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumTextAreaPickerFieldComponent, selector: "bryntum-text-area-picker-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minLength: "minLength", monitorResize: "monitorResize", name: "name", owner: "owner", picker: "picker", pickerAlignElement: "pickerAlignElement", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", resize: "resize", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTextAreaPickerFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-text-area-picker-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], picker: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], resize: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum TextField
 */
class BryntumTextFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumTextFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumTextFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumTextFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumTextFieldComponent.instanceClass = TextField;
BryntumTextFieldComponent.instanceName = 'TextField';
BryntumTextFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumTextFieldComponent.bryntumFeatureNames = [];
BryntumTextFieldComponent.bryntumConfigs = BryntumTextFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoComplete',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumTextFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoComplete',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minLength',
    'monitorResize',
    'name',
    'owner',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weight'
];
BryntumTextFieldComponent.bryntumProps = BryntumTextFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumTextFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTextFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumTextFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumTextFieldComponent, selector: "bryntum-text-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoComplete: "autoComplete", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minLength: "minLength", monitorResize: "monitorResize", name: "name", owner: "owner", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTextFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-text-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum TimeField
 */
class BryntumTimeFieldComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumTimeFieldComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumTimeFieldComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumTimeFieldComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumTimeFieldComponent.instanceClass = TimeField;
BryntumTimeFieldComponent.instanceName = 'TimeField';
BryntumTimeFieldComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTrigger'
];
BryntumTimeFieldComponent.bryntumFeatureNames = [];
BryntumTimeFieldComponent.bryntumConfigs = BryntumTimeFieldComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'format',
    'height',
    'hidden',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'keepDate',
    'keyStrokeChangeDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'min',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'name',
    'owner',
    'picker',
    'pickerAlignElement',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'step',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggers',
    'ui',
    'validateOnInput',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumTimeFieldComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearable',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'keyStrokeChangeDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minLength',
    'monitorResize',
    'name',
    'owner',
    'picker',
    'pickerAlignElement',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'ui',
    'validateOnInput',
    'weight'
];
BryntumTimeFieldComponent.bryntumProps = BryntumTimeFieldComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'format',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'keepDate',
    'label',
    'margin',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'min',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'step',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumTimeFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTimeFieldComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumTimeFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumTimeFieldComponent, selector: "bryntum-time-field", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearable: "clearable", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", keyStrokeChangeDelay: "keyStrokeChangeDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minLength: "minLength", monitorResize: "monitorResize", name: "name", owner: "owner", picker: "picker", pickerAlignElement: "pickerAlignElement", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", ui: "ui", validateOnInput: "validateOnInput", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", format: "format", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", keepDate: "keepDate", label: "label", margin: "margin", max: "max", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", min: "min", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", step: "step", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTimeFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-time-field',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearable: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], owner: [{
                type: Input
            }], picker: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], format: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], keepDate: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], max: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], min: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], step: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Timeline
 */
class BryntumTimelineComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAfterDependencyCreateDrop = new EventEmitter();
        this.onAfterDependencySave = new EventEmitter();
        this.onAfterDragCreate = new EventEmitter();
        this.onAfterEventDrop = new EventEmitter();
        this.onAfterEventEdit = new EventEmitter();
        this.onAfterEventSave = new EventEmitter();
        this.onAssignmentSelectionChange = new EventEmitter();
        this.onBeforeAssignmentDelete = new EventEmitter();
        this.onBeforeAssignmentSelectionChange = new EventEmitter();
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDependencyAdd = new EventEmitter();
        this.onBeforeDependencyCreateDrag = new EventEmitter();
        this.onBeforeDependencyCreateFinalize = new EventEmitter();
        this.onBeforeDependencyDelete = new EventEmitter();
        this.onBeforeDependencyEdit = new EventEmitter();
        this.onBeforeDependencyEditShow = new EventEmitter();
        this.onBeforeDependencySave = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeDragCreate = new EventEmitter();
        this.onBeforeDragCreateFinalize = new EventEmitter();
        this.onBeforeEventAdd = new EventEmitter();
        this.onBeforeEventDelete = new EventEmitter();
        this.onBeforeEventDrag = new EventEmitter();
        this.onBeforeEventDragSelect = new EventEmitter();
        this.onBeforeEventDropFinalize = new EventEmitter();
        this.onBeforeEventEdit = new EventEmitter();
        this.onBeforeEventEditShow = new EventEmitter();
        this.onBeforeEventResize = new EventEmitter();
        this.onBeforeEventResizeFinalize = new EventEmitter();
        this.onBeforeEventSave = new EventEmitter();
        this.onBeforeEventSelectionChange = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforePan = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforePresetChange = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeShowTerminals = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDateRangeChange = new EventEmitter();
        this.onDependenciesDrawn = new EventEmitter();
        this.onDependencyClick = new EventEmitter();
        this.onDependencyContextMenu = new EventEmitter();
        this.onDependencyCreateDragStart = new EventEmitter();
        this.onDependencyCreateDrop = new EventEmitter();
        this.onDependencyDblClick = new EventEmitter();
        this.onDependencyMouseOut = new EventEmitter();
        this.onDependencyMouseOver = new EventEmitter();
        this.onDependencyValidationComplete = new EventEmitter();
        this.onDependencyValidationStart = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragCreateEnd = new EventEmitter();
        this.onDragCreateStart = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onEventAutoCreated = new EventEmitter();
        this.onEventClick = new EventEmitter();
        this.onEventContextMenu = new EventEmitter();
        this.onEventDblClick = new EventEmitter();
        this.onEventDrag = new EventEmitter();
        this.onEventDragAbort = new EventEmitter();
        this.onEventDragModeChange = new EventEmitter();
        this.onEventDragReset = new EventEmitter();
        this.onEventDragStart = new EventEmitter();
        this.onEventDrop = new EventEmitter();
        this.onEventEditBeforeSetRecord = new EventEmitter();
        this.onEventKeyDown = new EventEmitter();
        this.onEventKeyUp = new EventEmitter();
        this.onEventMenuBeforeShow = new EventEmitter();
        this.onEventMenuItem = new EventEmitter();
        this.onEventMenuShow = new EventEmitter();
        this.onEventMouseDown = new EventEmitter();
        this.onEventMouseEnter = new EventEmitter();
        this.onEventMouseLeave = new EventEmitter();
        this.onEventMouseOut = new EventEmitter();
        this.onEventMouseOver = new EventEmitter();
        this.onEventMouseUp = new EventEmitter();
        this.onEventPartialResize = new EventEmitter();
        this.onEventResizeEnd = new EventEmitter();
        this.onEventResizeStart = new EventEmitter();
        this.onEventSelectionChange = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onNavigate = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPasteNotAllowed = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onPresetChange = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onReleaseEvent = new EventEmitter();
        this.onRenderEvent = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResourceHeaderClick = new EventEmitter();
        this.onResourceHeaderContextmenu = new EventEmitter();
        this.onResourceHeaderDblclick = new EventEmitter();
        this.onResourceMenuBeforeShow = new EventEmitter();
        this.onResourceMenuItem = new EventEmitter();
        this.onResourceMenuShow = new EventEmitter();
        this.onResourceMenuToggleItem = new EventEmitter();
        this.onResourceTimeRangeClick = new EventEmitter();
        this.onResourceTimeRangeContextMenu = new EventEmitter();
        this.onResourceTimeRangeDblClick = new EventEmitter();
        this.onResourceTimeRangeMouseDown = new EventEmitter();
        this.onResourceTimeRangeMouseOut = new EventEmitter();
        this.onResourceTimeRangeMouseOver = new EventEmitter();
        this.onResourceTimeRangeMouseUp = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScheduleClick = new EventEmitter();
        this.onScheduleContextMenu = new EventEmitter();
        this.onScheduleDblClick = new EventEmitter();
        this.onScheduleMenuBeforeShow = new EventEmitter();
        this.onScheduleMenuItem = new EventEmitter();
        this.onScheduleMenuShow = new EventEmitter();
        this.onScheduleMouseDown = new EventEmitter();
        this.onScheduleMouseEnter = new EventEmitter();
        this.onScheduleMouseLeave = new EventEmitter();
        this.onScheduleMouseMove = new EventEmitter();
        this.onScheduleMouseUp = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onTimeAxisChange = new EventEmitter();
        this.onTimeAxisHeaderClick = new EventEmitter();
        this.onTimeAxisHeaderContextMenu = new EventEmitter();
        this.onTimeAxisHeaderDblClick = new EventEmitter();
        this.onTimeAxisHeaderMenuBeforeShow = new EventEmitter();
        this.onTimeAxisHeaderMenuItem = new EventEmitter();
        this.onTimeAxisHeaderMenuShow = new EventEmitter();
        this.onTimelineContextChange = new EventEmitter();
        this.onTimelineViewportResize = new EventEmitter();
        this.onTimeRangeHeaderClick = new EventEmitter();
        this.onTimeRangeHeaderContextMenu = new EventEmitter();
        this.onTimeRangeHeaderDblClick = new EventEmitter();
        this.onTimeSelectionChange = new EventEmitter();
        this.onTimeSelectionElementClick = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.onVisibleDateRangeChange = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumTimelineComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumTimelineComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumTimelineComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumTimelineComponent.instanceClass = Timeline;
BryntumTimelineComponent.instanceName = 'Timeline';
BryntumTimelineComponent.bryntumEvents = [
    'onAfterDependencyCreateDrop',
    'onAfterDependencySave',
    'onAfterDragCreate',
    'onAfterEventDrop',
    'onAfterEventEdit',
    'onAfterEventSave',
    'onAssignmentSelectionChange',
    'onBeforeAssignmentDelete',
    'onBeforeAssignmentSelectionChange',
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDependencyAdd',
    'onBeforeDependencyCreateDrag',
    'onBeforeDependencyCreateFinalize',
    'onBeforeDependencyDelete',
    'onBeforeDependencyEdit',
    'onBeforeDependencyEditShow',
    'onBeforeDependencySave',
    'onBeforeDestroy',
    'onBeforeDragCreate',
    'onBeforeDragCreateFinalize',
    'onBeforeEventAdd',
    'onBeforeEventDelete',
    'onBeforeEventDrag',
    'onBeforeEventDragSelect',
    'onBeforeEventDropFinalize',
    'onBeforeEventEdit',
    'onBeforeEventEditShow',
    'onBeforeEventResize',
    'onBeforeEventResizeFinalize',
    'onBeforeEventSave',
    'onBeforeEventSelectionChange',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforePan',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforePresetChange',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeShowTerminals',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDateRangeChange',
    'onDependenciesDrawn',
    'onDependencyClick',
    'onDependencyContextMenu',
    'onDependencyCreateDragStart',
    'onDependencyCreateDrop',
    'onDependencyDblClick',
    'onDependencyMouseOut',
    'onDependencyMouseOver',
    'onDependencyValidationComplete',
    'onDependencyValidationStart',
    'onDestroy',
    'onDragCreateEnd',
    'onDragCreateStart',
    'onDragSelecting',
    'onEventAutoCreated',
    'onEventClick',
    'onEventContextMenu',
    'onEventDblClick',
    'onEventDrag',
    'onEventDragAbort',
    'onEventDragModeChange',
    'onEventDragReset',
    'onEventDragStart',
    'onEventDrop',
    'onEventEditBeforeSetRecord',
    'onEventKeyDown',
    'onEventKeyUp',
    'onEventMenuBeforeShow',
    'onEventMenuItem',
    'onEventMenuShow',
    'onEventMouseDown',
    'onEventMouseEnter',
    'onEventMouseLeave',
    'onEventMouseOut',
    'onEventMouseOver',
    'onEventMouseUp',
    'onEventPartialResize',
    'onEventResizeEnd',
    'onEventResizeStart',
    'onEventSelectionChange',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onMouseOut',
    'onMouseOver',
    'onNavigate',
    'onPaint',
    'onPaste',
    'onPasteNotAllowed',
    'onPdfExport',
    'onPointerOver',
    'onPresetChange',
    'onRecompose',
    'onReleaseEvent',
    'onRenderEvent',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResourceHeaderClick',
    'onResourceHeaderContextmenu',
    'onResourceHeaderDblclick',
    'onResourceMenuBeforeShow',
    'onResourceMenuItem',
    'onResourceMenuShow',
    'onResourceMenuToggleItem',
    'onResourceTimeRangeClick',
    'onResourceTimeRangeContextMenu',
    'onResourceTimeRangeDblClick',
    'onResourceTimeRangeMouseDown',
    'onResourceTimeRangeMouseOut',
    'onResourceTimeRangeMouseOver',
    'onResourceTimeRangeMouseUp',
    'onResponsive',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScheduleClick',
    'onScheduleContextMenu',
    'onScheduleDblClick',
    'onScheduleMenuBeforeShow',
    'onScheduleMenuItem',
    'onScheduleMenuShow',
    'onScheduleMouseDown',
    'onScheduleMouseEnter',
    'onScheduleMouseLeave',
    'onScheduleMouseMove',
    'onScheduleMouseUp',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onTimeAxisChange',
    'onTimeAxisHeaderClick',
    'onTimeAxisHeaderContextMenu',
    'onTimeAxisHeaderDblClick',
    'onTimeAxisHeaderMenuBeforeShow',
    'onTimeAxisHeaderMenuItem',
    'onTimeAxisHeaderMenuShow',
    'onTimelineContextChange',
    'onTimelineViewportResize',
    'onTimeRangeHeaderClick',
    'onTimeRangeHeaderContextMenu',
    'onTimeRangeHeaderDblClick',
    'onTimeSelectionChange',
    'onTimeSelectionElementClick',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onUnsplit',
    'onVisibleDateRangeChange'
];
BryntumTimelineComponent.bryntumFeatureNames = [
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnLinesFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'dependenciesFeature',
    'dependencyEditFeature',
    'eventCopyPasteFeature',
    'eventDragFeature',
    'eventDragCreateFeature',
    'eventDragSelectFeature',
    'eventEditFeature',
    'eventFilterFeature',
    'eventMenuFeature',
    'eventNonWorkingTimeFeature',
    'eventResizeFeature',
    'eventTooltipFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'headerZoomFeature',
    'labelsFeature',
    'mergeCellsFeature',
    'nonWorkingTimeFeature',
    'panFeature',
    'pdfExportFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'resourceMenuFeature',
    'resourceTimeRangesFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'scheduleContextFeature',
    'scheduleMenuFeature',
    'scheduleTooltipFeature',
    'searchFeature',
    'simpleEventEditFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stickyEventsFeature',
    'stripeFeature',
    'summaryFeature',
    'timeAxisHeaderMenuFeature',
    'timeRangesFeature',
    'timeSelectionFeature',
    'treeFeature',
    'treeGroupFeature'
];
BryntumTimelineComponent.bryntumConfigs = BryntumTimelineComponent.bryntumFeatureNames.concat([
    'adopt',
    'alignSelf',
    'allowCreate',
    'allowDropOnEventBar',
    'allowOver',
    'allowOverlap',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'assignments',
    'assignmentStore',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'barMargin',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'createEventOnDblClick',
    'creationTooltip',
    'creationTooltipTemplate',
    'crudManager',
    'crudManagerClass',
    'data',
    'dataset',
    'date',
    'dateFormat',
    'dateSeparator',
    'defaultRegion',
    'defaultResourceImageName',
    'dependencies',
    'dependencyStore',
    'descriptionFormat',
    'descriptionRenderer',
    'deselectAllOnScheduleClick',
    'deselectOnClick',
    'destroyStore',
    'destroyStores',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'displayDateFormat',
    'dock',
    'durationDisplayPrecision',
    'emptyText',
    'enableDeleteKey',
    'enableEventAnimations',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'endDate',
    'endParamName',
    'eventBarTextField',
    'eventBodyTemplate',
    'eventColor',
    'eventLayout',
    'eventRenderer',
    'eventRendererThisObj',
    'events',
    'eventSelectionDisabled',
    'eventStore',
    'eventStyle',
    'extraData',
    'features',
    'fillLastColumn',
    'fillTicks',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forceFit',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getDateConstraints',
    'getHtml',
    'getRowHeight',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hideRowHover',
    'highlightPredecessors',
    'highlightSuccessors',
    'horizontalEventSorterFn',
    'hoverDelay',
    'id',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'insertBefore',
    'insertFirst',
    'isEventSelectable',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'maintainSelectionOnDatasetChange',
    'managedEventSizing',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxTimeAxisUnit',
    'maxWidth',
    'maxZoomLevel',
    'milestoneAlign',
    'milestoneCharWidth',
    'milestoneLayoutMode',
    'milestoneTextPosition',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'modal',
    'mode',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'multiEventSelect',
    'narrowEventWidth',
    'overlappingEventSorter',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preCalculateHeightLimit',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'presets',
    'preventTooltipOnTouch',
    'project',
    'range',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceColumns',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceMargin',
    'resources',
    'resourceStore',
    'resourceTimeRangeRenderer',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'selectResourceOnEventNavigate',
    'selectResourceOnScheduleClick',
    'showCreationTooltip',
    'showDirty',
    'showEventColorPickers',
    'showLagInTooltip',
    'showOnClick',
    'showOnHover',
    'showRecurringUI',
    'showTooltip',
    'snap',
    'snapRelativeToEventStartDate',
    'startDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stepUnit',
    'stickyHeaders',
    'store',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'terminalCls',
    'terminalHideDelay',
    'terminalOffset',
    'terminalShowDelay',
    'terminalSides',
    'terminalSize',
    'tickSize',
    'timeAxis',
    'timeRanges',
    'timeRangeStore',
    'title',
    'tools',
    'tooltipTemplate',
    'trackMouse',
    'transitionDuration',
    'triggerSelectionChangeOnRemove',
    'ui',
    'updateTimelineContextOnScroll',
    'useInitialAnimation',
    'verticalTimeAxisColumn',
    'viewPreset',
    'visibleDate',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'width',
    'workingTime',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
]);
BryntumTimelineComponent.bryntumConfigsOnly = [
    'adopt',
    'allowCreate',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'ariaDescription',
    'ariaLabel',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'createEventOnDblClick',
    'creationTooltip',
    'crudManagerClass',
    'date',
    'defaultRegion',
    'defaultResourceImageName',
    'descriptionRenderer',
    'deselectAllOnScheduleClick',
    'deselectOnClick',
    'destroyStore',
    'destroyStores',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'durationDisplayPrecision',
    'enableDeleteKey',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'endParamName',
    'eventBarTextField',
    'eventBodyTemplate',
    'eventRenderer',
    'eventRendererThisObj',
    'eventSelectionDisabled',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getDateConstraints',
    'getHtml',
    'getRowHeight',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'highlightPredecessors',
    'highlightSuccessors',
    'horizontalEventSorterFn',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'maintainSelectionOnDatasetChange',
    'managedEventSizing',
    'maskDefaults',
    'masked',
    'maximizable',
    'maxTimeAxisUnit',
    'modal',
    'mode',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'multiEventSelect',
    'narrowEventWidth',
    'owner',
    'partner',
    'passStartEndParameters',
    'plugins',
    'preCalculateHeightLimit',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'range',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceColumns',
    'resourceImageExtension',
    'resourceImagePath',
    'resourceTimeRangeRenderer',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'scrollerClass',
    'scrollManager',
    'showCreationTooltip',
    'showDirty',
    'showEventColorPickers',
    'showLagInTooltip',
    'showOnClick',
    'showOnHover',
    'showRecurringUI',
    'showTooltip',
    'snapRelativeToEventStartDate',
    'startParamName',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stepUnit',
    'stickyHeaders',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'terminalCls',
    'terminalSides',
    'timeAxis',
    'trackMouse',
    'triggerSelectionChangeOnRemove',
    'ui',
    'verticalTimeAxisColumn',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
];
BryntumTimelineComponent.bryntumProps = BryntumTimelineComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'allowDropOnEventBar',
    'allowOverlap',
    'appendTo',
    'ariaLive',
    'assignments',
    'assignmentStore',
    'barMargin',
    'cls',
    'columnLines',
    'columns',
    'creationTooltipTemplate',
    'crudManager',
    'data',
    'dataset',
    'dateFormat',
    'dateSeparator',
    'dependencies',
    'dependencyStore',
    'descriptionFormat',
    'disabled',
    'displayDateFormat',
    'emptyText',
    'enableEventAnimations',
    'enableUndoRedoKeys',
    'endDate',
    'eventColor',
    'eventLayout',
    'events',
    'eventStore',
    'eventStyle',
    'extraData',
    'fillTicks',
    'flex',
    'forceFit',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hideRowHover',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'isEngineReady',
    'isEventSelectable',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'maxZoomLevel',
    'milestoneAlign',
    'milestoneCharWidth',
    'milestoneLayoutMode',
    'milestoneTextPosition',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'overlappingEventSorter',
    'preserveScroll',
    'presets',
    'project',
    'readOnly',
    'resourceMargin',
    'resources',
    'resourceStore',
    'resourceTimeRanges',
    'resourceTimeRangeStore',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollLeft',
    'scrollTop',
    'scrollX',
    'selectedAssignments',
    'selectedCell',
    'selectedCells',
    'selectedEvents',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'selectResourceOnEventNavigate',
    'selectResourceOnScheduleClick',
    'snap',
    'startDate',
    'state',
    'store',
    'terminalHideDelay',
    'terminalOffset',
    'terminalShowDelay',
    'terminalSize',
    'tickSize',
    'timeRanges',
    'timeRangeStore',
    'timeResolution',
    'timeZone',
    'title',
    'tools',
    'tooltip',
    'tooltipTemplate',
    'transitionDuration',
    'updateTimelineContextOnScroll',
    'useInitialAnimation',
    'viewPreset',
    'visibleDate',
    'width',
    'workingTime',
    'zoomLevel'
]);
BryntumTimelineComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTimelineComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumTimelineComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumTimelineComponent, selector: "bryntum-timeline", inputs: { adopt: "adopt", allowCreate: "allowCreate", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", animateTreeNodeToggle: "animateTreeNodeToggle", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoAdjustTimeAxis: "autoAdjustTimeAxis", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", bufferCoef: "bufferCoef", bufferThreshold: "bufferThreshold", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", createEventOnDblClick: "createEventOnDblClick", creationTooltip: "creationTooltip", crudManagerClass: "crudManagerClass", date: "date", defaultRegion: "defaultRegion", defaultResourceImageName: "defaultResourceImageName", descriptionRenderer: "descriptionRenderer", deselectAllOnScheduleClick: "deselectAllOnScheduleClick", deselectOnClick: "deselectOnClick", destroyStore: "destroyStore", destroyStores: "destroyStores", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", dock: "dock", durationDisplayPrecision: "durationDisplayPrecision", enableDeleteKey: "enableDeleteKey", enableRecurringEvents: "enableRecurringEvents", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", endParamName: "endParamName", eventBarTextField: "eventBarTextField", eventBodyTemplate: "eventBodyTemplate", eventRenderer: "eventRenderer", eventRendererThisObj: "eventRendererThisObj", eventSelectionDisabled: "eventSelectionDisabled", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getDateConstraints: "getDateConstraints", getHtml: "getHtml", getRowHeight: "getRowHeight", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", highlightPredecessors: "highlightPredecessors", highlightSuccessors: "highlightSuccessors", horizontalEventSorterFn: "horizontalEventSorterFn", ignoreDomEventsWhileScrolling: "ignoreDomEventsWhileScrolling", ignoreParentReadOnly: "ignoreParentReadOnly", infiniteScroll: "infiniteScroll", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", maintainSelectionOnDatasetChange: "maintainSelectionOnDatasetChange", managedEventSizing: "managedEventSizing", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", maxTimeAxisUnit: "maxTimeAxisUnit", modal: "modal", mode: "mode", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", multiEventSelect: "multiEventSelect", narrowEventWidth: "narrowEventWidth", owner: "owner", partner: "partner", passStartEndParameters: "passStartEndParameters", plugins: "plugins", preCalculateHeightLimit: "preCalculateHeightLimit", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", range: "range", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", resourceColumns: "resourceColumns", resourceImageExtension: "resourceImageExtension", resourceImagePath: "resourceImagePath", resourceTimeRangeRenderer: "resourceTimeRangeRenderer", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", scrollerClass: "scrollerClass", scrollManager: "scrollManager", showCreationTooltip: "showCreationTooltip", showDirty: "showDirty", showEventColorPickers: "showEventColorPickers", showLagInTooltip: "showLagInTooltip", showOnClick: "showOnClick", showOnHover: "showOnHover", showRecurringUI: "showRecurringUI", showTooltip: "showTooltip", snapRelativeToEventStartDate: "snapRelativeToEventStartDate", startParamName: "startParamName", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", stepUnit: "stepUnit", stickyHeaders: "stickyHeaders", strips: "strips", subGridConfigs: "subGridConfigs", suppressFit: "suppressFit", syncMask: "syncMask", tab: "tab", tbar: "tbar", terminalCls: "terminalCls", terminalSides: "terminalSides", timeAxis: "timeAxis", trackMouse: "trackMouse", triggerSelectionChangeOnRemove: "triggerSelectionChangeOnRemove", ui: "ui", verticalTimeAxisColumn: "verticalTimeAxisColumn", visibleZoomFactor: "visibleZoomFactor", weekStartDay: "weekStartDay", weight: "weight", zoomKeepsOriginalTimespan: "zoomKeepsOriginalTimespan", zoomOnMouseWheel: "zoomOnMouseWheel", zoomOnTimeAxisDoubleClick: "zoomOnTimeAxisDoubleClick", alignSelf: "alignSelf", allowDropOnEventBar: "allowDropOnEventBar", allowOverlap: "allowOverlap", appendTo: "appendTo", ariaLive: "ariaLive", assignments: "assignments", assignmentStore: "assignmentStore", barMargin: "barMargin", cls: "cls", columnLines: "columnLines", columns: "columns", creationTooltipTemplate: "creationTooltipTemplate", crudManager: "crudManager", data: "data", dataset: "dataset", dateFormat: "dateFormat", dateSeparator: "dateSeparator", dependencies: "dependencies", dependencyStore: "dependencyStore", descriptionFormat: "descriptionFormat", disabled: "disabled", displayDateFormat: "displayDateFormat", emptyText: "emptyText", enableEventAnimations: "enableEventAnimations", enableUndoRedoKeys: "enableUndoRedoKeys", endDate: "endDate", eventColor: "eventColor", eventLayout: "eventLayout", events: "events", eventStore: "eventStore", eventStyle: "eventStyle", extraData: "extraData", fillTicks: "fillTicks", flex: "flex", forceFit: "forceFit", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hideRowHover: "hideRowHover", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", isEventSelectable: "isEventSelectable", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", maxZoomLevel: "maxZoomLevel", milestoneAlign: "milestoneAlign", milestoneCharWidth: "milestoneCharWidth", milestoneLayoutMode: "milestoneLayoutMode", milestoneTextPosition: "milestoneTextPosition", minHeight: "minHeight", minWidth: "minWidth", minZoomLevel: "minZoomLevel", overlappingEventSorter: "overlappingEventSorter", preserveScroll: "preserveScroll", presets: "presets", project: "project", readOnly: "readOnly", resourceMargin: "resourceMargin", resources: "resources", resourceStore: "resourceStore", resourceTimeRanges: "resourceTimeRanges", resourceTimeRangeStore: "resourceTimeRangeStore", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", selectResourceOnEventNavigate: "selectResourceOnEventNavigate", selectResourceOnScheduleClick: "selectResourceOnScheduleClick", snap: "snap", startDate: "startDate", store: "store", terminalHideDelay: "terminalHideDelay", terminalOffset: "terminalOffset", terminalShowDelay: "terminalShowDelay", terminalSize: "terminalSize", tickSize: "tickSize", timeRanges: "timeRanges", timeRangeStore: "timeRangeStore", title: "title", tools: "tools", tooltipTemplate: "tooltipTemplate", transitionDuration: "transitionDuration", updateTimelineContextOnScroll: "updateTimelineContextOnScroll", useInitialAnimation: "useInitialAnimation", viewPreset: "viewPreset", visibleDate: "visibleDate", width: "width", workingTime: "workingTime", accessibility: "accessibility", isEngineReady: "isEngineReady", scrollLeft: "scrollLeft", scrollTop: "scrollTop", scrollX: "scrollX", selectedAssignments: "selectedAssignments", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedEvents: "selectedEvents", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", timeResolution: "timeResolution", timeZone: "timeZone", tooltip: "tooltip", zoomLevel: "zoomLevel", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnLinesFeature: "columnLinesFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", dependenciesFeature: "dependenciesFeature", dependencyEditFeature: "dependencyEditFeature", eventCopyPasteFeature: "eventCopyPasteFeature", eventDragFeature: "eventDragFeature", eventDragCreateFeature: "eventDragCreateFeature", eventDragSelectFeature: "eventDragSelectFeature", eventEditFeature: "eventEditFeature", eventFilterFeature: "eventFilterFeature", eventMenuFeature: "eventMenuFeature", eventNonWorkingTimeFeature: "eventNonWorkingTimeFeature", eventResizeFeature: "eventResizeFeature", eventTooltipFeature: "eventTooltipFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", headerZoomFeature: "headerZoomFeature", labelsFeature: "labelsFeature", mergeCellsFeature: "mergeCellsFeature", nonWorkingTimeFeature: "nonWorkingTimeFeature", panFeature: "panFeature", pdfExportFeature: "pdfExportFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", resourceMenuFeature: "resourceMenuFeature", resourceTimeRangesFeature: "resourceTimeRangesFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", scheduleContextFeature: "scheduleContextFeature", scheduleMenuFeature: "scheduleMenuFeature", scheduleTooltipFeature: "scheduleTooltipFeature", searchFeature: "searchFeature", simpleEventEditFeature: "simpleEventEditFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stickyEventsFeature: "stickyEventsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", timeAxisHeaderMenuFeature: "timeAxisHeaderMenuFeature", timeRangesFeature: "timeRangesFeature", timeSelectionFeature: "timeSelectionFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature" }, outputs: { onAfterDependencyCreateDrop: "onAfterDependencyCreateDrop", onAfterDependencySave: "onAfterDependencySave", onAfterDragCreate: "onAfterDragCreate", onAfterEventDrop: "onAfterEventDrop", onAfterEventEdit: "onAfterEventEdit", onAfterEventSave: "onAfterEventSave", onAssignmentSelectionChange: "onAssignmentSelectionChange", onBeforeAssignmentDelete: "onBeforeAssignmentDelete", onBeforeAssignmentSelectionChange: "onBeforeAssignmentSelectionChange", onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDependencyAdd: "onBeforeDependencyAdd", onBeforeDependencyCreateDrag: "onBeforeDependencyCreateDrag", onBeforeDependencyCreateFinalize: "onBeforeDependencyCreateFinalize", onBeforeDependencyDelete: "onBeforeDependencyDelete", onBeforeDependencyEdit: "onBeforeDependencyEdit", onBeforeDependencyEditShow: "onBeforeDependencyEditShow", onBeforeDependencySave: "onBeforeDependencySave", onBeforeDestroy: "onBeforeDestroy", onBeforeDragCreate: "onBeforeDragCreate", onBeforeDragCreateFinalize: "onBeforeDragCreateFinalize", onBeforeEventAdd: "onBeforeEventAdd", onBeforeEventDelete: "onBeforeEventDelete", onBeforeEventDrag: "onBeforeEventDrag", onBeforeEventDragSelect: "onBeforeEventDragSelect", onBeforeEventDropFinalize: "onBeforeEventDropFinalize", onBeforeEventEdit: "onBeforeEventEdit", onBeforeEventEditShow: "onBeforeEventEditShow", onBeforeEventResize: "onBeforeEventResize", onBeforeEventResizeFinalize: "onBeforeEventResizeFinalize", onBeforeEventSave: "onBeforeEventSave", onBeforeEventSelectionChange: "onBeforeEventSelectionChange", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforePan: "onBeforePan", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforePresetChange: "onBeforePresetChange", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeShowTerminals: "onBeforeShowTerminals", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDateRangeChange: "onDateRangeChange", onDependenciesDrawn: "onDependenciesDrawn", onDependencyClick: "onDependencyClick", onDependencyContextMenu: "onDependencyContextMenu", onDependencyCreateDragStart: "onDependencyCreateDragStart", onDependencyCreateDrop: "onDependencyCreateDrop", onDependencyDblClick: "onDependencyDblClick", onDependencyMouseOut: "onDependencyMouseOut", onDependencyMouseOver: "onDependencyMouseOver", onDependencyValidationComplete: "onDependencyValidationComplete", onDependencyValidationStart: "onDependencyValidationStart", onDestroy: "onDestroy", onDragCreateEnd: "onDragCreateEnd", onDragCreateStart: "onDragCreateStart", onDragSelecting: "onDragSelecting", onEventAutoCreated: "onEventAutoCreated", onEventClick: "onEventClick", onEventContextMenu: "onEventContextMenu", onEventDblClick: "onEventDblClick", onEventDrag: "onEventDrag", onEventDragAbort: "onEventDragAbort", onEventDragModeChange: "onEventDragModeChange", onEventDragReset: "onEventDragReset", onEventDragStart: "onEventDragStart", onEventDrop: "onEventDrop", onEventEditBeforeSetRecord: "onEventEditBeforeSetRecord", onEventKeyDown: "onEventKeyDown", onEventKeyUp: "onEventKeyUp", onEventMenuBeforeShow: "onEventMenuBeforeShow", onEventMenuItem: "onEventMenuItem", onEventMenuShow: "onEventMenuShow", onEventMouseDown: "onEventMouseDown", onEventMouseEnter: "onEventMouseEnter", onEventMouseLeave: "onEventMouseLeave", onEventMouseOut: "onEventMouseOut", onEventMouseOver: "onEventMouseOver", onEventMouseUp: "onEventMouseUp", onEventPartialResize: "onEventPartialResize", onEventResizeEnd: "onEventResizeEnd", onEventResizeStart: "onEventResizeStart", onEventSelectionChange: "onEventSelectionChange", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onNavigate: "onNavigate", onPaint: "onPaint", onPaste: "onPaste", onPasteNotAllowed: "onPasteNotAllowed", onPdfExport: "onPdfExport", onPointerOver: "onPointerOver", onPresetChange: "onPresetChange", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onReleaseEvent: "onReleaseEvent", onRenderEvent: "onRenderEvent", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResourceHeaderClick: "onResourceHeaderClick", onResourceHeaderContextmenu: "onResourceHeaderContextmenu", onResourceHeaderDblclick: "onResourceHeaderDblclick", onResourceMenuBeforeShow: "onResourceMenuBeforeShow", onResourceMenuItem: "onResourceMenuItem", onResourceMenuShow: "onResourceMenuShow", onResourceMenuToggleItem: "onResourceMenuToggleItem", onResourceTimeRangeClick: "onResourceTimeRangeClick", onResourceTimeRangeContextMenu: "onResourceTimeRangeContextMenu", onResourceTimeRangeDblClick: "onResourceTimeRangeDblClick", onResourceTimeRangeMouseDown: "onResourceTimeRangeMouseDown", onResourceTimeRangeMouseOut: "onResourceTimeRangeMouseOut", onResourceTimeRangeMouseOver: "onResourceTimeRangeMouseOver", onResourceTimeRangeMouseUp: "onResourceTimeRangeMouseUp", onResponsive: "onResponsive", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScheduleClick: "onScheduleClick", onScheduleContextMenu: "onScheduleContextMenu", onScheduleDblClick: "onScheduleDblClick", onScheduleMenuBeforeShow: "onScheduleMenuBeforeShow", onScheduleMenuItem: "onScheduleMenuItem", onScheduleMenuShow: "onScheduleMenuShow", onScheduleMouseDown: "onScheduleMouseDown", onScheduleMouseEnter: "onScheduleMouseEnter", onScheduleMouseLeave: "onScheduleMouseLeave", onScheduleMouseMove: "onScheduleMouseMove", onScheduleMouseUp: "onScheduleMouseUp", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onTimeAxisChange: "onTimeAxisChange", onTimeAxisHeaderClick: "onTimeAxisHeaderClick", onTimeAxisHeaderContextMenu: "onTimeAxisHeaderContextMenu", onTimeAxisHeaderDblClick: "onTimeAxisHeaderDblClick", onTimeAxisHeaderMenuBeforeShow: "onTimeAxisHeaderMenuBeforeShow", onTimeAxisHeaderMenuItem: "onTimeAxisHeaderMenuItem", onTimeAxisHeaderMenuShow: "onTimeAxisHeaderMenuShow", onTimelineContextChange: "onTimelineContextChange", onTimelineViewportResize: "onTimelineViewportResize", onTimeRangeHeaderClick: "onTimeRangeHeaderClick", onTimeRangeHeaderContextMenu: "onTimeRangeHeaderContextMenu", onTimeRangeHeaderDblClick: "onTimeRangeHeaderDblClick", onTimeSelectionChange: "onTimeSelectionChange", onTimeSelectionElementClick: "onTimeSelectionElementClick", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onUnsplit: "onUnsplit", onVisibleDateRangeChange: "onVisibleDateRangeChange" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTimelineComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-timeline',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], allowCreate: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoAdjustTimeAxis: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], bufferCoef: [{
                type: Input
            }], bufferThreshold: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], createEventOnDblClick: [{
                type: Input
            }], creationTooltip: [{
                type: Input
            }], crudManagerClass: [{
                type: Input
            }], date: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], defaultResourceImageName: [{
                type: Input
            }], descriptionRenderer: [{
                type: Input
            }], deselectAllOnScheduleClick: [{
                type: Input
            }], deselectOnClick: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], destroyStores: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], dock: [{
                type: Input
            }], durationDisplayPrecision: [{
                type: Input
            }], enableDeleteKey: [{
                type: Input
            }], enableRecurringEvents: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], endParamName: [{
                type: Input
            }], eventBarTextField: [{
                type: Input
            }], eventBodyTemplate: [{
                type: Input
            }], eventRenderer: [{
                type: Input
            }], eventRendererThisObj: [{
                type: Input
            }], eventSelectionDisabled: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getDateConstraints: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], highlightPredecessors: [{
                type: Input
            }], highlightSuccessors: [{
                type: Input
            }], horizontalEventSorterFn: [{
                type: Input
            }], ignoreDomEventsWhileScrolling: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], infiniteScroll: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], maintainSelectionOnDatasetChange: [{
                type: Input
            }], managedEventSizing: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], maxTimeAxisUnit: [{
                type: Input
            }], modal: [{
                type: Input
            }], mode: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], multiEventSelect: [{
                type: Input
            }], narrowEventWidth: [{
                type: Input
            }], owner: [{
                type: Input
            }], partner: [{
                type: Input
            }], passStartEndParameters: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preCalculateHeightLimit: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], range: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], resourceColumns: [{
                type: Input
            }], resourceImageExtension: [{
                type: Input
            }], resourceImagePath: [{
                type: Input
            }], resourceTimeRangeRenderer: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], showCreationTooltip: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showEventColorPickers: [{
                type: Input
            }], showLagInTooltip: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], showRecurringUI: [{
                type: Input
            }], showTooltip: [{
                type: Input
            }], snapRelativeToEventStartDate: [{
                type: Input
            }], startParamName: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], stepUnit: [{
                type: Input
            }], stickyHeaders: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], suppressFit: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], terminalCls: [{
                type: Input
            }], terminalSides: [{
                type: Input
            }], timeAxis: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], triggerSelectionChangeOnRemove: [{
                type: Input
            }], ui: [{
                type: Input
            }], verticalTimeAxisColumn: [{
                type: Input
            }], visibleZoomFactor: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], zoomKeepsOriginalTimespan: [{
                type: Input
            }], zoomOnMouseWheel: [{
                type: Input
            }], zoomOnTimeAxisDoubleClick: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], allowDropOnEventBar: [{
                type: Input
            }], allowOverlap: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], assignments: [{
                type: Input
            }], assignmentStore: [{
                type: Input
            }], barMargin: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], creationTooltipTemplate: [{
                type: Input
            }], crudManager: [{
                type: Input
            }], data: [{
                type: Input
            }], dataset: [{
                type: Input
            }], dateFormat: [{
                type: Input
            }], dateSeparator: [{
                type: Input
            }], dependencies: [{
                type: Input
            }], dependencyStore: [{
                type: Input
            }], descriptionFormat: [{
                type: Input
            }], disabled: [{
                type: Input
            }], displayDateFormat: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableEventAnimations: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], endDate: [{
                type: Input
            }], eventColor: [{
                type: Input
            }], eventLayout: [{
                type: Input
            }], events: [{
                type: Input
            }], eventStore: [{
                type: Input
            }], eventStyle: [{
                type: Input
            }], extraData: [{
                type: Input
            }], fillTicks: [{
                type: Input
            }], flex: [{
                type: Input
            }], forceFit: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hideRowHover: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], isEventSelectable: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], maxZoomLevel: [{
                type: Input
            }], milestoneAlign: [{
                type: Input
            }], milestoneCharWidth: [{
                type: Input
            }], milestoneLayoutMode: [{
                type: Input
            }], milestoneTextPosition: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], minZoomLevel: [{
                type: Input
            }], overlappingEventSorter: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], presets: [{
                type: Input
            }], project: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], resourceMargin: [{
                type: Input
            }], resources: [{
                type: Input
            }], resourceStore: [{
                type: Input
            }], resourceTimeRanges: [{
                type: Input
            }], resourceTimeRangeStore: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], selectResourceOnEventNavigate: [{
                type: Input
            }], selectResourceOnScheduleClick: [{
                type: Input
            }], snap: [{
                type: Input
            }], startDate: [{
                type: Input
            }], store: [{
                type: Input
            }], terminalHideDelay: [{
                type: Input
            }], terminalOffset: [{
                type: Input
            }], terminalShowDelay: [{
                type: Input
            }], terminalSize: [{
                type: Input
            }], tickSize: [{
                type: Input
            }], timeRanges: [{
                type: Input
            }], timeRangeStore: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltipTemplate: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], updateTimelineContextOnScroll: [{
                type: Input
            }], useInitialAnimation: [{
                type: Input
            }], viewPreset: [{
                type: Input
            }], visibleDate: [{
                type: Input
            }], width: [{
                type: Input
            }], workingTime: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], isEngineReady: [{
                type: Input
            }], scrollLeft: [{
                type: Input
            }], scrollTop: [{
                type: Input
            }], scrollX: [{
                type: Input
            }], selectedAssignments: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedEvents: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], timeResolution: [{
                type: Input
            }], timeZone: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], zoomLevel: [{
                type: Input
            }], onAfterDependencyCreateDrop: [{
                type: Output
            }], onAfterDependencySave: [{
                type: Output
            }], onAfterDragCreate: [{
                type: Output
            }], onAfterEventDrop: [{
                type: Output
            }], onAfterEventEdit: [{
                type: Output
            }], onAfterEventSave: [{
                type: Output
            }], onAssignmentSelectionChange: [{
                type: Output
            }], onBeforeAssignmentDelete: [{
                type: Output
            }], onBeforeAssignmentSelectionChange: [{
                type: Output
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDependencyAdd: [{
                type: Output
            }], onBeforeDependencyCreateDrag: [{
                type: Output
            }], onBeforeDependencyCreateFinalize: [{
                type: Output
            }], onBeforeDependencyDelete: [{
                type: Output
            }], onBeforeDependencyEdit: [{
                type: Output
            }], onBeforeDependencyEditShow: [{
                type: Output
            }], onBeforeDependencySave: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeDragCreate: [{
                type: Output
            }], onBeforeDragCreateFinalize: [{
                type: Output
            }], onBeforeEventAdd: [{
                type: Output
            }], onBeforeEventDelete: [{
                type: Output
            }], onBeforeEventDrag: [{
                type: Output
            }], onBeforeEventDragSelect: [{
                type: Output
            }], onBeforeEventDropFinalize: [{
                type: Output
            }], onBeforeEventEdit: [{
                type: Output
            }], onBeforeEventEditShow: [{
                type: Output
            }], onBeforeEventResize: [{
                type: Output
            }], onBeforeEventResizeFinalize: [{
                type: Output
            }], onBeforeEventSave: [{
                type: Output
            }], onBeforeEventSelectionChange: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforePan: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforePresetChange: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeShowTerminals: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDateRangeChange: [{
                type: Output
            }], onDependenciesDrawn: [{
                type: Output
            }], onDependencyClick: [{
                type: Output
            }], onDependencyContextMenu: [{
                type: Output
            }], onDependencyCreateDragStart: [{
                type: Output
            }], onDependencyCreateDrop: [{
                type: Output
            }], onDependencyDblClick: [{
                type: Output
            }], onDependencyMouseOut: [{
                type: Output
            }], onDependencyMouseOver: [{
                type: Output
            }], onDependencyValidationComplete: [{
                type: Output
            }], onDependencyValidationStart: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragCreateEnd: [{
                type: Output
            }], onDragCreateStart: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onEventAutoCreated: [{
                type: Output
            }], onEventClick: [{
                type: Output
            }], onEventContextMenu: [{
                type: Output
            }], onEventDblClick: [{
                type: Output
            }], onEventDrag: [{
                type: Output
            }], onEventDragAbort: [{
                type: Output
            }], onEventDragModeChange: [{
                type: Output
            }], onEventDragReset: [{
                type: Output
            }], onEventDragStart: [{
                type: Output
            }], onEventDrop: [{
                type: Output
            }], onEventEditBeforeSetRecord: [{
                type: Output
            }], onEventKeyDown: [{
                type: Output
            }], onEventKeyUp: [{
                type: Output
            }], onEventMenuBeforeShow: [{
                type: Output
            }], onEventMenuItem: [{
                type: Output
            }], onEventMenuShow: [{
                type: Output
            }], onEventMouseDown: [{
                type: Output
            }], onEventMouseEnter: [{
                type: Output
            }], onEventMouseLeave: [{
                type: Output
            }], onEventMouseOut: [{
                type: Output
            }], onEventMouseOver: [{
                type: Output
            }], onEventMouseUp: [{
                type: Output
            }], onEventPartialResize: [{
                type: Output
            }], onEventResizeEnd: [{
                type: Output
            }], onEventResizeStart: [{
                type: Output
            }], onEventSelectionChange: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onNavigate: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPasteNotAllowed: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onPresetChange: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onReleaseEvent: [{
                type: Output
            }], onRenderEvent: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResourceHeaderClick: [{
                type: Output
            }], onResourceHeaderContextmenu: [{
                type: Output
            }], onResourceHeaderDblclick: [{
                type: Output
            }], onResourceMenuBeforeShow: [{
                type: Output
            }], onResourceMenuItem: [{
                type: Output
            }], onResourceMenuShow: [{
                type: Output
            }], onResourceMenuToggleItem: [{
                type: Output
            }], onResourceTimeRangeClick: [{
                type: Output
            }], onResourceTimeRangeContextMenu: [{
                type: Output
            }], onResourceTimeRangeDblClick: [{
                type: Output
            }], onResourceTimeRangeMouseDown: [{
                type: Output
            }], onResourceTimeRangeMouseOut: [{
                type: Output
            }], onResourceTimeRangeMouseOver: [{
                type: Output
            }], onResourceTimeRangeMouseUp: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScheduleClick: [{
                type: Output
            }], onScheduleContextMenu: [{
                type: Output
            }], onScheduleDblClick: [{
                type: Output
            }], onScheduleMenuBeforeShow: [{
                type: Output
            }], onScheduleMenuItem: [{
                type: Output
            }], onScheduleMenuShow: [{
                type: Output
            }], onScheduleMouseDown: [{
                type: Output
            }], onScheduleMouseEnter: [{
                type: Output
            }], onScheduleMouseLeave: [{
                type: Output
            }], onScheduleMouseMove: [{
                type: Output
            }], onScheduleMouseUp: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onTimeAxisChange: [{
                type: Output
            }], onTimeAxisHeaderClick: [{
                type: Output
            }], onTimeAxisHeaderContextMenu: [{
                type: Output
            }], onTimeAxisHeaderDblClick: [{
                type: Output
            }], onTimeAxisHeaderMenuBeforeShow: [{
                type: Output
            }], onTimeAxisHeaderMenuItem: [{
                type: Output
            }], onTimeAxisHeaderMenuShow: [{
                type: Output
            }], onTimelineContextChange: [{
                type: Output
            }], onTimelineViewportResize: [{
                type: Output
            }], onTimeRangeHeaderClick: [{
                type: Output
            }], onTimeRangeHeaderContextMenu: [{
                type: Output
            }], onTimeRangeHeaderDblClick: [{
                type: Output
            }], onTimeSelectionChange: [{
                type: Output
            }], onTimeSelectionElementClick: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], onVisibleDateRangeChange: [{
                type: Output
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnLinesFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], dependenciesFeature: [{
                type: Input
            }], dependencyEditFeature: [{
                type: Input
            }], eventCopyPasteFeature: [{
                type: Input
            }], eventDragFeature: [{
                type: Input
            }], eventDragCreateFeature: [{
                type: Input
            }], eventDragSelectFeature: [{
                type: Input
            }], eventEditFeature: [{
                type: Input
            }], eventFilterFeature: [{
                type: Input
            }], eventMenuFeature: [{
                type: Input
            }], eventNonWorkingTimeFeature: [{
                type: Input
            }], eventResizeFeature: [{
                type: Input
            }], eventTooltipFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], headerZoomFeature: [{
                type: Input
            }], labelsFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], nonWorkingTimeFeature: [{
                type: Input
            }], panFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], resourceMenuFeature: [{
                type: Input
            }], resourceTimeRangesFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], scheduleContextFeature: [{
                type: Input
            }], scheduleMenuFeature: [{
                type: Input
            }], scheduleTooltipFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], simpleEventEditFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stickyEventsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], timeAxisHeaderMenuFeature: [{
                type: Input
            }], timeRangesFeature: [{
                type: Input
            }], timeSelectionFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum TimelineHistogram
 */
class BryntumTimelineHistogramComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeAssignmentDelete = new EventEmitter();
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeEventDelete = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeHistogramDataCacheSet = new EventEmitter();
        this.onBeforePan = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforePresetChange = new EventEmitter();
        this.onBeforeRenderHistogramRow = new EventEmitter();
        this.onBeforeRenderRecordHistogram = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDateRangeChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onHistogramDataCacheSet = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onPresetChange = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResourceMenuBeforeShow = new EventEmitter();
        this.onResourceMenuItem = new EventEmitter();
        this.onResourceMenuShow = new EventEmitter();
        this.onResourceMenuToggleItem = new EventEmitter();
        this.onResourceTimeRangeClick = new EventEmitter();
        this.onResourceTimeRangeContextMenu = new EventEmitter();
        this.onResourceTimeRangeDblClick = new EventEmitter();
        this.onResourceTimeRangeMouseDown = new EventEmitter();
        this.onResourceTimeRangeMouseOut = new EventEmitter();
        this.onResourceTimeRangeMouseOver = new EventEmitter();
        this.onResourceTimeRangeMouseUp = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onTimeAxisChange = new EventEmitter();
        this.onTimeAxisHeaderClick = new EventEmitter();
        this.onTimeAxisHeaderContextMenu = new EventEmitter();
        this.onTimeAxisHeaderDblClick = new EventEmitter();
        this.onTimeAxisHeaderMenuBeforeShow = new EventEmitter();
        this.onTimeAxisHeaderMenuItem = new EventEmitter();
        this.onTimeAxisHeaderMenuShow = new EventEmitter();
        this.onTimelineContextChange = new EventEmitter();
        this.onTimelineViewportResize = new EventEmitter();
        this.onTimeRangeHeaderClick = new EventEmitter();
        this.onTimeRangeHeaderContextMenu = new EventEmitter();
        this.onTimeRangeHeaderDblClick = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.onVisibleDateRangeChange = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumTimelineHistogramComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
        // Backwards compatibility for gridInstance, schedulerInstance etc.
        // @ts-ignore
        me[StringHelper.uncapitalize(instanceName) + 'Instance'] = me.instance;
        // 
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumTimelineHistogramComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumTimelineHistogramComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumTimelineHistogramComponent.instanceClass = TimelineHistogram;
BryntumTimelineHistogramComponent.instanceName = 'TimelineHistogram';
BryntumTimelineHistogramComponent.bryntumEvents = [
    'onBeforeAssignmentDelete',
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDestroy',
    'onBeforeEventDelete',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforeHistogramDataCacheSet',
    'onBeforePan',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforePresetChange',
    'onBeforeRenderHistogramRow',
    'onBeforeRenderRecordHistogram',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDateRangeChange',
    'onDestroy',
    'onDragSelecting',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onHistogramDataCacheSet',
    'onMouseOut',
    'onMouseOver',
    'onPaint',
    'onPaste',
    'onPdfExport',
    'onPointerOver',
    'onPresetChange',
    'onRecompose',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResourceMenuBeforeShow',
    'onResourceMenuItem',
    'onResourceMenuShow',
    'onResourceMenuToggleItem',
    'onResourceTimeRangeClick',
    'onResourceTimeRangeContextMenu',
    'onResourceTimeRangeDblClick',
    'onResourceTimeRangeMouseDown',
    'onResourceTimeRangeMouseOut',
    'onResourceTimeRangeMouseOver',
    'onResourceTimeRangeMouseUp',
    'onResponsive',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onTimeAxisChange',
    'onTimeAxisHeaderClick',
    'onTimeAxisHeaderContextMenu',
    'onTimeAxisHeaderDblClick',
    'onTimeAxisHeaderMenuBeforeShow',
    'onTimeAxisHeaderMenuItem',
    'onTimeAxisHeaderMenuShow',
    'onTimelineContextChange',
    'onTimelineViewportResize',
    'onTimeRangeHeaderClick',
    'onTimeRangeHeaderContextMenu',
    'onTimeRangeHeaderDblClick',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onUnsplit',
    'onVisibleDateRangeChange'
];
BryntumTimelineHistogramComponent.bryntumFeatureNames = [
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnLinesFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'headerZoomFeature',
    'mergeCellsFeature',
    'nonWorkingTimeFeature',
    'panFeature',
    'pdfExportFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'resourceMenuFeature',
    'resourceTimeRangesFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'scheduleTooltipFeature',
    'searchFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stripeFeature',
    'summaryFeature',
    'timeAxisHeaderMenuFeature',
    'timeRangesFeature',
    'treeFeature',
    'treeGroupFeature'
];
BryntumTimelineHistogramComponent.bryntumConfigs = BryntumTimelineHistogramComponent.bryntumFeatureNames.concat([
    'adopt',
    'aggregateDataEntry',
    'aggregateHistogramDataForGroups',
    'alignSelf',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'barMargin',
    'barTooltipTemplate',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'data',
    'dataModelField',
    'dataset',
    'defaultRegion',
    'destroyStore',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'displayDateFormat',
    'dock',
    'durationDisplayPrecision',
    'emptyText',
    'enableEventAnimations',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'endDate',
    'eventColor',
    'eventStyle',
    'extraData',
    'features',
    'fillLastColumn',
    'fillTicks',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forceFit',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getBarText',
    'getBarTip',
    'getDataEntryForAggregating',
    'getHtml',
    'getOutlineClass',
    'getRecordData',
    'getRectClass',
    'getRowHeight',
    'hardRefreshOnTimeAxisReconfigure',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hideRowHover',
    'histogramWidget',
    'histogramWidgetClass',
    'hoverDelay',
    'id',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'initAggregatedDataEntry',
    'insertBefore',
    'insertFirst',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'managedEventSizing',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxTimeAxisUnit',
    'maxWidth',
    'maxZoomLevel',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'partner',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'presets',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceTimeRangeRenderer',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'scaleColumn',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'series',
    'showBarTip',
    'showDirty',
    'showOnClick',
    'showOnHover',
    'snap',
    'snapRelativeToEventStartDate',
    'startDate',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stickyHeaders',
    'store',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'tickSize',
    'timeAxis',
    'timeZone',
    'title',
    'tools',
    'trackMouse',
    'transitionDuration',
    'ui',
    'viewPreset',
    'visibleDate',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'width',
    'workingTime',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
]);
BryntumTimelineHistogramComponent.bryntumConfigsOnly = [
    'adopt',
    'aggregateDataEntry',
    'aggregateHistogramDataForGroups',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'ariaDescription',
    'ariaLabel',
    'autoAdjustTimeAxis',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'barTooltipTemplate',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'bufferCoef',
    'bufferThreshold',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'dataModelField',
    'defaultRegion',
    'destroyStore',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'durationDisplayPrecision',
    'enableRecurringEvents',
    'enableSticky',
    'enableTextSelection',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getBarText',
    'getBarTip',
    'getDataEntryForAggregating',
    'getHtml',
    'getOutlineClass',
    'getRecordData',
    'getRectClass',
    'getRowHeight',
    'hardRefreshOnTimeAxisReconfigure',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'histogramWidgetClass',
    'ignoreDomEventsWhileScrolling',
    'ignoreParentReadOnly',
    'infiniteScroll',
    'initAggregatedDataEntry',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'managedEventSizing',
    'maskDefaults',
    'masked',
    'maximizable',
    'maxTimeAxisUnit',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'partner',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'resourceTimeRangeRenderer',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'scaleColumn',
    'scrollerClass',
    'scrollManager',
    'series',
    'showDirty',
    'showOnClick',
    'showOnHover',
    'snapRelativeToEventStartDate',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'stickyHeaders',
    'strips',
    'subGridConfigs',
    'suppressFit',
    'syncMask',
    'tab',
    'tbar',
    'timeAxis',
    'trackMouse',
    'ui',
    'visibleZoomFactor',
    'weekStartDay',
    'weight',
    'zoomKeepsOriginalTimespan',
    'zoomOnMouseWheel',
    'zoomOnTimeAxisDoubleClick'
];
BryntumTimelineHistogramComponent.bryntumProps = BryntumTimelineHistogramComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'appendTo',
    'ariaLive',
    'barMargin',
    'cls',
    'columnLines',
    'columns',
    'data',
    'dataset',
    'disabled',
    'displayDateFormat',
    'emptyText',
    'enableEventAnimations',
    'enableUndoRedoKeys',
    'endDate',
    'eventColor',
    'eventStyle',
    'extraData',
    'fillTicks',
    'flex',
    'forceFit',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hideRowHover',
    'histogramWidget',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'maxZoomLevel',
    'minHeight',
    'minWidth',
    'minZoomLevel',
    'preserveScroll',
    'presets',
    'readOnly',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollLeft',
    'scrollTop',
    'scrollX',
    'selectedCell',
    'selectedCells',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'showBarTip',
    'snap',
    'startDate',
    'state',
    'store',
    'tickSize',
    'timeResolution',
    'timeZone',
    'title',
    'tools',
    'tooltip',
    'transitionDuration',
    'viewPreset',
    'visibleDate',
    'width',
    'workingTime',
    'zoomLevel'
]);
BryntumTimelineHistogramComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTimelineHistogramComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumTimelineHistogramComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumTimelineHistogramComponent, selector: "bryntum-timeline-histogram", inputs: { adopt: "adopt", aggregateDataEntry: "aggregateDataEntry", aggregateHistogramDataForGroups: "aggregateHistogramDataForGroups", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", animateTreeNodeToggle: "animateTreeNodeToggle", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoAdjustTimeAxis: "autoAdjustTimeAxis", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", barTooltipTemplate: "barTooltipTemplate", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", bufferCoef: "bufferCoef", bufferThreshold: "bufferThreshold", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", dataModelField: "dataModelField", defaultRegion: "defaultRegion", destroyStore: "destroyStore", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", dock: "dock", durationDisplayPrecision: "durationDisplayPrecision", enableRecurringEvents: "enableRecurringEvents", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getBarText: "getBarText", getBarTip: "getBarTip", getDataEntryForAggregating: "getDataEntryForAggregating", getHtml: "getHtml", getOutlineClass: "getOutlineClass", getRecordData: "getRecordData", getRectClass: "getRectClass", getRowHeight: "getRowHeight", hardRefreshOnTimeAxisReconfigure: "hardRefreshOnTimeAxisReconfigure", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", histogramWidgetClass: "histogramWidgetClass", ignoreDomEventsWhileScrolling: "ignoreDomEventsWhileScrolling", ignoreParentReadOnly: "ignoreParentReadOnly", infiniteScroll: "infiniteScroll", initAggregatedDataEntry: "initAggregatedDataEntry", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", managedEventSizing: "managedEventSizing", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", maxTimeAxisUnit: "maxTimeAxisUnit", modal: "modal", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", owner: "owner", partner: "partner", plugins: "plugins", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", resourceTimeRangeRenderer: "resourceTimeRangeRenderer", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", scaleColumn: "scaleColumn", scrollerClass: "scrollerClass", scrollManager: "scrollManager", series: "series", showDirty: "showDirty", showOnClick: "showOnClick", showOnHover: "showOnHover", snapRelativeToEventStartDate: "snapRelativeToEventStartDate", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", stickyHeaders: "stickyHeaders", strips: "strips", subGridConfigs: "subGridConfigs", suppressFit: "suppressFit", syncMask: "syncMask", tab: "tab", tbar: "tbar", timeAxis: "timeAxis", trackMouse: "trackMouse", ui: "ui", visibleZoomFactor: "visibleZoomFactor", weekStartDay: "weekStartDay", weight: "weight", zoomKeepsOriginalTimespan: "zoomKeepsOriginalTimespan", zoomOnMouseWheel: "zoomOnMouseWheel", zoomOnTimeAxisDoubleClick: "zoomOnTimeAxisDoubleClick", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", barMargin: "barMargin", cls: "cls", columnLines: "columnLines", columns: "columns", data: "data", dataset: "dataset", disabled: "disabled", displayDateFormat: "displayDateFormat", emptyText: "emptyText", enableEventAnimations: "enableEventAnimations", enableUndoRedoKeys: "enableUndoRedoKeys", endDate: "endDate", eventColor: "eventColor", eventStyle: "eventStyle", extraData: "extraData", fillTicks: "fillTicks", flex: "flex", forceFit: "forceFit", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hideRowHover: "hideRowHover", histogramWidget: "histogramWidget", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", maxZoomLevel: "maxZoomLevel", minHeight: "minHeight", minWidth: "minWidth", minZoomLevel: "minZoomLevel", preserveScroll: "preserveScroll", presets: "presets", readOnly: "readOnly", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", showBarTip: "showBarTip", snap: "snap", startDate: "startDate", store: "store", tickSize: "tickSize", timeZone: "timeZone", title: "title", tools: "tools", transitionDuration: "transitionDuration", viewPreset: "viewPreset", visibleDate: "visibleDate", width: "width", workingTime: "workingTime", accessibility: "accessibility", scrollLeft: "scrollLeft", scrollTop: "scrollTop", scrollX: "scrollX", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", timeResolution: "timeResolution", tooltip: "tooltip", zoomLevel: "zoomLevel", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnLinesFeature: "columnLinesFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", headerZoomFeature: "headerZoomFeature", mergeCellsFeature: "mergeCellsFeature", nonWorkingTimeFeature: "nonWorkingTimeFeature", panFeature: "panFeature", pdfExportFeature: "pdfExportFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", resourceMenuFeature: "resourceMenuFeature", resourceTimeRangesFeature: "resourceTimeRangesFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", scheduleTooltipFeature: "scheduleTooltipFeature", searchFeature: "searchFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", timeAxisHeaderMenuFeature: "timeAxisHeaderMenuFeature", timeRangesFeature: "timeRangesFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature" }, outputs: { onBeforeAssignmentDelete: "onBeforeAssignmentDelete", onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDestroy: "onBeforeDestroy", onBeforeEventDelete: "onBeforeEventDelete", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforeHistogramDataCacheSet: "onBeforeHistogramDataCacheSet", onBeforePan: "onBeforePan", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforePresetChange: "onBeforePresetChange", onBeforeRenderHistogramRow: "onBeforeRenderHistogramRow", onBeforeRenderRecordHistogram: "onBeforeRenderRecordHistogram", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDateRangeChange: "onDateRangeChange", onDestroy: "onDestroy", onDragSelecting: "onDragSelecting", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onHistogramDataCacheSet: "onHistogramDataCacheSet", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onPaint: "onPaint", onPaste: "onPaste", onPdfExport: "onPdfExport", onPointerOver: "onPointerOver", onPresetChange: "onPresetChange", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResourceMenuBeforeShow: "onResourceMenuBeforeShow", onResourceMenuItem: "onResourceMenuItem", onResourceMenuShow: "onResourceMenuShow", onResourceMenuToggleItem: "onResourceMenuToggleItem", onResourceTimeRangeClick: "onResourceTimeRangeClick", onResourceTimeRangeContextMenu: "onResourceTimeRangeContextMenu", onResourceTimeRangeDblClick: "onResourceTimeRangeDblClick", onResourceTimeRangeMouseDown: "onResourceTimeRangeMouseDown", onResourceTimeRangeMouseOut: "onResourceTimeRangeMouseOut", onResourceTimeRangeMouseOver: "onResourceTimeRangeMouseOver", onResourceTimeRangeMouseUp: "onResourceTimeRangeMouseUp", onResponsive: "onResponsive", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onTimeAxisChange: "onTimeAxisChange", onTimeAxisHeaderClick: "onTimeAxisHeaderClick", onTimeAxisHeaderContextMenu: "onTimeAxisHeaderContextMenu", onTimeAxisHeaderDblClick: "onTimeAxisHeaderDblClick", onTimeAxisHeaderMenuBeforeShow: "onTimeAxisHeaderMenuBeforeShow", onTimeAxisHeaderMenuItem: "onTimeAxisHeaderMenuItem", onTimeAxisHeaderMenuShow: "onTimeAxisHeaderMenuShow", onTimelineContextChange: "onTimelineContextChange", onTimelineViewportResize: "onTimelineViewportResize", onTimeRangeHeaderClick: "onTimeRangeHeaderClick", onTimeRangeHeaderContextMenu: "onTimeRangeHeaderContextMenu", onTimeRangeHeaderDblClick: "onTimeRangeHeaderDblClick", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onUnsplit: "onUnsplit", onVisibleDateRangeChange: "onVisibleDateRangeChange" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTimelineHistogramComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-timeline-histogram',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], aggregateDataEntry: [{
                type: Input
            }], aggregateHistogramDataForGroups: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoAdjustTimeAxis: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], barTooltipTemplate: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], bufferCoef: [{
                type: Input
            }], bufferThreshold: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], dataModelField: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], dock: [{
                type: Input
            }], durationDisplayPrecision: [{
                type: Input
            }], enableRecurringEvents: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getBarText: [{
                type: Input
            }], getBarTip: [{
                type: Input
            }], getDataEntryForAggregating: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getOutlineClass: [{
                type: Input
            }], getRecordData: [{
                type: Input
            }], getRectClass: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], hardRefreshOnTimeAxisReconfigure: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], histogramWidgetClass: [{
                type: Input
            }], ignoreDomEventsWhileScrolling: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], infiniteScroll: [{
                type: Input
            }], initAggregatedDataEntry: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], managedEventSizing: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], maxTimeAxisUnit: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], owner: [{
                type: Input
            }], partner: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], resourceTimeRangeRenderer: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scaleColumn: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], series: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], snapRelativeToEventStartDate: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], stickyHeaders: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], suppressFit: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], timeAxis: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], ui: [{
                type: Input
            }], visibleZoomFactor: [{
                type: Input
            }], weekStartDay: [{
                type: Input
            }], weight: [{
                type: Input
            }], zoomKeepsOriginalTimespan: [{
                type: Input
            }], zoomOnMouseWheel: [{
                type: Input
            }], zoomOnTimeAxisDoubleClick: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], barMargin: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], data: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], displayDateFormat: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableEventAnimations: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], endDate: [{
                type: Input
            }], eventColor: [{
                type: Input
            }], eventStyle: [{
                type: Input
            }], extraData: [{
                type: Input
            }], fillTicks: [{
                type: Input
            }], flex: [{
                type: Input
            }], forceFit: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hideRowHover: [{
                type: Input
            }], histogramWidget: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], maxZoomLevel: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], minZoomLevel: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], presets: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], showBarTip: [{
                type: Input
            }], snap: [{
                type: Input
            }], startDate: [{
                type: Input
            }], store: [{
                type: Input
            }], tickSize: [{
                type: Input
            }], timeZone: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], viewPreset: [{
                type: Input
            }], visibleDate: [{
                type: Input
            }], width: [{
                type: Input
            }], workingTime: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], scrollLeft: [{
                type: Input
            }], scrollTop: [{
                type: Input
            }], scrollX: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], timeResolution: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], zoomLevel: [{
                type: Input
            }], onBeforeAssignmentDelete: [{
                type: Output
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeEventDelete: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeHistogramDataCacheSet: [{
                type: Output
            }], onBeforePan: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforePresetChange: [{
                type: Output
            }], onBeforeRenderHistogramRow: [{
                type: Output
            }], onBeforeRenderRecordHistogram: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDateRangeChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onHistogramDataCacheSet: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onPresetChange: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResourceMenuBeforeShow: [{
                type: Output
            }], onResourceMenuItem: [{
                type: Output
            }], onResourceMenuShow: [{
                type: Output
            }], onResourceMenuToggleItem: [{
                type: Output
            }], onResourceTimeRangeClick: [{
                type: Output
            }], onResourceTimeRangeContextMenu: [{
                type: Output
            }], onResourceTimeRangeDblClick: [{
                type: Output
            }], onResourceTimeRangeMouseDown: [{
                type: Output
            }], onResourceTimeRangeMouseOut: [{
                type: Output
            }], onResourceTimeRangeMouseOver: [{
                type: Output
            }], onResourceTimeRangeMouseUp: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onTimeAxisChange: [{
                type: Output
            }], onTimeAxisHeaderClick: [{
                type: Output
            }], onTimeAxisHeaderContextMenu: [{
                type: Output
            }], onTimeAxisHeaderDblClick: [{
                type: Output
            }], onTimeAxisHeaderMenuBeforeShow: [{
                type: Output
            }], onTimeAxisHeaderMenuItem: [{
                type: Output
            }], onTimeAxisHeaderMenuShow: [{
                type: Output
            }], onTimelineContextChange: [{
                type: Output
            }], onTimelineViewportResize: [{
                type: Output
            }], onTimeRangeHeaderClick: [{
                type: Output
            }], onTimeRangeHeaderContextMenu: [{
                type: Output
            }], onTimeRangeHeaderDblClick: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], onVisibleDateRangeChange: [{
                type: Output
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnLinesFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], headerZoomFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], nonWorkingTimeFeature: [{
                type: Input
            }], panFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], resourceMenuFeature: [{
                type: Input
            }], resourceTimeRangesFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], scheduleTooltipFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], timeAxisHeaderMenuFeature: [{
                type: Input
            }], timeRangesFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum TimePicker
 */
class BryntumTimePickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTimeChange = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumTimePickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumTimePickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumTimePickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumTimePickerComponent.instanceClass = TimePicker;
BryntumTimePickerComponent.instanceName = 'TimePicker';
BryntumTimePickerComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onCollapse',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow',
    'onTimeChange',
    'onToolClick'
];
BryntumTimePickerComponent.bryntumFeatureNames = [];
BryntumTimePickerComponent.bryntumConfigs = BryntumTimePickerComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'footer',
    'format',
    'header',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'min',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'seconds',
    'showAnimation',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'value',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumTimePickerComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'footer',
    'header',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'seconds',
    'showAnimation',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'trapFocus',
    'ui',
    'weight'
];
BryntumTimePickerComponent.bryntumProps = BryntumTimePickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'format',
    'height',
    'hidden',
    'html',
    'id',
    'initialValue',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'max',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'min',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'value',
    'values',
    'width',
    'x',
    'y'
]);
BryntumTimePickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTimePickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumTimePickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumTimePickerComponent, selector: "bryntum-time-picker", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", footer: "footer", header: "header", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", seconds: "seconds", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", trapFocus: "trapFocus", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", format: "format", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", max: "max", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", min: "min", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", initialValue: "initialValue", isSettingValues: "isSettingValues", isValid: "isValid", state: "state", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow", onTimeChange: "onTimeChange", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTimePickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-time-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], footer: [{
                type: Input
            }], header: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], seconds: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], format: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], max: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], min: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], initialValue: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], state: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTimeChange: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Toolbar
 */
class BryntumToolbarComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumToolbarComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumToolbarComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumToolbarComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumToolbarComponent.instanceClass = Toolbar;
BryntumToolbarComponent.instanceName = 'Toolbar';
BryntumToolbarComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumToolbarComponent.bryntumFeatureNames = [];
BryntumToolbarComponent.bryntumConfigs = BryntumToolbarComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'overflow',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'strictRecordMapping',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'ui',
    'weight',
    'widgetCls',
    'width',
    'x',
    'y'
]);
BryntumToolbarComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'overflow',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'textContent',
    'title',
    'ui',
    'weight',
    'widgetCls'
];
BryntumToolbarComponent.bryntumProps = BryntumToolbarComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'strictRecordMapping',
    'tools',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumToolbarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumToolbarComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumToolbarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumToolbarComponent, selector: "bryntum-toolbar", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", overflow: "overflow", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", textContent: "textContent", title: "title", ui: "ui", weight: "weight", widgetCls: "widgetCls", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumToolbarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-toolbar',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], overflow: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], widgetCls: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum TreeCombo
 */
class BryntumTreeComboComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onInput = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumTreeComboComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumTreeComboComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumTreeComboComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumTreeComboComponent.instanceClass = TreeCombo;
BryntumTreeComboComponent.instanceName = 'TreeCombo';
BryntumTreeComboComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onTrigger'
];
BryntumTreeComboComponent.bryntumFeatureNames = [];
BryntumTreeComboComponent.bryntumConfigs = BryntumTreeComboComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'cls',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'editable',
    'emptyText',
    'encodeFilterParams',
    'extraData',
    'filterOnEnter',
    'filterOperator',
    'filterParamName',
    'filterSelected',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxLength',
    'maxWidth',
    'minChars',
    'minHeight',
    'minLength',
    'minWidth',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'picker',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'readOnly',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'store',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'tooltip',
    'triggerAction',
    'triggers',
    'ui',
    'validateFilter',
    'validateOnInput',
    'value',
    'valueField',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumTreeComboComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoComplete',
    'autoExpand',
    'autoSelect',
    'bubbleEvents',
    'callOnFunctions',
    'caseSensitive',
    'centered',
    'chipView',
    'clearable',
    'clearTextOnPickerHide',
    'clearTextOnSelection',
    'config',
    'constrainTo',
    'container',
    'containValues',
    'contentElementCls',
    'createOnUnmatched',
    'defaultBindProperty',
    'displayField',
    'displayValueRenderer',
    'dock',
    'draggable',
    'emptyText',
    'encodeFilterParams',
    'filterOnEnter',
    'filterParamName',
    'filterSelected',
    'floating',
    'hideAnimation',
    'hidePickerOnSelect',
    'hideTrigger',
    'highlightExternalChange',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inline',
    'inlinePicker',
    'inputAlign',
    'inputAttributes',
    'inputType',
    'inputWidth',
    'items',
    'keyStrokeChangeDelay',
    'keyStrokeFilterDelay',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxLength',
    'minChars',
    'minLength',
    'monitorResize',
    'multiSelect',
    'multiValueSeparator',
    'name',
    'overlayAnchor',
    'owner',
    'pickerAlignElement',
    'pickerWidth',
    'placeholder',
    'positioned',
    'preventTooltipOnTouch',
    'primaryFilter',
    'relayStoreEvents',
    'required',
    'revertOnEscape',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'spellCheck',
    'tab',
    'tabIndex',
    'textAlign',
    'title',
    'triggerAction',
    'ui',
    'validateFilter',
    'validateOnInput',
    'valueField',
    'weight'
];
BryntumTreeComboComponent.bryntumProps = BryntumTreeComboComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'content',
    'dataset',
    'disabled',
    'editable',
    'extraData',
    'filterOperator',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'picker',
    'readOnly',
    'scrollable',
    'store',
    'tooltip',
    'triggers',
    'value',
    'width',
    'x',
    'y'
]);
BryntumTreeComboComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTreeComboComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumTreeComboComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumTreeComboComponent, selector: "bryntum-tree-combo", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoComplete: "autoComplete", autoExpand: "autoExpand", autoSelect: "autoSelect", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", caseSensitive: "caseSensitive", centered: "centered", chipView: "chipView", clearable: "clearable", clearTextOnPickerHide: "clearTextOnPickerHide", clearTextOnSelection: "clearTextOnSelection", config: "config", constrainTo: "constrainTo", container: "container", containValues: "containValues", contentElementCls: "contentElementCls", createOnUnmatched: "createOnUnmatched", defaultBindProperty: "defaultBindProperty", displayField: "displayField", displayValueRenderer: "displayValueRenderer", dock: "dock", draggable: "draggable", emptyText: "emptyText", encodeFilterParams: "encodeFilterParams", filterOnEnter: "filterOnEnter", filterParamName: "filterParamName", filterSelected: "filterSelected", floating: "floating", hideAnimation: "hideAnimation", hidePickerOnSelect: "hidePickerOnSelect", hideTrigger: "hideTrigger", highlightExternalChange: "highlightExternalChange", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inline: "inline", inlinePicker: "inlinePicker", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputType: "inputType", inputWidth: "inputWidth", items: "items", keyStrokeChangeDelay: "keyStrokeChangeDelay", keyStrokeFilterDelay: "keyStrokeFilterDelay", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listCls: "listCls", listeners: "listeners", listItemTpl: "listItemTpl", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxLength: "maxLength", minChars: "minChars", minLength: "minLength", monitorResize: "monitorResize", multiSelect: "multiSelect", multiValueSeparator: "multiValueSeparator", name: "name", overlayAnchor: "overlayAnchor", owner: "owner", pickerAlignElement: "pickerAlignElement", pickerWidth: "pickerWidth", placeholder: "placeholder", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", primaryFilter: "primaryFilter", relayStoreEvents: "relayStoreEvents", required: "required", revertOnEscape: "revertOnEscape", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", spellCheck: "spellCheck", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", title: "title", triggerAction: "triggerAction", ui: "ui", validateFilter: "validateFilter", validateOnInput: "validateOnInput", valueField: "valueField", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", dataset: "dataset", disabled: "disabled", editable: "editable", extraData: "extraData", filterOperator: "filterOperator", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", picker: "picker", readOnly: "readOnly", store: "store", tooltip: "tooltip", triggers: "triggers", value: "value", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", content: "content", html: "html", input: "input", scrollable: "scrollable" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onInput: "onInput", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTreeComboComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-tree-combo',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], autoExpand: [{
                type: Input
            }], autoSelect: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], caseSensitive: [{
                type: Input
            }], centered: [{
                type: Input
            }], chipView: [{
                type: Input
            }], clearable: [{
                type: Input
            }], clearTextOnPickerHide: [{
                type: Input
            }], clearTextOnSelection: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], container: [{
                type: Input
            }], containValues: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], createOnUnmatched: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], displayField: [{
                type: Input
            }], displayValueRenderer: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], encodeFilterParams: [{
                type: Input
            }], filterOnEnter: [{
                type: Input
            }], filterParamName: [{
                type: Input
            }], filterSelected: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hidePickerOnSelect: [{
                type: Input
            }], hideTrigger: [{
                type: Input
            }], highlightExternalChange: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inline: [{
                type: Input
            }], inlinePicker: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputType: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], items: [{
                type: Input
            }], keyStrokeChangeDelay: [{
                type: Input
            }], keyStrokeFilterDelay: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listCls: [{
                type: Input
            }], listeners: [{
                type: Input
            }], listItemTpl: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minChars: [{
                type: Input
            }], minLength: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], multiValueSeparator: [{
                type: Input
            }], name: [{
                type: Input
            }], overlayAnchor: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerAlignElement: [{
                type: Input
            }], pickerWidth: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], primaryFilter: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], required: [{
                type: Input
            }], revertOnEscape: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], spellCheck: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], triggerAction: [{
                type: Input
            }], ui: [{
                type: Input
            }], validateFilter: [{
                type: Input
            }], validateOnInput: [{
                type: Input
            }], valueField: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], filterOperator: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], picker: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], store: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], triggers: [{
                type: Input
            }], value: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], content: [{
                type: Input
            }], html: [{
                type: Input
            }], input: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onInput: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum TreeGrid
 */
class BryntumTreeGridComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumTreeGridComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
        // Backwards compatibility for gridInstance, schedulerInstance etc.
        // @ts-ignore
        me[StringHelper.uncapitalize(instanceName) + 'Instance'] = me.instance;
        // 
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumTreeGridComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumTreeGridComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumTreeGridComponent.instanceClass = TreeGrid;
BryntumTreeGridComponent.instanceName = 'TreeGrid';
BryntumTreeGridComponent.bryntumEvents = [
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDestroy',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDestroy',
    'onDragSelecting',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onMouseOut',
    'onMouseOver',
    'onPaint',
    'onPaste',
    'onPdfExport',
    'onPointerOver',
    'onRecompose',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResponsive',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onUnsplit'
];
BryntumTreeGridComponent.bryntumFeatureNames = [
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'mergeCellsFeature',
    'pdfExportFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'searchFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stripeFeature',
    'summaryFeature',
    'treeFeature',
    'treeGroupFeature'
];
BryntumTreeGridComponent.bryntumConfigs = BryntumTreeGridComponent.bryntumFeatureNames.concat([
    'adopt',
    'alignSelf',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'data',
    'dataset',
    'defaultRegion',
    'destroyStore',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'emptyText',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'extraData',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getHtml',
    'getRowHeight',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hoverDelay',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxWidth',
    'minHeight',
    'minWidth',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'showDirty',
    'showOnClick',
    'showOnHover',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'store',
    'strips',
    'subGridConfigs',
    'syncMask',
    'tab',
    'tbar',
    'title',
    'tools',
    'trackMouse',
    'transitionDuration',
    'ui',
    'weight',
    'width'
]);
BryntumTreeGridComponent.bryntumConfigsOnly = [
    'adopt',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'defaultRegion',
    'destroyStore',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'enableSticky',
    'enableTextSelection',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getHtml',
    'getRowHeight',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'ignoreParentReadOnly',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'maskDefaults',
    'masked',
    'maximizable',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'scrollerClass',
    'scrollManager',
    'showDirty',
    'showOnClick',
    'showOnHover',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'subGridConfigs',
    'syncMask',
    'tab',
    'tbar',
    'trackMouse',
    'ui',
    'weight'
];
BryntumTreeGridComponent.bryntumProps = BryntumTreeGridComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaLive',
    'cls',
    'columnLines',
    'columns',
    'data',
    'dataset',
    'disabled',
    'emptyText',
    'enableUndoRedoKeys',
    'extraData',
    'flex',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'minHeight',
    'minWidth',
    'preserveScroll',
    'readOnly',
    'rowHeight',
    'rowLines',
    'scrollable',
    'selectedCell',
    'selectedCells',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'state',
    'store',
    'title',
    'tools',
    'tooltip',
    'transitionDuration',
    'width'
]);
BryntumTreeGridComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTreeGridComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumTreeGridComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumTreeGridComponent, selector: "bryntum-tree-grid", inputs: { adopt: "adopt", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", defaultRegion: "defaultRegion", destroyStore: "destroyStore", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", dock: "dock", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getHtml: "getHtml", getRowHeight: "getRowHeight", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", ignoreParentReadOnly: "ignoreParentReadOnly", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", modal: "modal", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", owner: "owner", plugins: "plugins", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", scrollerClass: "scrollerClass", scrollManager: "scrollManager", showDirty: "showDirty", showOnClick: "showOnClick", showOnHover: "showOnHover", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", subGridConfigs: "subGridConfigs", syncMask: "syncMask", tab: "tab", tbar: "tbar", trackMouse: "trackMouse", ui: "ui", weight: "weight", alignSelf: "alignSelf", animateTreeNodeToggle: "animateTreeNodeToggle", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", columnLines: "columnLines", columns: "columns", data: "data", dataset: "dataset", disabled: "disabled", emptyText: "emptyText", enableUndoRedoKeys: "enableUndoRedoKeys", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", preserveScroll: "preserveScroll", readOnly: "readOnly", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", store: "store", title: "title", tools: "tools", transitionDuration: "transitionDuration", width: "width", accessibility: "accessibility", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", tooltip: "tooltip", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", mergeCellsFeature: "mergeCellsFeature", pdfExportFeature: "pdfExportFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", searchFeature: "searchFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature" }, outputs: { onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDestroy: "onBeforeDestroy", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDestroy: "onDestroy", onDragSelecting: "onDragSelecting", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onPaint: "onPaint", onPaste: "onPaste", onPdfExport: "onPdfExport", onPointerOver: "onPointerOver", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResponsive: "onResponsive", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onUnsplit: "onUnsplit" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumTreeGridComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-tree-grid',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], dock: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], owner: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], data: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], store: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], width: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum UndoRedo
 */
class BryntumUndoRedoComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumUndoRedoComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumUndoRedoComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumUndoRedoComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumUndoRedoComponent.instanceClass = UndoRedo;
BryntumUndoRedoComponent.instanceName = 'UndoRedo';
BryntumUndoRedoComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumUndoRedoComponent.bryntumFeatureNames = [];
BryntumUndoRedoComponent.bryntumConfigs = BryntumUndoRedoComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'color',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'project',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scheduler',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'showZeroActionBadge',
    'strictRecordMapping',
    'tab',
    'tag',
    'text',
    'textAlign',
    'textContent',
    'title',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumUndoRedoComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'color',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scheduler',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'showZeroActionBadge',
    'tab',
    'tag',
    'text',
    'textAlign',
    'textContent',
    'title',
    'ui',
    'weight'
];
BryntumUndoRedoComponent.bryntumProps = BryntumUndoRedoComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'project',
    'readOnly',
    'record',
    'scrollable',
    'strictRecordMapping',
    'tooltip',
    'values',
    'width',
    'x',
    'y'
]);
BryntumUndoRedoComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumUndoRedoComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumUndoRedoComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumUndoRedoComponent, selector: "bryntum-undo-redo", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", color: "color", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scheduler: "scheduler", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", showZeroActionBadge: "showZeroActionBadge", tab: "tab", tag: "tag", text: "text", textAlign: "textAlign", textContent: "textContent", title: "title", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", project: "project", readOnly: "readOnly", record: "record", scrollable: "scrollable", strictRecordMapping: "strictRecordMapping", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumUndoRedoComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-undo-redo',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], color: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scheduler: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], showZeroActionBadge: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], text: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], project: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum VersionGrid
 */
class BryntumVersionGridComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeCancelCellEdit = new EventEmitter();
        this.onBeforeCellDelete = new EventEmitter();
        this.onBeforeCellEditStart = new EventEmitter();
        this.onBeforeClose = new EventEmitter();
        this.onBeforeColumnDragStart = new EventEmitter();
        this.onBeforeColumnDropFinalize = new EventEmitter();
        this.onBeforeColumnResize = new EventEmitter();
        this.onBeforeCopy = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeFillHandleDragStart = new EventEmitter();
        this.onBeforeFinishCellEdit = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforePaste = new EventEmitter();
        this.onBeforePdfExport = new EventEmitter();
        this.onBeforeRenderRow = new EventEmitter();
        this.onBeforeRenderRows = new EventEmitter();
        this.onBeforeSelectionChange = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onBeforeToggleGroup = new EventEmitter();
        this.onBeforeToggleNode = new EventEmitter();
        this.onCancelCellEdit = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCellClick = new EventEmitter();
        this.onCellContextMenu = new EventEmitter();
        this.onCellDblClick = new EventEmitter();
        this.onCellMenuBeforeShow = new EventEmitter();
        this.onCellMenuItem = new EventEmitter();
        this.onCellMenuShow = new EventEmitter();
        this.onCellMenuToggleItem = new EventEmitter();
        this.onCellMouseEnter = new EventEmitter();
        this.onCellMouseLeave = new EventEmitter();
        this.onCellMouseOut = new EventEmitter();
        this.onCellMouseOver = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onCollapseNode = new EventEmitter();
        this.onColumnDrag = new EventEmitter();
        this.onColumnDragStart = new EventEmitter();
        this.onColumnDrop = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onColumnResizeStart = new EventEmitter();
        this.onCompare = new EventEmitter();
        this.onContextMenuItem = new EventEmitter();
        this.onContextMenuToggleItem = new EventEmitter();
        this.onCopy = new EventEmitter();
        this.onDataChange = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onDragSelecting = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onExpandNode = new EventEmitter();
        this.onFileDrop = new EventEmitter();
        this.onFillHandleBeforeDragFinalize = new EventEmitter();
        this.onFillHandleDrag = new EventEmitter();
        this.onFillHandleDragAbort = new EventEmitter();
        this.onFillHandleDragEnd = new EventEmitter();
        this.onFillHandleDragStart = new EventEmitter();
        this.onFinishCellEdit = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onGridRowAbort = new EventEmitter();
        this.onGridRowBeforeDragStart = new EventEmitter();
        this.onGridRowBeforeDropFinalize = new EventEmitter();
        this.onGridRowDrag = new EventEmitter();
        this.onGridRowDragStart = new EventEmitter();
        this.onGridRowDrop = new EventEmitter();
        this.onHeaderClick = new EventEmitter();
        this.onHeaderMenuBeforeShow = new EventEmitter();
        this.onHeaderMenuItem = new EventEmitter();
        this.onHeaderMenuShow = new EventEmitter();
        this.onHeaderMenuToggleItem = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onMouseOut = new EventEmitter();
        this.onMouseOver = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onPaste = new EventEmitter();
        this.onPdfExport = new EventEmitter();
        this.onPointerOver = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onRenderRow = new EventEmitter();
        this.onRenderRows = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onResponsive = new EventEmitter();
        this.onRestore = new EventEmitter();
        this.onRowMouseEnter = new EventEmitter();
        this.onRowMouseLeave = new EventEmitter();
        this.onScroll = new EventEmitter();
        this.onSelectionChange = new EventEmitter();
        this.onSelectionModeChange = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onSplit = new EventEmitter();
        this.onSplitterCollapseClick = new EventEmitter();
        this.onSplitterDragEnd = new EventEmitter();
        this.onSplitterDragStart = new EventEmitter();
        this.onSplitterExpandClick = new EventEmitter();
        this.onStartCellEdit = new EventEmitter();
        this.onStopCompare = new EventEmitter();
        this.onSubGridCollapse = new EventEmitter();
        this.onSubGridExpand = new EventEmitter();
        this.onToggleGroup = new EventEmitter();
        this.onToggleNode = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.onUnsplit = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumVersionGridComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumVersionGridComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumVersionGridComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumVersionGridComponent.instanceClass = VersionGrid;
BryntumVersionGridComponent.instanceName = 'VersionGrid';
BryntumVersionGridComponent.bryntumEvents = [
    'onBeforeCancelCellEdit',
    'onBeforeCellDelete',
    'onBeforeCellEditStart',
    'onBeforeClose',
    'onBeforeColumnDragStart',
    'onBeforeColumnDropFinalize',
    'onBeforeColumnResize',
    'onBeforeCopy',
    'onBeforeDestroy',
    'onBeforeFillHandleDragStart',
    'onBeforeFinishCellEdit',
    'onBeforeHide',
    'onBeforePaste',
    'onBeforePdfExport',
    'onBeforeRenderRow',
    'onBeforeRenderRows',
    'onBeforeSelectionChange',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onBeforeToggleGroup',
    'onBeforeToggleNode',
    'onCancelCellEdit',
    'onCatchAll',
    'onCellClick',
    'onCellContextMenu',
    'onCellDblClick',
    'onCellMenuBeforeShow',
    'onCellMenuItem',
    'onCellMenuShow',
    'onCellMenuToggleItem',
    'onCellMouseEnter',
    'onCellMouseLeave',
    'onCellMouseOut',
    'onCellMouseOver',
    'onCollapse',
    'onCollapseNode',
    'onColumnDrag',
    'onColumnDragStart',
    'onColumnDrop',
    'onColumnResize',
    'onColumnResizeStart',
    'onCompare',
    'onContextMenuItem',
    'onContextMenuToggleItem',
    'onCopy',
    'onDataChange',
    'onDestroy',
    'onDragSelecting',
    'onExpand',
    'onExpandNode',
    'onFileDrop',
    'onFillHandleBeforeDragFinalize',
    'onFillHandleDrag',
    'onFillHandleDragAbort',
    'onFillHandleDragEnd',
    'onFillHandleDragStart',
    'onFinishCellEdit',
    'onFocusIn',
    'onFocusOut',
    'onGridRowAbort',
    'onGridRowBeforeDragStart',
    'onGridRowBeforeDropFinalize',
    'onGridRowDrag',
    'onGridRowDragStart',
    'onGridRowDrop',
    'onHeaderClick',
    'onHeaderMenuBeforeShow',
    'onHeaderMenuItem',
    'onHeaderMenuShow',
    'onHeaderMenuToggleItem',
    'onHide',
    'onMouseOut',
    'onMouseOver',
    'onPaint',
    'onPaste',
    'onPdfExport',
    'onPointerOver',
    'onRecompose',
    'onRenderRow',
    'onRenderRows',
    'onResize',
    'onResponsive',
    'onRestore',
    'onRowMouseEnter',
    'onRowMouseLeave',
    'onScroll',
    'onSelectionChange',
    'onSelectionModeChange',
    'onShow',
    'onSplit',
    'onSplitterCollapseClick',
    'onSplitterDragEnd',
    'onSplitterDragStart',
    'onSplitterExpandClick',
    'onStartCellEdit',
    'onStopCompare',
    'onSubGridCollapse',
    'onSubGridExpand',
    'onToggleGroup',
    'onToggleNode',
    'onToolClick',
    'onUnsplit'
];
BryntumVersionGridComponent.bryntumFeatureNames = [
    'cellCopyPasteFeature',
    'cellEditFeature',
    'cellMenuFeature',
    'cellTooltipFeature',
    'columnAutoWidthFeature',
    'columnDragToolbarFeature',
    'columnPickerFeature',
    'columnRenameFeature',
    'columnReorderFeature',
    'columnResizeFeature',
    'excelExporterFeature',
    'fileDropFeature',
    'fillHandleFeature',
    'filterFeature',
    'filterBarFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerMenuFeature',
    'mergeCellsFeature',
    'pdfExportFeature',
    'printFeature',
    'quickFindFeature',
    'regionResizeFeature',
    'rowCopyPasteFeature',
    'rowExpanderFeature',
    'rowReorderFeature',
    'rowResizeFeature',
    'searchFeature',
    'sortFeature',
    'splitFeature',
    'stickyCellsFeature',
    'stripeFeature',
    'summaryFeature',
    'treeFeature',
    'treeGroupFeature'
];
BryntumVersionGridComponent.bryntumConfigs = BryntumVersionGridComponent.bryntumFeatureNames.concat([
    'adopt',
    'alignSelf',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'closable',
    'closeAction',
    'cls',
    'collapsed',
    'collapsible',
    'columnLines',
    'columns',
    'comparingVersionId',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'data',
    'dataset',
    'dateFormat',
    'defaultRegion',
    'destroyStore',
    'disabled',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'emptyText',
    'enableSticky',
    'enableTextSelection',
    'enableUndoRedoKeys',
    'extraData',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'flex',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getHtml',
    'getRowHeight',
    'header',
    'height',
    'hidden',
    'hideDelay',
    'hideFooters',
    'hideHeaders',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'hoverDelay',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'longPressTime',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizable',
    'maximized',
    'maxWidth',
    'minHeight',
    'minWidth',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScroll',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'project',
    'readOnly',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'rowHeight',
    'rowLines',
    'scrollable',
    'scrollerClass',
    'scrollManager',
    'selectionMode',
    'showDirty',
    'showNamedVersionsOnly',
    'showOnClick',
    'showOnHover',
    'showUnattachedTransactions',
    'showVersions',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'store',
    'strips',
    'subGridConfigs',
    'syncMask',
    'tab',
    'tbar',
    'title',
    'tools',
    'trackMouse',
    'transitionDuration',
    'ui',
    'weight',
    'width'
]);
BryntumVersionGridComponent.bryntumConfigsOnly = [
    'adopt',
    'allowOver',
    'anchorToTarget',
    'animateRemovingRows',
    'ariaDescription',
    'ariaLabel',
    'autoClose',
    'autoHeight',
    'autoHide',
    'autoShow',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'closable',
    'closeAction',
    'collapsed',
    'collapsible',
    'config',
    'contentElementCls',
    'contextMenuTriggerEvent',
    'dateFormat',
    'defaultRegion',
    'destroyStore',
    'disableGridColumnIdWarning',
    'disableGridRowModelWarning',
    'dismissDelay',
    'dock',
    'enableSticky',
    'enableTextSelection',
    'features',
    'fillLastColumn',
    'fixedRowHeight',
    'focusOnToFront',
    'footer',
    'forElement',
    'forSelector',
    'fullRowRefresh',
    'getHtml',
    'getRowHeight',
    'header',
    'hideDelay',
    'hideHorizontalScrollbar',
    'hideOnDelegateChange',
    'ignoreParentReadOnly',
    'keyMap',
    'listeners',
    'loadingMsg',
    'loadMask',
    'loadMaskDefaults',
    'loadMaskError',
    'localizable',
    'maskDefaults',
    'masked',
    'maximizable',
    'modal',
    'monitorResize',
    'mouseOffsetX',
    'mouseOffsetY',
    'owner',
    'plugins',
    'preserveFocusOnDatasetChange',
    'preserveScrollOnDatasetChange',
    'preventTooltipOnTouch',
    'project',
    'relayStoreEvents',
    'resizeToFitIncludesHeader',
    'responsiveLevels',
    'ripple',
    'rootElement',
    'scrollerClass',
    'scrollManager',
    'showDirty',
    'showOnClick',
    'showOnHover',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'subGridConfigs',
    'syncMask',
    'tab',
    'tbar',
    'trackMouse',
    'ui',
    'weight'
];
BryntumVersionGridComponent.bryntumProps = BryntumVersionGridComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'animateTreeNodeToggle',
    'appendTo',
    'ariaLive',
    'cls',
    'columnLines',
    'columns',
    'comparingVersionId',
    'data',
    'dataset',
    'disabled',
    'emptyText',
    'enableUndoRedoKeys',
    'extraData',
    'flex',
    'height',
    'hidden',
    'hideFooters',
    'hideHeaders',
    'hoverDelay',
    'id',
    'insertBefore',
    'insertFirst',
    'longPressTime',
    'margin',
    'maxHeight',
    'maximized',
    'maxWidth',
    'minHeight',
    'minWidth',
    'preserveScroll',
    'readOnly',
    'rowHeight',
    'rowLines',
    'scrollable',
    'selectedCell',
    'selectedCells',
    'selectedRecord',
    'selectedRecords',
    'selectedRows',
    'selectionMode',
    'showNamedVersionsOnly',
    'showUnattachedTransactions',
    'showVersions',
    'state',
    'store',
    'title',
    'tools',
    'tooltip',
    'transitionDuration',
    'width'
]);
BryntumVersionGridComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumVersionGridComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumVersionGridComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumVersionGridComponent, selector: "bryntum-version-grid", inputs: { adopt: "adopt", allowOver: "allowOver", anchorToTarget: "anchorToTarget", animateRemovingRows: "animateRemovingRows", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoClose: "autoClose", autoHeight: "autoHeight", autoHide: "autoHide", autoShow: "autoShow", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", closable: "closable", closeAction: "closeAction", collapsed: "collapsed", collapsible: "collapsible", config: "config", contentElementCls: "contentElementCls", contextMenuTriggerEvent: "contextMenuTriggerEvent", dateFormat: "dateFormat", defaultRegion: "defaultRegion", destroyStore: "destroyStore", disableGridColumnIdWarning: "disableGridColumnIdWarning", disableGridRowModelWarning: "disableGridRowModelWarning", dismissDelay: "dismissDelay", dock: "dock", enableSticky: "enableSticky", enableTextSelection: "enableTextSelection", features: "features", fillLastColumn: "fillLastColumn", fixedRowHeight: "fixedRowHeight", focusOnToFront: "focusOnToFront", footer: "footer", forElement: "forElement", forSelector: "forSelector", fullRowRefresh: "fullRowRefresh", getHtml: "getHtml", getRowHeight: "getRowHeight", header: "header", hideDelay: "hideDelay", hideHorizontalScrollbar: "hideHorizontalScrollbar", hideOnDelegateChange: "hideOnDelegateChange", ignoreParentReadOnly: "ignoreParentReadOnly", keyMap: "keyMap", listeners: "listeners", loadingMsg: "loadingMsg", loadMask: "loadMask", loadMaskDefaults: "loadMaskDefaults", loadMaskError: "loadMaskError", localizable: "localizable", maskDefaults: "maskDefaults", masked: "masked", maximizable: "maximizable", modal: "modal", monitorResize: "monitorResize", mouseOffsetX: "mouseOffsetX", mouseOffsetY: "mouseOffsetY", owner: "owner", plugins: "plugins", preserveFocusOnDatasetChange: "preserveFocusOnDatasetChange", preserveScrollOnDatasetChange: "preserveScrollOnDatasetChange", preventTooltipOnTouch: "preventTooltipOnTouch", project: "project", relayStoreEvents: "relayStoreEvents", resizeToFitIncludesHeader: "resizeToFitIncludesHeader", responsiveLevels: "responsiveLevels", ripple: "ripple", rootElement: "rootElement", scrollerClass: "scrollerClass", scrollManager: "scrollManager", showDirty: "showDirty", showOnClick: "showOnClick", showOnHover: "showOnHover", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", subGridConfigs: "subGridConfigs", syncMask: "syncMask", tab: "tab", tbar: "tbar", trackMouse: "trackMouse", ui: "ui", weight: "weight", alignSelf: "alignSelf", animateTreeNodeToggle: "animateTreeNodeToggle", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", columnLines: "columnLines", columns: "columns", comparingVersionId: "comparingVersionId", data: "data", dataset: "dataset", disabled: "disabled", emptyText: "emptyText", enableUndoRedoKeys: "enableUndoRedoKeys", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", hideFooters: "hideFooters", hideHeaders: "hideHeaders", hoverDelay: "hoverDelay", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", longPressTime: "longPressTime", margin: "margin", maxHeight: "maxHeight", maximized: "maximized", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", preserveScroll: "preserveScroll", readOnly: "readOnly", rowHeight: "rowHeight", rowLines: "rowLines", scrollable: "scrollable", selectionMode: "selectionMode", showNamedVersionsOnly: "showNamedVersionsOnly", showUnattachedTransactions: "showUnattachedTransactions", showVersions: "showVersions", store: "store", title: "title", tools: "tools", transitionDuration: "transitionDuration", width: "width", accessibility: "accessibility", selectedCell: "selectedCell", selectedCells: "selectedCells", selectedRecord: "selectedRecord", selectedRecords: "selectedRecords", selectedRows: "selectedRows", state: "state", tooltip: "tooltip", cellCopyPasteFeature: "cellCopyPasteFeature", cellEditFeature: "cellEditFeature", cellMenuFeature: "cellMenuFeature", cellTooltipFeature: "cellTooltipFeature", columnAutoWidthFeature: "columnAutoWidthFeature", columnDragToolbarFeature: "columnDragToolbarFeature", columnPickerFeature: "columnPickerFeature", columnRenameFeature: "columnRenameFeature", columnReorderFeature: "columnReorderFeature", columnResizeFeature: "columnResizeFeature", excelExporterFeature: "excelExporterFeature", fileDropFeature: "fileDropFeature", fillHandleFeature: "fillHandleFeature", filterFeature: "filterFeature", filterBarFeature: "filterBarFeature", groupFeature: "groupFeature", groupSummaryFeature: "groupSummaryFeature", headerMenuFeature: "headerMenuFeature", mergeCellsFeature: "mergeCellsFeature", pdfExportFeature: "pdfExportFeature", printFeature: "printFeature", quickFindFeature: "quickFindFeature", regionResizeFeature: "regionResizeFeature", rowCopyPasteFeature: "rowCopyPasteFeature", rowExpanderFeature: "rowExpanderFeature", rowReorderFeature: "rowReorderFeature", rowResizeFeature: "rowResizeFeature", searchFeature: "searchFeature", sortFeature: "sortFeature", splitFeature: "splitFeature", stickyCellsFeature: "stickyCellsFeature", stripeFeature: "stripeFeature", summaryFeature: "summaryFeature", treeFeature: "treeFeature", treeGroupFeature: "treeGroupFeature" }, outputs: { onBeforeCancelCellEdit: "onBeforeCancelCellEdit", onBeforeCellDelete: "onBeforeCellDelete", onBeforeCellEditStart: "onBeforeCellEditStart", onBeforeClose: "onBeforeClose", onBeforeColumnDragStart: "onBeforeColumnDragStart", onBeforeColumnDropFinalize: "onBeforeColumnDropFinalize", onBeforeColumnResize: "onBeforeColumnResize", onBeforeCopy: "onBeforeCopy", onBeforeDestroy: "onBeforeDestroy", onBeforeFillHandleDragStart: "onBeforeFillHandleDragStart", onBeforeFinishCellEdit: "onBeforeFinishCellEdit", onBeforeHide: "onBeforeHide", onBeforePaste: "onBeforePaste", onBeforePdfExport: "onBeforePdfExport", onBeforeRenderRow: "onBeforeRenderRow", onBeforeRenderRows: "onBeforeRenderRows", onBeforeSelectionChange: "onBeforeSelectionChange", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onBeforeToggleGroup: "onBeforeToggleGroup", onBeforeToggleNode: "onBeforeToggleNode", onCancelCellEdit: "onCancelCellEdit", onCatchAll: "onCatchAll", onCellClick: "onCellClick", onCellContextMenu: "onCellContextMenu", onCellDblClick: "onCellDblClick", onCellMenuBeforeShow: "onCellMenuBeforeShow", onCellMenuItem: "onCellMenuItem", onCellMenuShow: "onCellMenuShow", onCellMenuToggleItem: "onCellMenuToggleItem", onCellMouseEnter: "onCellMouseEnter", onCellMouseLeave: "onCellMouseLeave", onCellMouseOut: "onCellMouseOut", onCellMouseOver: "onCellMouseOver", onCollapse: "onCollapse", onCollapseNode: "onCollapseNode", onColumnDrag: "onColumnDrag", onColumnDragStart: "onColumnDragStart", onColumnDrop: "onColumnDrop", onColumnResize: "onColumnResize", onColumnResizeStart: "onColumnResizeStart", onCompare: "onCompare", onContextMenuItem: "onContextMenuItem", onContextMenuToggleItem: "onContextMenuToggleItem", onCopy: "onCopy", onDataChange: "onDataChange", onDestroy: "onDestroy", onDragSelecting: "onDragSelecting", onExpand: "onExpand", onExpandNode: "onExpandNode", onFileDrop: "onFileDrop", onFillHandleBeforeDragFinalize: "onFillHandleBeforeDragFinalize", onFillHandleDrag: "onFillHandleDrag", onFillHandleDragAbort: "onFillHandleDragAbort", onFillHandleDragEnd: "onFillHandleDragEnd", onFillHandleDragStart: "onFillHandleDragStart", onFinishCellEdit: "onFinishCellEdit", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onGridRowAbort: "onGridRowAbort", onGridRowBeforeDragStart: "onGridRowBeforeDragStart", onGridRowBeforeDropFinalize: "onGridRowBeforeDropFinalize", onGridRowDrag: "onGridRowDrag", onGridRowDragStart: "onGridRowDragStart", onGridRowDrop: "onGridRowDrop", onHeaderClick: "onHeaderClick", onHeaderMenuBeforeShow: "onHeaderMenuBeforeShow", onHeaderMenuItem: "onHeaderMenuItem", onHeaderMenuShow: "onHeaderMenuShow", onHeaderMenuToggleItem: "onHeaderMenuToggleItem", onHide: "onHide", onMouseOut: "onMouseOut", onMouseOver: "onMouseOver", onPaint: "onPaint", onPaste: "onPaste", onPdfExport: "onPdfExport", onPointerOver: "onPointerOver", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onRenderRow: "onRenderRow", onRenderRows: "onRenderRows", onResize: "onResize", onResponsive: "onResponsive", onRestore: "onRestore", onRowMouseEnter: "onRowMouseEnter", onRowMouseLeave: "onRowMouseLeave", onScroll: "onScroll", onSelectionChange: "onSelectionChange", onSelectionModeChange: "onSelectionModeChange", onShow: "onShow", onSplit: "onSplit", onSplitterCollapseClick: "onSplitterCollapseClick", onSplitterDragEnd: "onSplitterDragEnd", onSplitterDragStart: "onSplitterDragStart", onSplitterExpandClick: "onSplitterExpandClick", onStartCellEdit: "onStartCellEdit", onStopCompare: "onStopCompare", onSubGridCollapse: "onSubGridCollapse", onSubGridExpand: "onSubGridExpand", onToggleGroup: "onToggleGroup", onToggleNode: "onToggleNode", onToolClick: "onToolClick", onUnsplit: "onUnsplit" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumVersionGridComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-version-grid',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], allowOver: [{
                type: Input
            }], anchorToTarget: [{
                type: Input
            }], animateRemovingRows: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoClose: [{
                type: Input
            }], autoHeight: [{
                type: Input
            }], autoHide: [{
                type: Input
            }], autoShow: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], closable: [{
                type: Input
            }], closeAction: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], contextMenuTriggerEvent: [{
                type: Input
            }], dateFormat: [{
                type: Input
            }], defaultRegion: [{
                type: Input
            }], destroyStore: [{
                type: Input
            }], disableGridColumnIdWarning: [{
                type: Input
            }], disableGridRowModelWarning: [{
                type: Input
            }], dismissDelay: [{
                type: Input
            }], dock: [{
                type: Input
            }], enableSticky: [{
                type: Input
            }], enableTextSelection: [{
                type: Input
            }], features: [{
                type: Input
            }], fillLastColumn: [{
                type: Input
            }], fixedRowHeight: [{
                type: Input
            }], focusOnToFront: [{
                type: Input
            }], footer: [{
                type: Input
            }], forElement: [{
                type: Input
            }], forSelector: [{
                type: Input
            }], fullRowRefresh: [{
                type: Input
            }], getHtml: [{
                type: Input
            }], getRowHeight: [{
                type: Input
            }], header: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], hideHorizontalScrollbar: [{
                type: Input
            }], hideOnDelegateChange: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], keyMap: [{
                type: Input
            }], listeners: [{
                type: Input
            }], loadingMsg: [{
                type: Input
            }], loadMask: [{
                type: Input
            }], loadMaskDefaults: [{
                type: Input
            }], loadMaskError: [{
                type: Input
            }], localizable: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maximizable: [{
                type: Input
            }], modal: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], mouseOffsetX: [{
                type: Input
            }], mouseOffsetY: [{
                type: Input
            }], owner: [{
                type: Input
            }], plugins: [{
                type: Input
            }], preserveFocusOnDatasetChange: [{
                type: Input
            }], preserveScrollOnDatasetChange: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], project: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], resizeToFitIncludesHeader: [{
                type: Input
            }], responsiveLevels: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollerClass: [{
                type: Input
            }], scrollManager: [{
                type: Input
            }], showDirty: [{
                type: Input
            }], showOnClick: [{
                type: Input
            }], showOnHover: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], subGridConfigs: [{
                type: Input
            }], syncMask: [{
                type: Input
            }], tab: [{
                type: Input
            }], tbar: [{
                type: Input
            }], trackMouse: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], animateTreeNodeToggle: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], columnLines: [{
                type: Input
            }], columns: [{
                type: Input
            }], comparingVersionId: [{
                type: Input
            }], data: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], emptyText: [{
                type: Input
            }], enableUndoRedoKeys: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], hideFooters: [{
                type: Input
            }], hideHeaders: [{
                type: Input
            }], hoverDelay: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], longPressTime: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximized: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], preserveScroll: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowLines: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], showNamedVersionsOnly: [{
                type: Input
            }], showUnattachedTransactions: [{
                type: Input
            }], showVersions: [{
                type: Input
            }], store: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], width: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], selectedCell: [{
                type: Input
            }], selectedCells: [{
                type: Input
            }], selectedRecord: [{
                type: Input
            }], selectedRecords: [{
                type: Input
            }], selectedRows: [{
                type: Input
            }], state: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], onBeforeCancelCellEdit: [{
                type: Output
            }], onBeforeCellDelete: [{
                type: Output
            }], onBeforeCellEditStart: [{
                type: Output
            }], onBeforeClose: [{
                type: Output
            }], onBeforeColumnDragStart: [{
                type: Output
            }], onBeforeColumnDropFinalize: [{
                type: Output
            }], onBeforeColumnResize: [{
                type: Output
            }], onBeforeCopy: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeFillHandleDragStart: [{
                type: Output
            }], onBeforeFinishCellEdit: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforePaste: [{
                type: Output
            }], onBeforePdfExport: [{
                type: Output
            }], onBeforeRenderRow: [{
                type: Output
            }], onBeforeRenderRows: [{
                type: Output
            }], onBeforeSelectionChange: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onBeforeToggleGroup: [{
                type: Output
            }], onBeforeToggleNode: [{
                type: Output
            }], onCancelCellEdit: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCellClick: [{
                type: Output
            }], onCellContextMenu: [{
                type: Output
            }], onCellDblClick: [{
                type: Output
            }], onCellMenuBeforeShow: [{
                type: Output
            }], onCellMenuItem: [{
                type: Output
            }], onCellMenuShow: [{
                type: Output
            }], onCellMenuToggleItem: [{
                type: Output
            }], onCellMouseEnter: [{
                type: Output
            }], onCellMouseLeave: [{
                type: Output
            }], onCellMouseOut: [{
                type: Output
            }], onCellMouseOver: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onCollapseNode: [{
                type: Output
            }], onColumnDrag: [{
                type: Output
            }], onColumnDragStart: [{
                type: Output
            }], onColumnDrop: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onColumnResizeStart: [{
                type: Output
            }], onCompare: [{
                type: Output
            }], onContextMenuItem: [{
                type: Output
            }], onContextMenuToggleItem: [{
                type: Output
            }], onCopy: [{
                type: Output
            }], onDataChange: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onDragSelecting: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onExpandNode: [{
                type: Output
            }], onFileDrop: [{
                type: Output
            }], onFillHandleBeforeDragFinalize: [{
                type: Output
            }], onFillHandleDrag: [{
                type: Output
            }], onFillHandleDragAbort: [{
                type: Output
            }], onFillHandleDragEnd: [{
                type: Output
            }], onFillHandleDragStart: [{
                type: Output
            }], onFinishCellEdit: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onGridRowAbort: [{
                type: Output
            }], onGridRowBeforeDragStart: [{
                type: Output
            }], onGridRowBeforeDropFinalize: [{
                type: Output
            }], onGridRowDrag: [{
                type: Output
            }], onGridRowDragStart: [{
                type: Output
            }], onGridRowDrop: [{
                type: Output
            }], onHeaderClick: [{
                type: Output
            }], onHeaderMenuBeforeShow: [{
                type: Output
            }], onHeaderMenuItem: [{
                type: Output
            }], onHeaderMenuShow: [{
                type: Output
            }], onHeaderMenuToggleItem: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onMouseOut: [{
                type: Output
            }], onMouseOver: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onPaste: [{
                type: Output
            }], onPdfExport: [{
                type: Output
            }], onPointerOver: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onRenderRow: [{
                type: Output
            }], onRenderRows: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onResponsive: [{
                type: Output
            }], onRestore: [{
                type: Output
            }], onRowMouseEnter: [{
                type: Output
            }], onRowMouseLeave: [{
                type: Output
            }], onScroll: [{
                type: Output
            }], onSelectionChange: [{
                type: Output
            }], onSelectionModeChange: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onSplit: [{
                type: Output
            }], onSplitterCollapseClick: [{
                type: Output
            }], onSplitterDragEnd: [{
                type: Output
            }], onSplitterDragStart: [{
                type: Output
            }], onSplitterExpandClick: [{
                type: Output
            }], onStartCellEdit: [{
                type: Output
            }], onStopCompare: [{
                type: Output
            }], onSubGridCollapse: [{
                type: Output
            }], onSubGridExpand: [{
                type: Output
            }], onToggleGroup: [{
                type: Output
            }], onToggleNode: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }], onUnsplit: [{
                type: Output
            }], cellCopyPasteFeature: [{
                type: Input
            }], cellEditFeature: [{
                type: Input
            }], cellMenuFeature: [{
                type: Input
            }], cellTooltipFeature: [{
                type: Input
            }], columnAutoWidthFeature: [{
                type: Input
            }], columnDragToolbarFeature: [{
                type: Input
            }], columnPickerFeature: [{
                type: Input
            }], columnRenameFeature: [{
                type: Input
            }], columnReorderFeature: [{
                type: Input
            }], columnResizeFeature: [{
                type: Input
            }], excelExporterFeature: [{
                type: Input
            }], fileDropFeature: [{
                type: Input
            }], fillHandleFeature: [{
                type: Input
            }], filterFeature: [{
                type: Input
            }], filterBarFeature: [{
                type: Input
            }], groupFeature: [{
                type: Input
            }], groupSummaryFeature: [{
                type: Input
            }], headerMenuFeature: [{
                type: Input
            }], mergeCellsFeature: [{
                type: Input
            }], pdfExportFeature: [{
                type: Input
            }], printFeature: [{
                type: Input
            }], quickFindFeature: [{
                type: Input
            }], regionResizeFeature: [{
                type: Input
            }], rowCopyPasteFeature: [{
                type: Input
            }], rowExpanderFeature: [{
                type: Input
            }], rowReorderFeature: [{
                type: Input
            }], rowResizeFeature: [{
                type: Input
            }], searchFeature: [{
                type: Input
            }], sortFeature: [{
                type: Input
            }], splitFeature: [{
                type: Input
            }], stickyCellsFeature: [{
                type: Input
            }], stripeFeature: [{
                type: Input
            }], summaryFeature: [{
                type: Input
            }], treeFeature: [{
                type: Input
            }], treeGroupFeature: [{
                type: Input
            }] } });

/**
 * Angular wrapper for Bryntum ViewPresetCombo
 */
class BryntumViewPresetComboComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onAction = new EventEmitter();
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onChange = new EventEmitter();
        this.onClear = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onTrigger = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumViewPresetComboComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumViewPresetComboComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumViewPresetComboComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumViewPresetComboComponent.instanceClass = ViewPresetCombo;
BryntumViewPresetComboComponent.instanceName = 'ViewPresetCombo';
BryntumViewPresetComboComponent.bryntumEvents = [
    'onAction',
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onChange',
    'onClear',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onTrigger'
];
BryntumViewPresetComboComponent.bryntumFeatureNames = [];
BryntumViewPresetComboComponent.bryntumConfigs = BryntumViewPresetComboComponent.bryntumFeatureNames.concat([
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'badge',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearTextOnSelection',
    'client',
    'cls',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'editable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'hidePickerOnSelect',
    'hint',
    'hintHtml',
    'id',
    'ignoreParentReadOnly',
    'inputAlign',
    'inputAttributes',
    'inputWidth',
    'insertBefore',
    'insertFirst',
    'label',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'name',
    'overlayAnchor',
    'owner',
    'pickerWidth',
    'positioned',
    'presets',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tabIndex',
    'textAlign',
    'ui',
    'useFixedDuration',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumViewPresetComboComponent.bryntumConfigsOnly = [
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'clearTextOnSelection',
    'client',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'hidePickerOnSelect',
    'hint',
    'hintHtml',
    'ignoreParentReadOnly',
    'inputAlign',
    'inputAttributes',
    'inputWidth',
    'labelCls',
    'labelPosition',
    'labels',
    'labelWidth',
    'listCls',
    'listeners',
    'listItemTpl',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'name',
    'overlayAnchor',
    'owner',
    'pickerWidth',
    'positioned',
    'presets',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tabIndex',
    'textAlign',
    'ui',
    'useFixedDuration',
    'weight'
];
BryntumViewPresetComboComponent.bryntumProps = BryntumViewPresetComboComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'badge',
    'cls',
    'disabled',
    'editable',
    'extraData',
    'flex',
    'height',
    'hidden',
    'id',
    'input',
    'insertBefore',
    'insertFirst',
    'label',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'picker',
    'readOnly',
    'tooltip',
    'value',
    'width',
    'x',
    'y'
]);
BryntumViewPresetComboComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumViewPresetComboComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumViewPresetComboComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumViewPresetComboComponent, selector: "bryntum-view-preset-combo", inputs: { align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", clearTextOnSelection: "clearTextOnSelection", client: "client", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", hidePickerOnSelect: "hidePickerOnSelect", hint: "hint", hintHtml: "hintHtml", ignoreParentReadOnly: "ignoreParentReadOnly", inputAlign: "inputAlign", inputAttributes: "inputAttributes", inputWidth: "inputWidth", labelCls: "labelCls", labelPosition: "labelPosition", labels: "labels", labelWidth: "labelWidth", listCls: "listCls", listeners: "listeners", listItemTpl: "listItemTpl", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", name: "name", overlayAnchor: "overlayAnchor", owner: "owner", pickerWidth: "pickerWidth", positioned: "positioned", presets: "presets", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tabIndex: "tabIndex", textAlign: "textAlign", ui: "ui", useFixedDuration: "useFixedDuration", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", badge: "badge", cls: "cls", disabled: "disabled", editable: "editable", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", label: "label", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize", input: "input", picker: "picker", tooltip: "tooltip", value: "value" }, outputs: { onAction: "onAction", onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onChange: "onChange", onClear: "onClear", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onTrigger: "onTrigger" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumViewPresetComboComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-view-preset-combo',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], clearTextOnSelection: [{
                type: Input
            }], client: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hidePickerOnSelect: [{
                type: Input
            }], hint: [{
                type: Input
            }], hintHtml: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], inputAlign: [{
                type: Input
            }], inputAttributes: [{
                type: Input
            }], inputWidth: [{
                type: Input
            }], labelCls: [{
                type: Input
            }], labelPosition: [{
                type: Input
            }], labels: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], listCls: [{
                type: Input
            }], listeners: [{
                type: Input
            }], listItemTpl: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], name: [{
                type: Input
            }], overlayAnchor: [{
                type: Input
            }], owner: [{
                type: Input
            }], pickerWidth: [{
                type: Input
            }], positioned: [{
                type: Input
            }], presets: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], ui: [{
                type: Input
            }], useFixedDuration: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], badge: [{
                type: Input
            }], cls: [{
                type: Input
            }], disabled: [{
                type: Input
            }], editable: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], label: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], input: [{
                type: Input
            }], picker: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], value: [{
                type: Input
            }], onAction: [{
                type: Output
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onClear: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onTrigger: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum Widget
 */
class BryntumWidgetComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onShow = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumWidgetComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumWidgetComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumWidgetComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumWidgetComponent.instanceClass = Widget;
BryntumWidgetComponent.instanceName = 'Widget';
BryntumWidgetComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeShow',
    'onCatchAll',
    'onDestroy',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onShow'
];
BryntumWidgetComponent.bryntumFeatureNames = [];
BryntumWidgetComponent.bryntumConfigs = BryntumWidgetComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'height',
    'hidden',
    'hideAnimation',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'title',
    'tooltip',
    'ui',
    'weight',
    'width',
    'x',
    'y'
]);
BryntumWidgetComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'dock',
    'draggable',
    'floating',
    'hideAnimation',
    'htmlCls',
    'ignoreParentReadOnly',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'monitorResize',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'tab',
    'tag',
    'textAlign',
    'title',
    'ui',
    'weight'
];
BryntumWidgetComponent.bryntumProps = BryntumWidgetComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'scrollable',
    'tooltip',
    'width',
    'x',
    'y'
]);
BryntumWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumWidgetComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumWidgetComponent, selector: "bryntum-widget", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", dock: "dock", draggable: "draggable", floating: "floating", hideAnimation: "hideAnimation", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", monitorResize: "monitorResize", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", tab: "tab", tag: "tag", textAlign: "textAlign", title: "title", ui: "ui", weight: "weight", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", scrollable: "scrollable", tooltip: "tooltip", width: "width", x: "x", y: "y", accessibility: "accessibility", anchorSize: "anchorSize" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onDestroy: "onDestroy", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onShow: "onShow" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-widget',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], title: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onShow: [{
                type: Output
            }] } });

/**
 * Angular wrapper for Bryntum YearPicker
 */
class BryntumYearPickerComponent {
    constructor(element) {
        this.bryntumConfig = {
            adopt: undefined,
            appendTo: undefined,
            href: undefined,
            angularComponent: this,
            features: {},
            listeners: {}
        };
        // Events
        this.onBeforeDestroy = new EventEmitter();
        this.onBeforeHide = new EventEmitter();
        this.onBeforeSetRecord = new EventEmitter();
        this.onBeforeShow = new EventEmitter();
        this.onCatchAll = new EventEmitter();
        this.onCollapse = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.onExpand = new EventEmitter();
        this.onFocusIn = new EventEmitter();
        this.onFocusOut = new EventEmitter();
        this.onHide = new EventEmitter();
        this.onPaint = new EventEmitter();
        this.onReadOnly = new EventEmitter();
        this.onRecompose = new EventEmitter();
        this.onResize = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.onShow = new EventEmitter();
        this.onToolClick = new EventEmitter();
        this.elementRef = element;
    }
    /**
     * Create and append the underlying widget
     */
    ngOnInit() {
        const me = this, { elementRef, bryntumConfig } = me, { instanceClass, instanceName, bryntumConfigs, bryntumEvents } = BryntumYearPickerComponent;
        bryntumConfigs.filter(prop => prop in this).forEach(prop => {
            // @ts-ignore
            WrapperHelper.applyPropValue(bryntumConfig, prop, this[prop]);
            if (['features', 'config'].includes(prop)) {
                WrapperHelper.devWarningConfigProp(instanceName, prop);
            }
        });
        // @ts-ignore
        bryntumEvents.filter(event => this[event] && this[event].observers.length > 0).forEach(event => {
            const uncapitalize = (str) => str.charAt(0).toLowerCase() + str.slice(1), eventName = (str) => uncapitalize(str.slice(2));
            // @ts-ignore
            bryntumConfig.listeners[eventName(event)] = e => {
                // @ts-ignore
                me[event].emit(e);
                // EventEmitter does not return values in the normal way, work around it by setting `returnValue` flag
                // in Angular listeners
                return e.returnValue;
            };
        });
        // If component has no container specified in config then use adopt to Wrapper's element
        const containerParam = [
            'adopt',
            'appendTo',
            'insertAfter',
            'insertBefore'
            // @ts-ignore
        ].find(prop => bryntumConfig[prop]);
        if (!containerParam) {
            if (instanceName === 'Button' || elementRef.nativeElement.getRootNode() instanceof ShadowRoot) {
                // Button should always be <a> or <button> inside owner element
                bryntumConfig.appendTo = elementRef.nativeElement;
            }
            else {
                bryntumConfig.adopt = elementRef.nativeElement;
            }
        }
        else {
            WrapperHelper.devWarningContainer(instanceName, containerParam);
        }
        // @ts-ignore
        me.instance = instanceName === 'Widget' ? WidgetHelper.createWidget(bryntumConfig) : new instanceClass(bryntumConfig);
    }
    /**
     * Watch for changes
     * @param changes
     */
    ngOnChanges(changes) {
        const { instance } = this, { instanceName } = BryntumYearPickerComponent;
        if (!instance) {
            return;
        }
        // Iterate over all changes
        Object.entries(changes).forEach(([prop, change]) => {
            const newValue = change.currentValue, { instance } = this, { bryntumConfigsOnly, bryntumProps } = BryntumYearPickerComponent;
            if (bryntumProps.includes(prop)) {
                WrapperHelper.applyPropValue(instance, prop, newValue, false);
                if (bryntumConfigsOnly.includes(prop)) {
                    WrapperHelper.devWarningUpdateProp(instanceName, prop);
                }
            }
        });
    }
    /**
     * Destroy the component
     */
    ngOnDestroy() {
        // @ts-ignore
        this.instance && this.instance.destroy && this.instance.destroy();
    }
}
BryntumYearPickerComponent.instanceClass = YearPicker;
BryntumYearPickerComponent.instanceName = 'YearPicker';
BryntumYearPickerComponent.bryntumEvents = [
    'onBeforeDestroy',
    'onBeforeHide',
    'onBeforeSetRecord',
    'onBeforeShow',
    'onCatchAll',
    'onCollapse',
    'onDestroy',
    'onExpand',
    'onFocusIn',
    'onFocusOut',
    'onHide',
    'onPaint',
    'onRecompose',
    'onResize',
    'onSelect',
    'onShow',
    'onToolClick'
];
BryntumYearPickerComponent.bryntumFeatureNames = [];
BryntumYearPickerComponent.bryntumConfigs = BryntumYearPickerComponent.bryntumFeatureNames.concat([
    'adopt',
    'align',
    'alignSelf',
    'anchor',
    'appendTo',
    'ariaDescription',
    'ariaLabel',
    'ariaLive',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'cls',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'content',
    'contentElementCls',
    'dataset',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'disabled',
    'dock',
    'draggable',
    'extraData',
    'flex',
    'floating',
    'footer',
    'header',
    'height',
    'hidden',
    'hideAnimation',
    'hideWhenEmpty',
    'html',
    'htmlCls',
    'id',
    'ignoreParentReadOnly',
    'insertBefore',
    'insertFirst',
    'itemCls',
    'items',
    'layout',
    'layoutStyle',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'margin',
    'maskDefaults',
    'masked',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'maxYear',
    'minHeight',
    'minWidth',
    'minYear',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'readOnly',
    'record',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollable',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'startYear',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strictRecordMapping',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'title',
    'tools',
    'tooltip',
    'trapFocus',
    'ui',
    'weight',
    'width',
    'x',
    'y',
    'year',
    'yearButtonCount'
]);
BryntumYearPickerComponent.bryntumConfigsOnly = [
    'adopt',
    'align',
    'anchor',
    'ariaDescription',
    'ariaLabel',
    'autoUpdateRecord',
    'bbar',
    'bodyCls',
    'bubbleEvents',
    'callOnFunctions',
    'centered',
    'collapsed',
    'collapsible',
    'config',
    'constrainTo',
    'contentElementCls',
    'defaultBindProperty',
    'defaultFocus',
    'defaults',
    'dock',
    'draggable',
    'floating',
    'footer',
    'header',
    'hideAnimation',
    'hideWhenEmpty',
    'htmlCls',
    'ignoreParentReadOnly',
    'itemCls',
    'lazyItems',
    'listeners',
    'localeClass',
    'localizable',
    'localizableProperties',
    'maskDefaults',
    'masked',
    'maxYear',
    'minYear',
    'monitorResize',
    'namedItems',
    'owner',
    'positioned',
    'preventTooltipOnTouch',
    'relayStoreEvents',
    'ripple',
    'rootElement',
    'scrollAction',
    'showAnimation',
    'showTooltipWhenDisabled',
    'stateful',
    'statefulEvents',
    'stateId',
    'stateProvider',
    'strips',
    'tab',
    'tag',
    'tbar',
    'textAlign',
    'textContent',
    'trapFocus',
    'ui',
    'weight',
    'yearButtonCount'
];
BryntumYearPickerComponent.bryntumProps = BryntumYearPickerComponent.bryntumFeatureNames.concat([
    'accessibility',
    'alignSelf',
    'anchorSize',
    'appendTo',
    'ariaLive',
    'cls',
    'content',
    'dataset',
    'disabled',
    'extraData',
    'flex',
    'height',
    'hidden',
    'html',
    'id',
    'insertBefore',
    'insertFirst',
    'isSettingValues',
    'isValid',
    'items',
    'layout',
    'layoutStyle',
    'margin',
    'maxHeight',
    'maximizeOnMobile',
    'maxWidth',
    'minHeight',
    'minWidth',
    'readOnly',
    'record',
    'scrollable',
    'startYear',
    'state',
    'strictRecordMapping',
    'title',
    'tools',
    'tooltip',
    'value',
    'values',
    'width',
    'x',
    'y',
    'year'
]);
BryntumYearPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumYearPickerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BryntumYearPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.0", type: BryntumYearPickerComponent, selector: "bryntum-year-picker", inputs: { adopt: "adopt", align: "align", anchor: "anchor", ariaDescription: "ariaDescription", ariaLabel: "ariaLabel", autoUpdateRecord: "autoUpdateRecord", bbar: "bbar", bodyCls: "bodyCls", bubbleEvents: "bubbleEvents", callOnFunctions: "callOnFunctions", centered: "centered", collapsed: "collapsed", collapsible: "collapsible", config: "config", constrainTo: "constrainTo", contentElementCls: "contentElementCls", defaultBindProperty: "defaultBindProperty", defaultFocus: "defaultFocus", defaults: "defaults", dock: "dock", draggable: "draggable", floating: "floating", footer: "footer", header: "header", hideAnimation: "hideAnimation", hideWhenEmpty: "hideWhenEmpty", htmlCls: "htmlCls", ignoreParentReadOnly: "ignoreParentReadOnly", itemCls: "itemCls", lazyItems: "lazyItems", listeners: "listeners", localeClass: "localeClass", localizable: "localizable", localizableProperties: "localizableProperties", maskDefaults: "maskDefaults", masked: "masked", maxYear: "maxYear", minYear: "minYear", monitorResize: "monitorResize", namedItems: "namedItems", owner: "owner", positioned: "positioned", preventTooltipOnTouch: "preventTooltipOnTouch", relayStoreEvents: "relayStoreEvents", ripple: "ripple", rootElement: "rootElement", scrollAction: "scrollAction", showAnimation: "showAnimation", showTooltipWhenDisabled: "showTooltipWhenDisabled", stateful: "stateful", statefulEvents: "statefulEvents", stateId: "stateId", stateProvider: "stateProvider", strips: "strips", tab: "tab", tag: "tag", tbar: "tbar", textAlign: "textAlign", textContent: "textContent", trapFocus: "trapFocus", ui: "ui", weight: "weight", yearButtonCount: "yearButtonCount", alignSelf: "alignSelf", appendTo: "appendTo", ariaLive: "ariaLive", cls: "cls", content: "content", dataset: "dataset", disabled: "disabled", extraData: "extraData", flex: "flex", height: "height", hidden: "hidden", html: "html", id: "id", insertBefore: "insertBefore", insertFirst: "insertFirst", items: "items", layout: "layout", layoutStyle: "layoutStyle", margin: "margin", maxHeight: "maxHeight", maximizeOnMobile: "maximizeOnMobile", maxWidth: "maxWidth", minHeight: "minHeight", minWidth: "minWidth", readOnly: "readOnly", record: "record", scrollable: "scrollable", startYear: "startYear", strictRecordMapping: "strictRecordMapping", title: "title", tools: "tools", tooltip: "tooltip", width: "width", x: "x", y: "y", year: "year", accessibility: "accessibility", anchorSize: "anchorSize", isSettingValues: "isSettingValues", isValid: "isValid", state: "state", value: "value", values: "values" }, outputs: { onBeforeDestroy: "onBeforeDestroy", onBeforeHide: "onBeforeHide", onBeforeSetRecord: "onBeforeSetRecord", onBeforeShow: "onBeforeShow", onCatchAll: "onCatchAll", onCollapse: "onCollapse", onDestroy: "onDestroy", onExpand: "onExpand", onFocusIn: "onFocusIn", onFocusOut: "onFocusOut", onHide: "onHide", onPaint: "onPaint", onReadOnly: "onReadOnly", onRecompose: "onRecompose", onResize: "onResize", onSelect: "onSelect", onShow: "onShow", onToolClick: "onToolClick" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumYearPickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'bryntum-year-picker',
                    template: ''
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { adopt: [{
                type: Input
            }], align: [{
                type: Input
            }], anchor: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], autoUpdateRecord: [{
                type: Input
            }], bbar: [{
                type: Input
            }], bodyCls: [{
                type: Input
            }], bubbleEvents: [{
                type: Input
            }], callOnFunctions: [{
                type: Input
            }], centered: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapsible: [{
                type: Input
            }], config: [{
                type: Input
            }], constrainTo: [{
                type: Input
            }], contentElementCls: [{
                type: Input
            }], defaultBindProperty: [{
                type: Input
            }], defaultFocus: [{
                type: Input
            }], defaults: [{
                type: Input
            }], dock: [{
                type: Input
            }], draggable: [{
                type: Input
            }], floating: [{
                type: Input
            }], footer: [{
                type: Input
            }], header: [{
                type: Input
            }], hideAnimation: [{
                type: Input
            }], hideWhenEmpty: [{
                type: Input
            }], htmlCls: [{
                type: Input
            }], ignoreParentReadOnly: [{
                type: Input
            }], itemCls: [{
                type: Input
            }], lazyItems: [{
                type: Input
            }], listeners: [{
                type: Input
            }], localeClass: [{
                type: Input
            }], localizable: [{
                type: Input
            }], localizableProperties: [{
                type: Input
            }], maskDefaults: [{
                type: Input
            }], masked: [{
                type: Input
            }], maxYear: [{
                type: Input
            }], minYear: [{
                type: Input
            }], monitorResize: [{
                type: Input
            }], namedItems: [{
                type: Input
            }], owner: [{
                type: Input
            }], positioned: [{
                type: Input
            }], preventTooltipOnTouch: [{
                type: Input
            }], relayStoreEvents: [{
                type: Input
            }], ripple: [{
                type: Input
            }], rootElement: [{
                type: Input
            }], scrollAction: [{
                type: Input
            }], showAnimation: [{
                type: Input
            }], showTooltipWhenDisabled: [{
                type: Input
            }], stateful: [{
                type: Input
            }], statefulEvents: [{
                type: Input
            }], stateId: [{
                type: Input
            }], stateProvider: [{
                type: Input
            }], strips: [{
                type: Input
            }], tab: [{
                type: Input
            }], tag: [{
                type: Input
            }], tbar: [{
                type: Input
            }], textAlign: [{
                type: Input
            }], textContent: [{
                type: Input
            }], trapFocus: [{
                type: Input
            }], ui: [{
                type: Input
            }], weight: [{
                type: Input
            }], yearButtonCount: [{
                type: Input
            }], alignSelf: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], ariaLive: [{
                type: Input
            }], cls: [{
                type: Input
            }], content: [{
                type: Input
            }], dataset: [{
                type: Input
            }], disabled: [{
                type: Input
            }], extraData: [{
                type: Input
            }], flex: [{
                type: Input
            }], height: [{
                type: Input
            }], hidden: [{
                type: Input
            }], html: [{
                type: Input
            }], id: [{
                type: Input
            }], insertBefore: [{
                type: Input
            }], insertFirst: [{
                type: Input
            }], items: [{
                type: Input
            }], layout: [{
                type: Input
            }], layoutStyle: [{
                type: Input
            }], margin: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], maximizeOnMobile: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], record: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], startYear: [{
                type: Input
            }], strictRecordMapping: [{
                type: Input
            }], title: [{
                type: Input
            }], tools: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], width: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], year: [{
                type: Input
            }], accessibility: [{
                type: Input
            }], anchorSize: [{
                type: Input
            }], isSettingValues: [{
                type: Input
            }], isValid: [{
                type: Input
            }], state: [{
                type: Input
            }], value: [{
                type: Input
            }], values: [{
                type: Input
            }], onBeforeDestroy: [{
                type: Output
            }], onBeforeHide: [{
                type: Output
            }], onBeforeSetRecord: [{
                type: Output
            }], onBeforeShow: [{
                type: Output
            }], onCatchAll: [{
                type: Output
            }], onCollapse: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }], onExpand: [{
                type: Output
            }], onFocusIn: [{
                type: Output
            }], onFocusOut: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onPaint: [{
                type: Output
            }], onReadOnly: [{
                type: Output
            }], onRecompose: [{
                type: Output
            }], onResize: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], onShow: [{
                type: Output
            }], onToolClick: [{
                type: Output
            }] } });

/**
* Angular Bryntum Scheduler Pro Shared module
*/
class BryntumSchedulerProModule {
}
BryntumSchedulerProModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerProModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
BryntumSchedulerProModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerProModule, declarations: [BryntumFullscreenButtonComponent,
        BryntumDemoHeaderComponent,
        BryntumButtonComponent,
        BryntumButtonGroupComponent,
        BryntumCalendarFieldComponent,
        BryntumCheckboxComponent,
        BryntumChipViewComponent,
        BryntumColorFieldComponent,
        BryntumColorPickerComponent,
        BryntumComboComponent,
        BryntumConstraintTypePickerComponent,
        BryntumContainerComponent,
        BryntumCycleResolutionPopupComponent,
        BryntumDateFieldComponent,
        BryntumDatePickerComponent,
        BryntumDateTimeFieldComponent,
        BryntumDependencyTypePickerComponent,
        BryntumDisplayFieldComponent,
        BryntumDurationFieldComponent,
        BryntumEffortFieldComponent,
        BryntumEndDateFieldComponent,
        BryntumEventColorFieldComponent,
        BryntumEventColorPickerComponent,
        BryntumFieldFilterPickerComponent,
        BryntumFieldFilterPickerGroupComponent,
        BryntumFileFieldComponent,
        BryntumFilePickerComponent,
        BryntumFilterFieldComponent,
        BryntumGanttTaskEditorComponent,
        BryntumGridComponent,
        BryntumGridBaseComponent,
        BryntumGridFieldFilterPickerComponent,
        BryntumGridFieldFilterPickerGroupComponent,
        BryntumGroupBarComponent,
        BryntumLabelComponent,
        BryntumListComponent,
        BryntumMenuComponent,
        BryntumModelComboComponent,
        BryntumNumberFieldComponent,
        BryntumPagingToolbarComponent,
        BryntumPanelComponent,
        BryntumPasswordFieldComponent,
        BryntumProjectComboComponent,
        BryntumSchedulerProProjectModelComponent,
        BryntumProjectModelComponent,
        BryntumRadioComponent,
        BryntumRadioGroupComponent,
        BryntumResourceComboComponent,
        BryntumResourceFilterComponent,
        BryntumResourceHistogramComponent,
        BryntumResourceUtilizationComponent,
        BryntumSchedulerComponent,
        BryntumSchedulerBaseComponent,
        BryntumSchedulerDatePickerComponent,
        BryntumSchedulerProComponent,
        BryntumSchedulerProBaseComponent,
        BryntumSchedulerTaskEditorComponent,
        BryntumSchedulingDirectionPickerComponent,
        BryntumSchedulingIssueResolutionPopupComponent,
        BryntumSchedulingModePickerComponent,
        BryntumSliderComponent,
        BryntumSlideToggleComponent,
        BryntumSplitterComponent,
        BryntumStartDateFieldComponent,
        BryntumTabPanelComponent,
        BryntumTextAreaFieldComponent,
        BryntumTextAreaPickerFieldComponent,
        BryntumTextFieldComponent,
        BryntumTimeFieldComponent,
        BryntumTimelineComponent,
        BryntumTimelineHistogramComponent,
        BryntumTimePickerComponent,
        BryntumToolbarComponent,
        BryntumTreeComboComponent,
        BryntumTreeGridComponent,
        BryntumUndoRedoComponent,
        BryntumVersionGridComponent,
        BryntumViewPresetComboComponent,
        BryntumWidgetComponent,
        BryntumYearPickerComponent], exports: [BryntumFullscreenButtonComponent,
        BryntumDemoHeaderComponent,
        BryntumButtonComponent,
        BryntumButtonGroupComponent,
        BryntumCalendarFieldComponent,
        BryntumCheckboxComponent,
        BryntumChipViewComponent,
        BryntumColorFieldComponent,
        BryntumColorPickerComponent,
        BryntumComboComponent,
        BryntumConstraintTypePickerComponent,
        BryntumContainerComponent,
        BryntumCycleResolutionPopupComponent,
        BryntumDateFieldComponent,
        BryntumDatePickerComponent,
        BryntumDateTimeFieldComponent,
        BryntumDependencyTypePickerComponent,
        BryntumDisplayFieldComponent,
        BryntumDurationFieldComponent,
        BryntumEffortFieldComponent,
        BryntumEndDateFieldComponent,
        BryntumEventColorFieldComponent,
        BryntumEventColorPickerComponent,
        BryntumFieldFilterPickerComponent,
        BryntumFieldFilterPickerGroupComponent,
        BryntumFileFieldComponent,
        BryntumFilePickerComponent,
        BryntumFilterFieldComponent,
        BryntumGanttTaskEditorComponent,
        BryntumGridComponent,
        BryntumGridBaseComponent,
        BryntumGridFieldFilterPickerComponent,
        BryntumGridFieldFilterPickerGroupComponent,
        BryntumGroupBarComponent,
        BryntumLabelComponent,
        BryntumListComponent,
        BryntumMenuComponent,
        BryntumModelComboComponent,
        BryntumNumberFieldComponent,
        BryntumPagingToolbarComponent,
        BryntumPanelComponent,
        BryntumPasswordFieldComponent,
        BryntumProjectComboComponent,
        BryntumSchedulerProProjectModelComponent,
        BryntumProjectModelComponent,
        BryntumRadioComponent,
        BryntumRadioGroupComponent,
        BryntumResourceComboComponent,
        BryntumResourceFilterComponent,
        BryntumResourceHistogramComponent,
        BryntumResourceUtilizationComponent,
        BryntumSchedulerComponent,
        BryntumSchedulerBaseComponent,
        BryntumSchedulerDatePickerComponent,
        BryntumSchedulerProComponent,
        BryntumSchedulerProBaseComponent,
        BryntumSchedulerTaskEditorComponent,
        BryntumSchedulingDirectionPickerComponent,
        BryntumSchedulingIssueResolutionPopupComponent,
        BryntumSchedulingModePickerComponent,
        BryntumSliderComponent,
        BryntumSlideToggleComponent,
        BryntumSplitterComponent,
        BryntumStartDateFieldComponent,
        BryntumTabPanelComponent,
        BryntumTextAreaFieldComponent,
        BryntumTextAreaPickerFieldComponent,
        BryntumTextFieldComponent,
        BryntumTimeFieldComponent,
        BryntumTimelineComponent,
        BryntumTimelineHistogramComponent,
        BryntumTimePickerComponent,
        BryntumToolbarComponent,
        BryntumTreeComboComponent,
        BryntumTreeGridComponent,
        BryntumUndoRedoComponent,
        BryntumVersionGridComponent,
        BryntumViewPresetComboComponent,
        BryntumWidgetComponent,
        BryntumYearPickerComponent] });
BryntumSchedulerProModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerProModule, imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.0", ngImport: i0, type: BryntumSchedulerProModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        BryntumFullscreenButtonComponent,
                        BryntumDemoHeaderComponent,
                        BryntumButtonComponent,
                        BryntumButtonGroupComponent,
                        BryntumCalendarFieldComponent,
                        BryntumCheckboxComponent,
                        BryntumChipViewComponent,
                        BryntumColorFieldComponent,
                        BryntumColorPickerComponent,
                        BryntumComboComponent,
                        BryntumConstraintTypePickerComponent,
                        BryntumContainerComponent,
                        BryntumCycleResolutionPopupComponent,
                        BryntumDateFieldComponent,
                        BryntumDatePickerComponent,
                        BryntumDateTimeFieldComponent,
                        BryntumDependencyTypePickerComponent,
                        BryntumDisplayFieldComponent,
                        BryntumDurationFieldComponent,
                        BryntumEffortFieldComponent,
                        BryntumEndDateFieldComponent,
                        BryntumEventColorFieldComponent,
                        BryntumEventColorPickerComponent,
                        BryntumFieldFilterPickerComponent,
                        BryntumFieldFilterPickerGroupComponent,
                        BryntumFileFieldComponent,
                        BryntumFilePickerComponent,
                        BryntumFilterFieldComponent,
                        BryntumGanttTaskEditorComponent,
                        BryntumGridComponent,
                        BryntumGridBaseComponent,
                        BryntumGridFieldFilterPickerComponent,
                        BryntumGridFieldFilterPickerGroupComponent,
                        BryntumGroupBarComponent,
                        BryntumLabelComponent,
                        BryntumListComponent,
                        BryntumMenuComponent,
                        BryntumModelComboComponent,
                        BryntumNumberFieldComponent,
                        BryntumPagingToolbarComponent,
                        BryntumPanelComponent,
                        BryntumPasswordFieldComponent,
                        BryntumProjectComboComponent,
                        BryntumSchedulerProProjectModelComponent,
                        BryntumProjectModelComponent,
                        BryntumRadioComponent,
                        BryntumRadioGroupComponent,
                        BryntumResourceComboComponent,
                        BryntumResourceFilterComponent,
                        BryntumResourceHistogramComponent,
                        BryntumResourceUtilizationComponent,
                        BryntumSchedulerComponent,
                        BryntumSchedulerBaseComponent,
                        BryntumSchedulerDatePickerComponent,
                        BryntumSchedulerProComponent,
                        BryntumSchedulerProBaseComponent,
                        BryntumSchedulerTaskEditorComponent,
                        BryntumSchedulingDirectionPickerComponent,
                        BryntumSchedulingIssueResolutionPopupComponent,
                        BryntumSchedulingModePickerComponent,
                        BryntumSliderComponent,
                        BryntumSlideToggleComponent,
                        BryntumSplitterComponent,
                        BryntumStartDateFieldComponent,
                        BryntumTabPanelComponent,
                        BryntumTextAreaFieldComponent,
                        BryntumTextAreaPickerFieldComponent,
                        BryntumTextFieldComponent,
                        BryntumTimeFieldComponent,
                        BryntumTimelineComponent,
                        BryntumTimelineHistogramComponent,
                        BryntumTimePickerComponent,
                        BryntumToolbarComponent,
                        BryntumTreeComboComponent,
                        BryntumTreeGridComponent,
                        BryntumUndoRedoComponent,
                        BryntumVersionGridComponent,
                        BryntumViewPresetComboComponent,
                        BryntumWidgetComponent,
                        BryntumYearPickerComponent
                    ],
                    imports: [],
                    exports: [
                        BryntumFullscreenButtonComponent,
                        BryntumDemoHeaderComponent,
                        BryntumButtonComponent,
                        BryntumButtonGroupComponent,
                        BryntumCalendarFieldComponent,
                        BryntumCheckboxComponent,
                        BryntumChipViewComponent,
                        BryntumColorFieldComponent,
                        BryntumColorPickerComponent,
                        BryntumComboComponent,
                        BryntumConstraintTypePickerComponent,
                        BryntumContainerComponent,
                        BryntumCycleResolutionPopupComponent,
                        BryntumDateFieldComponent,
                        BryntumDatePickerComponent,
                        BryntumDateTimeFieldComponent,
                        BryntumDependencyTypePickerComponent,
                        BryntumDisplayFieldComponent,
                        BryntumDurationFieldComponent,
                        BryntumEffortFieldComponent,
                        BryntumEndDateFieldComponent,
                        BryntumEventColorFieldComponent,
                        BryntumEventColorPickerComponent,
                        BryntumFieldFilterPickerComponent,
                        BryntumFieldFilterPickerGroupComponent,
                        BryntumFileFieldComponent,
                        BryntumFilePickerComponent,
                        BryntumFilterFieldComponent,
                        BryntumGanttTaskEditorComponent,
                        BryntumGridComponent,
                        BryntumGridBaseComponent,
                        BryntumGridFieldFilterPickerComponent,
                        BryntumGridFieldFilterPickerGroupComponent,
                        BryntumGroupBarComponent,
                        BryntumLabelComponent,
                        BryntumListComponent,
                        BryntumMenuComponent,
                        BryntumModelComboComponent,
                        BryntumNumberFieldComponent,
                        BryntumPagingToolbarComponent,
                        BryntumPanelComponent,
                        BryntumPasswordFieldComponent,
                        BryntumProjectComboComponent,
                        BryntumSchedulerProProjectModelComponent,
                        BryntumProjectModelComponent,
                        BryntumRadioComponent,
                        BryntumRadioGroupComponent,
                        BryntumResourceComboComponent,
                        BryntumResourceFilterComponent,
                        BryntumResourceHistogramComponent,
                        BryntumResourceUtilizationComponent,
                        BryntumSchedulerComponent,
                        BryntumSchedulerBaseComponent,
                        BryntumSchedulerDatePickerComponent,
                        BryntumSchedulerProComponent,
                        BryntumSchedulerProBaseComponent,
                        BryntumSchedulerTaskEditorComponent,
                        BryntumSchedulingDirectionPickerComponent,
                        BryntumSchedulingIssueResolutionPopupComponent,
                        BryntumSchedulingModePickerComponent,
                        BryntumSliderComponent,
                        BryntumSlideToggleComponent,
                        BryntumSplitterComponent,
                        BryntumStartDateFieldComponent,
                        BryntumTabPanelComponent,
                        BryntumTextAreaFieldComponent,
                        BryntumTextAreaPickerFieldComponent,
                        BryntumTextFieldComponent,
                        BryntumTimeFieldComponent,
                        BryntumTimelineComponent,
                        BryntumTimelineHistogramComponent,
                        BryntumTimePickerComponent,
                        BryntumToolbarComponent,
                        BryntumTreeComboComponent,
                        BryntumTreeGridComponent,
                        BryntumUndoRedoComponent,
                        BryntumVersionGridComponent,
                        BryntumViewPresetComboComponent,
                        BryntumWidgetComponent,
                        BryntumYearPickerComponent
                    ]
                }]
        }] });

/*
 * Public API Surface of Bryntum Scheduler Pro
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BryntumButtonComponent, BryntumButtonGroupComponent, BryntumCalendarFieldComponent, BryntumCheckboxComponent, BryntumChipViewComponent, BryntumColorFieldComponent, BryntumColorPickerComponent, BryntumComboComponent, BryntumConstraintTypePickerComponent, BryntumContainerComponent, BryntumCycleResolutionPopupComponent, BryntumDateFieldComponent, BryntumDatePickerComponent, BryntumDateTimeFieldComponent, BryntumDemoHeaderComponent, BryntumDependencyTypePickerComponent, BryntumDisplayFieldComponent, BryntumDurationFieldComponent, BryntumEffortFieldComponent, BryntumEndDateFieldComponent, BryntumEventColorFieldComponent, BryntumEventColorPickerComponent, BryntumFieldFilterPickerComponent, BryntumFieldFilterPickerGroupComponent, BryntumFileFieldComponent, BryntumFilePickerComponent, BryntumFilterFieldComponent, BryntumFullscreenButtonComponent, BryntumGanttTaskEditorComponent, BryntumGridBaseComponent, BryntumGridComponent, BryntumGridFieldFilterPickerComponent, BryntumGridFieldFilterPickerGroupComponent, BryntumGroupBarComponent, BryntumLabelComponent, BryntumListComponent, BryntumMenuComponent, BryntumModelComboComponent, BryntumNumberFieldComponent, BryntumPagingToolbarComponent, BryntumPanelComponent, BryntumPasswordFieldComponent, BryntumProjectComboComponent, BryntumProjectModelComponent, BryntumRadioComponent, BryntumRadioGroupComponent, BryntumResourceComboComponent, BryntumResourceFilterComponent, BryntumResourceHistogramComponent, BryntumResourceUtilizationComponent, BryntumSchedulerBaseComponent, BryntumSchedulerComponent, BryntumSchedulerDatePickerComponent, BryntumSchedulerProBaseComponent, BryntumSchedulerProComponent, BryntumSchedulerProModule, BryntumSchedulerProProjectModelComponent, BryntumSchedulerTaskEditorComponent, BryntumSchedulingDirectionPickerComponent, BryntumSchedulingIssueResolutionPopupComponent, BryntumSchedulingModePickerComponent, BryntumSlideToggleComponent, BryntumSliderComponent, BryntumSplitterComponent, BryntumStartDateFieldComponent, BryntumTabPanelComponent, BryntumTextAreaFieldComponent, BryntumTextAreaPickerFieldComponent, BryntumTextFieldComponent, BryntumTimeFieldComponent, BryntumTimePickerComponent, BryntumTimelineComponent, BryntumTimelineHistogramComponent, BryntumToolbarComponent, BryntumTreeComboComponent, BryntumTreeGridComponent, BryntumUndoRedoComponent, BryntumVersionGridComponent, BryntumViewPresetComboComponent, BryntumWidgetComponent, BryntumYearPickerComponent };
